/**
 * JurisdictionManager Component
 * 
 * UX Enhancement: Master-Detail workspace layout for managing multiple jurisdictions.
 * Replaces vertical stacking with a sidebar list + tabbed detail panel.
 * 
 * Design: Swiss & High-Contrast archetype (per design_guidelines.json)
 * - Left Panel: Scrollable list of jurisdictions with status indicators
 * - Right Panel: Tabbed detail view (Contact Info, MLC & Workflow, Tax Summary)
 * 
 * NO BACKEND CHANGES - This is purely a frontend layout reorganization.
 * 
 * @author TitleFlow Team
 * @version Phase 3 (Mar 2026)
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import {
  JURISDICTION_TYPE_OPTIONS,
  isCanonicalJurisdictionType,
  getJurisdictionTypeLabel,
  getJurisdictionTypeOptionsForState,
  getJurisdictionTypeLabelForState,
} from '../../lib/jurisdictionTaxonomy';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { DatePickerInput } from '../ui/date-picker-input';
import MailAwayShipmentDetails from '../orders/MailAwayShipmentDetails';
import { CopyIconButton } from '../ui/copy-field';
import JurisdictionSelector, { requiresSubJurisdiction, SUB_JURISDICTION_TYPES, isMunicipalBoroughState } from './JurisdictionSelector';
import { RequestMissingJurisdictionAction, CAN_REQUEST_ROLES } from '../jurisdiction/RequestMissingJurisdictionDialog';
import { holdsAnyRole } from '../../utils/heldRoles';
import { CorrectionEntry } from '../jurisdiction/CorrectionEntry';
import { useOrderCorrections, groupCorrectionsByJurisdiction, CorrectionHistorySection } from '../jurisdiction/CorrectionHistory';
import { StatusReasonField } from '../orders/finalize/ReasonField';
import { validateParcelsForEmail } from '../../utils/parcelValidation';
import { fetchTaxEmailPreflight, parcelsForJurisdiction } from '../../lib/taxEmailApi';
import { TaxEmailConfirmDialog } from '../orders/TaxEmailConfirmDialog';
import { titleCaseExemption } from '../../utils/titleCaseExemption';
import CollectorGuidanceBanner from './CollectorGuidanceBanner';
import TaxCollectorPicker from './TaxCollectorPicker';
import OperationalDefaultsStrip from './OperationalDefaultsStrip';
import { ContactTile } from './ContactTile';
import TaxCategorySelect from './TaxCategorySelect';
import AuthorityTypeSelect from './AuthorityTypeSelect';
import { AnimatedHeight } from './AnimatedHeight';
import { resolveTaxCategoryCode } from '../../utils/jurisdictionCategory';
import { isCombinedBreakdownAvailable } from '../../lib/combinedPayoffVisibility';
import { getUserFromToken } from '../../utils/adminApi';
import {
  Plus,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  Phone,
  Printer,
  Users,
  GitBranch,
  DollarSign,
  Building,
  ChevronRight,
  MapPin,
  Mail,
  Globe,
  Clock,
  FileText,
  Calendar,
  GripVertical,
  Upload,
  HelpCircle,
  CheckCircle,
  Send,
  Eye,
  RefreshCw,
  File,
  Mic,
  AlertCircle,
  Info,
  RotateCcw,
  ClipboardCheck,
  ShieldAlert,
  Lock,
  Check,
  ChevronDown,
  ShieldCheck,
  ExternalLink,
  ChevronsUpDown,
  Link2,
  Paperclip,
  Landmark,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/alert-dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { toTitleCase, formatDateTimeShortET } from '../../utils/formatters';
import { isMichigan, getMiAvailableSeasons, getMichiganTaxDisplayLabel, getMichiganNotesText, getMichiganNotesConsolidated } from '../../utils/michiganTax';
import { generateSplitInstallmentNotes, generateSplitQuarterlyNotes } from '../../lib/taxNotesEngine';
import { fetchTaxNotesPreview } from '../../lib/taxNotesApi';
import {
  isSpecialtyInstallment,
  getAvailableSpecialtyOptions,
  appendSpecialtyNotes,
  formatTaxYearDisplay,
  jurisdictionSpecialtyLabel,
  partitionInstallments,
  getStandardInstallmentOptions,
  STANDARD_INSTALLMENT_LABELS,
  installmentPlanLabel,
  installmentPlanOrdinal,
  INSTALLMENT_PLAN_MAX,
  getSpecialtyCategory,
  toUtilityBillWording,
} from '../../lib/specialtyInstallments';
import { generateNewParcelNote } from '../../lib/newParcelNotes';
import { appendBreakdownNaNotes, computeCoveredYears } from '../../lib/paidBreakdownNaNotes';
import EvidenceDropZone from '../shared/EvidenceDropZone';
import { JurisdictionEmptyState, JurisdictionSidebarEmpty, JurisdictionSidebarArt } from './TaxEmptyState';

// Exemption pill definitions (must match TaxCertificatePanel constants)
const COMPACT_NA_OPTION = 'No Exemptions (N/A)';
const COMPACT_EXEMPTION_PILLS = [
  { stored: COMPACT_NA_OPTION, label: 'No Exemptions', isNone: true },
  { stored: 'Homestead exemptions', label: 'Homestead' },
  { stored: 'Senior citizen exemptions', label: 'Senior' },
  { stored: 'Military exemptions', label: 'Military' },
  { stored: 'Disability exemptions', label: 'Disability' },
];

// Additional exemptions shown inside the "+ More" popover
const MORE_EXEMPTION_OPTIONS = [
  { stored: 'Widow/Widower exemptions', label: 'Widow/Widower Exemption' },
  { stored: 'Agricultural exemptions', label: 'Agricultural Exemption' },
  { stored: 'Hardship exemptions', label: 'Hardship Exemption' },
  { stored: 'Nonprofit/Religious/Government exemptions', label: 'Nonprofit / Religious / Government Exemption' },
  { stored: 'Home Improvement exemptions', label: 'Home Improvement Exemption' },
  { stored: 'Renewable Energy exemptions', label: 'Renewable Energy Exemption' },
  { stored: 'STAR Program (NY)', label: 'STAR Program (NY)' },
  { stored: 'Specialized exemptions', label: 'Specialized Exemption' },
  // May 5, 2026 — `Other (Custom)` exposes the existing `exemption_other`
  // text-input scaffolding that was wired in earlier but had no toggle.
  // Selecting this reveals an inline "Enter exemption name" field and
  // the custom value substitutes "Other" in the PDF render.
  { stored: 'Other', label: 'Other (Custom)' },
];

// API base URL
const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Pay status options — only base intents (partial states are auto-derived)
// May 17, 2026: added "Refer Note" — surfaces only as a base dropdown
// option used in conjunction with the New Parcel toggle; treated as a
// non-numeric "see attached note" status. Validation gates that allow it
// live in `getJurisdictionStatus` (skipped when the parcel is_new_parcel).
// Jul 2026: added "Exempt" — user-friendly per-year exemption (e.g. priors
// paid, current year exempt). No Paid Date/Amount required; balance is $0.00
// and the certificate renders the year as EXEMPT.
// Jul 2026: added "N/A" (NA-PAY-STATUS-001) — tax amount unavailable (not
// yet released, collector won't provide, pending certification, etc.).
// Dates stay editable/required; monetary fields + Paid Date are neither
// required nor validated; the cert renders N/A in monetary cells and the
// notes engine emits NO auto explanation (processor documents the reason
// in Additional Notes).
const PAY_STATUS_OPTIONS = [
  { value: 'Due', label: 'Due' },
  { value: 'Paid', label: 'Paid' },
  { value: 'Exempt', label: 'Exempt' },
  { value: 'N/A', label: 'N/A' },
  { value: 'Delinquent', label: 'Delinquent' },
  { value: 'Refer Note', label: 'Refer Note' }
];

// Map any stored/derived status back to base dropdown value
const getBaseStatus = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'paid') return 'Paid';
  if (s === 'partially paid') return 'Due';
  if (s === 'delinquent' || s === 'partially delinquent' || s === 'partially dlq') return 'Delinquent';
  // May 17, 2026 — `Refer Note` (New Parcel mode) is a surfaced base option,
  // not a derived state; preserve verbatim.
  if (s === 'refer note') return 'Refer Note';
  // Jul 2026 — `Exempt` is a surfaced base option, preserved verbatim.
  if (s === 'exempt') return 'Exempt';
  // Jul 2026 — `N/A` is a surfaced base option, preserved verbatim.
  if (s === 'n/a') return 'N/A';
  return 'Due';
};

// Jul 2026 — NA-PAY-STATUS-001: explicit "tax amount unavailable" rows.
const isNaStatusRow = (inst) =>
  !!inst && String(inst.pay_status || '').trim().toLowerCase() === 'n/a';

// Check if the current date has reached or passed the DLQ date (current date >= DLQ date)
const isDlqDatePassed = (dlqDateVal) => {
  if (!dlqDateVal) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dateStr = typeof dlqDateVal === 'string' ? dlqDateVal : String(dlqDateVal);
  let dlqDate;
  if (dateStr.includes('/')) {
    const parts = dateStr.split('/');
    if (parts.length === 3) dlqDate = new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1]));
  } else if (dateStr.includes('-')) {
    const parts = dateStr.split('-');
    if (parts.length === 3) dlqDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }
  if (!dlqDate || isNaN(dlqDate.getTime())) return false;
  dlqDate.setHours(0, 0, 0, 0);
  return today >= dlqDate;
};

// Derive the effective pay status from financial data
// DLQ Date is a reference date — only triggers Delinquent if current date >= DLQ date AND balance > 0
// Paid always takes priority (balance <= 0 = Paid regardless of DLQ date)
const derivePayStatus = (inst) => {
  // Feb 2026 — Paid Breakdown N/A: the operator has explicitly declared
  // the row paid via a non-itemised payment. Treat as Paid regardless of
  // the (necessarily approximate) base_tax / paid_amount delta.
  if (inst && inst.paid_breakdown_na) return 'Paid';
  // Jul 2026 — Exempt is an explicit operator declaration, never derived
  // from financials. Short-circuit so $0 rows don't degrade to "Due".
  if (inst && String(inst.pay_status || '').trim().toLowerCase() === 'exempt') return 'Exempt';
  // Jul 2026 — N/A is likewise an explicit declaration (amount unavailable).
  if (inst && String(inst.pay_status || '').trim().toLowerCase() === 'n/a') return 'N/A';
  const baseTax = parseFloat(inst.base_tax) || 0;
  const discPenalty = parseFloat(inst.discount_penalty) || 0;
  const hasBaseTax = inst.base_tax !== null && inst.base_tax !== undefined && inst.base_tax !== '';
  const totalAmount = hasBaseTax ? rc(baseTax + discPenalty) : null;
  const paidAmt = parseFloat(inst.paid_amount) || 0;
  const balance = totalAmount !== null ? rc(totalAmount - paidAmt) : null;
  const dlqDate = inst.dlq_date;

  // Fully paid takes absolute priority regardless of DLQ date
  if (totalAmount !== null && balance !== null && balance <= 0) return 'Paid';

  // DLQ date only triggers delinquent if the date has actually passed
  const isDlqActive = dlqDate && isDlqDatePassed(dlqDate);

  if (paidAmt > 0 && isDlqActive && balance !== null && balance > 0) return 'Partially Delinquent';
  if (paidAmt > 0) return 'Partially Paid';
  if (isDlqActive && (totalAmount === null || balance > 0)) return 'Delinquent';
  return 'Due';
};

// Balance field color based on derived status
const getBalanceColorClass = (derivedStatus) => {
  switch (derivedStatus) {
    case 'Paid': return 'bg-slate-100 text-slate-500';
    case 'Due': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'Delinquent': return 'bg-red-50 text-red-700 border-red-200';
    case 'Partially Delinquent': return 'bg-red-50 text-red-700 border-red-200';
    case 'Partially Paid': return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'Exempt': return 'bg-sky-50 text-sky-700 border-sky-200';
    default: return 'bg-slate-100 text-slate-700';
  }
};

// Source options for Information Source (how info is obtained)
const SOURCE_OPTIONS = [
  { value: 'calling', label: 'Calling' },
  { value: 'online', label: 'Online' },
  { value: 'calling_online', label: 'Calling & Online' },
  { value: 'email', label: 'Email' },
  { value: 'fax', label: 'Fax' },
  { value: 'mail', label: 'Mail' },
  { value: 'other', label: 'Other' }
];

// Info Obtained Back options (how info was received back from jurisdiction - method only, no date)
const RESPONSE_METHOD_OPTIONS = [
  { value: 'mail', label: 'Mail' },
  { value: 'email', label: 'Email' },
  { value: 'fax', label: 'Fax' },
  { value: 'phone', label: 'Phone' },
  { value: 'online_portal', label: 'Online Portal' },
  { value: 'in_person', label: 'In Person' },
  { value: 'other', label: 'Other' }
];

/**
 * Cross-parcel source check: determines if a jurisdiction's source/evidence requirement
 * is already satisfied by the same jurisdiction in another parcel.
 * "Same jurisdiction" = matching jurisdiction_name (case-insensitive) + jurisdiction_type.
 */
const isSourceCoveredByAnotherParcel = (jurName, jurType, allParcelsData, currentParcelIndex) => {
  if (!jurName || !allParcelsData || allParcelsData.length <= 1) return false;
  const name = jurName.toLowerCase().trim();
  const type = (jurType || '').toLowerCase().trim();
  return allParcelsData.some((parcel, pIdx) => {
    if (pIdx === currentParcelIndex) return false;
    return (parcel.jurisdictions || []).some(j => {
      const jName = (j.jurisdiction_name || '').toLowerCase().trim();
      const jType = (j.jurisdiction_type || '').toLowerCase().trim();
      return jName === name && jType === type && j.source;
    });
  });
};

/**
 * Cross-parcel source LOOKUP: when the same jurisdiction has a source filled
 * in another parcel, return WHICH parcel and WHAT source/evidence — so the
 * UI can show "Source already captured from Parcel N (Online)" + offer a
 * one-click "Apply same source" button.
 *
 * Scope (Jun 1, 2026, deliberately narrow):
 *   • Inspects `source` and `source_evidence` ONLY.
 *   • Does NOT propagate MLC charges, MLC ETA, SASE Required,
 *     Info Obtained Back, collector details, or contact details —
 *     those remain per-parcel (or are managed via Jurisdiction Manager).
 *
 * Returns: { parcelIndex, source, source_evidence } | null
 */
const findSourceCoverageFromOtherParcel = (jurName, jurType, allParcelsData, currentParcelIndex) => {
  if (!jurName || !allParcelsData || allParcelsData.length <= 1) return null;
  const name = jurName.toLowerCase().trim();
  const type = (jurType || '').toLowerCase().trim();
  for (let pIdx = 0; pIdx < allParcelsData.length; pIdx++) {
    if (pIdx === currentParcelIndex) continue;
    const match = (allParcelsData[pIdx]?.jurisdictions || []).find(j => {
      const jName = (j.jurisdiction_name || '').toLowerCase().trim();
      const jType = (j.jurisdiction_type || '').toLowerCase().trim();
      return jName === name && jType === type && j.source;
    });
    if (match) {
      return {
        parcelIndex: pIdx,
        source: match.source,
        source_evidence: match.source_evidence || null,
      };
    }
  }
  return null;
};

// Currency formatting helper
const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]+/g, '')) : value;
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(num);
};

// Round to 2 decimal places — eliminates floating-point artifacts in monetary calculations
const rc = (v) => v !== null && v !== undefined && !isNaN(v) ? Math.round(v * 100) / 100 : v;

// Parse currency input (rounds to 2 decimals on output)
const parseCurrencyInput = (value) => {
  if (!value) return null;
  const cleaned = value.replace(/[^0-9.-]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : rc(num);
};

/**
 * LocalInput — text input with local state to prevent parent re-renders on every keystroke.
 * Commits value to parent only on blur. Syncs external value changes when not focused.
 */
const LocalInput = React.memo(({ value, onCommit, ...props }) => {
  const [localValue, setLocalValue] = useState(value ?? '');
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalValue(value ?? '');
    }
  }, [value]);

  const { onFocus: extOnFocus, onBlur: extOnBlur, onChange: _discarded, ...inputProps } = props;

  return (
    <Input
      {...inputProps}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onFocus={(e) => {
        isFocusedRef.current = true;
        extOnFocus?.(e);
      }}
      onBlur={(e) => {
        isFocusedRef.current = false;
        onCommit?.(localValue);
        extOnBlur?.(e);
      }}
    />
  );
});

/**
 * LocalCurrencyInput — currency input with local state.
 *
 * Display contract (fixed May 6, 2026 — client-reported inconsistency in
 * the Tax Certificate installment table):
 *   • When the input is NOT focused → display a fully formatted currency
 *     string via `formatCurrency()`, e.g. `$1,974.33`.
 *   • When the input IS focused     → display the raw editable number,
 *     e.g. `1974.33`, so the user can backspace/overtype cleanly
 *     without fighting the `$` / comma characters.
 *   • On blur → the parent is committed a parsed number (via
 *     `parseCurrencyInput`), and the visible text is re-formatted.
 *
 * Why this matters: the previous implementation called
 * `String(rc(value))` in both mounted + effect paths, which only rounds
 * to 2dp — it never adds `$` or commas. Orders saved with pre-formatted
 * string values like `"$1,912.29"` happened to display correctly by
 * accident (the `isNaN` branch in `rc` returned them untouched), while
 * orders saved with numeric values like `1974.33` displayed raw. The
 * same component was showing `$1,912.29` on one row and `1974.33` on
 * the next depending on storage type — the user-reported bug.
 */
const LocalCurrencyInput = React.memo(({ value, onCommit, ...props }) => {
  // Formatted display (e.g. "$1,974.33"). Falls back to '' for null/invalid.
  const toDisplay = (v) => formatCurrency(v);
  // Raw editable text (e.g. "1974.33"). Used while focused.
  const toRaw = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const num = typeof v === 'string' ? parseFloat(v.replace(/[^0-9.-]+/g, '')) : v;
    return isNaN(num) ? '' : String(rc(num));
  };

  const [localValue, setLocalValue] = useState(() => toDisplay(value));
  const isFocusedRef = useRef(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!isFocusedRef.current) {
      // Inlined to keep `value` as the only dep — avoids the need for
      // an eslint-disable comment (`react-hooks/exhaustive-deps` isn't
      // registered in this project's ESLint config; the disable-comment
      // itself would fail the build — see compile error logged May 6).
      setLocalValue(formatCurrency(value));
    }
  }, [value]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const { onFocus: extOnFocus, onBlur: extOnBlur, onChange: _discarded, ...inputProps } = props;

  return (
    <Input
      {...inputProps}
      value={localValue}
      onChange={(e) => {
        const newVal = e.target.value;
        setLocalValue(newVal);
        // Debounced commit — triggers recalculation while user is typing (300ms)
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          onCommit?.(parseCurrencyInput(newVal));
        }, 300);
      }}
      onFocus={(e) => {
        isFocusedRef.current = true;
        // Switch to raw editable digits so the user can type without
        // the `$` / commas getting in the way. Source of truth is the
        // parent-committed `value`, not the possibly-formatted localValue.
        setLocalValue(toRaw(value));
        extOnFocus?.(e);
      }}
      onBlur={(e) => {
        isFocusedRef.current = false;
        // Cancel any pending debounce and commit immediately on blur
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const committedVal = parseCurrencyInput(localValue);
        onCommit?.(committedVal);
        // Re-format the now-committed value for the rest state.
        setLocalValue(toDisplay(committedVal));
        extOnBlur?.(e);
      }}
    />
  );
});

/**
 * LocalTextarea — textarea with local state to prevent parent re-renders on every keystroke.
 */
const LocalTextarea = React.memo(({ value, onCommit, ...props }) => {
  const [localValue, setLocalValue] = useState(value ?? '');
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalValue(value ?? '');
    }
  }, [value]);

  const { onFocus: extOnFocus, onBlur: extOnBlur, onChange: _discarded, ...inputProps } = props;

  return (
    <Textarea
      {...inputProps}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onFocus={(e) => {
        isFocusedRef.current = true;
        extOnFocus?.(e);
      }}
      onBlur={(e) => {
        isFocusedRef.current = false;
        onCommit?.(localValue);
        extOnBlur?.(e);
      }}
    />
  );
});

/**
 * LocalDatePickerInput — DatePicker with local state to prevent parent re-renders on every keystroke.
 * Commits value to parent on blur or when a complete date is entered (calendar pick / auto-format).
 * The underlying DatePickerInput manages its own display state, keeping the input responsive.
 */
const LocalDatePickerInput = React.memo(({ value, onCommit, className, disabled, placeholder, 'data-testid': testId, ...rest }) => {
  const [localValue, setLocalValue] = useState(value ?? '');
  const localValueRef = useRef(value ?? '');
  const commitTimerRef = useRef(null);

  // Sync from parent when value changes externally
  useEffect(() => {
    const v = value ?? '';
    setLocalValue(v);
    localValueRef.current = v;
  }, [value]);

  // Cleanup timer on unmount
  useEffect(() => () => {
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
  }, []);

  return (
    <div
      onBlur={(e) => {
        // Only commit when focus leaves the entire date-picker (input + calendar button)
        if (e.currentTarget.contains(e.relatedTarget)) return;
        // Short delay: DatePickerInput's auto-format fires onChange during blur
        // before externalOnBlur, so we wait for localValueRef to capture the formatted value
        if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
        commitTimerRef.current = setTimeout(() => {
          onCommit?.(localValueRef.current);
        }, 30);
      }}
    >
      <DatePickerInput
        value={localValue}
        className={className}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={testId}
        {...rest}
        onChange={(e) => {
          const v = e?.target?.value ?? e;
          setLocalValue(v);
          localValueRef.current = v;
          // Commit immediately for complete dates (calendar selection or auto-format)
          if (typeof v === 'string' && v.length === 10 && /^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
            if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
            onCommit?.(v);
          }
        }}
      />
    </div>
  );
});


/**
 * Source Evidence Configuration
 * Defines allowed file types, limits, and categories for each source type
 */
const SOURCE_EVIDENCE_CONFIG = {
  calling: {
    maxFiles: 3,
    category: 'audio',
    allowedTypes: ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/mp4', 'audio/x-m4a', 'audio/ogg'],
    allowedExtensions: ['.mp3', '.wav', '.m4a', '.ogg'],
    acceptString: 'audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,audio/ogg,.mp3,.wav,.m4a,.ogg',
    maxSize: 50 * 1024 * 1024, // 50MB
    label: 'Call Recording',
    formatDisplay: 'MP3, WAV, M4A (max 50MB)'
  },
  online: {
    maxFiles: 3,
    category: 'image',
    allowedTypes: ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'],
    allowedExtensions: ['.png', '.jpg', '.jpeg', '.pdf'],
    acceptString: 'image/png,image/jpeg,image/jpg,application/pdf,.png,.jpg,.jpeg,.pdf',
    maxSize: 15 * 1024 * 1024,
    label: 'Screenshot',
    formatDisplay: 'PNG, JPG, PDF (max 15MB)'
  },
  email: {
    maxFiles: 3,
    category: 'email_evidence',
    allowedTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'message/rfc822'],
    allowedExtensions: ['.pdf', '.png', '.jpg', '.jpeg', '.eml'],
    acceptString: 'application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg,.eml',
    maxSize: 15 * 1024 * 1024,
    label: 'Email Evidence',
    formatDisplay: 'PDF, PNG, JPG, EML (max 15MB)'
  },
  fax: {
    maxFiles: 3,
    category: 'fax_evidence',
    allowedTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/tiff'],
    allowedExtensions: ['.pdf', '.png', '.jpg', '.jpeg', '.tif', '.tiff'],
    acceptString: 'application/pdf,image/png,image/jpeg,image/tiff,.pdf,.png,.jpg,.jpeg,.tif,.tiff',
    maxSize: 15 * 1024 * 1024,
    label: 'Fax Document',
    formatDisplay: 'PDF, PNG, JPG, TIFF (max 15MB)'
  },
  mail: {
    maxFiles: 3,
    category: 'mail_evidence',
    allowedTypes: ['application/pdf', 'image/png', 'image/jpeg'],
    allowedExtensions: ['.pdf', '.png', '.jpg', '.jpeg'],
    acceptString: 'application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg',
    maxSize: 15 * 1024 * 1024,
    label: 'Mail Evidence',
    formatDisplay: 'PDF, PNG, JPG (max 15MB)'
  },
  other: {
    maxFiles: 3,
    category: 'supporting_document',
    allowedTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg'],
    allowedExtensions: ['.pdf', '.doc', '.docx', '.png', '.jpg', '.jpeg'],
    acceptString: 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,.pdf,.doc,.docx,.png,.jpg,.jpeg',
    maxSize: 15 * 1024 * 1024,
    label: 'Supporting Document',
    formatDisplay: 'PDF, DOC, DOCX, PNG, JPG (max 15MB)'
  }
};

// Color themes for evidence sections
const EVIDENCE_THEMES = {
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', textMuted: 'text-purple-600', btnBg: 'hover:bg-purple-100', btnBorder: 'border-purple-300', btnText: 'text-purple-700', successBg: 'bg-purple-100' },
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-800', textMuted: 'text-indigo-600', btnBg: 'hover:bg-indigo-100', btnBorder: 'border-indigo-300', btnText: 'text-indigo-700', successBg: 'bg-indigo-100' },
  cyan: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-800', textMuted: 'text-cyan-600', btnBg: 'hover:bg-cyan-100', btnBorder: 'border-cyan-300', btnText: 'text-cyan-700', successBg: 'bg-cyan-100' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800', textMuted: 'text-orange-600', btnBg: 'hover:bg-orange-100', btnBorder: 'border-orange-300', btnText: 'text-orange-700', successBg: 'bg-orange-100' },
  green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', textMuted: 'text-green-600', btnBg: 'hover:bg-green-100', btnBorder: 'border-green-300', btnText: 'text-green-700', successBg: 'bg-green-100' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', textMuted: 'text-blue-600', btnBg: 'hover:bg-blue-100', btnBorder: 'border-blue-300', btnText: 'text-blue-700', successBg: 'bg-blue-100' }
};

/**
 * CompactEvidenceRow Component
 * Thin wrapper around the reusable <EvidenceDropZone/> for Tax Information
 * Source rows. Owns the business rules (backend endpoint, source_type wiring,
 * category mapping) and delegates ALL UI + drag-and-drop to the shared
 * component. Feb 22, 2026 (Session 21 Phase 1).
 */
const CompactEvidenceRow = ({
  files = [],
  sourceType,
  jurisdictionId,
  onFilesChange,
  readOnly,
  icon: IconComponent = Upload,
  label,
  orderId,
  helperText,
  minRequired = 0,
}) => {
  const config = SOURCE_EVIDENCE_CONFIG[sourceType] || SOURCE_EVIDENCE_CONFIG.other;
  const currentFiles = Array.isArray(files) ? files : [];

  // Feb 26, 2026 — A1 stale-closure fix. The `uploadHandler` below is recreated
  // on every render, but <EvidenceDropZone>'s sequential upload loop awaits the
  // SAME closure for all queued files. Without this ref, each iteration captured
  // the initial empty `currentFiles` and `onFilesChange([...currentFiles,…])`
  // overwrote prior appends, leaving only the LAST file visible in the panel
  // (while the backend correctly persisted all of them — see Files tab). The
  // ref always points at the most recent `files` prop, synchronized at render
  // time, so append/remove math is always against the live evidence list.
  const filesRef = useRef(currentFiles);
  filesRef.current = currentFiles;

  // Upload one file to the orders/files endpoint and return the uploaded
  // descriptor so <EvidenceDropZone> can show progress → done. The batch
  // success toast is emitted ONCE per drop by EvidenceDropZone (B1/B2);
  // we no longer emit a per-file "Uploaded (N/M)" toast here, which was
  // both misleading (always read "1/3" due to the stale closure) and noisy
  // for multi-file drops.
  const uploadHandler = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', config.category);
    formData.append('jurisdiction_id', jurisdictionId || '');
    formData.append('source_type', sourceType);

    const response = await fetch(`${API}/orders/${orderId}/files/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to upload file');
    }

    const uploadedFile = await response.json();
    const fileId = uploadedFile.file_id || uploadedFile.id;

    const newFile = {
      id: fileId,
      filename: file.name,
      size: file.size,
      url:
        uploadedFile.url ||
        uploadedFile.file_url ||
        `${API}/orders/${orderId}/files/${fileId}/download`,
      uploaded_at: new Date().toISOString(),
      category: config.category,
      source_type: sourceType,
    };

    // Always read from the live ref so each sequential upload appends to the
    // latest array, not the closure snapshot from the start of the batch.
    const latestFiles = Array.isArray(filesRef.current) ? filesRef.current : [];
    onFilesChange([...latestFiles, newFile]);
    return newFile;
  };

  // Delete handler: removes from local evidence state AND from the files
  // collection (so the order's Files tab stays in sync). Uses the same ref
  // so manual removals during in-flight uploads can't clobber state either.
  const removeHandler = async (fileId) => {
    const latestFiles = Array.isArray(filesRef.current) ? filesRef.current : [];
    const updated = latestFiles.filter((f) => f.id !== fileId);
    onFilesChange(updated);
    try {
      await fetch(`${API}/orders/${orderId}/files/${fileId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      // Notify the Files tab to refresh in case it's mounted in a sibling view.
      try {
        window.dispatchEvent(new CustomEvent('orderFilesChanged', {
          detail: { orderId, action: 'delete', fileId },
        }));
        // Bridge to the pre-existing TaxCertificatePanel listener that
        // owns the `orderFiles` array the evidence panel hydrates from.
        window.dispatchEvent(new CustomEvent('filesChangedInOrder', {
          detail: { orderId, action: 'delete', fileId },
        }));
      } catch (_) { /* noop */ }
    } catch (err) {
      console.error('Failed to delete file from files collection:', err);
    }
    toast.success('File removed');
  };

  const openAuthFile = (file) => {
    const { openAuthenticatedFile } = require('../../utils/filePreview');
    openAuthenticatedFile(file.url, file.filename);
  };

  return (
    <EvidenceDropZone
      label={label}
      icon={IconComponent}
      helperText={helperText}
      formatDisplay={config.formatDisplay}
      acceptString={config.acceptString}
      acceptedMimeTypes={config.allowedTypes}
      acceptedExtensions={config.allowedExtensions}
      maxFiles={config.maxFiles}
      maxSize={config.maxSize}
      minRequired={minRequired}
      existingFiles={currentFiles}
      uploadHandler={uploadHandler}
      removeHandler={removeHandler}
      viewAction={openAuthFile}
      viewOnly={readOnly}
      multiple
      testIdPrefix={`tax-evidence-${sourceType}`}
    />
  );
};

/**
 * CompactEvidenceSection Component  
 * Ultra-compact evidence section with all evidence types in rows
 */
const CompactEvidenceSection = ({
  source,
  sourceEvidence,
  jurisdictionId,
  jurisdictionName = '',
  jurisdictionType = '',
  onUpdate,
  readOnly,
  orderId,
  mailAwayMatch = null,
  orderFiles = [],
  allParcelsData = [],
  activeParcelIndex = 0,
}) => {
  // Feb 26, 2026 — Evidence-panel hydration fix.
  //
  // The Fulfillment evidence panel previously rendered only from the draft's
  // `source_evidence` block. That block is a denormalized copy persisted to
  // the tax_certificate_drafts collection — and it only gets updated on the
  // server when the user clicks Save. As a result:
  //   - Uploads done in a session that wasn't saved looked "lost" after page
  //     reload (Files tab still showed them — the backend `files` collection
  //     is the actual source of truth).
  //   - The user's testing reproduced this exact symptom: 3 files in Files
  //     tab, but only 1 chip + "1/3" counter in the evidence panel.
  //
  // The fix is to MERGE `source_evidence[type]` (in-flight session state) with
  // `orderFiles` filtered by `source_type === type && jurisdiction_id ===
  // jurisdictionId` (backend truth), deduped by file id. This way the panel
  // is a faithful view of the `files` collection for this jurisdiction +
  // source, while still picking up in-session writes immediately.
  //
  // Jun 2, 2026 — Cross-parcel evidence inheritance.
  //
  // Same-jurisdiction multi-parcel orders share evidence: when Parcel 1
  // uploads a portal screenshot for "New Castle County / county", Parcel 2's
  // identical jurisdiction row must show the same file without forcing the
  // user to re-upload. The file is stored once in the `files` collection
  // tagged with the uploader-parcel's jurisdiction_id, so plain `===`
  // filtering misses it from the sibling parcel's view.
  //
  // Resolution: build a SET of jurisdiction_ids that should be treated as
  // "this same jurisdiction" — the current row's id, plus every sibling
  // parcel's row matching by (name, type) case-insensitively. Pulled from
  // BOTH source_evidence draft state (in-flight) and orderFiles
  // (server truth) so a sibling's uploads appear immediately, even before
  // the draft is saved. Mirrors the "Source already captured from Parcel N"
  // banner predicate and the backend _is_source_covered_by_other_parcel().
  const _normName = (jurisdictionName || '').toLowerCase().trim();
  const _normType = (jurisdictionType || '').toLowerCase().trim();

  const _siblingJurisdictionIds = (() => {
    const ids = new Set();
    if (jurisdictionId) ids.add(jurisdictionId);
    if (!_normName || !Array.isArray(allParcelsData)) return ids;
    allParcelsData.forEach((p, pIdx) => {
      (p?.jurisdictions || []).forEach(j => {
        const jn = (j?.jurisdiction_name || '').toLowerCase().trim();
        const jt = (j?.jurisdiction_type || '').toLowerCase().trim();
        if (jn === _normName && jt === _normType && j?.id) {
          ids.add(j.id);
        }
      });
    });
    return ids;
  })();

  // Sibling parcel jurisdictions with the same name+type — used to pull
  // their in-flight source_evidence entries (uploads made in this session
  // before a save) into the current panel.
  const _siblingDraftEvidence = (type) => {
    const out = [];
    if (!_normName || !Array.isArray(allParcelsData)) return out;
    allParcelsData.forEach((p, pIdx) => {
      if (pIdx === activeParcelIndex) return;  // skip self — handled by sourceEvidence
      (p?.jurisdictions || []).forEach(j => {
        const jn = (j?.jurisdiction_name || '').toLowerCase().trim();
        const jt = (j?.jurisdiction_type || '').toLowerCase().trim();
        if (jn === _normName && jt === _normType) {
          const siblingFiles = getSourceEvidence(j?.source_evidence, type) || [];
          siblingFiles.forEach(f => { if (f && f.id) out.push(f); });
        }
      });
    });
    return out;
  };

  const getFiles = (type) => {
    const fromDraft = getSourceEvidence(sourceEvidence, type) || [];
    const fromSiblingDrafts = _siblingDraftEvidence(type);
    const fromFilesCollection = (Array.isArray(orderFiles) ? orderFiles : [])
      .filter(f => f && f.source_type === type && _siblingJurisdictionIds.has(f.jurisdiction_id));

    // Jun 2026 — Layer 1 integrity filter. A draft entry whose `id` is
    // NOT present in `orderFiles` (db.files truth) is an ORPHAN — the
    // underlying file row has been deleted (or was never written), so
    // its download URL is dead. Including it inflates the panel counter
    // past `maxFiles` and falsely blocks new uploads (see Jun 2026
    // ticket on order 87c07085-… where ghost entries `Portal print.pdf`
    // + `Supporting docs.pdf` pushed the count to 4/3).
    //
    // We compute the live-id set ACROSS THE WHOLE orderFiles list
    // (not just the source_type+jurisdiction-filtered view) so a draft
    // entry that legitimately exists in db.files under a different
    // source_type / jurisdiction is still kept — we only drop entries
    // whose id has no backing row at all.
    //
    // To avoid masking pre-save in-session uploads (which DO exist in
    // db.files but might not yet be in the orderFiles snapshot), we
    // ONLY apply the filter once `orderFiles` is a non-empty array.
    // First-page-load with `orderFiles=[]` falls through to the old
    // behaviour to preserve the Feb 26, 2026 hydration fix.
    const orderFilesArr = Array.isArray(orderFiles) ? orderFiles : [];
    const liveFileIds = orderFilesArr.length > 0
      ? new Set(orderFilesArr.map(f => f?.id || f?.file_id).filter(Boolean))
      : null;
    const isLiveOrUnknown = (entry) => {
      if (!liveFileIds) return true;          // skip filter when orderFiles not hydrated
      const eid = entry?.id || entry?.file_id;
      if (!eid) return true;                  // missing id → keep (defensive)
      return liveFileIds.has(eid);
    };

    // Dedupe by id. Priority: own draft → sibling draft → server files
    // (own draft entries have the freshest url/category from in-session
    // uploads; sibling drafts share the same shape; backend file entries
    // contribute canonical filename/uploaded_at for persisted files).
    const seen = new Map();
    for (const f of fromDraft) {
      if (f && f.id && isLiveOrUnknown(f)) seen.set(f.id, f);
    }
    for (const f of fromSiblingDrafts) {
      if (f && f.id && !seen.has(f.id) && isLiveOrUnknown(f)) seen.set(f.id, f);
    }
    for (const f of fromFilesCollection) {
      const fid = f.id || f.file_id;
      if (!fid || seen.has(fid)) continue;
      seen.set(fid, {
        id: fid,
        filename: f.original_filename || f.filename || f.name || 'file',
        size: f.file_size || f.size,
        url: f.url || f.file_url || `${API}/orders/${orderId}/files/${fid}/download`,
        uploaded_at: f.uploaded_at || f.created_at,
        category: f.category,
        source_type: f.source_type,
      });
    }
    return Array.from(seen.values());
  };

  const handleFilesChange = (sourceType, files) => {
    const newEvidence = updateSourceEvidence(sourceEvidence, sourceType, files);
    onUpdate('source_evidence', newEvidence);
  };

  // Get source icon based on type
  const getSourceIcon = (sourceType) => {
    switch (sourceType) {
      case 'calling': return Mic;
      case 'online': return Globe;
      case 'email': return Mail;
      case 'fax': return FileText;
      case 'mail': return Mail;
      default: return FileText;
    }
  };

  // Get source label
  const getSourceLabel = (sourceType) => {
    switch (sourceType) {
      case 'calling': return 'Call Recording';
      case 'online': return 'Portal Screenshot';
      case 'email': return 'Email Evidence';
      case 'fax': return 'Fax Document';
      case 'mail': return 'Mail Evidence';
      case 'other': return 'Supporting Doc';
      default: return 'Evidence';
    }
  };

  // Render evidence rows based on source type
  const renderEvidenceRows = () => {
    switch (source) {
      case 'calling':
        return (
          <CompactEvidenceRow
            files={getFiles('calling')}
            sourceType="calling"
            jurisdictionId={jurisdictionId}
            onFilesChange={(files) => handleFilesChange('calling', files)}
            readOnly={readOnly}
            icon={Mic}
            label="Call Recording"
            orderId={orderId}
            minRequired={1}
          />
        );
      
      case 'online':
        return (
          <CompactEvidenceRow
            files={getFiles('online')}
            sourceType="online"
            jurisdictionId={jurisdictionId}
            onFilesChange={(files) => handleFilesChange('online', files)}
            readOnly={readOnly}
            icon={Globe}
            label="Portal Screenshot"
            orderId={orderId}
            minRequired={1}
          />
        );
      
      case 'calling_online':
        return (
          <>
            <CompactEvidenceRow
              files={getFiles('calling')}
              sourceType="calling"
              jurisdictionId={jurisdictionId}
              onFilesChange={(files) => handleFilesChange('calling', files)}
              readOnly={readOnly}
              icon={Mic}
              label="Call Recording"
              orderId={orderId}
              minRequired={1}
            />
            <CompactEvidenceRow
              files={getFiles('online')}
              sourceType="online"
              jurisdictionId={jurisdictionId}
              onFilesChange={(files) => handleFilesChange('online', files)}
              readOnly={readOnly}
              icon={Globe}
              label="Portal Screenshot"
              orderId={orderId}
              minRequired={1}
            />
          </>
        );
      
      case 'email':
        return (
          <CompactEvidenceRow
            files={getFiles('email')}
            sourceType="email"
            jurisdictionId={jurisdictionId}
            onFilesChange={(files) => handleFilesChange('email', files)}
            readOnly={readOnly}
            icon={Mail}
            label="Email Evidence"
            orderId={orderId}
            minRequired={1}
          />
        );
      
      case 'fax':
        return (
          <CompactEvidenceRow
            files={getFiles('fax')}
            sourceType="fax"
            jurisdictionId={jurisdictionId}
            onFilesChange={(files) => handleFilesChange('fax', files)}
            readOnly={readOnly}
            icon={FileText}
            label="Fax Document"
            orderId={orderId}
            minRequired={1}
          />
        );
      
      case 'mail':
        return (
          <CompactEvidenceRow
            files={getFiles('mail')}
            sourceType="mail"
            jurisdictionId={jurisdictionId}
            onFilesChange={(files) => handleFilesChange('mail', files)}
            readOnly={readOnly}
            icon={Mail}
            label="Mail Evidence"
            orderId={orderId}
            minRequired={1}
          />
        );
      
      case 'other':
        return (
          <CompactEvidenceRow
            files={getFiles('other')}
            sourceType="other"
            jurisdictionId={jurisdictionId}
            onFilesChange={(files) => handleFilesChange('other', files)}
            readOnly={readOnly}
            icon={FileText}
            label="Supporting Doc"
            orderId={orderId}
          />
        );
      
      default:
        return null;
    }
  };

  // Get minimum requirement message
  const getRequirementMessage = () => {
    const validation = validateEvidenceRequirements(sourceEvidence, source, null, mailAwayMatch, orderFiles);
    if (source === 'calling_online') {
      return {
        text: 'Min: 1 recording + 1 screenshot',
        isValid: validation.valid
      };
    }
    return {
      text: `Min: 1 ${getSourceLabel(source).toLowerCase()}`,
      isValid: validation.valid
    };
  };

  if (!source) return null;

  const requirement = getRequirementMessage();

  return (
    <div data-testid="compact-evidence-section">
      <div className="space-y-3">
        {renderEvidenceRows()}
      </div>
      
      {/* Requirement notice */}
      <div className={`mt-2 text-xs flex items-center gap-1 ${
        requirement.isValid ? 'text-emerald-600' : 'text-slate-500'
      }`}>
        {requirement.isValid ? (
          <CheckCircle className="w-3 h-3" />
        ) : (
          <AlertCircle className="w-3 h-3" />
        )}
        {requirement.text}
      </div>
    </div>
  );
};

/**
 * MultiFileEvidenceSection Component (Legacy - kept for backward compatibility)
 * Handles multiple file uploads per source type with proper validation
 */
const MultiFileEvidenceSection = ({ 
  files = [], // Array of file objects
  sourceType, 
  jurisdictionId, 
  onFilesChange, // Callback to update files array
  readOnly,
  colorTheme = 'blue',
  icon: IconComponent = Upload,
  title,
  description,
  orderId
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  
  const config = SOURCE_EVIDENCE_CONFIG[sourceType] || SOURCE_EVIDENCE_CONFIG.other;
  const theme = EVIDENCE_THEMES[colorTheme] || EVIDENCE_THEMES.blue;
  const currentFiles = Array.isArray(files) ? files : [];
  const canUploadMore = currentFiles.length < config.maxFiles;
  const remainingSlots = config.maxFiles - currentFiles.length;

  // Validate file type on frontend
  const validateFileType = (file) => {
    const extension = '.' + file.name.split('.').pop().toLowerCase();
    const isValidExtension = config.allowedExtensions.includes(extension);
    const isValidMime = config.allowedTypes.some(type => 
      file.type === type || file.type.startsWith(type.split('/')[0] + '/')
    );
    return isValidExtension || isValidMime;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file limit
    if (!canUploadMore) {
      toast.error(`Maximum ${config.maxFiles} files allowed for ${config.label}`);
      return;
    }
    
    // Frontend file type validation
    if (!validateFileType(file)) {
      toast.error(`Invalid file type. Allowed: ${config.formatDisplay}`);
      return;
    }
    
    // File size validation
    if (file.size > config.maxSize) {
      const maxSizeMB = config.maxSize / (1024 * 1024);
      toast.error(`File size exceeds ${maxSizeMB}MB limit`);
      return;
    }
    
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', config.category);
      formData.append('jurisdiction_id', jurisdictionId || '');
      formData.append('source_type', sourceType);
      
      const response = await fetch(`${API}/orders/${orderId}/files/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to upload file');
      }
      
      const uploadedFile = await response.json();
      const fileId = uploadedFile.file_id || uploadedFile.id;
      
      // Add new file to the array
      const newFile = {
        id: fileId,
        filename: file.name,
        url: uploadedFile.url || uploadedFile.file_url || `${API}/orders/${orderId}/files/${fileId}/download`,
        uploaded_at: new Date().toISOString(),
        category: config.category,
        source_type: sourceType
      };
      
      onFilesChange([...currentFiles, newFile]);
      toast.success(`${config.label} uploaded successfully (${currentFiles.length + 1}/${config.maxFiles})`);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteFile = async (fileId) => {
    const updatedFiles = currentFiles.filter(f => f.id !== fileId);
    onFilesChange(updatedFiles);

    // Also delete from the files collection (syncs with Files tab)
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/orders/${orderId}/files/${fileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to delete file from files collection:', err);
    }

    toast.success('File removed');
  };

  return (
    <div className={`p-3 ${theme.bg} ${theme.border} border rounded-md space-y-3`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className={`text-sm ${theme.text}`}>
          <IconComponent className="w-4 h-4 inline mr-1" />
          <strong>{description}</strong>
        </p>
        <span className={`text-xs ${theme.textMuted} font-medium`}>
          {currentFiles.length}/{config.maxFiles} files
        </span>
      </div>

      {/* Uploaded Files List */}
      {currentFiles.length > 0 && (
        <div className="space-y-2">
          {currentFiles.map((file, index) => (
            <div 
              key={file.id || index} 
              className={`flex items-center justify-between p-2 ${theme.successBg} rounded-md`}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{file.filename}</p>
                  {file.uploaded_at && (
                    <p className="text-xs text-slate-500">
                      {formatDateTimeShortET(file.uploaded_at)}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {file.url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => {
                      const { openAuthenticatedFile } = require('../../utils/filePreview');
                      openAuthenticatedFile(file.url, file.filename);
                    }}
                  >
                    <Eye className="w-3 h-3" />
                  </Button>
                )}
                {!readOnly && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 hover:bg-red-100 text-red-600"
                    onClick={() => handleDeleteFile(file.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      {canUploadMore ? (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={`bg-white ${theme.btnBg} ${theme.btnBorder} ${theme.btnText}`}
            onClick={() => fileInputRef.current?.click()}
            disabled={readOnly || isUploading}
          >
            {isUploading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload {title}
              </>
            )}
          </Button>
          {remainingSlots > 0 && remainingSlots < config.maxFiles && (
            <span className={`text-xs ${theme.textMuted}`}>
              {remainingSlots} more allowed
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-amber-600">
          <AlertCircle className="w-4 h-4" />
          <span className="text-xs font-medium">Maximum {config.maxFiles} files reached</span>
        </div>
      )}

      {/* Format hint */}
      <p className={`text-xs ${theme.textMuted}`}>Accepts: {config.formatDisplay}</p>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={config.acceptString}
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

/**
 * Helper function to get evidence for a specific source type
 * Returns array of files for that source
 */
const getSourceEvidence = (sourceEvidence, sourceType) => {
  if (!sourceEvidence) return [];
  
  // Handle new structure: { calling: [...], online: [...], ... }
  if (sourceEvidence[sourceType] && Array.isArray(sourceEvidence[sourceType])) {
    return sourceEvidence[sourceType];
  }
  
  // Handle legacy single-file structure: { filename, url, ... }
  // Only return if it matches the current source type
  if (sourceEvidence.filename && sourceEvidence.source_type === sourceType) {
    return [sourceEvidence];
  }
  
  // Handle legacy structure with category matching
  if (sourceEvidence.filename && !sourceEvidence.source_type) {
    const config = SOURCE_EVIDENCE_CONFIG[sourceType];
    if (config && sourceEvidence.category === config.category) {
      return [{ ...sourceEvidence, source_type: sourceType }];
    }
  }
  
  return [];
};

/**
 * Helper function to update evidence for a specific source type
 * Preserves evidence for other source types
 */
const updateSourceEvidence = (currentEvidence, sourceType, newFiles) => {
  const evidence = currentEvidence || {};
  
  // Initialize structure if needed
  const newEvidence = { ...evidence };
  
  // Clear legacy single-file format if present
  if (newEvidence.filename) {
    delete newEvidence.filename;
    delete newEvidence.url;
    delete newEvidence.uploaded_at;
    delete newEvidence.category;
    delete newEvidence.file_id;
    delete newEvidence.source_type;
  }
  
  // Update the specific source type
  newEvidence[sourceType] = newFiles;
  
  return newEvidence;
};

/**
 * Validate if evidence requirements are met for a source type.
 * Mirrors the backend _resolve_evidence_satisfied() logic exactly.
 * Checks (in order):
 *   1. Per-jurisdiction source_evidence files
 *   2. Global order files (Files tab / any upload location)
 *   3. Fax Received responses
 *   4. Mail-Away Received responses
 *
 * @param {object} sourceEvidence - Per-jurisdiction source_evidence object
 * @param {string} source - Source type (online, fax, mail, etc.)
 * @param {object|null} faxDelivery - Fax delivery data (for fax bypass)
 * @param {object|null} mailAwayMatch - Matching mail-away request for this jurisdiction (for mail bypass)
 * @param {Array} orderFiles - Global order files from Files tab (default [])
 */
const validateEvidenceRequirements = (sourceEvidence, source, faxDelivery = null, mailAwayMatch = null, orderFiles = []) => {
  if (!source) return { valid: true, message: '' };
  
  const getFiles = (type) => getSourceEvidence(sourceEvidence, type);
  
  // ── Global file helpers (mirror backend _resolve_evidence_satisfied) ──
  const DOC_CATEGORIES = new Set([
    'image', 'supporting_document', 'final_document',
    'tax_search_document', 'fax_evidence', 'mail_evidence', 'email_evidence',
  ]);
  const DOC_CONTENT_TYPES = new Set([
    'application/pdf', 'image/png', 'image/jpeg', 'image/jpg',
    'image/gif', 'image/tiff',
  ]);
  
  const hasGlobalDocument = () => {
    for (const f of (orderFiles || [])) {
      if (DOC_CATEGORIES.has(f.file_category)) return true;
      const ct = f.content_type || f.file_type || '';
      if (DOC_CONTENT_TYPES.has(ct)) return true;
    }
    return false;
  };
  
  const hasGlobalAudio = () => {
    for (const f of (orderFiles || [])) {
      if (f.file_category === 'audio') return true;
      const ct = f.content_type || f.file_type || '';
      if (ct.startsWith('audio/')) return true;
    }
    return false;
  };
  
  switch (source) {
    case 'calling':
      return {
        valid: getFiles('calling').length >= 1 || hasGlobalAudio(),
        message: 'At least 1 call recording required'
      };
    case 'online':
      return {
        valid: getFiles('online').length >= 1 || hasGlobalDocument(),
        message: 'At least 1 screenshot/document required'
      };
    case 'calling_online': {
      const hasCall = getFiles('calling').length >= 1 || hasGlobalAudio();
      const hasOnline = getFiles('online').length >= 1 || hasGlobalDocument();
      if (!hasCall && !hasOnline) {
        return { valid: false, message: 'At least 1 call recording and 1 screenshot required' };
      } else if (!hasCall) {
        return { valid: false, message: 'At least 1 call recording required' };
      } else if (!hasOnline) {
        return { valid: false, message: 'At least 1 screenshot/document required' };
      }
      return { valid: true, message: '' };
    }
    case 'email':
      return {
        valid: getFiles('email').length >= 1 || hasGlobalDocument(),
        message: 'At least 1 email evidence file required'
      };
    case 'fax': {
      const hasJurisdictionFaxFiles = getFiles('fax').length >= 1;
      const hasFaxReceivedFile = faxDelivery?.status === 'received' && faxDelivery?.received_file_id;
      return {
        valid: hasJurisdictionFaxFiles || !!hasFaxReceivedFile || hasGlobalDocument(),
        message: 'At least 1 fax document required (upload via source evidence or use Received action)'
      };
    }
    case 'mail': {
      const hasJurisdictionMailFiles = getFiles('mail').length >= 1;
      const hasMailAwayResponse = mailAwayMatch?.status === 'response_received'
        && Array.isArray(mailAwayMatch?.response_files)
        && mailAwayMatch.response_files.length > 0;
      return {
        valid: hasJurisdictionMailFiles || !!hasMailAwayResponse || hasGlobalDocument(),
        message: 'At least 1 mail evidence file required (upload via source evidence or use Mail-Away Received action)'
      };
    }
    case 'other':
      return {
        valid: getFiles('other').length >= 1 || hasGlobalDocument(),
        message: 'At least 1 supporting document required'
      };
    default:
      return { valid: true, message: '' };
  }
};

/**
 * Parse a date string (MM/DD/YYYY or YYYY-MM-DD) into a comparable Date, or null
 */
const parseValidationDate = (val) => {
  if (!val) return null;
  const s = String(val).trim();
  if (!s) return null;
  // MM/DD/YYYY
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) return new Date(+mdy[3], +mdy[1] - 1, +mdy[2]);
  // YYYY-MM-DD
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return new Date(+ymd[1], +ymd[2] - 1, +ymd[3]);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Check if a numeric-like value is present and > 0
 */
const hasAmount = (val) => {
  if (val === null || val === undefined || val === '') return false;
  const n = parseFloat(val);
  return !isNaN(n) && n > 0;
};

/**
 * Edge case (May 12, 2026): a Fully Exempt jurisdiction with a 100% zero-tax
 * tax row has no real payment date because nothing was actually paid — "Paid"
 * here only means "no balance due". For that single specific shape we relax:
 *   • R1: Paid Date is NOT required even when Pay Status = Paid.
 *   • R2: a non-blank Paid Date may equal the Due Date even if the Due Date
 *         is in the future (any other future paid date is still rejected).
 * Scoped strictly — must satisfy ALL conditions, so normal paid rows and
 * Partially-Paid rows are unaffected.
 *
 * @param {object} jurisdiction  - jurisdiction object (carries fully_exempt)
 * @param {object} inst          - tax-record installment row
 * @returns {boolean}
 */
const isExemptZeroRow = (jurisdiction, inst) => {
  if (!jurisdiction || !inst) return false;
  if (!jurisdiction.fully_exempt) return false;
  const status = String(inst.pay_status || '').trim().toLowerCase();
  if (status !== 'paid') return false;
  const isZero = (v) => {
    if (v === null || v === undefined || v === '') return true;
    const n = parseFloat(v);
    return !isNaN(n) && n === 0;
  };
  return (
    isZero(inst.base_tax) &&
    isZero(inst.total_amount) &&
    isZero(inst.paid_amount) &&
    isZero(inst.balance_amount)
  );
};

/**
 * Check if a valid Mail-Away or Fax response already exists for a jurisdiction.
 * Mirrors backend _has_workflow_response() exactly.
 * When True, source-selection and evidence requirements are fully satisfied.
 *
 * @param {string} jurisdictionName
 * @param {string} jurisdictionType
 * @param {object|null} mailAwayMatch - Pre-matched mail-away request for this jurisdiction
 * @param {object|null} faxDelivery - Order-level fax delivery data
 * @param {Array} mailAwayRequests - All mail-away requests (used when mailAwayMatch not provided)
 * @param {Array} orderFiles - Global order files from Files tab
 */
const hasWorkflowResponse = (jurisdictionName, jurisdictionType, mailAwayMatch, faxDelivery, mailAwayRequests = [], orderFiles = []) => {
  // 1. Structured mail-away request with response_received + response_files
  if (mailAwayMatch?.status === 'response_received'
      && Array.isArray(mailAwayMatch?.response_files)
      && mailAwayMatch.response_files.length > 0) {
    return true;
  }

  // Also check full list in case mailAwayMatch wasn't pre-filtered
  const jn = (jurisdictionName || '').trim().toLowerCase();
  const jt = (jurisdictionType || '').trim().toLowerCase();
  for (const req of (mailAwayRequests || [])) {
    const rn = (req.jurisdiction_name || '').trim().toLowerCase();
    const rt = (req.jurisdiction_type || '').trim().toLowerCase();
    if (rn === jn && rt === jt && req.status === 'response_received') {
      if (Array.isArray(req.response_files) && req.response_files.length > 0) {
        return true;
      }
    }
  }

  // 2. Fax delivery with received status + file
  if (faxDelivery?.status === 'received' && faxDelivery?.received_file_id) {
    return true;
  }

  // 3. Files tab: files with mail-away/fax response categories or filename patterns
  for (const f of (orderFiles || [])) {
    const cat = (f.file_category || '').toLowerCase();
    const fname = (f.filename || '').toLowerCase();
    if (['mail_away_response', 'fax_response', 'fax_evidence', 'mail_evidence'].includes(cat)) {
      return true;
    }
    if (fname.includes('mail-away response') || fname.includes('mail away response')) {
      return true;
    }
    if (fname.includes('fax response') || fname.includes('fax received')) {
      return true;
    }
  }

  return false;
};

/**
 * Get jurisdiction completion status with full business-rule validation
 * @param {object} jurisdiction
 * @param {boolean} isExternalPortal
 * @param {object|null} crossParcelData
 * @param {object|null} faxDelivery
 * @param {object|null} mailAwayMatch
 * @param {Array} orderFiles
 * @param {Array} mailAwayRequests
 * @param {boolean} isNewParcel — May 17, 2026. When true, the parent
 *   parcel is marked as a brand-new parcel; amount-based, paid-date,
 *   delinquent-history, and "REFER NOTE" pay-status checks are skipped.
 *   Identity fields (jurisdiction name/type, source, tax_year, due_date,
 *   mail-out date if entered, installment name) remain required.
 * @returns {{ status: 'ready' | 'in_progress' | 'error', label: string, errors: string[] }}
 */
const getJurisdictionStatus = (jurisdiction, isExternalPortal = false, crossParcelData = null, faxDelivery = null, mailAwayMatch = null, orderFiles = [], mailAwayRequests = [], isNewParcel = false) => {
  const errors = [];

  // ── Identity checks ──
  const hasName = Boolean(jurisdiction.jurisdiction_name);
  const hasType = Boolean(jurisdiction.jurisdiction_type);
  if (!hasName) errors.push('Jurisdiction name is required');
  if (!hasType) errors.push('Jurisdiction type is required');

  // ── Workflow response bypass ──
  // If a Mail-Away/Fax response already exists, skip source + evidence checks
  const wfBypass = hasWorkflowResponse(
    jurisdiction.jurisdiction_name, jurisdiction.jurisdiction_type,
    mailAwayMatch, faxDelivery, mailAwayRequests, orderFiles
  );

  if (!wfBypass) {
    // ── Source check (skip if same jurisdiction in another parcel already has source) ──
    if (!jurisdiction.source) {
      const coveredByOtherParcel = crossParcelData
        ? isSourceCoveredByAnotherParcel(
            jurisdiction.jurisdiction_name, jurisdiction.jurisdiction_type,
            crossParcelData.allParcelsData, crossParcelData.parcelIndex
          )
        : false;
      if (!coveredByOtherParcel) {
        errors.push('Source is required');
      }
    }

    // ── Evidence validation — source-specific evidence files must be uploaded ──
    if (jurisdiction.source) {
      const evidenceResult = validateEvidenceRequirements(jurisdiction.source_evidence, jurisdiction.source, faxDelivery, mailAwayMatch, orderFiles);
      if (!evidenceResult.valid) {
        errors.push(`Evidence: ${evidenceResult.message}`);
      }
    }
  }

  // ── Fully Exempt validation — exemption type is mandatory when fully_exempt ──
  if (jurisdiction.fully_exempt) {
    const realExemptions = (jurisdiction.exemption_types || []).filter(
      t => t && t !== 'No Exemptions (N/A)'
    );
    if (realExemptions.length === 0) {
      errors.push('Exemption type is required when Fully Exempt is enabled');
    }
  }

  // ── Skip installment/tax-summary validation for External Portal ──
  if (isExternalPortal) {
    if (errors.length === 0) {
      return { status: 'ready', label: 'Ready for Review', errors: [] };
    }
    if (!hasName || !hasType) {
      return { status: 'error', label: 'In Progress', errors };
    }
    return { status: 'in_progress', label: 'In Progress', errors };
  }

  // ── Installments check (internal only) ──
  const installments = jurisdiction.installments || [];
  if (installments.length === 0) {
    errors.push('At least one installment is required');
  }

  // ── Per-installment validation ──
  // TAX-PBNA-COMPLETE-001 (Jul 2026): rows covered by a Paid Breakdown N/A
  // declaration (row flag, per-year group, or Selected-Years entry) are
  // collector-confirmed PAID — validate them as Paid regardless of the raw
  // pay_status, and skip Paid Amount checks (no breakdown exists by design).
  const _pbnaCoveredYears = computeCoveredYears(jurisdiction);
  const _pbnaGroupsMap = (jurisdiction && jurisdiction.paid_breakdown_na_groups) || {};
  for (let i = 0; i < installments.length; i++) {
    const inst = installments[i];
    const label = inst.installment_name || `Installment ${i + 1}`;
    const _instYear = String(inst.tax_year || '').trim();
    const _instGroup = _pbnaGroupsMap[_instYear];
    const _rowPbna = !!inst.paid_breakdown_na
      || !!(_instGroup && _instGroup.enabled)
      || _pbnaCoveredYears.has(_instYear);
    // G6: Use derived pay status (computed from financial data) for consistent evaluation
    const derived = _rowPbna ? 'Paid' : derivePayStatus(inst);
    const status = derived.toLowerCase();

    // Mandatory for every row
    if (!inst.installment_name) errors.push(`${label}: Installment name is required`);
    if (!inst.pay_status)       errors.push(`${label}: Pay status is required`);
    if (!inst.mailing_issue_date && !isNewParcel) errors.push(`${label}: Mail-out date is required`);
    if (!inst.due_date)         errors.push(`${label}: Due date is required`);
    const baseVal = inst.amount_due ?? inst.base_tax;
    // When fully_exempt OR New Parcel mode, zero/empty base amount is valid.
    // May 17, 2026 — New Parcel bypass: rows ship with $0.00 amounts.
    // Jul 2026 — Exempt rows carry no meaningful base amount ($0 or blank).
    if (!jurisdiction.fully_exempt && !isNewParcel && status !== 'exempt' && status !== 'n/a') {
      if (baseVal === null || baseVal === undefined || baseVal === '') {
        errors.push(`${label}: Base amount is required`);
      }
    }

    // ── Conditional by pay status (skip financial amount checks when fully_exempt or New Parcel) ──
    const _exemptZero = isExemptZeroRow(jurisdiction, inst);
    if (status === 'paid') {
      if (!inst.paid_date && !_exemptZero && !isNewParcel && !_rowPbna) errors.push(`${label}: Paid date is required when Paid`);
      if (!jurisdiction.fully_exempt && !isNewParcel && !_rowPbna && !hasAmount(inst.paid_amount))   errors.push(`${label}: Paid amount is required when Paid`);
    }
    // "Due" → due_date already required above
    // "Refer Note" → no further checks (system uses the auto-appended PDF note).
    // "Exempt" (Jul 2026) → no Paid Date/Amount checks; the year is exempt.
    // "N/A" (Jul 2026) → no monetary/Paid Date checks; amount unavailable.

    // Jun 2026 — Combined Payoff mode: the jurisdiction-level legacy
    // payoff fields (dlq_payoff_amount / dlq_good_thru_date) stay empty
    // in combined mode; the Combined Payoff Amount + Combined Good-
    // Through Date are authoritative and are strictly enforced in the
    // combined-mode block below. Skip the legacy checks here so a
    // populated Combined Payoff doesn't false-block completion.
    const _combinedModeRow = (jurisdiction.dlq_payoff_breakdown_mode || 'year_wise') === 'combined';

    if (status === 'delinquent' && !isNewParcel) {
      if (!inst.dlq_date) errors.push(`${label}: Delinquent date is required when Delinquent`);
      if (!_combinedModeRow && !jurisdiction.fully_exempt && !hasAmount(jurisdiction.dlq_payoff_amount) && !hasAmount(inst.payoff_amount)) errors.push(`${label}: Payoff amount is required when Delinquent`);
      if (!_combinedModeRow && !jurisdiction.dlq_good_thru_date && !inst.payoff_good_through_date) errors.push(`${label}: Good thru date is required when Delinquent`);
    }

    if (status === 'partially paid' && !isNewParcel) {
      if (!inst.paid_date)                errors.push(`${label}: Paid date is required when Partially Paid`);
      if (!jurisdiction.fully_exempt && !hasAmount(inst.paid_amount))   errors.push(`${label}: Paid amount is required when Partially Paid`);
      if (!jurisdiction.fully_exempt && !hasAmount(inst.balance_amount)) errors.push(`${label}: Balance is required when Partially Paid`);
    }

    if (['partially delinquent', 'partially dlq'].includes(status) && !isNewParcel) {
      if (!inst.dlq_date) errors.push(`${label}: Delinquent date is required when Partially Delinquent`);
      if (!_combinedModeRow && !jurisdiction.fully_exempt && !hasAmount(jurisdiction.dlq_payoff_amount) && !hasAmount(inst.payoff_amount)) errors.push(`${label}: Payoff amount is required when Partially Delinquent`);
      if (!_combinedModeRow && !jurisdiction.dlq_good_thru_date && !inst.payoff_good_through_date) errors.push(`${label}: Good thru date is required when Partially Delinquent`);
    }

    // ── G1: Paid Date present without Paid Amount (cross-field integrity) ──
    if (inst.paid_date && !hasAmount(inst.paid_amount) && !jurisdiction.fully_exempt && !isNewParcel && status !== 'n/a' && !_rowPbna) {
      errors.push(`${label}: Paid amount is required when a paid date is entered`);
    }

    // ── Date validation rules ──
    if (inst.mailing_issue_date && inst.due_date) {
      const md = parseValidationDate(inst.mailing_issue_date);
      const dueD = parseValidationDate(inst.due_date);
      if (md && dueD && md > dueD) {
        errors.push(`${label}: Mail date cannot be after due date`);
      }
    }
    if (inst.paid_date && inst.mailing_issue_date) {
      const pd = parseValidationDate(inst.paid_date);
      const md = parseValidationDate(inst.mailing_issue_date);
      if (pd && md && pd < md) {
        errors.push(`${label}: Paid date cannot be earlier than mail-out date`);
      }
    }
    // G4: Paid Date must not be in the future
    // Exception: exempt zero-tax rows may use a paid_date that equals the
    // due_date even if the due_date is in the future (no real payment occurred).
    if (inst.paid_date) {
      const pd = parseValidationDate(inst.paid_date);
      if (pd) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (pd > today) {
          let allowFuture = false;
          if (_exemptZero && inst.due_date) {
            const dueD = parseValidationDate(inst.due_date);
            if (dueD && pd.getTime() === dueD.getTime()) allowFuture = true;
          }
          if (!allowFuture) {
            errors.push(`${label}: Paid date cannot be in the future`);
          }
        }
      }
    }
    if (inst.dlq_date && inst.due_date) {
      const dd = parseValidationDate(inst.dlq_date);
      const dueD = parseValidationDate(inst.due_date);
      if (dd && dueD && dd < dueD) {
        errors.push(`${label}: Delinquent date cannot be earlier than due date`);
      }
    }
    if (jurisdiction.dlq_good_thru_date && inst.dlq_date) {
      const gd = parseValidationDate(jurisdiction.dlq_good_thru_date);
      const dd = parseValidationDate(inst.dlq_date);
      if (gd && dd && gd < dd) {
        errors.push(`${label}: Good thru date cannot be earlier than delinquent date`);
      }
    }

    // ── Payoff Good Through must be a future date ──
    // Feb 2026 — Skip per-installment validation entirely when the
    // jurisdiction is in `combined` breakdown mode. The per-row payoff
    // fields are soft-hidden in that mode (values preserved for soft-
    // toggle) and the authoritative validation is on the jurisdiction-
    // level combined fields, enforced below.
    const _isCombinedMode = (jurisdiction.dlq_payoff_breakdown_mode || 'year_wise') === 'combined';
    if (!_isCombinedMode && inst.payoff_good_through_date) {
      const gt = parseValidationDate(inst.payoff_good_through_date);
      if (gt) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (gt <= today) {
          errors.push(`${label}: Payoff Good Through must be a future date.`);
        }
        // Payoff Good Through cannot be earlier than delinquent date
        if (inst.dlq_date) {
          const dd = parseValidationDate(inst.dlq_date);
          if (dd && gt < dd) {
            errors.push(`${label}: Good thru date cannot be earlier than delinquent date`);
          }
        }
      }
    }
  }

  // ── Duplicate Due Date check (multi-installment only) ──
  if (installments.length > 1) {
    // MI: Only flag duplicates within the SAME season (SUMMER vs WINTER can share due dates)
    const hasSeason = installments.some(inst => (inst.season || '').trim());
    const dueDateCounts = {};
    for (const inst of installments) {
      const d = (inst.due_date || '').trim();
      if (d) {
        const key = hasSeason ? `${(inst.season || '').toUpperCase()}::${d}` : d;
        dueDateCounts[key] = (dueDateCounts[key] || 0) + 1;
      }
    }
    const hasDuplicates = Object.values(dueDateCounts).some(c => c > 1);
    if (hasDuplicates) {
      errors.push('Each installment must have a unique Due Date within the same season. Duplicate due dates detected.');
    }
  }

  // ── Feb 2026 — Combined Delinquent Payoff (jurisdiction-level) ──
  // When the user selects "Combined Payoff",
  // BOTH the Combined Payoff Amount and the Combined Good-Through Date
  // are required before a jurisdiction can move to Ready for Review.
  // The optional combined-payoff note remains optional.
  if ((jurisdiction.dlq_payoff_breakdown_mode || 'year_wise') === 'combined') {
    const anyDelinqStatus = installments.some(inst => {
      const s = (inst.pay_status || '').toLowerCase();
      return s === 'delinquent' || s === 'partially delinquent' || s === 'partially dlq';
    });
    if (anyDelinqStatus) {
      const combinedAmtNum = parseFloat(jurisdiction.dlq_combined_payoff_amount);
      const hasCombinedAmt = !isNaN(combinedAmtNum) && combinedAmtNum > 0;
      if (!hasCombinedAmt) {
        errors.push('Combined Payoff Amount is required when "Combined Payoff" is selected.');
      }
      if (!jurisdiction.dlq_combined_payoff_good_through_date) {
        errors.push('Combined Payoff Good-Through Date is required when "Combined Payoff" is selected.');
      }
      // Future-date check on the combined good-through date
      if (jurisdiction.dlq_combined_payoff_good_through_date) {
        const gt = parseValidationDate(jurisdiction.dlq_combined_payoff_good_through_date);
        if (gt) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (gt <= today) {
            errors.push('Combined Payoff Good Through must be a future date.');
          }
        }
      }
    }
  }

  // ── Determine status ──
  if (errors.length === 0) {
    return { status: 'ready', label: 'Ready for Review', errors: [] };
  }

  // Identity-level issues → error
  if (!hasName || !hasType) {
    return { status: 'error', label: 'In Progress', errors };
  }

  return { status: 'in_progress', label: 'In Progress', errors };
};

/**
 * Get type badge color
 */
const getTypeBadgeColor = (type) => {
  const colors = {
    'County': 'bg-blue-100 text-blue-800',
    'City': 'bg-purple-100 text-purple-800',
    'School': 'bg-orange-100 text-orange-800',
    'Village': 'bg-green-100 text-green-800',
    'Township': 'bg-cyan-100 text-cyan-800',
    'Special District': 'bg-pink-100 text-pink-800'
  };
  return colors[type] || 'bg-slate-100 text-slate-800';
};

/**
 * Generate consolidated tax certification notes based on tax records
 * Follows existing business rules for tax note generation:
 * - Prior tax status
 * - Current tax status with jurisdiction type (county, city, etc.)
 * - Tax collection frequency (annual, semi-annual, quarterly)
 * - Adjustment details (discount/penalty per installment)
 * - Payment details (partial/full per installment)
 * - Delinquency details with dates
 * - Due/delinquent schedule
 * - Balance information for delinquent taxes
 */
/**
 * TAXCERT-INSTALLMENT-PLAN-001 (Sep 2026) — narrative for Tax Collected =
 * "Installment Plan". Mirrors the Quarterly structure with installment
 * vocabulary, NO fixed slot count (no "not yet available"), statuses derived
 * from the entered rows only. Row order = "Nth Installment" ordinal, else
 * entry order. Pushes into `notes` (caller has cleared it).
 */
const INSTALLMENT_ORDINAL_WORDS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth'];
const generateInstallmentPlanNotes = ({ notes, sortedYears, yearGroups, jurisdiction, catLabelText, formatDate, getDpQualifier, statusWithQualifier, getFinancials }) => {
  notes.length = 0;
  const formatDateMMDD = (dateVal) => {
    const full = formatDate(dateVal);
    if (!full) return null;
    const parts = full.split('/');
    return parts.length >= 2 ? `${parts[0]}/${parts[1]}` : null;
  };
  const joinWithAnd = (items) => {
    if (items.length <= 1) return items[0] || '';
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return items.slice(0, -1).join(', ') + ', and ' + items[items.length - 1];
  };
  const normStatus = (inst) => {
    const s = (inst.pay_status || '').toLowerCase();
    if (s === 'partially delinquent' || s === 'partially dlq') return 'partially delinquent';
    return s || 'due';
  };
  const orderRows = (rows) => rows
    .map((inst, idx) => ({ inst, ord: installmentPlanOrdinal(inst.installment_name) || (idx + 1) }))
    .sort((a, b) => a.ord - b.ord)
    .map(({ inst, ord }, idx) => ({ inst, label: INSTALLMENT_ORDINAL_WORDS[ord - 1] || INSTALLMENT_ORDINAL_WORDS[idx] || `${ord}th`, status: normStatus(inst) }));

  notes.push(sortedYears.length === 1 ? 'All prior taxes are paid.' : `All taxes prior to ${sortedYears[0]} are paid.`);

  let hasAnyDelinquent = false;
  sortedYears.forEach(yr => {
    const rows = orderRows((yearGroups.get(yr) || []).filter(inst => !isNaStatusRow(inst)));
    if (rows.length === 0) return;
    rows.forEach(r => { if (r.status === 'delinquent' || r.status === 'partially delinquent') hasAnyDelinquent = true; });
    const qualified = rows.map(r => statusWithQualifier(r.status, getDpQualifier(r.inst)));

    if (rows.every(r => r.status === 'paid')) {
      const uniq = [...new Set(qualified)];
      notes.push(`${yr} ${catLabelText}taxes are ${uniq.length === 1 ? uniq[0] : 'paid'}.`.replace('  ', ' '));
      return;
    }
    const needsFinancial = rows.some(r => r.status === 'partially paid' || r.status === 'partially delinquent');
    if (needsFinancial) {
      rows.forEach((r, i) => {
        const cap = r.label.charAt(0).toUpperCase() + r.label.slice(1);
        notes.push(i === 0 ? `${yr} ${r.label} installment taxes are ${qualified[i]}.` : `${cap} installment taxes are ${qualified[i]}.`);
        if (r.status === 'partially paid' || r.status === 'partially delinquent') {
          const { paidAmt, balance } = getFinancials(r.inst);
          if (paidAmt > 0 && balance !== null && balance > 0) {
            const isDelinqBalance = r.status === 'partially delinquent' || (r.inst.dlq_date && isDlqDatePassed(r.inst.dlq_date));
            notes.push(`A payment of ${formatCurrency(paidAmt)} has been applied.`);
            notes.push(isDelinqBalance ? `The remaining unpaid delinquent balance is ${formatCurrency(balance)}.` : `The remaining balance due is ${formatCurrency(balance)}.`);
          }
        }
      });
      return;
    }
    const groups = [];
    rows.forEach((r, i) => {
      const last = groups[groups.length - 1];
      if (last && last.status === qualified[i]) last.labels.push(r.label);
      else groups.push({ status: qualified[i], labels: [r.label] });
    });
    const parts = groups.map(g => `${joinWithAnd(g.labels)} installment taxes are ${g.status}`);
    notes.push(`${yr} ${parts.join('; ')}.`);
  });

  if (hasAnyDelinquent) {
    const payoffAmt = parseFloat(jurisdiction.dlq_payoff_amount);
    const goodThruDate = formatDate(jurisdiction.dlq_good_thru_date);
    if (!isNaN(payoffAmt) && payoffAmt > 0 && goodThruDate) {
      notes.push(`Total payoff amount is ${formatCurrency(payoffAmt)}, good through ${goodThruDate}.`);
    }
  }

  // Plan line — dates from the latest year with the most installment rows
  let source = [];
  sortedYears.forEach(yr => { const rows = yearGroups.get(yr) || []; if (rows.length >= source.length) source = rows; });
  const dueDates = [];
  const dlqDates = [];
  orderRows(source).forEach(({ inst }) => {
    const dd = formatDateMMDD(inst.due_date);
    const dq = formatDateMMDD(inst.dlq_date);
    if (dd) dueDates.push(dd);
    if (dq) dlqDates.push(dq);
  });
  notes.push('Taxes are payable under an installment plan.');
  if (dueDates.length > 0) {
    let line = `Installment due dates are ${joinWithAnd(dueDates)}`;
    if (dlqDates.length > 0) line += ` and become delinquent after ${joinWithAnd(dlqDates)}`;
    notes.push(line + '.');
  }
};

const generateTaxCertificationNotes = (jurisdiction, installments, stateCode = '', __subBlock = false, __utilityBlock = false) => {
  if (!installments || installments.length === 0) {
    return '';
  }

  // Jun 2026 — effective Paid Date for Paid Breakdown N/A rows: rows
  // covered by a selected-years lump sum or a per-year group carry their
  // canonical Paid Date on the group entry, not the row. Prefill it here
  // (row-level dates always win) so status wording — incl. the
  // paid-after-DLQ "paid with penalty" detection — works even when the
  // operator never touched the row-level Paid Date. Mirrors the PDF
  // flatten fallback in pdf_service.py.
  //
  // Jul 2026 — DLQ-PAID-STATUS-001: a collector confirmation asserts the
  // covered rows are FULLY paid, so their final status resolves to Paid
  // even when the raw pay_status was left Delinquent/Due (the DLQ date is
  // historical metadata only). Paid-variants and Exempt are never
  // overwritten. When the group lump-sum exceeds the year's base taxes
  // (money evidence, honoring the Feb 2026 money-based-penalty contract)
  // AND payment landed after the DLQ date, the year narrates the
  // late-payment penalty explicitly via the `pbna_late_penalty` flag.
  const _pbnaCoveredSet = computeCoveredYears(jurisdiction);
  const _pbnaGroups = (jurisdiction && jurisdiction.paid_breakdown_na_groups) || {};
  const _pbnaAllDate = ((jurisdiction && jurisdiction.paid_breakdown_na_all_years) || {}).paid_date || '';
  const _pbnaParseDate = (d) => {
    const s = String(d || '').trim();
    let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) return new Date(+m[3], +m[1] - 1, +m[2]).getTime();
    m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(+m[1], +m[2] - 1, +m[3]).getTime();
    return null;
  };
  const _pbnaPenaltyYears = new Set();
  Object.entries(_pbnaGroups).forEach(([gy, ge]) => {
    if (!ge || !ge.enabled) return;
    const gyS = String(gy).trim();
    const gTotal = parseFloat(ge.total_paid_amount);
    if (!isFinite(gTotal)) return;
    const rowsY = installments.filter(i => i && String(i.tax_year || '').trim() === gyS);
    if (!rowsY.length) return;
    const sumBase = rowsY.reduce((a, r) => a + (parseFloat(r.base_tax) || 0), 0);
    if (gTotal - sumBase <= 0.01) return;
    const late = rowsY.some(r => {
      const pd = _pbnaParseDate(r.paid_date || ge.paid_date || '');
      const dq = _pbnaParseDate(r.dlq_date);
      return pd !== null && dq !== null && pd > dq;
    });
    if (late) _pbnaPenaltyYears.add(gyS);
  });
  installments = installments.map((inst) => {
    if (!inst) return inst;
    const y = String(inst.tax_year || '').trim();
    const g = _pbnaGroups[y];
    const covered = _pbnaCoveredSet.has(y) || !!(g && g.enabled) || !!inst.paid_breakdown_na;
    if (!covered) return inst;
    const next = { ...inst };
    if (!next.paid_date) {
      const eff = _pbnaCoveredSet.has(y)
        ? _pbnaAllDate
        : (g && g.enabled ? (g.paid_date || '') : '');
      if (eff) next.paid_date = eff;
    }
    const st = String(next.pay_status || '').trim().toLowerCase();
    if (!st.startsWith('paid') && st !== 'exempt' && st !== 'n/a') next.pay_status = 'Paid';
    if (_pbnaPenaltyYears.has(y)) next.pbna_late_penalty = true;
    return next;
  });

  // ── Feb 2026 (P0) — Mixed-Category sub-block partition ──────────────────
  // When a jurisdiction carries BOTH standard installments AND utility
  // specialty installments (Solid Waste / Water / Sewer / Storm Water /
  // Water & Sewer), render them as SEPARATE sub-blocks with their own
  // headers so a county's standard real-estate narrative isn't polluted
  // by Solid Waste due dates and vice versa.
  //
  // Standard sub-block header: jurisdiction TYPE uppercased
  //   (`COUNTY:` / `CITY:` / `SCHOOL:` …).
  // Utility sub-block header: specialty installment_name uppercased
  //   (`SOLID WASTE:` / `WATER:` / `SEWER:` …).
  //
  // Each sub-block reuses the existing narrative path via a recursive
  // call with `__subBlock=true` so the partition runs only at the top
  // level (no infinite recursion). When `__subBlock=true`, the jurisdiction
  // TYPE qualifier is also suppressed inside the recursed narrative so
  // the Solid Waste sub-block reads "2026 taxes are due" instead of the
  // misleading "2026 county taxes are due".
  //
  // Michigan flows (Summer/Winter notes handle their own structure) enter
  // the partition ONLY when they carry a Utility installment — Sep 2026,
  // TAXCERT-NOTES-BILL-WORDING-001 — so the Bill wording applies while
  // the Summer/Winter narrative of the remaining rows is untouched.
  // Mirrored in `pdf_service.generate_dynamic_tax_notes`.
  const _hasUtilityRows = !__subBlock && installments.some((_i) => {
    const _n = (_i && _i.installment_name) || '';
    return _n && isSpecialtyInstallment(_n) && getSpecialtyCategory(_n) === 'utility';
  });
  if (!__subBlock && (!isMichigan(stateCode) || _hasUtilityRows)) {
    const _standardInsts = [];
    const _utilityGroups = new Map(); // preserve insertion order
    for (const _inst of installments) {
      const _name = ((_inst && _inst.installment_name) || '').toString().trim();
      if (_name && isSpecialtyInstallment(_name) && getSpecialtyCategory(_name) === 'utility') {
        if (!_utilityGroups.has(_name)) _utilityGroups.set(_name, []);
        _utilityGroups.get(_name).push(_inst);
      } else {
        _standardInsts.push(_inst);
      }
    }

    if (_utilityGroups.size > 0) {
      // Sep 2026 — TAXCERT-NOTES-SUBBLOCK-001 (presentation only): a
      // jurisdiction whose rows are ALL one utility name (e.g. "SOLID WASTE
      // FEE" carrying only "Solid Waste" rows) has nothing to partition.
      // Render the same sub-block sentences directly under the jurisdiction
      // header instead of a redundant "SOLID WASTE:" sub-header.
      if (_standardInsts.length === 0 && _utilityGroups.size === 1) {
        const _onlyName = _utilityGroups.keys().next().value;
        const _onlyInsts = _utilityGroups.values().next().value;
        // TAXCERT-NOTES-BILL-WORDING-001: Utility narratives say Bill/Bills.
        return toUtilityBillWording(
          (generateTaxCertificationNotes(jurisdiction, _onlyInsts, stateCode, true, true) || '').trim(), _onlyName
        );
      }

      const _subBlocks = [];

      // Standard sub-block (skip when no standard rows).
      if (_standardInsts.length > 0) {
        const _stdNotes = generateTaxCertificationNotes(
          jurisdiction, _standardInsts, stateCode, true
        );
        if (_stdNotes && _stdNotes.trim()) {
          // Jul 2026 — TAX-CATEGORY-001: stored bill category wins.
          const _jtRaw = ((jurisdiction && (jurisdiction.tax_category || jurisdiction.jurisdiction_type)) || '').toString().trim();
          const _stdHeader = _jtRaw
            ? _jtRaw.replace(/_/g, ' ').toUpperCase()
            : ((jurisdiction && jurisdiction.jurisdiction_name) || '').toUpperCase();
          _subBlocks.push(`${_stdHeader}:\n${_stdNotes.trim()}`);
        }
      }

      // Utility sub-blocks — one per distinct specialty name.
      for (const [_utilName, _utilInsts] of _utilityGroups) {
        const _utilNotes = generateTaxCertificationNotes(
          jurisdiction, _utilInsts, stateCode, true, true
        );
        if (_utilNotes && _utilNotes.trim()) {
          // TAXCERT-NOTES-BILL-WORDING-001: Utility narratives say Bill/Bills.
          _subBlocks.push(`${_utilName.toUpperCase()}:\n${toUtilityBillWording(_utilNotes.trim(), _utilName)}`);
        }
      }

      if (_subBlocks.length > 0) {
        return _subBlocks.join('\n\n');
      }
    }
  }
  // ── end Mixed-Category partition ───────────────────────────────────────


  // Michigan: use Summer/Winter notes generation (a Utility block is
  // narrated by the standard templates instead — BILL-WORDING-001)
  if (isMichigan(stateCode) && !__utilityBlock) {
    const allNotes = [];
    const taxCollected = jurisdiction.tax_collected || '';
    // Jun 2026 — Phase 20 (UAT-MI-FULLY-EXEMPT-NOTES). Plumb
    // fully-exempt context into the MI generators so MI parcels
    // emit the same "qualifies for a 100% <Exemption> exemption;
    // therefore, the Summer/Winter tax amounts are zero" sentence
    // Florida already does (FL branch at L2094 below). Without
    // this, the early `return` here completely bypassed the
    // exemption narrative for MI orders.
    const miExemptionOpts = {
      fullyExempt: !!jurisdiction.fully_exempt,
      exemptionTypes: jurisdiction.exemption_types || [],
      exemptionOther: jurisdiction.exemption_other || '',
    };
    // Group installments by tax year
    // Jul 2026 — NA-PAY-STATUS-001: N/A rows produce NO auto narration
    // (the processor documents the reason in Additional Notes). Excluded
    // here so neither the consolidator nor the per-year engine sees them.
    const byYear = {};
    for (const inst of installments) {
      if (isNaStatusRow(inst)) continue;
      const yr = inst.tax_year || 'Unknown';
      if (!byYear[yr]) byYear[yr] = [];
      byYear[yr].push(inst);
    }
    const sortedYears = Object.keys(byYear).sort();

    // Feb 26, 2026 — Multi-year consolidator (Option A wrapper, MI only).
    // When ≥2 years exist for a jurisdiction, emit ONE paragraph that dedupes
    // the "All prior taxes are paid" / "Taxes are collected semi-annually"
    // preambles and groups years sharing identical status + due/dlq patterns.
    // Specialty years still flow through the per-year engine internally.
    // Single-year jurisdictions stay on the legacy code path (bit-for-bit
    // unchanged).
    if (sortedYears.length >= 2) {
      const consolidated = getMichiganNotesConsolidated(byYear, installments, {
        taxCollected,
        jurisdictionType: jurisdiction.jurisdiction_type || '',
        ...miExemptionOpts,
      });
      allNotes.push(...consolidated);
    } else {
      for (const yr of sortedYears) {
        const yrNotes = getMichiganNotesText(byYear[yr], yr, {
          taxCollected,
          allYearsInstallments: installments,
          jurisdictionType: jurisdiction.jurisdiction_type || '',
          ...miExemptionOpts,
        });
        allNotes.push(...yrNotes);
      }
    }
    return allNotes.join('\n');
  }

  const notes = [];
  const jurisdictionName = jurisdiction.jurisdiction_name || 'General';
  const jurisdictionType = jurisdiction.jurisdiction_type || '';
  
  // Determine jurisdiction type label for notes
  const getTypeLabel = () => {
    // Feb 2026 (P0) — when this call is a Mixed-Category sub-block
    // (e.g. SOLID WASTE: paragraph recursed from a county jurisdiction),
    // the sub-header already labels the category. Suppress the
    // qualifier so the narrative reads "2026 taxes are due" instead of
    // the misleading "2026 county taxes are due".
    if (__subBlock) return '';
    // Jul 2026 — TAX-CATEGORY-001: the STORED bill category wins over
    // the entity type (one collector may carry County/City/School bills).
    const rawCat = ((jurisdiction && jurisdiction.tax_category) || '').toString().trim().toLowerCase();
    if (rawCat && rawCat !== 'other') return rawCat;
    const type = jurisdictionType.toLowerCase();
    if (type.includes('county')) return 'county';
    if (type.includes('city') || type.includes('municipal')) return 'city';
    if (type.includes('school')) return 'school';
    if (type.includes('township')) return 'township';
    if (type.includes('village')) return 'village';
    if (type.includes('district')) return 'district';
    return '';
  };
  
  const typeLabel = getTypeLabel();
  const typeLabelText = typeLabel ? `${typeLabel} ` : '';
  // Jul 2026 — TAX-CATEGORY-001: narrative label sourced ONLY from the
  // stored tax_category (never the entity type), so unset behavior is
  // bit-for-bit unchanged while categorized entries read
  // "2025 county taxes are due" — matching the PDF engine's type_label.
  const catLabelText = (() => {
    if (__subBlock) return '';
    const c = ((jurisdiction && jurisdiction.tax_category) || '').toString().trim().toLowerCase();
    return (c && c !== 'other') ? `${c} ` : '';
  })();

  // ═══════════════════════════════════════════════════════════════════════
  // FULLY EXEMPT — Generate exemption-specific notes and return early
  // ═══════════════════════════════════════════════════════════════════════
  if (jurisdiction.fully_exempt) {
    // Get exemption type label (first real exemption, not "No Exemptions")
    const exemptionTypes = (jurisdiction.exemption_types || []).filter(
      t => t !== 'No Exemptions (N/A)' && t
    );
    // When the user picked "Other" (stored value) — UI label is "Other (Custom)" —
    // prefer their custom `exemption_other` text so notes render
    // `100% <CustomText> exemption` instead of `100% Other exemption`.
    // Defensive: also match the legacy label form `"Other (Custom)"` in case
    // a stale doc stored the display label instead of the canonical token.
    // Jun 2026 — include ALL selected exemption types. Previously only
    // exemptionTypes[0] was used, so "Homestead + Military" rendered as
    // just "100% Homestead exemption" in the notes/PDF and operators had
    // to add a manual comment (user-reported on a rush file).
    const resolveExemptionLabel = (t) => {
      const trimmed = (t || '').trim();
      const lower = trimmed.toLowerCase();
      if (lower === 'other' || lower === 'other (custom)') {
        const custom = (jurisdiction.exemption_other || '').trim();
        return custom || 'Other';
      }
      return trimmed;
    };
    const exemptionLabels = exemptionTypes
      .map(t => resolveExemptionLabel(t).replace(/ exemptions?$/i, '').trim())
      .filter(Boolean);
    const joinExemptionLabels = (arr) => {
      if (arr.length <= 1) return arr[0] || '';
      if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
      return `${arr.slice(0, -1).join(', ')}, and ${arr[arr.length - 1]}`;
    };
    const exemptionLabel = joinExemptionLabels(exemptionLabels) || 'tax';
    // Grammar: "a 100% Homestead exemption" vs "100% Homestead and Military exemptions"
    const exemptionNoun = exemptionLabels.length > 1 ? 'exemptions' : 'exemption';
    const exemptionArticle = exemptionLabels.length > 1 ? '' : 'a ';

    // Helper to format date
    const fmtDate = (dateVal) => {
      if (!dateVal) return null;
      if (typeof dateVal === 'string' && dateVal.includes('/')) return dateVal;
      if (typeof dateVal === 'string' && dateVal.includes('-')) {
        const parts = dateVal.split('-');
        if (parts.length === 3) return `${parts[1]}/${parts[2]}/${parts[0]}`;
      }
      return dateVal;
    };

    // Feb 2026 P0 (FULLY-EXEMPT-MULTI-YEAR-NOTE) — Collect EVERY
    // distinct tax year present in the installments, not just the
    // first one. The legacy code took `installments[0]?.tax_year`
    // which produced "For 2023 taxes..." for a parcel that was
    // exempt across 2023, 2024, AND 2025 (user-reported). The
    // expected wording is "For 2023, 2024, and 2025 taxes..." with
    // the same Oxford-comma joiner already used for multi-exemption
    // labels above. Pure-number sort handles mixed string / number
    // tax_year values that have appeared in the wild.
    const taxYearsAll = Array.from(
      new Set(
        installments
          .map(i => (i?.tax_year ?? '').toString().trim())
          .filter(Boolean)
      )
    ).sort((a, b) => Number(a) - Number(b));
    const joinYears = (arr) => {
      if (arr.length === 0) return '';
      if (arr.length === 1) return arr[0];
      if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
      return `${arr.slice(0, -1).join(', ')}, and ${arr[arr.length - 1]}`;
    };
    const taxYearJoined = joinYears(taxYearsAll);
    const yearText = taxYearJoined ? `${taxYearJoined} ` : '';
    // True when the exemption spans more than one tax year. Drives
    // the date-phrasing branch below: with multiple years we don't
    // try to enumerate every due / delinquency date, we use the
    // generic "applicable delinquency dates" wording.
    const isMultiYearExempt = taxYearsAll.length > 1;
    const typeText = typeLabel ? `${typeLabel} ` : '';

    // Prior-tax prefix: preserve existing prior-year logic
    const hasDelinquent = installments.some(inst =>
      ['delinquent', 'partially delinquent', 'partially dlq'].includes((inst.pay_status || '').toLowerCase())
    );
    const priorTaxPrefix = !hasDelinquent ? 'All prior taxes are paid. ' : '';

    // Determine collection frequency
    const explicitFrequency = (jurisdiction.collection_frequency || '').toLowerCase();
    const installmentNames = installments.map(i => (i.installment_name || '').toLowerCase());
    const isSemiAnnual = explicitFrequency === 'semi_annually' || (!explicitFrequency && installmentNames.some(n =>
      n.includes('half') || n.includes('1st') || n.includes('2nd')
    ));
    const isQuarterly = explicitFrequency === 'quarterly' || (!explicitFrequency && installmentNames.some(n =>
      n.includes('q1') || n.includes('q2') || n.includes('q3') || n.includes('q4') || n.includes('quarter')
    ));

    if (explicitFrequency === 'installment_plan') {
      // TAXCERT-INSTALLMENT-PLAN-001 — variable installment count: generic dates wording.
      return priorTaxPrefix + `For ${yearText}taxes, the homeowner qualifies for ${exemptionArticle}100% ${exemptionLabel} ${exemptionNoun}; therefore, the ${typeText}tax amounts are zero. Taxes are payable under an installment plan and become delinquent after the applicable delinquency dates.`;
    }

    if (isSemiAnnual) {
      // Semi-Annual: include both installments
      const first = installments.find(i => {
        const n = (i.installment_name || '').toLowerCase();
        return n.includes('1st') || n.includes('first');
      });
      const second = installments.find(i => {
        const n = (i.installment_name || '').toLowerCase();
        return n.includes('2nd') || n.includes('second');
      });

      let exemptNote = `For ${yearText}taxes, the homeowner qualifies for ${exemptionArticle}100% ${exemptionLabel} ${exemptionNoun}; therefore, the ${typeText}tax amounts are zero.`;

      // Feb 2026 P0 (FULLY-EXEMPT-MULTI-YEAR-NOTE) — Multi-year cases
      // produce too many due/dlq dates to enumerate. Use the generic
      // "applicable delinquency dates" phrasing.
      if (isMultiYearExempt) {
        exemptNote += ' Taxes become delinquent after the applicable delinquency dates.';
      } else if (first || second) {
        const dateParts = [];
        if (first) {
          const due = fmtDate(first.due_date);
          const dlq = fmtDate(first.dlq_date);
          if (due) {
            let part = `The first-half due date is ${due}`;
            if (dlq) part += `, and taxes become delinquent after ${dlq}`;
            dateParts.push(part);
          }
        }
        if (second) {
          const due = fmtDate(second.due_date);
          const dlq = fmtDate(second.dlq_date);
          if (due) {
            let part = `the second-half due date is ${due}`;
            if (dlq) part += `, and taxes become delinquent after ${dlq}`;
            dateParts.push(part);
          }
        }
        if (dateParts.length > 0) {
          exemptNote += ' ' + dateParts.join('; ') + '.';
        }
      }

      return priorTaxPrefix + exemptNote;
    } else if (isQuarterly) {
      // Quarterly: include all quarters
      const quarterLabels = ['first', 'second', 'third', 'fourth'];
      let exemptNote = `For ${yearText}taxes, the homeowner qualifies for ${exemptionArticle}100% ${exemptionLabel} ${exemptionNoun}; therefore, the ${typeText}tax amounts are zero.`;

      // Feb 2026 P0 (FULLY-EXEMPT-MULTI-YEAR-NOTE) — see Semi-Annual
      // branch above. Multi-year quarterly means up to 16 dates; we
      // use the generic phrasing instead of listing each.
      if (isMultiYearExempt) {
        exemptNote += ' Taxes become delinquent after the applicable delinquency dates.';
        return priorTaxPrefix + exemptNote;
      }

      const dateParts = [];
      for (let qi = 0; qi < 4; qi++) {
        const label = quarterLabels[qi];
        let inst = installments.find(i => {
          const n = (i.installment_name || '').toLowerCase();
          return n.includes(label) || n.includes(`q${qi + 1}`);
        });
        if (!inst && qi < installments.length) inst = installments[qi];

        if (inst) {
          const due = fmtDate(inst.due_date);
          const dlq = fmtDate(inst.dlq_date);
          if (due) {
            let part = `${label} quarter due date is ${due}`;
            if (dlq) part += ` and becomes delinquent after ${dlq}`;
            dateParts.push(part);
          }
        }
      }

      if (dateParts.length > 0) {
        exemptNote += ' The ' + dateParts.join('; ') + '.';
      }

      return priorTaxPrefix + exemptNote;
    } else {
      // Annual: single continuous note
      const inst = installments[0];
      const dueDate = inst ? fmtDate(inst.due_date) : null;
      const dlqDate = inst ? fmtDate(inst.dlq_date) : null;

      let exemptNote = `For ${yearText}taxes, the homeowner qualifies for ${exemptionArticle}100% ${exemptionLabel} ${exemptionNoun}; therefore, the ${typeText}tax amounts are zero.`;

      // Feb 2026 P0 (FULLY-EXEMPT-MULTI-YEAR-NOTE) — multi-year
      // exempt parcels emit the generic phrasing instead of the
      // single-installment-date sentence (which would only describe
      // the first record's dates and be misleading for the rest).
      if (isMultiYearExempt) {
        exemptNote += ' Taxes become delinquent after the applicable delinquency dates.';
      } else if (dueDate) {
        exemptNote += ` The due date is ${dueDate}`;
        if (dlqDate) {
          exemptNote += `, and taxes become delinquent after ${dlqDate}`;
        }
        exemptNote += '.';
      }

      return priorTaxPrefix + exemptNote;
    }
  }
  
  // Helper to get status text
  const getStatusText = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'paid') return 'paid';
    if (s === 'due') return 'due';
    if (s === 'delinquent') return 'delinquent';
    if (s === 'partially delinquent' || s === 'partially dlq') return 'partially paid and currently delinquent';
    if (s === 'partially paid') return 'partially paid';
    return s;
  };

  // Helper: detect discount/penalty/fee qualifier from installment row.
  //
  // Feb 2026 — BILLING/CERT P0: penalty is determined ONLY from
  // money-based signals, never from paid_date vs dlq_date alone.
  // A late payment is a timing fact, not a penalty fact — the
  // collector either assessed a penalty (visible as a non-zero
  // discount_penalty or paid_amount > base_tax) or they did not.
  const getDpQualifier = (inst) => {
    if (!inst) return null;
    const dp = parseFloat(inst.discount_penalty) || 0;
    if (dp < 0) return 'discount';
    if (dp > 0) {
      const paidDate = inst.paid_date ? new Date(inst.paid_date) : null;
      const dueDate = inst.due_date ? new Date(inst.due_date) : null;
      if (paidDate && dueDate && paidDate <= dueDate) return 'fee';
      return 'penalty';
    }
    // Implicit penalty: paid_amount strictly greater than base_tax.
    const pa = parseFloat(inst.paid_amount) || 0;
    const bt = parseFloat(inst.base_tax) || 0;
    if (pa - bt > 0.01) return 'penalty';
    return null;
  };

  // Helper: add discount/penalty/fee qualifier to status text
  const statusWithQualifier = (status, qualifier) => {
    if (!qualifier) return status;
    if (qualifier === 'discount') {
      if (status === 'paid') return 'paid at a discount';
      if (status === 'due') return 'due at a discount';
    } else if (qualifier === 'penalty') {
      if (status === 'paid') return 'paid with penalty';
      if (status === 'due') return 'due with penalty';
    } else if (qualifier === 'fee') {
      if (status === 'paid') return 'paid with additional fees';
      if (status === 'due') return 'due with additional fees';
    }
    return status;
  };
  
  // Helper to format date - handles string, Date objects, null, undefined safely
  const formatDate = (dateVal) => {
    if (!dateVal) return null;
    
    // Handle Date objects
    if (dateVal instanceof Date) {
      if (isNaN(dateVal.getTime())) return null; // Invalid date
      const month = String(dateVal.getMonth() + 1).padStart(2, '0');
      const day = String(dateVal.getDate()).padStart(2, '0');
      const year = dateVal.getFullYear();
      return `${month}/${day}/${year}`;
    }
    
    // Convert to string if not already
    const dateStr = typeof dateVal === 'string' ? dateVal : String(dateVal);
    
    // Skip invalid string conversions like [object Object]
    if (dateStr === '[object Object]' || dateStr === 'undefined' || dateStr === 'null') {
      return null;
    }
    
    // If already in MM/DD/YYYY format, return as-is
    if (dateStr.includes('/')) return dateStr;
    
    // If in YYYY-MM-DD format, convert
    if (dateStr.includes('-')) {
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        return `${parts[1]}/${parts[2]}/${parts[0]}`;
      }
    }
    
    return dateStr;
  };

  // Helper: compute financial data for an installment (single source of truth)
  const getFinancials = (inst) => {
    const baseTax = parseFloat(inst.base_tax) || 0;
    const discPenalty = parseFloat(inst.discount_penalty) || 0;
    const hasBaseTax = inst.base_tax !== null && inst.base_tax !== undefined && inst.base_tax !== '';
    const totalAmount = hasBaseTax ? rc(baseTax + discPenalty) : null;
    const paidAmt = parseFloat(inst.paid_amount) || 0;
    const balance = totalAmount !== null ? rc(totalAmount - paidAmt) : null;
    return { baseTax, discPenalty, hasBaseTax, totalAmount, paidAmt, balance };
  };

  // Helper: generate financial detail notes for a single installment
  // Order: Adjustment → Payment → Delinquency
  const getInstallmentFinancialNotes = (inst, labelPrefix) => {
    const detail = [];
    const { discPenalty, hasBaseTax, totalAmount, paidAmt, balance } = getFinancials(inst);
    const derivedStatus = derivePayStatus(inst);
    const dlqDateStr = formatDate(inst.dlq_date);
    const prefix = labelPrefix ? `${labelPrefix}: ` : '';

    // 1. Adjustment note (discount, penalty, or fee) — includes resulting total
    // For fee + paid scenarios, we merge the fee mention into the payment note instead
    const qualifier = (hasBaseTax && discPenalty > 0) ? getDpQualifier(inst) : null;
    if (hasBaseTax && discPenalty !== 0 && totalAmount !== null) {
      // For fee + fully paid: skip standalone fee note — merged into payment note below
      if (qualifier === 'fee' && derivedStatus === 'Paid') {
        // handled in payment note section
      } else if (discPenalty > 0) {
        if (qualifier === 'fee') {
          detail.push(`${prefix}Additional fees of ${formatCurrency(discPenalty)} have been added, bringing the total amount due to ${formatCurrency(totalAmount)}.`);
        } else {
          detail.push(`${prefix}Penalty/interest of ${formatCurrency(discPenalty)} has been added, bringing the total amount due to ${formatCurrency(totalAmount)}.`);
        }
      } else {
        detail.push(`${prefix}A discount of ${formatCurrency(Math.abs(discPenalty))} has been applied, bringing the total amount due to ${formatCurrency(totalAmount)}.`);
      }
    }

    // 2. Payment note
    if (hasBaseTax && totalAmount !== null) {
      if (derivedStatus === 'Paid') {
        if (qualifier === 'fee' && discPenalty > 0) {
          detail.push(`${prefix}Taxes are paid in full, with an additional fee of ${formatCurrency(discPenalty)} included in the total payment of ${formatCurrency(totalAmount)}.`);
        } else if (qualifier === 'discount' || (discPenalty < 0)) {
          detail.push(`${prefix}Taxes have been paid in full at a discount.`);
        } else {
          detail.push(`${prefix}Taxes have been paid in full.`);
        }
      } else if (paidAmt > 0 && balance !== null && balance > 0) {
        detail.push(`${prefix}An amount of ${formatCurrency(paidAmt)} has been paid, leaving an unpaid balance of ${formatCurrency(balance)}.`);
      }
    }

    return detail;
  };

  // Helper: generate date notes for a single installment
  // DLQ date is always included as a reference date — "becomes delinquent after MM/DD/YYYY"
  const getInstallmentDateNotes = (inst, labelPrefix) => {
    const dateNotes = [];
    const dueDate = formatDate(inst.due_date);
    const dlqDate = formatDate(inst.dlq_date);
    if (dueDate) {
      let dateNote = `${labelPrefix}due date is ${dueDate}`;
      if (dlqDate) dateNote += ` and becomes delinquent after ${dlqDate}`;
      dateNotes.push(dateNote + '.');
    }
    return dateNotes;
  };
  
  // Check for delinquent status
  const hasDelinquent = installments.some(inst => 
    ['delinquent', 'partially delinquent', 'partially dlq'].includes((inst.pay_status || '').toLowerCase())
  );
  
  // Add prior tax status (only if not delinquent)
  if (!hasDelinquent) {
    notes.push('All prior taxes are paid.');
  }
  
  // Get tax year from first installment (skip if empty — notes engine should not assume)
  const taxYear = installments[0]?.tax_year || '';
  
  // If no tax year is set, skip all year-specific notes generation
  if (!taxYear.trim()) {
    return notes.join(' ');
  }
  
  // Determine collection frequency — prefer explicit jurisdiction field, fall back to name inference
  const explicitFrequency = (jurisdiction.collection_frequency || '').toLowerCase();
  const installmentNames = installments.map(i => (i.installment_name || '').toLowerCase());
  // TAXCERT-INSTALLMENT-PLAN-001 — explicit only, never inferred from names.
  const isInstallmentPlan = explicitFrequency === 'installment_plan';
  const isSemiAnnual = explicitFrequency === 'semi_annually' || (!explicitFrequency && installmentNames.some(n => 
    n.includes('half') || n.includes('1st') || n.includes('2nd')
  ));
  const isQuarterly = explicitFrequency === 'quarterly' || (!explicitFrequency && installmentNames.some(n => 
    n.includes('q1') || n.includes('q2') || n.includes('q3') || n.includes('q4') ||
    n.includes('quarter')
  ));

  // ═══════════════════════════════════════════════════════════════════════
  // Group ALL installments by tax_year so manually added records are included
  // ═══════════════════════════════════════════════════════════════════════
  const yearGroups = new Map();
  installments.forEach(inst => {
    const yr = (inst.tax_year || '').trim() || taxYear;
    if (!yearGroups.has(yr)) yearGroups.set(yr, []);
    yearGroups.get(yr).push(inst);
  });

  // Sort years chronologically
  const sortedYears = [...yearGroups.keys()].sort((a, b) => parseInt(a) - parseInt(b));
  
  // Generate tax status note for EACH year group
  if (isInstallmentPlan) {
    generateInstallmentPlanNotes({
      notes, sortedYears, yearGroups, jurisdiction, catLabelText,
      formatDate, getDpQualifier, statusWithQualifier, getFinancials,
    });
  } else if (isSemiAnnual) {
    // ═══════════════════════════════════════════════════════════════════════
    // SEMI-ANNUAL — Standardized Case-Based Notes (Phase 2)
    // Terminology: "first installment" / "second installment"
    // Structure: Prior → Status → Financial → Frequency+dates → Future mailing
    // ═══════════════════════════════════════════════════════════════════════

    notes.length = 0;

    // Line 1: Always present
    notes.push('All prior taxes are paid.');

    // Helper: MM/DD only (no year) for the combined frequency line
    const formatDateMMDD = (dateVal) => {
      const full = formatDate(dateVal);
      if (!full) return null;
      const parts = full.split('/');
      return parts.length >= 2 ? `${parts[0]}/${parts[1]}` : null;
    };

    // Helper: normalize status text for semi-annual
    const getSemiStatus = (inst) => {
      if (!inst) return null;
      const s = (inst.pay_status || '').toLowerCase();
      if (s === 'paid') return 'paid';
      if (s === 'due') return 'due';
      if (s === 'delinquent') return 'delinquent';
      if (s === 'partially paid') return 'partially paid';
      if (s === 'partially delinquent' || s === 'partially dlq') return 'partially delinquent';
      return s || null;
    };

    // Helper: check if installment has meaningful data (base_tax filled)
    const hasInstData = (inst) => {
      if (!inst) return false;
      return inst.base_tax !== null && inst.base_tax !== undefined && inst.base_tax !== '' && parseFloat(inst.base_tax) !== 0;
    };

    // Process each year group
    let allPaidSemi = true;

    sortedYears.forEach(yr => {
      const yearInsts = yearGroups.get(yr);
      const first = yearInsts.find(i => {
        const n = (i.installment_name || '').toLowerCase();
        return n.includes('1st') || n.includes('first');
      });
      const second = yearInsts.find(i => {
        const n = (i.installment_name || '').toLowerCase();
        return n.includes('2nd') || n.includes('second');
      });

      const firstNa = isNaStatusRow(first);
      const secondNa = isNaStatusRow(second);
      const firstStatus = (hasInstData(first) && !firstNa) ? getSemiStatus(first) : null;
      const secondStatus = (hasInstData(second) && !secondNa) ? getSemiStatus(second) : null;

      // Track all-paid flag for future mailing logic
      if (firstStatus && firstStatus !== 'paid') allPaidSemi = false;
      if (secondStatus && secondStatus !== 'paid') allPaidSemi = false;
      if (!firstStatus && !secondStatus) allPaidSemi = false;

      // Determine "not yet available" for missing installments
      // Jul 2026 — NA-PAY-STATUS-001: N/A rows are treated as unavailable
      // for narration, but suppress the auto "not yet available" wording —
      // the processor explains the reason in Additional Notes.
      const firstAvailable = first && hasInstData(first) && !firstNa;
      const secondAvailable = second && hasInstData(second) && !secondNa;

      // --- Status sentence ---
      if (!firstAvailable && !secondAvailable) {
        if (firstNa || secondNa) {
          // Explicit N/A year — no auto explanation.
        } else {
          // Scenario 7: Both not yet available
          notes.push(`${yr} ${catLabelText}taxes are not yet available.`.replace('  ', ' '));
        }
      } else if (firstAvailable && secondAvailable) {

        // Check if any installment needs financial detail (partially paid/delinquent)
        const firstNeedsFinancial = firstStatus === 'partially paid' || firstStatus === 'partially delinquent';
        const secondNeedsFinancial = secondStatus === 'partially paid' || secondStatus === 'partially delinquent';

        if (!firstNeedsFinancial && !secondNeedsFinancial) {
          // Detect discount/penalty qualifiers
          const firstQual = getDpQualifier(first);
          const secondQual = getDpQualifier(second);
          const hasAnyDp = firstQual || secondQual;
          const firstQualified = statusWithQualifier(firstStatus, firstQual);
          const secondQualified = statusWithQualifier(secondStatus, secondQual);

          if (hasAnyDp) {
            // Discount/penalty format: use "installment" terminology
            if (firstQualified === secondQualified) {
              notes.push(`${yr} first and second installments are ${firstQualified}.`);
            } else {
              notes.push(`${yr} first installment is ${firstQualified} and second installment taxes are ${secondQualified}.`);
            }
          } else {
            // Standard format (no discount/penalty)
            if (firstStatus === secondStatus) {
              if (firstStatus === 'paid') {
                notes.push(`${yr} ${catLabelText}taxes are paid.`.replace('  ', ' '));
              } else {
                notes.push(`${yr} first and second installment taxes are ${firstStatus}.`);
              }
            } else {
              notes.push(`${yr} first installment taxes are ${firstStatus} and second installment taxes are ${secondStatus}.`);
            }
          }
        } else {
          // Financial details needed — split format to keep financial grouped with its installment
          // First installment status + financial
          notes.push(`${yr} first installment taxes are ${firstStatus}.`);
          if (firstNeedsFinancial) {
            const { paidAmt, balance } = getFinancials(first);
            if (paidAmt > 0 && balance !== null && balance > 0) {
              const isDelinqBalance = firstStatus === 'partially delinquent' ||
                (first.dlq_date && isDlqDatePassed(first.dlq_date));
              notes.push(`A payment of ${formatCurrency(paidAmt)} has been applied.`);
              if (isDelinqBalance) {
                notes.push(`The remaining unpaid delinquent balance is ${formatCurrency(balance)}.`);
              } else {
                notes.push(`The remaining balance due is ${formatCurrency(balance)}.`);
              }
            }
          }
          // Second installment status + financial
          notes.push(`Second installment taxes are ${secondStatus}.`);
          if (secondNeedsFinancial) {
            const { paidAmt, balance } = getFinancials(second);
            if (paidAmt > 0 && balance !== null && balance > 0) {
              const isDelinqBalance = secondStatus === 'partially delinquent' ||
                (second.dlq_date && isDlqDatePassed(second.dlq_date));
              notes.push(`A payment of ${formatCurrency(paidAmt)} has been applied.`);
              if (isDelinqBalance) {
                notes.push(`The remaining unpaid delinquent balance is ${formatCurrency(balance)}.`);
              } else {
                notes.push(`The remaining balance due is ${formatCurrency(balance)}.`);
              }
            }
          }
        }

      } else if (firstAvailable && !secondAvailable) {
        // Scenario: only first installment exists for this year.
        // Emit a clean first-installment sentence — do NOT inject fallback
        // wording about the second installment being unavailable, since the
        // absence of a second-installment record isn't a certifiable fact
        // (semi-annual jurisdictions often have only one installment present
        // at a given point in the year). Matches PDF generator behavior.
        notes.push(`${yr} first installment taxes are ${firstStatus}.`);
        // Financial details for first if partial
        if (firstStatus === 'partially paid' || firstStatus === 'partially delinquent') {
          const { paidAmt, balance } = getFinancials(first);
          if (paidAmt > 0 && balance !== null && balance > 0) {
            const isDelinqBalance = firstStatus === 'partially delinquent' ||
              (first.dlq_date && isDlqDatePassed(first.dlq_date));
            notes.push(`A payment of ${formatCurrency(paidAmt)} has been applied.`);
            if (isDelinqBalance) {
              notes.push(`The remaining unpaid delinquent balance is ${formatCurrency(balance)}.`);
            } else {
              notes.push(`The remaining balance due is ${formatCurrency(balance)}.`);
            }
          }
        }
      } else if (!firstAvailable && secondAvailable) {
        // Scenario: only second installment exists for this year.
        // Emit a clean second-installment sentence — do NOT inject fallback
        // wording about the first installment being "not available" (the PDF
        // generator omits this same phrase, and the UI must match).
        notes.push(`${yr} second installment taxes are ${secondStatus}.`);
        if (secondStatus === 'partially paid' || secondStatus === 'partially delinquent') {
          const { paidAmt, balance } = getFinancials(second);
          if (paidAmt > 0 && balance !== null && balance > 0) {
            const isDelinqBalance = secondStatus === 'partially delinquent' ||
              (second.dlq_date && isDlqDatePassed(second.dlq_date));
            notes.push(`A payment of ${formatCurrency(paidAmt)} has been applied.`);
            if (isDelinqBalance) {
              notes.push(`The remaining unpaid delinquent balance is ${formatCurrency(balance)}.`);
            } else {
              notes.push(`The remaining balance due is ${formatCurrency(balance)}.`);
            }
          }
        }
      }
    });

    // --- Delinquent payoff (jurisdiction-level) ---
    const anyDelinqStatus = installments.some(inst => {
      const s = (inst.pay_status || '').toLowerCase();
      return s === 'delinquent' || s === 'partially delinquent' || s === 'partially dlq';
    });
    if (anyDelinqStatus) {
      const payoffAmt = parseFloat(jurisdiction.dlq_payoff_amount);
      const goodThruDate = formatDate(jurisdiction.dlq_good_thru_date);
      if (!isNaN(payoffAmt) && payoffAmt > 0 && goodThruDate) {
        notes.push(`Total payoff amount is ${formatCurrency(payoffAmt)}, good through ${goodThruDate}.`);
      }
    }
    // --- Frequency + due/delinquent date line ---
    // Collect MM/DD dates from first year's installments
    const firstYearInsts = yearGroups.get(sortedYears[0]) || [];
    const firstInst = firstYearInsts.find(i => {
      const n = (i.installment_name || '').toLowerCase();
      return n.includes('1st') || n.includes('first');
    });
    const secondInst = firstYearInsts.find(i => {
      const n = (i.installment_name || '').toLowerCase();
      return n.includes('2nd') || n.includes('second');
    });
    const dueDates = [firstInst, secondInst].map(i => i ? formatDateMMDD(i.due_date) : null).filter(Boolean);
    const dlqDates = [firstInst, secondInst].map(i => i ? formatDateMMDD(i.dlq_date) : null).filter(Boolean);

    let freqLine = 'Taxes are paid semi-annually';
    if (dueDates.length > 0) {
      freqLine += `; normal due dates are ${dueDates.join(' and ')}`;
      if (dlqDates.length > 0) {
        freqLine += ` and become delinquent after ${dlqDates.join(' and ')}`;
      }
    }
    freqLine += '.';
    notes.push(freqLine);

    // --- Future mailing (only when ALL installments across all years are paid) ---
    if (allPaidSemi) {
      const latestYear = sortedYears[sortedYears.length - 1];
      let mailDate = null;
      for (let i = sortedYears.length - 1; i >= 0; i--) {
        const yr = sortedYears[i];
        const insts = yearGroups.get(yr);
        for (const inst of insts) {
          const d = formatDate(inst.mailing_issue_date);
          if (d) { mailDate = d; break; }
        }
        if (mailDate) break;
      }
      if (mailDate) {
        const nextYear = parseInt(latestYear) + 1;
        const mailParts = mailDate.split('/');
        const projectedMonth = parseInt(mailParts[0]) - 1;
        const projectedDay = parseInt(mailParts[1]);
        const mailYear = parseInt(mailParts[2]);
        const futureDate = new Date(mailYear + 1, projectedMonth, projectedDay);
        const dow = futureDate.getDay();
        if (dow === 6) futureDate.setDate(futureDate.getDate() + 2);
        else if (dow === 0) futureDate.setDate(futureDate.getDate() + 1);
        const fmMonth = String(futureDate.getMonth() + 1).padStart(2, '0');
        const fmDay = String(futureDate.getDate()).padStart(2, '0');
        const fmYear = futureDate.getFullYear();
        notes.push(`${nextYear} taxes will be mailed on ${fmMonth}/${fmDay}/${fmYear}.`);
      }
    }

  } else if (isQuarterly) {
    // ═══════════════════════════════════════════════════════════════════════
    // QUARTERLY — Standardized Case-Based Notes (Phase 3)
    // Terminology: "first quarter", "second quarter", etc.
    // Structure: Prior taxes → Year summaries (asc) → Payoff → Frequency line
    // ═══════════════════════════════════════════════════════════════════════

    notes.length = 0;

    const quarterLabels = ['first', 'second', 'third', 'fourth'];

    // Helper: MM/DD format for frequency line
    const formatDateMMDD = (dateVal) => {
      const full = formatDate(dateVal);
      if (!full) return null;
      const parts = full.split('/');
      return parts.length >= 2 ? `${parts[0]}/${parts[1]}` : null;
    };

    // Helper: normalize quarterly status
    const getQStatus = (inst) => {
      if (!inst) return null;
      const s = (inst.pay_status || '').toLowerCase();
      if (s === 'paid') return 'paid';
      if (s === 'due') return 'due';
      if (s === 'delinquent') return 'delinquent';
      if (s === 'partially paid') return 'partially paid';
      if (s === 'partially delinquent' || s === 'partially dlq') return 'partially delinquent';
      return s || null;
    };

    // Helper: check if installment has meaningful data
    const hasQData = (inst) => {
      if (!inst) return false;
      return inst.base_tax !== null && inst.base_tax !== undefined && inst.base_tax !== '' && parseFloat(inst.base_tax) !== 0;
    };

    // Helper: join list with commas and "and" → "02/01, 05/01, 08/01, and 11/01"
    const joinWithAnd = (items) => {
      if (items.length <= 1) return items[0] || '';
      if (items.length === 2) return `${items[0]} and ${items[1]}`;
      return items.slice(0, -1).join(', ') + ', and ' + items[items.length - 1];
    };

    // ── Jun 2026 — quarter slot mapping (two-pass) ──
    // Pass 1 assigns rows whose installment_name identifies a quarter
    // ("4th Quarter" → slot 3); pass 2 fills remaining unnamed rows into
    // the empty slots by index. The old single-pass logic dropped a lone
    // "4th Quarter" row into the FIRST slot, which (with the year-collapse
    // below) produced the misleading "YYYY taxes are delinquent." wording
    // for partially-present years (user-reported PDF defect).
    const mapYearQuarters = (yearInsts) => {
      const ordinals = ['1st', '2nd', '3rd', '4th'];
      // Jun 2026 follow-up — the standard Installment dropdown labels are
      // 'Q1'..'Q4' (STANDARD_INSTALLMENT_LABELS.quarterly), which the
      // word/ordinal checks missed, so a lone 'Q4' row still fell through
      // to the index fallback and landed in slot 0 ("first quarter is
      // delinquent" — user re-reported). Regexes cover 'Q4', 'Q 4',
      // '2024-Q4' and 'Quarter 4' without matching the 'q' in 'quarter'.
      const qRegexes = [1, 2, 3, 4].map(k =>
        new RegExp(`(^|[^a-z0-9])q\\s*${k}(?![0-9])|quarter\\s*${k}(?![0-9])`)
      );
      const slots = new Array(4).fill(null);
      const used = new Set();
      for (let qi = 0; qi < 4; qi++) {
        const inst = yearInsts.find(i => {
          if (used.has(i)) return false;
          const n = (i.installment_name || '').toLowerCase();
          return n.includes(quarterLabels[qi]) || n.includes(ordinals[qi]) || qRegexes[qi].test(n);
        });
        if (inst) { slots[qi] = inst; used.add(inst); }
      }
      let slot = 0;
      yearInsts.forEach(i => {
        if (used.has(i)) return;
        while (slot < 4 && slots[slot]) slot++;
        if (slot < 4) { slots[slot] = i; used.add(i); }
      });
      return slots;
    };

    // Track whether any delinquent quarter exists across all years
    let hasAnyDelinqQuarter = false;

    // Pre-build quarter data per year. Leading quarters that are NOT on
    // the certificate (already paid and dropped) are marked `omitted` —
    // they are covered by the "All taxes prior to ..." line and must not
    // be narrated ("not yet available" stays reserved for trailing gaps).
    const yearQuarterData = new Map();
    sortedYears.forEach(yr => {
      const slots = mapYearQuarters(yearGroups.get(yr));
      const firstPresentIdx = slots.findIndex(s => hasQData(s));
      const quarters = slots.map((inst, qi) => {
        // Jul 2026 — NA-PAY-STATUS-001: explicit N/A quarters are skipped
        // from narration entirely (marked omitted; no "not yet available").
        const isNa = isNaStatusRow(inst);
        const available = hasQData(inst) && !isNa;
        const omitted = isNa || (!available && firstPresentIdx >= 0 && qi < firstPresentIdx);
        const status = available ? getQStatus(inst) : 'not yet available';
        if (status === 'delinquent' || status === 'partially delinquent') hasAnyDelinqQuarter = true;
        return { label: quarterLabels[qi], inst, available, status, omitted };
      });
      yearQuarterData.set(yr, quarters);
    });

    // Line 1: Prior taxes — when the earliest year is only partially on
    // the certificate (e.g. only its 4th quarter remains), anchor the
    // prior-paid statement to the first present quarter so the narrative
    // never implies the whole year is on the certificate (Jun 2026).
    const _firstYearQuarters = yearQuarterData.get(sortedYears[0]) || [];
    const _firstPresentQ = _firstYearQuarters.findIndex(q => q.available);
    if (_firstPresentQ > 0) {
      // Operator-approved wording (Jun 2026): anchor to the LAST PAID
      // quarter — "All taxes prior to 2024 third quarter are paid." when
      // the first present row is Q4 ("prior to" reads inclusively in the
      // certificate language).
      notes.push(`All taxes prior to ${sortedYears[0]} ${quarterLabels[_firstPresentQ - 1]} quarter are paid.`);
    } else if (sortedYears.length === 1) {
      notes.push('All prior taxes are paid.');
    } else {
      notes.push(`All taxes prior to ${sortedYears[0]} are paid.`);
    }

    // Process each year in chronological order
    sortedYears.forEach(yr => {
      const quarters = yearQuarterData.get(yr);

      // Check if all available quarters are paid
      const availableQuarters = quarters.filter(q => q.available);
      const allQuartersPaid = availableQuarters.length === 4 && availableQuarters.every(q => q.status === 'paid');

      if (allQuartersPaid) {
        // Check if all paid quarters share the same discount/penalty qualifier
        const paidQualifiers = availableQuarters.map(q => getDpQualifier(q.inst));
        const uniqueQualifiers = [...new Set(paidQualifiers)];
        if (uniqueQualifiers.length === 1 && uniqueQualifiers[0]) {
          notes.push(`${yr} ${catLabelText}taxes are ${statusWithQualifier('paid', uniqueQualifiers[0])}.`.replace('  ', ' '));
        } else {
          notes.push(`${yr} ${catLabelText}taxes are paid.`.replace('  ', ' '));
        }
      } else {
        // Check if any quarter needs financial detail (split format)
        const anyNeedsFinancial = quarters.some(q =>
          q.available && (q.status === 'partially paid' || q.status === 'partially delinquent')
        );

        if (anyNeedsFinancial) {
          // Split format: each quarter on its own with financial details grouped
          const parts = [];
          quarters.forEach((q, qi) => {
            parts.push(`${q.label} quarter taxes are ${q.status}`);
          });
          // Build the combined sentence first, then inject financial details
          // Actually, for split format we need to output each quarter separately
          // Jun 2026 — omitted leading quarters (prior-paid) are skipped.
          quarters.filter(q => !q.omitted).forEach((q, qi) => {
            const needsFinancial = q.available && (q.status === 'partially paid' || q.status === 'partially delinquent');
            if (qi === 0) {
              notes.push(`${yr} ${q.label} quarter taxes are ${q.status}.`);
            } else {
              const cap = q.label.charAt(0).toUpperCase() + q.label.slice(1);
              notes.push(`${cap} quarter taxes are ${q.status}.`);
            }
            if (needsFinancial && q.inst) {
              const { paidAmt, balance } = getFinancials(q.inst);
              if (paidAmt > 0 && balance !== null && balance > 0) {
                const isDelinqBalance = q.status === 'partially delinquent' ||
                  (q.inst.dlq_date && isDlqDatePassed(q.inst.dlq_date));
                notes.push(`A payment of ${formatCurrency(paidAmt)} has been applied.`);
                if (isDelinqBalance) {
                  notes.push(`The remaining unpaid delinquent balance is ${formatCurrency(balance)}.`);
                } else {
                  notes.push(`The remaining balance due is ${formatCurrency(balance)}.`);
                }
              }
            }
          });
        } else {
          // Combined format: group quarters by status into one sentence
          // Check for discount/penalty qualifiers
          quarters.forEach(q => {
            q.qualifier = q.available ? getDpQualifier(q.inst) : null;
            q.qualifiedStatus = statusWithQualifier(q.status, q.qualifier);
          });
          const hasAnyDp = quarters.some(q => q.qualifier);

          if (hasAnyDp) {
            // Discount/penalty aware format
            const allQualified = quarters.filter(q => q.available).map(q => q.qualifiedStatus);
            // Jun 2026 — collapse to "YYYY taxes are X." ONLY when all four
            // quarters are actually on the certificate; for partial years
            // the narrative must stay at quarter level ("YYYY fourth
            // quarter is delinquent."), never implying the whole year.
            if (new Set(allQualified).size === 1 && availableQuarters.length === 4) {
              // All quarters same qualified status
              notes.push(`${yr} ${catLabelText}taxes are ${allQualified[0]}.`.replace('  ', ' '));
            } else {
              // Mixed statuses — group consecutive quarters by their qualified status
              const statusGroups = [];
              let currentGroup = null;
              quarters.filter(q => q.available).forEach(q => {
                if (!currentGroup || currentGroup.qualifiedStatus !== q.qualifiedStatus) {
                  currentGroup = { qualifiedStatus: q.qualifiedStatus, labels: [q.label] };
                  statusGroups.push(currentGroup);
                } else {
                  currentGroup.labels.push(q.label);
                }
              });

              const groupParts = statusGroups.map(g => {
                const labelStr = joinWithAnd(g.labels);
                const verb = g.labels.length === 1 ? 'is' : 'are';
                return `${labelStr} quarter${g.labels.length === 1 ? '' : 's'} ${verb} ${g.qualifiedStatus}`;
              });

              let yearLine;
              if (groupParts.length === 1) {
                yearLine = `${yr} ${groupParts[0]}.`;
              } else if (groupParts.length === 2) {
                yearLine = `${yr} ${groupParts[0]} and ${groupParts[1]}.`;
              } else {
                const last = groupParts.pop();
                yearLine = `${yr} ${groupParts.join(', ')}, and ${last}.`;
              }
              notes.push(yearLine);
            }
          } else {
            // No discount/penalty — existing behavior
            // Group consecutive-status quarters together preserving order
            // (Jun 2026 — omitted leading quarters are prior-paid: skipped)
            const statusGroups = [];
            let currentGroup = null;
            quarters.filter(q => !q.omitted).forEach(q => {
              if (!currentGroup || currentGroup.status !== q.status) {
                currentGroup = { status: q.status, labels: [q.label] };
                statusGroups.push(currentGroup);
              } else {
                currentGroup.labels.push(q.label);
              }
            });

            const groupParts = statusGroups.map(g => {
              const labelStr = joinWithAnd(g.labels);
              return `${labelStr} quarter taxes are ${g.status}`;
            });

            // Join groups: "first quarter taxes are paid and second, third, and fourth quarter taxes are due."
            // Use comma for 3+, "and" before last
            let yearLine;
            if (groupParts.length === 1) {
              yearLine = `${yr} ${groupParts[0]}.`;
            } else if (groupParts.length === 2) {
              yearLine = `${yr} ${groupParts[0]} and ${groupParts[1]}.`;
            } else {
              const last = groupParts.pop();
              yearLine = `${yr} ${groupParts.join(', ')}, and ${last}.`;
            }
            notes.push(yearLine);
          }
        }
      }
    });

    // --- Delinquent payoff (jurisdiction-level, once after all year summaries) ---
    if (hasAnyDelinqQuarter) {
      const payoffAmt = parseFloat(jurisdiction.dlq_payoff_amount);
      const goodThruDate = formatDate(jurisdiction.dlq_good_thru_date);
      if (!isNaN(payoffAmt) && payoffAmt > 0 && goodThruDate) {
        notes.push(`Total payoff amount is ${formatCurrency(payoffAmt)}, good through ${goodThruDate}.`);
      }
    }

    // --- Frequency line with all 4 due/delinquent dates ---
    // Use the latest year with the most complete quarter data, not the earliest
    let freqSourceInsts = null;
    for (let i = sortedYears.length - 1; i >= 0; i--) {
      const yrInsts = yearGroups.get(sortedYears[i]) || [];
      if (yrInsts.length >= 4) { freqSourceInsts = yrInsts; break; }
    }
    // Fall back: latest year, then earliest
    if (!freqSourceInsts) freqSourceInsts = yearGroups.get(sortedYears[sortedYears.length - 1]) || yearGroups.get(sortedYears[0]) || [];

    const dueDates = [];
    const dlqDates = [];
    for (let qi = 0; qi < 4; qi++) {
      const inst = freqSourceInsts[qi];
      if (inst) {
        const dd = formatDateMMDD(inst.due_date);
        const dq = formatDateMMDD(inst.dlq_date);
        if (dd) dueDates.push(dd);
        if (dq) dlqDates.push(dq);
      }
    }

    let freqLine = 'Taxes are paid quarterly';
    if (dueDates.length > 0) {
      freqLine += `; normal due dates are ${joinWithAnd(dueDates)}`;
      if (dlqDates.length > 0) {
        freqLine += ` and become delinquent after ${joinWithAnd(dlqDates)}`;
      }
    }
    freqLine += '.';
    notes.push(freqLine);

  } else {
    // ═══════════════════════════════════════════════════════════════════════
    // ANNUAL — Standardized Case-Based Notes (Phase 1)
    // Templates: All Paid, All Due, Mixed, Delinquent, Partially Paid/Dlq
    // ═══════════════════════════════════════════════════════════════════════

    // Clear pre-existing notes (the shared "All prior taxes" may or may not be there)
    // and rebuild with the standard Annual structure from scratch
    notes.length = 0;

    // Line 1: Always present
    notes.push('All prior taxes are paid.');

    // Line 2: Always present
    notes.push('Taxes are collected annually.');

    // Collect per-year status and discount/penalty qualifier (use first installment per year for annual)
    const yearStatusMap = new Map();
    sortedYears.forEach(yr => {
      const inst = yearGroups.get(yr)[0];
      // Jul 2026 — NA-PAY-STATUS-001: explicit N/A years get no sentence.
      if (isNaStatusRow(inst)) return;
      const raw = (inst.pay_status || '').toLowerCase();
      let status;
      if (raw === 'paid') status = 'paid';
      else if (raw === 'due') status = 'due';
      else if (raw === 'delinquent') status = 'delinquent';
      else if (raw === 'partially paid') status = 'partially paid';
      else if (raw === 'partially delinquent' || raw === 'partially dlq') status = 'partially delinquent';
      else status = raw || 'due';
      const qualifier = getDpQualifier(inst);
      yearStatusMap.set(yr, { status, qualifier, inst });
    });

    // Group years by (status + qualifier) so discount/penalty years get separate entries
    const annualStatusGroups = new Map();
    yearStatusMap.forEach(({ status, qualifier }, yr) => {
      const groupKey = qualifier ? `${status}|${qualifier}` : status;
      if (!annualStatusGroups.has(groupKey)) annualStatusGroups.set(groupKey, { status, qualifier, years: [] });
      annualStatusGroups.get(groupKey).years.push(yr);
    });

    // Helper: join years list → "2025" or "2025 and 2026" or "2024, 2025 and 2026"
    const joinYears = (years) => {
      if (years.length === 1) return years[0];
      return years.slice(0, -1).join(', ') + ' and ' + years[years.length - 1];
    };

    // Line 3+: Status sentences — output in logical order with discount/penalty qualifiers
    // Paid/Due/Delinquent can be grouped; Partially Paid/Partially Delinquent get per-year detail
    const statusOutputOrder = ['paid', 'exempt', 'due', 'partially paid', 'delinquent', 'partially delinquent'];
    const qualifierOutputOrder = [null, 'discount', 'penalty', 'fee'];

    statusOutputOrder.forEach(status => {
      qualifierOutputOrder.forEach(qual => {
        const groupKey = qual ? `${status}|${qual}` : status;
        const group = annualStatusGroups.get(groupKey);
        if (!group || group.years.length === 0) return;

        const qualifiedStatus = statusWithQualifier(status, qual);

        if (status === 'paid' || status === 'exempt' || status === 'due' || status === 'delinquent') {
          if (status === 'paid' && qual === 'fee') {
            // Paid with fee: include fee amount detail
            group.years.forEach(yr => {
              const { inst } = yearStatusMap.get(yr);
              const { discPenalty, totalAmount } = getFinancials(inst);
              if (discPenalty > 0 && totalAmount !== null) {
                notes.push(`${yr} ${catLabelText}taxes are paid in full, with an additional fee of ${formatCurrency(discPenalty)} included in the total payment of ${formatCurrency(totalAmount)}.`);
              } else {
                notes.push(`${yr} ${catLabelText}taxes are ${qualifiedStatus}.`);
              }
            });
          } else {
            // Grouped: "2025 and 2026 taxes are paid at a discount."
            notes.push(`${joinYears(group.years)} ${catLabelText}taxes are ${qualifiedStatus}.`);
          }
        } else if (status === 'partially paid') {
          // Per-year with financial detail
          group.years.forEach(yr => {
            const { inst } = yearStatusMap.get(yr);
            notes.push(`${yr} ${catLabelText}taxes are partially paid.`.replace('  ', ' '));
            const { paidAmt, balance } = getFinancials(inst);
            if (paidAmt > 0 && balance !== null && balance > 0) {
              notes.push(`An amount of ${formatCurrency(paidAmt)} has been paid, leaving an unpaid balance of ${formatCurrency(balance)}.`);
            }
          });
        } else if (status === 'partially delinquent') {
          // Per-year with financial detail
          group.years.forEach(yr => {
            const { inst } = yearStatusMap.get(yr);
            notes.push(`${yr} ${catLabelText}taxes are partially delinquent.`.replace('  ', ' '));
            const { paidAmt, balance } = getFinancials(inst);
            if (paidAmt > 0 && balance !== null && balance > 0) {
              notes.push(`An amount of ${formatCurrency(paidAmt)} has been paid, leaving an unpaid balance of ${formatCurrency(balance)}.`);
            }
          });
        }
      });
    });

    // Due date sentence — one combined line from the latest year's record
    const latestYear = sortedYears[sortedYears.length - 1];
    const latestInst = yearGroups.get(latestYear)?.[0];
    if (latestInst) {
      const dueDate = formatDate(latestInst.due_date);
      const dlqDate = formatDate(latestInst.dlq_date);
      if (dueDate) {
        let dateLine = `Taxes due date is ${dueDate}`;
        if (dlqDate) dateLine += ` and becomes delinquent after ${dlqDate}`;
        notes.push(dateLine + '.');
      }
    }

    // Delinquent payoff — only when any year is delinquent or partially delinquent
    const hasAnyDelinquent = [...yearStatusMap.values()].some(
      ({ status }) => status === 'delinquent' || status === 'partially delinquent'
    );
    if (hasAnyDelinquent) {
      const payoffAmt = parseFloat(jurisdiction.dlq_payoff_amount);
      const goodThruDate = formatDate(jurisdiction.dlq_good_thru_date);
      if (!isNaN(payoffAmt) && payoffAmt > 0 && goodThruDate) {
        notes.push(`Outstanding delinquent payoff is ${formatCurrency(payoffAmt)} good thru ${goodThruDate}.`);
      }
    }

    // Future mailing — only when ALL years are paid AND a mailing date exists
    // Scan backwards (latest year first) for the last non-empty mailing date
    const allYearsPaid = [...yearStatusMap.values()].every(({ status }) => status === 'paid');
    if (allYearsPaid) {
      let mailDate = null;
      for (let i = sortedYears.length - 1; i >= 0; i--) {
        const yr = sortedYears[i];
        const insts = yearGroups.get(yr);
        for (const inst of insts) {
          const d = formatDate(inst.mailing_issue_date);
          if (d) { mailDate = d; break; }
        }
        if (mailDate) break;
      }
      if (mailDate) {
        // Extract MM/DD from the found mailing date and project into next tax year
        const nextYear = parseInt(latestYear) + 1;
        const mailParts = mailDate.split('/');
        const projectedMonth = parseInt(mailParts[0]) - 1; // JS months are 0-indexed
        const projectedDay = parseInt(mailParts[1]);
        const mailYear = parseInt(mailParts[2]);
        const futureDate = new Date(mailYear + 1, projectedMonth, projectedDay);

        // Weekend adjustment: Saturday → Monday (+2), Sunday → Monday (+1)
        const dow = futureDate.getDay();
        if (dow === 6) futureDate.setDate(futureDate.getDate() + 2);
        else if (dow === 0) futureDate.setDate(futureDate.getDate() + 1);

        const fmMonth = String(futureDate.getMonth() + 1).padStart(2, '0');
        const fmDay = String(futureDate.getDate()).padStart(2, '0');
        const fmYear = futureDate.getFullYear();
        const projectedMailDate = `${fmMonth}/${fmDay}/${fmYear}`;

        notes.push(`${nextYear} tax bills are expected to be mailed on or around ${projectedMailDate} (Tentative).`);
      }
    }
  }
  
  // (All frequency branches now handle their own delinquent payoff internally)
  
  return notes.join(' ');
};

/**
 * Individual Installment Row within Tax Summary tab
 * 
 * Financial field logic:
 *   Total Amount = Base Tax + Disc/Penalty  (auto-calculated)
 *   Balance = Total Amount - Paid Amount    (auto-calculated)
 *   
 * Status derivation (from data, not dropdown):
 *   Balance = 0           → Paid
 *   Paid > 0 + DLQ passed → Partially Delinquent
 *   Paid > 0              → Partially Paid
 *   DLQ Date passed       → Delinquent
 *   else                  → Due
 *
 * Dropdown only has: Due / Paid / Delinquent (base intents)
 * User dropdown selection takes priority — auto-derive only on field changes
 * System auto-derives: Partially Paid, Partially Delinquent
 */
const InstallmentRow = React.memo(({
  installment,
  index,
  onUpdate,
  onRemove,
  canRemove,
  readOnly = false,
  hasDuplicateDueDate = false,
  allInstallments = [],
  stateCode = '',
  fullyExempt = false,
  // May 17, 2026 — when the parent parcel is in New Parcel mode, the
  // row-level "Paid Date required when Paid/Partially Paid" inline
  // validation is suppressed because the standardised PDF note already
  // covers the missing payment context.
  isNewParcel = false,
  // Jun 2026 — required-field inline errors ("DLQ Date is required",
  // "Paid Date is required...", "Due/Mail Date is required") are gated:
  // they appear only after the operator has interacted with that field
  // or after a Review Tax Sheet attempt (validationAttempted), so e.g.
  // setting Pay Status = Paid no longer instantly floods the row with
  // red "required" messages.
  validationAttempted = false,
  // Feb 2026 — Combined Delinquent Payoff mode (jurisdiction-level toggle).
  // When the parent jurisdiction is in `combined` breakdown mode, the
  // per-row Payoff Amount + Payoff Good-Through fields are soft-disabled
  // — values are preserved on the model so toggling back to year-wise
  // restores them, but the inputs become read-only and a hint explains
  // that the combined fields are authoritative.
  payoffFieldsDisabled = false,
  // Feb 2026 — Bug fix: once a row is set to a Specialty option (Solid
  // Waste / SA / etc.) the dropdown was hiding the standard frequency
  // labels (Q1 / 1st Half / Annual …), trapping the row in the
  // specialty bucket. Passing the current Tax Collected frequency in
  // lets the dropdown render BOTH the standard labels for the active
  // frequency AND the specialty groups so users can flip rows in
  // either direction.
  collectionFrequency = '',
  // Feb 2026 v2 — `(tax_year, jurisdiction)` group-level Paid Breakdown
  // N/A flag, propagated from the year-group header. When true, this
  // row is part of a lump-sum group: hide Adj/Paid/Balance cells,
  // force pay_status = Paid, suppress derived delinquency UI.
  groupBreakdownNa = false,
}) => {
  // Compute derived financial values
  const baseTax = parseFloat(installment.base_tax) || 0;
  const discPenalty = parseFloat(installment.discount_penalty) || 0;
  const hasBaseTax = installment.base_tax !== null && installment.base_tax !== undefined && installment.base_tax !== '';
  const computedTotalAmount = hasBaseTax ? rc(baseTax + discPenalty) : null;
  const paidAmount = parseFloat(installment.paid_amount) || 0;
  // Feb 2026 — When Paid Breakdown N/A is ON, the operator has declared
  // the row paid via a non-itemised total. Force balance to 0 so the
  // Balance pill and derived-status badge don't surface a misleading
  // "Partially Delinquent" indicator when paid_amount differs from
  // base_tax (which it almost always will, since the breakdown is unknown).
  // Jul 2026 — Exempt rows always balance to $0.00, whatever the amounts say.
  const _isExemptStatusRow = String(installment.pay_status || '').trim().toLowerCase() === 'exempt';
  // Jul 2026 — N/A rows have no computable balance (amount unavailable).
  const _isNaBalanceRow = String(installment.pay_status || '').trim().toLowerCase() === 'n/a';
  const computedBalance = _isNaBalanceRow
    ? null
    : ((installment.paid_breakdown_na || _isExemptStatusRow)
        ? 0
        : (computedTotalAmount !== null
            ? Math.max(0, rc(computedTotalAmount - paidAmount))
            : null));

  // Derive effective status from financial data.
  // Feb 2026 v2 — When this row is part of a (year, jurisdiction) group
  // whose Paid Breakdown N/A toggle is ON (or carries the legacy v1
  // row-level flag), force the derived status to "Paid" so the
  // derived-status pill doesn't surface a stale "Delinquent" /
  // "Partially Delinquent" label from before the group was activated.
  const _effectiveBreakdownNa = !!groupBreakdownNa || !!installment.paid_breakdown_na;
  const derivedStatus = _effectiveBreakdownNa ? 'Paid' : derivePayStatus(installment);
  const baseDropdownValue = _effectiveBreakdownNa ? 'Paid' : getBaseStatus(installment.pay_status);
  // May 17, 2026 — suppress the derived-status hint when the parent
  // parcel is in New Parcel mode. The dropdown is intentionally locked
  // to "Refer Note" / "Due" so the financial-derived label ("Paid",
  // "Partially Paid", etc.) is irrelevant and creates visual noise.
  const showDerivedBadge = derivedStatus !== baseDropdownValue && !isNewParcel;

  // Is the record currently in a delinquent state? (for Total Amount editability)
  const isDelinquentStatus = ['delinquent', 'partially delinquent'].includes(
    (installment.pay_status || '').toLowerCase()
  );

  // Adjustment Type state — tracks user's explicit selection independently of value
  // Date-aware: if paid on/before due date, extra amount is a "fee" not "penalty"
  const getAutoAdjType = (dpValue, inst) => {
    if (dpValue < 0) return 'discount';
    if (dpValue > 0) {
      const paidDate = inst?.paid_date ? new Date(inst.paid_date) : null;
      const dueDate = inst?.due_date ? new Date(inst.due_date) : null;
      // If paid on or before due date → "fee" (not penalty)
      if (paidDate && dueDate && paidDate <= dueDate) return 'fee';
      return 'penalty';
    }
    return 'none';
  };
  const derivedAdjType = getAutoAdjType(discPenalty, installment);
  const [adjTypeIntent, setAdjTypeIntent] = useState(derivedAdjType);
  // Tracks whether the user made an explicit Adj Type selection. When true, the
  // date-aware auto-derivation (paidDate vs dueDate → fee/penalty) must NOT
  // overwrite the manual choice. Sign-incompatible values (e.g. manual='penalty'
  // but discount_penalty < 0) still force a re-derive because the manual label
  // is then logically impossible.
  const [adjTypeIsManual, setAdjTypeIsManual] = useState(false);

  // Sync intent when discount_penalty changes externally (e.g., status change resets)
  useEffect(() => {
    if (discPenalty !== 0) {
      // Preserve the user's explicit Adj Type selection when the sign of
      // discount_penalty is still compatible with it:
      //   - manual 'discount'       → requires discPenalty < 0
      //   - manual 'penalty' / 'fee' → requires discPenalty > 0
      // Only re-derive when the manual label is no longer compatible with the
      // sign of the adjustment (hard sign flip), in which case we also drop the
      // manual flag so subsequent changes use auto-inference again.
      const manualIsCompatible = adjTypeIsManual && (
        (adjTypeIntent === 'discount' && discPenalty < 0) ||
        ((adjTypeIntent === 'penalty' || adjTypeIntent === 'fee') && discPenalty > 0)
      );
      if (!manualIsCompatible) {
        setAdjTypeIntent(getAutoAdjType(discPenalty, installment));
        if (adjTypeIsManual) setAdjTypeIsManual(false);
      }
    } else if (adjTypeIntent !== 'none' && discPenalty === 0) {
      // Keep the intent if user just selected a type but hasn't entered amount yet
    }
  }, [discPenalty, installment.paid_date, installment.due_date]);

  // Reset adj type intent when leaving delinquent with cleared penalty
  useEffect(() => {
    if (!isDelinquentStatus && discPenalty === 0) {
      setAdjTypeIntent('none');
      setAdjTypeIsManual(false);
    }
  }, [isDelinquentStatus, discPenalty]);
  
  // Adj Amt is disabled only when type intent is 'none'
  const adjAmtDisabled = readOnly || adjTypeIntent === 'none';

  // ── Paid Breakdown N/A (Feb 2026 — v2: year + jurisdiction group level) ──
  // The canonical flag lives on the parent jurisdiction:
  //   `jurisdiction.paid_breakdown_na_groups[tax_year].enabled`
  // and is propagated into this row via the `groupBreakdownNa` prop.
  // The legacy row-level flag (`installment.paid_breakdown_na`) is kept
  // as a read-time fallback so already-saved data renders correctly
  // before the next save migrates it to group level.
  //
  // When effective ON:
  //   - Hides Adj Type / Adj Amt / Total Amt / Paid Amt / Balance
  //   - Keeps Base Tax visible (assessed value still recorded)
  //   - Pay Status auto-forced to "Paid" by the year-group handler
  //   - The PDF table renders "See Notes" in the Paid Amt column for
  //     every installment in the group; the notes block carries the
  //     lump-sum total + paid date once per group.
  const breakdownNa = !!groupBreakdownNa || !!installment.paid_breakdown_na;

  // Paid Amount is always editable — entering a value under Due or Delinquent triggers partial derivation
  const isPaidAmountEditable = true;
  // Paid Date is always editable — user can enter it alongside any payment

  // ── Real-time inline date validation ──
  // Jun 2026 — per-field interaction tracking: required-type errors only
  // render once the field was touched (committed a change) or after a
  // failed Review attempt. Chronology/format errors stay immediate (they
  // can only fire after the operator typed into the involved fields).
  const [touchedFields, setTouchedFields] = useState({});
  const _showReq = (field) => validationAttempted || !!touchedFields[field];

  const dateErrors = (() => {
    const errors = {};

    // Tax Year must be a 4-digit year (YYYY) or a fiscal range
    // YYYY/YYYY (e.g. 2025/2026 — required by MI/PA/IL fiscal rolls).
    // SUMMER/WINTER and other season labels live in the separate
    // `season` field, so this regex never sees them.
    const tyRaw = (installment.tax_year || '').toString().trim();
    if (tyRaw && !/^\d{4}(\/\d{4})?$/.test(tyRaw)) {
      errors.tax_year = 'Tax Year must be a 4-digit year (e.g., 2024) or a fiscal range YYYY/YYYY (e.g., 2025/2026).';
    }

    const dueDate = parseValidationDate(installment.due_date);
    const mailDate = parseValidationDate(installment.mailing_issue_date);
    const paidDate = parseValidationDate(installment.paid_date);
    const dlqDate = parseValidationDate(installment.dlq_date);
    const goodThruDate = parseValidationDate(installment.payoff_good_through_date);

    // Rule 1: Mail date cannot be after due date
    if (mailDate && dueDate && mailDate > dueDate) {
      errors.mailing_issue_date = 'Mail date cannot be after due date.';
    }
    // Rule 2: Paid date cannot be earlier than mail date
    if (paidDate && mailDate && paidDate < mailDate) {
      errors.paid_date = 'Paid date cannot be earlier than mail date.';
    }
    // Rule 3: Delinquent date cannot be earlier than due date
    if (dlqDate && dueDate && dlqDate < dueDate) {
      errors.dlq_date = 'Delinquent date cannot be earlier than due date.';
    }
    // Rule 4: Good thru date cannot be earlier than delinquent date
    if (goodThruDate && dlqDate && goodThruDate < dlqDate) {
      errors.payoff_good_through_date = 'Good thru date cannot be earlier than delinquent date.';
    }
    // Rule 5: Paid Date is required when Pay Status = Paid or Partially Paid
    // Exception: a Fully Exempt zero-tax row with Pay Status = Paid has no
    // real payment date — relaxed here. Mirrors isExemptZeroRow() helper.
    // May 17, 2026 — also relaxed when the parent parcel is in New Parcel
    // mode; the standardised PDF note covers the missing payment context.
    // Jun 2026 v2.5 — also relaxed under Paid Breakdown N/A treatment
    // (per-year group or All-Years): the canonical Paid Date lives on the
    // group/all-years entry, not the row.
    const payStatus = (installment.pay_status || '').toLowerCase();
    const _rowIsExemptZero = isExemptZeroRow({ fully_exempt: fullyExempt }, installment);
    if ((payStatus === 'paid' || payStatus === 'partially paid') && !installment.paid_date && !isNewParcel && !breakdownNa && _showReq('paid_date')) {
      if (!(payStatus === 'paid' && _rowIsExemptZero)) {
        errors.paid_date = 'Paid Date is required when status is ' + installment.pay_status + '.';
      }
    }
    // Rule 6: DLQ Date is always required — but only when the row has user-entered data
    // Excludes auto-populated defaults: tax_year (may be copied), installment_name (auto-set),
    // pay_status "Due" (system default), base_tax 0/$0.00 (placeholder default)
    const hasUserEnteredData = !!(
      installment.due_date ||
      installment.mailing_issue_date ||
      installment.paid_date ||
      (payStatus && payStatus !== 'due') ||
      (installment.base_tax !== null && installment.base_tax !== undefined && String(installment.base_tax).trim() !== '' && parseFloat(installment.base_tax) !== 0)
    );
    if (hasUserEnteredData && !installment.dlq_date && !breakdownNa && _showReq('dlq_date')) {
      errors.dlq_date = 'DLQ Date is required.';
    }
    // Rule 7 (Jun 2026): Due Date is mandatory — same gating as Rule 6.
    if (hasUserEnteredData && !installment.due_date && !breakdownNa && !isNewParcel && _showReq('due_date')) {
      errors.due_date = 'Due Date is required.';
    }
    // Rule 8 (Jun 2026): Mail Date is mandatory — same gating as Rule 6.
    if (hasUserEnteredData && !installment.mailing_issue_date && !breakdownNa && !isNewParcel && _showReq('mailing_issue_date')) {
      errors.mailing_issue_date = 'Mail Date is required.';
    }
    return errors;
  })();

  const handleFieldChange = (field, value) => {
    // Jun 2026 — mark the field as touched so its required-type inline
    // error may render from now on (see _showReq above).
    setTouchedFields((t) => (t[field] ? t : { ...t, [field]: true }));
    const updated = { ...installment, [field]: value };

    // Recalculate total amount
    const newBaseTax = field === 'base_tax' ? parseCurrencyInput(String(value ?? '')) : installment.base_tax;
    const newDiscPenalty = field === 'discount_penalty' ? parseCurrencyInput(String(value ?? '')) : installment.discount_penalty;
    const numBaseTax = parseFloat(newBaseTax) || 0;
    const numDiscPenalty = parseFloat(newDiscPenalty) || 0;
    const newHasBaseTax = newBaseTax !== null && newBaseTax !== undefined && newBaseTax !== '';
    const newTotalAmount = newHasBaseTax ? rc(numBaseTax + numDiscPenalty) : null;

    // Store computed total in amount_due for backward compat & PDF
    updated.amount_due = newTotalAmount;

    // Handle dropdown selection (base intent) — auto-fill behavior
    if (field === 'pay_status') {
      const intent = (value || '').toLowerCase();
      const prevStatus = (installment.pay_status || '').toLowerCase();
      const wasDelinquent = prevStatus === 'delinquent' || prevStatus === 'partially delinquent';
      if (intent === 'paid') {
        // Auto-discount gets highest priority: if paid_amount is already entered
        // and less than base_tax, auto-populate discount regardless of previous status
        const existingPaid = parseFloat(installment.paid_amount) || 0;
        if (newHasBaseTax && existingPaid > 0 && existingPaid < numBaseTax) {
          const discountAmt = rc(numBaseTax - existingPaid);
          updated.discount_penalty = -discountAmt;
          updated.amount_due = rc(numBaseTax + (-discountAmt));
          updated.paid_amount = existingPaid;
        } else if (wasDelinquent) {
          // Clear auto-penalty adjustment when leaving Delinquent (no discount needed)
          updated.discount_penalty = 0;
          const effectiveTotal = newHasBaseTax ? numBaseTax : null;
          updated.amount_due = effectiveTotal;
          updated.paid_amount = effectiveTotal;
        } else {
          // Standard: set paid amount to total
          updated.paid_amount = newTotalAmount;
        }
        // Auto-fill: balance = 0
        // NOTE: paid_date is NOT auto-filled — user must enter the actual date from the tax collector
        updated.balance_amount = 0;
        // Clear payoff fields when no longer delinquent
        updated.payoff_amount = null;
        updated.payoff_good_through_date = null;
      } else if (intent === 'due') {
        // Clear auto-penalty adjustment when leaving Delinquent
        if (wasDelinquent) {
          updated.discount_penalty = 0;
          const effectiveTotal = newHasBaseTax ? numBaseTax : null;
          updated.amount_due = effectiveTotal;
          updated.balance_amount = effectiveTotal;
        } else {
          updated.balance_amount = newTotalAmount;
        }
        // Clear payment fields
        updated.paid_amount = null;
        updated.dlq_date = null;
        // Clear payoff fields when no longer delinquent
        updated.payoff_amount = null;
        updated.payoff_good_through_date = null;
      } else if (intent === 'delinquent') {
        // Clear paid amount, keep/expect DLQ date
        updated.paid_amount = null;
        updated.balance_amount = newTotalAmount;
        // Auto-populate payoff from total amount (unless user previously overrode it)
        if (!installment.payoff_manual) {
          updated.payoff_amount = newTotalAmount;
        }
      } else if (intent === 'exempt') {
        // Jul 2026 — Exempt: no payment occurs. Clear payment fields,
        // force $0.00 balance, and drop any payoff carry-over.
        updated.paid_amount = null;
        updated.paid_date = null;
        updated.balance_amount = 0;
        updated.payoff_amount = null;
        updated.payoff_good_through_date = null;
      } else if (intent === 'n/a') {
        // Jul 2026 — N/A (NA-PAY-STATUS-001): amount unavailable. Clear
        // payment/payoff fields; monetary fields stay blank (not $0).
        updated.paid_amount = null;
        updated.paid_date = null;
        updated.balance_amount = null;
        updated.payoff_amount = null;
        updated.payoff_good_through_date = null;
      }
    } else {
      // For any other field change, recalculate balance
      const newPaidAmount = field === 'paid_amount' ? parseCurrencyInput(String(value ?? '')) : installment.paid_amount;
      const numPaid = parseFloat(newPaidAmount) || 0;

      // Feb 2026 — Paid Breakdown N/A: skip the auto-discount/penalty
      // derivation. The tax collector didn't provide a breakdown, so we
      // intentionally do NOT infer Adj Type/Amt from the base_tax vs
      // paid_amount delta. Keep discount_penalty = 0 and let balance =
      // base_tax - paid_amount (clamped at 0).
      if (updated.paid_breakdown_na) {
        updated.discount_penalty = 0;
        updated.amount_due = newHasBaseTax ? rc(numBaseTax) : null;
        updated.balance_amount = newHasBaseTax ? Math.max(0, rc(numBaseTax - numPaid)) : null;
      } else {
      // Auto-discount/penalty: when paid_amount and base_tax are both present,
      // infer discount or penalty from the difference. Runs for ANY status — the amount
      // comparison determines the adjustment, not the dropdown selection.
      const currentStatus = (updated.pay_status || '').toLowerCase();
      const existingDP = parseFloat(updated.discount_penalty) || 0;
      if ((field === 'paid_amount' || field === 'base_tax') && newHasBaseTax && numPaid > 0) {
        if (numPaid < numBaseTax && existingDP <= 0) {
          // Paid less than base with no manual penalty → auto-populate discount
          // Runs when dp = 0 (no adjustment) or dp < 0 (recalculate existing discount)
          const discountAmt = rc(numBaseTax - numPaid);
          updated.discount_penalty = -discountAmt;
          updated.amount_due = rc(numBaseTax + (-discountAmt));
          updated.balance_amount = 0;
        } else if (numPaid > numBaseTax) {
          // Paid more than base → auto-populate penalty (positive discount_penalty)
          // Only auto-set if discount_penalty was not already manually set to a positive value
          const existingDP = parseFloat(updated.discount_penalty) || 0;
          if (existingDP <= 0) {
            const penaltyAmt = rc(numPaid - numBaseTax);
            updated.discount_penalty = penaltyAmt;
          }
          const effectiveDP = parseFloat(updated.discount_penalty) || 0;
          const effectiveTotal = rc(numBaseTax + effectiveDP);
          updated.amount_due = effectiveTotal;
          updated.balance_amount = 0;
        } else {
          // Paid exactly equals base → clear any auto-discount/penalty, recalculate
          if (parseFloat(updated.discount_penalty) < 0) {
            updated.discount_penalty = 0;
          }
          const effectiveDP = parseFloat(updated.discount_penalty) || 0;
          const effectiveTotal = rc(numBaseTax + effectiveDP);
          updated.amount_due = effectiveTotal;
          updated.balance_amount = Math.max(0, rc(effectiveTotal - numPaid));
        }
      } else {
        updated.balance_amount = newTotalAmount !== null ? Math.max(0, rc(newTotalAmount - numPaid)) : null;
      }
      }
    }

    // When user explicitly selects from dropdown, respect their choice
    // Only auto-derive when other fields change (financial data updates)
    // Feb 2026 — Paid Breakdown N/A locks pay_status to "Paid".
    // TAX-PBNA-COMPLETE-001 (Jul 2026) — the v2 per-year group / Selected-
    // Years coverage (groupBreakdownNa prop) locks it too; previously only
    // the legacy row flag was honored, so editing any field on a covered
    // row silently re-derived and PERSISTED "Delinquent" (past DLQ date +
    // no breakdown amounts), which later blocked order completion.
    if (field !== 'pay_status') {
      if (updated.paid_breakdown_na || groupBreakdownNa) {
        updated.pay_status = 'Paid';
      } else {
        updated.pay_status = derivePayStatus(updated);
      }
    }

    // Universal payoff sync: after all status logic (explicit or derived),
    // ensure payoff_amount stays in sync with the final effective status.
    // Financial or status-affecting field changes always override manual payoff.
    // Rule: Fully Delinquent → payoff = total_amount
    //       Partially Delinquent/Paid → payoff = remaining balance (total - paid)
    const finalStatus = (updated.pay_status || '').toLowerCase();
    const isFullyDelinquent = finalStatus === 'delinquent';
    const isPartiallyDelinquent = finalStatus === 'partially delinquent';
    const isDelinquent = isFullyDelinquent || isPartiallyDelinquent;
    if (isDelinquent) {
      const financialOrStatusChange = ['base_tax', 'discount_penalty', 'pay_status', 'dlq_date', 'paid_amount'].includes(field);
      if (financialOrStatusChange || !updated.payoff_manual) {
        if (isPartiallyDelinquent) {
          // Partially paid/delinquent: payoff = remaining balance
          const balanceAmt = parseFloat(updated.balance_amount) || 0;
          updated.payoff_amount = balanceAmt > 0 ? balanceAmt : newTotalAmount;
        } else {
          // Fully delinquent: payoff = full total amount
          updated.payoff_amount = newTotalAmount;
        }
        if (financialOrStatusChange) updated.payoff_manual = false;
      }
    } else if (!isDelinquent && field !== 'pay_status') {
      updated.payoff_amount = null;
      updated.payoff_good_through_date = null;
      updated.payoff_manual = false;
    }

    onUpdate(index, updated);
  };

  // Delinquent Total Amount edit: back-calculate penalty from the difference
  // Builds the updated installment directly (bypasses auto-derive to preserve user's explicit Delinquent status)
  const handleTotalAmountEdit = (enteredTotal) => {
    const numTotal = parseFloat(enteredTotal) || 0;
    const numBase = parseFloat(installment.base_tax) || 0;
    const hasBase = installment.base_tax !== null && installment.base_tax !== undefined && installment.base_tax !== '';

    let penalty = 0;
    if (numTotal > numBase && numBase > 0) {
      penalty = Math.round((numTotal - numBase) * 100) / 100;
      // Respect the user's manual Penalty/Fee selection when sign-compatible
      // (the back-calculated penalty is always positive, so 'penalty' and 'fee'
      // are both valid; only re-derive when no manual selection is active).
      const manualIsCompatible = adjTypeIsManual &&
        (adjTypeIntent === 'penalty' || adjTypeIntent === 'fee');
      if (!manualIsCompatible) {
        setAdjTypeIntent(getAutoAdjType(penalty, installment));
      }
    } else {
      setAdjTypeIntent('none');
      setAdjTypeIsManual(false);
    }

    const updated = { ...installment };
    updated.discount_penalty = penalty;
    const effectiveTotal = hasBase ? rc(numBase + penalty) : null;
    updated.amount_due = effectiveTotal;

    // Recalculate balance
    const numPaid = parseFloat(installment.paid_amount) || 0;
    updated.balance_amount = effectiveTotal !== null ? rc(effectiveTotal - numPaid) : null;

    // Payoff sync (preserve delinquent status — don't auto-derive)
    const status = (installment.pay_status || '').toLowerCase();
    if (status === 'delinquent') {
      updated.payoff_amount = effectiveTotal;
      updated.payoff_manual = false;
    } else if (status === 'partially delinquent') {
      const bal = parseFloat(updated.balance_amount) || 0;
      updated.payoff_amount = bal > 0 ? bal : effectiveTotal;
      updated.payoff_manual = false;
    }

    onUpdate(index, updated);
  };

  return (
    <div 
      className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3 shadow-sm hover:shadow transition-shadow duration-200"
      data-testid={`installment-row-${index}`}
    >
      {/* Row 1: Tax Year | Installment | Due Date | DLQ Date | Paid Date | Pay Status | Mail Date */}
      <div className="gap-2.5" style={{ display: 'grid', gridTemplateColumns: '0.7fr 0.85fr 1.25fr 1.25fr 1.25fr 0.9fr 1.1fr' }}>
        {/* Tax Year */}
        <div>
          <Label className={`text-xs mb-1.5 block font-medium ${dateErrors.tax_year ? 'text-red-600' : 'text-slate-600'}`}>Tax Year *</Label>
          <LocalInput
            value={installment.tax_year || ''}
            onCommit={(val) => handleFieldChange('tax_year', val)}
            placeholder="YYYY"
            className={`h-9 ${dateErrors.tax_year ? 'border-red-500 ring-1 ring-red-300' : ''}`}
            disabled={readOnly}
            data-testid={`installment-tax-year-${index}`}
          />
          {dateErrors.tax_year && (
            <p className="text-xs text-red-500 mt-0.5" data-testid={`tax-year-error-${index}`}>{dateErrors.tax_year}</p>
          )}
          {/* Feb 19, 2026 — Year-suffix annotation mirrors pdf_service.py.
              Surfaces "2026-1" for SB-1 / 1st Half / Q1, "2026-2" for SB-2 /
              2nd Half / Q2, etc. — so the React preview matches the PDF's
              Current Tax Summary column verbatim. Hidden when the
              installment carries no suffix (plain SB / SA / utility rows). */}
          {(() => {
            const display = formatTaxYearDisplay(installment.tax_year, installment.installment_name);
            const raw = (installment.tax_year || '').toString().trim();
            if (!display || !raw || display === raw) return null;
            return (
              <p
                className="text-[10px] text-slate-500 mt-0.5 font-mono tracking-tight"
                data-testid={`installment-tax-year-display-${index}`}
                title="Computed Year label used on the PDF Current Tax Summary"
              >
                PDF: {display}
              </p>
            );
          })()}
        </div>

        {/* Installment Name — for non-MI: editable dropdown (default + specialty groups); for MI: hidden (auto-generated from Season) */}
        {!isMichigan(stateCode) && (
        <div>
          <Label className="text-xs text-slate-600 mb-1.5 block font-medium">Installment</Label>
          {(() => {
            // Feb 19, 2026 → Feb 2026 (bug fix):
            //   The dropdown originally only re-listed the row's own
            //   current STANDARD label (or nothing if it had been
            //   switched to a Specialty) — making it impossible to
            //   flip a Solid Waste / Water / SA row back to a
            //   standard Q1 / 1st Half / Annual label.
            //
            //   It now always renders:
            //     1. A "Standard" group with the full label set for
            //        the active Tax Collected frequency
            //        (Annual / 1st Half + 2nd Half / Q1–Q4).
            //     2. Any legacy / unrecognised standard label the
            //        row is currently bound to (defensive — keeps
            //        a row pre-existing from before this code path
            //        selectable in its own dropdown).
            //     3. The Specialty groups (SB / SA / Utility …)
            //        gated by state via getAvailableSpecialtyOptions.
            //
            //   Standard ↔ Specialty switches are now symmetric.
            const current = installment.installment_name || '';
            const specialty = isSpecialtyInstallment(current);
            const specialtyGroups = getAvailableSpecialtyOptions(stateCode);
            const standardOptions = getStandardInstallmentOptions(collectionFrequency);
            // If the current value is a standard label that's NOT in
            // the active frequency set (legacy data), expose it as a
            // separate "Other" option so the row can still display
            // and be switched away — without polluting the standard
            // group with stale labels.
            const legacyStandard =
              current && !specialty && !standardOptions.includes(current)
                ? current
                : '';
            return (
              <div className="flex items-center gap-1.5">
                <Select
                  value={current}
                  onValueChange={(val) => handleFieldChange('installment_name', val)}
                  disabled={readOnly}
                >
                  <SelectTrigger
                    className={`h-9 flex-1 min-w-0 ${specialty ? 'bg-amber-50 border-amber-300 text-amber-900 focus:ring-amber-200' : ''}`}
                    data-testid={`installment-name-${index}`}
                  >
                    <SelectValue placeholder="Select installment" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Standard labels for the active Tax Collected frequency */}
                    <div>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                        Standard
                      </div>
                      {standardOptions.map((opt) => (
                        <SelectItem
                          key={`std-${opt}`}
                          value={opt}
                          data-testid={`installment-option-standard-${opt.replace(/[^a-zA-Z0-9]/g, '_')}-${index}`}
                        >
                          {opt}
                        </SelectItem>
                      ))}
                    </div>
                    {legacyStandard && (
                      <div>
                        <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                          Other
                        </div>
                        <SelectItem
                          value={legacyStandard}
                          data-testid={`installment-option-legacy-${index}`}
                        >
                          {legacyStandard}
                        </SelectItem>
                      </div>
                    )}
                    {specialtyGroups.map((group, gIdx) => (
                      <div key={`grp-${gIdx}`}>
                        <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                          {group.label}
                        </div>
                        {group.options.map((opt) => (
                          <SelectItem
                            key={opt}
                            value={opt}
                            data-testid={`installment-option-specialty-${opt.replace(/[^a-zA-Z0-9]/g, '_')}-${index}`}
                          >
                            {opt}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
                {specialty && (
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200 whitespace-nowrap"
                    data-testid={`installment-specialty-badge-${index}`}
                    title="Non-standard / specialty bill type"
                  >
                    Specialty
                  </span>
                )}
              </div>
            );
          })()}
        </div>
        )}

        {/* MI Season Selector — only for Michigan orders, replaces Installment column */}
        {isMichigan(stateCode) && (
        <div>
          <Label className="text-xs mb-1.5 block font-medium text-slate-600">Season <span className="text-red-500">*</span></Label>
          <Select
            value={installment.season || ''}
            onValueChange={(val) => handleFieldChange('season', val)}
            disabled={readOnly}
          >
            <SelectTrigger className="h-9" data-testid={`installment-season-${index}`}>
              <SelectValue placeholder="Select season" />
            </SelectTrigger>
            <SelectContent>
              {/* Feb 2026 — Annual-aware MI Season options.
                  When Tax Collected = Annual, a user-friendly "Annual"
                  option is added alongside SUMMER / WINTER for MI
                  jurisdictions (e.g. Village of Capac) that bill
                  annually with no Summer/Winter distinction. Non-
                  annual frequencies keep showing only SUMMER /
                  WINTER. Auto-generation defaults stay SUMMER. */}
              {getMiAvailableSeasons(collectionFrequency).map(s => (
                <SelectItem
                  key={s.value}
                  value={s.value}
                  data-testid={`installment-season-option-${s.value}-${index}`}
                >
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        )}

        {/* Due Date */}
        <div>
          <Label className={`text-xs mb-1.5 block font-medium ${(hasDuplicateDueDate || dateErrors.due_date) ? 'text-red-600' : 'text-slate-600'}`}>Due Date *{hasDuplicateDueDate ? ' (Duplicate)' : ''}</Label>
          <LocalDatePickerInput
            value={installment.due_date || ''}
            onCommit={(val) => handleFieldChange('due_date', val)}
            placeholder="MM/DD/YYYY"
            className={`h-9 ${(hasDuplicateDueDate || dateErrors.due_date) ? 'border-red-500 ring-1 ring-red-300' : ''}`}
            disabled={readOnly}
            data-testid={`installment-due-date-${index}`}
          />
          {hasDuplicateDueDate && (
            <p className="text-xs text-red-500 mt-0.5" data-testid={`duplicate-due-date-warning-${index}`}>Duplicate due date</p>
          )}
          {!hasDuplicateDueDate && dateErrors.due_date && (
            <p className="text-xs text-red-500 mt-0.5" data-testid={`due-date-error-${index}`}>{dateErrors.due_date}</p>
          )}
        </div>

        {/* DLQ Date */}
        <div>
          <Label className={`text-xs mb-1.5 block font-medium ${dateErrors.dlq_date ? 'text-red-600' : 'text-slate-600'}`}>Dlq Date</Label>
          <LocalDatePickerInput
            value={installment.dlq_date || ''}
            onCommit={(val) => handleFieldChange('dlq_date', val)}
            placeholder="MM/DD/YYYY"
            className={`h-9 ${dateErrors.dlq_date ? 'border-red-500 ring-1 ring-red-300' : ''}`}
            disabled={readOnly}
            data-testid={`installment-dlq-date-${index}`}
          />
          {dateErrors.dlq_date && (
            <p className="text-xs text-red-500 mt-0.5" data-testid={`dlq-date-error-${index}`}>{dateErrors.dlq_date}</p>
          )}
        </div>

        {/* Paid Date */}
        <div>
          <Label className={`text-xs mb-1.5 block font-medium ${dateErrors.paid_date ? 'text-red-600' : 'text-slate-600'}`}>Paid Date</Label>
          <LocalDatePickerInput
            value={installment.paid_date || ''}
            onCommit={(val) => handleFieldChange('paid_date', val)}
            placeholder="MM/DD/YYYY"
            className={`h-9 ${dateErrors.paid_date ? 'border-red-500 ring-1 ring-red-300' : ''}`}
            disabled={readOnly}
            data-testid={`installment-paid-date-${index}`}
          />
          {dateErrors.paid_date && (
            <p className="text-xs text-red-500 mt-0.5" data-testid={`paid-date-error-${index}`}>{dateErrors.paid_date}</p>
          )}
        </div>

        {/* Pay Status — Dropdown (base intent) + Derived badge */}
        <div>
          <Label className="text-xs text-slate-600 mb-1.5 block font-medium">Pay Status</Label>
          <Select
            value={baseDropdownValue}
            onValueChange={(val) => handleFieldChange('pay_status', val)}
            disabled={readOnly || breakdownNa}
          >
            <SelectTrigger className="h-9" data-testid={`installment-status-${index}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAY_STATUS_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value} data-testid={`installment-status-option-${opt.value.replace(/[^a-zA-Z0-9]/g, '_')}-${index}`}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showDerivedBadge && (
            <span className={`inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
              derivedStatus === 'Partially Delinquent' ? 'bg-red-100 text-red-700' :
              derivedStatus === 'Partially Paid' ? 'bg-orange-100 text-orange-700' :
              'bg-slate-100 text-slate-600'
            }`} data-testid={`installment-derived-status-${index}`}>
              {derivedStatus}
            </span>
          )}
        </div>

        {/* Mail Date — grouped with status, icon label */}
        <div>
          <Label className={`text-xs mb-1.5 flex items-center gap-1 font-medium ${dateErrors.mailing_issue_date ? 'text-red-600' : 'text-slate-600'}`}>
            <Mail className="w-3 h-3" /> Mail Date *
          </Label>
          <LocalDatePickerInput
            value={installment.mailing_issue_date || ''}
            onCommit={(val) => handleFieldChange('mailing_issue_date', val)}
            placeholder="MM/DD/YYYY"
            className={`h-9 ${dateErrors.mailing_issue_date ? 'border-red-500 ring-1 ring-red-300' : ''}`}
            disabled={readOnly}
            data-testid={`installment-mailing-date-${index}`}
          />
          {dateErrors.mailing_issue_date && (
            <p className="text-xs text-red-500 mt-0.5" data-testid={`mailing-date-error-${index}`}>{dateErrors.mailing_issue_date}</p>
          )}
        </div>
      </div>

      {/* Row 2: Base Tax | Adj Type | Adj Amt | Total Amount | Paid Amount | Balance | Remove */}
      <div className="gap-2.5 items-end" style={{ display: 'grid', gridTemplateColumns: breakdownNa ? '1fr auto' : '1fr 1.3fr 0.8fr 0.9fr 0.9fr 0.8fr auto' }}>
        {/* Base Tax */}
        <div>
          <Label className="text-xs text-slate-600 mb-1.5 block font-medium">Base Tax</Label>
          {/* Jun 2026 — UAT-COMBINED-PAYOFF-BASE-NA-DISPLAY (v3):
              The input STAYS editable at all times, even in Combined
              Payoff mode. Two real-world cases must both work:
                • Collector provides BOTH base + payoff → operator
                  types the base amount → renders normally in PDF.
                • Collector provides ONLY payoff → operator leaves
                  base blank → PDF + Review render "N/A" and the
                  validator bypass (already in place) keeps the
                  cert finalisable.
              The placeholder shifts to "N/A" in combined mode so
              the processor sees what the PDF will display if they
              choose to skip the field — but the input is never
              disabled or replaced with a non-editable pill. The v2
              helper hint was removed at user request to avoid
              implicitly instructing operators to skip data the
              collector has provided. */}
          <LocalCurrencyInput
            value={installment.base_tax}
            onCommit={(val) => handleFieldChange('base_tax', val)}
            placeholder={payoffFieldsDisabled ? 'N/A (or enter amount)' : '$0.00'}
            className="h-9"
            disabled={readOnly}
            data-testid={`installment-base-tax-${index}`}
          />
        </div>

        {/* Adjustment Type — wider for dropdown readability */}
        {!breakdownNa && (
        <div>
          <Label className="text-xs text-slate-600 mb-1.5 block font-medium">Adj Type</Label>
          <Select
            value={adjTypeIntent}
            onValueChange={(val) => {
              setAdjTypeIntent(val);
              // Mark as user-manual for discount/penalty/fee so the date-aware
              // auto-derivation cannot overwrite it. 'none' opts back into
              // full auto-inference.
              setAdjTypeIsManual(val !== 'none');
              const currentAmt = Math.abs(parseFloat(installment.discount_penalty) || 0);
              if (val === 'none') {
                handleFieldChange('discount_penalty', 0);
              } else if (val === 'discount') {
                handleFieldChange('discount_penalty', currentAmt > 0 ? -currentAmt : 0);
              } else {
                handleFieldChange('discount_penalty', currentAmt > 0 ? currentAmt : 0);
              }
            }}
            disabled={readOnly}
          >
            <SelectTrigger className="h-9" data-testid={`installment-adj-type-${index}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="discount">Discount</SelectItem>
              <SelectItem value="penalty">Penalty</SelectItem>
              <SelectItem value="fee">Fee</SelectItem>
            </SelectContent>
          </Select>
        </div>
        )}

        {/* Adjustment Amount — disabled when Adj Type = None */}
        {!breakdownNa && (
        <div>
          <Label className="text-xs text-slate-600 mb-1.5 block font-medium">Adj Amt</Label>
          <LocalCurrencyInput
            value={
              installment.discount_penalty !== null && installment.discount_penalty !== undefined && installment.discount_penalty !== '' && parseFloat(installment.discount_penalty) !== 0
                ? Math.abs(parseFloat(installment.discount_penalty))
                : ''
            }
            onCommit={(rawAmt) => {
              // LocalCurrencyInput has already parsed the text to a number
              // (or null for empty). Take absolute value and apply the
              // discount/penalty sign per the Adj Type selector.
              const absAmt = rawAmt !== null && rawAmt !== undefined ? Math.abs(rawAmt) : 0;
              const isDiscount = adjTypeIntent === 'discount';
              handleFieldChange('discount_penalty', absAmt > 0 ? (isDiscount ? -absAmt : absAmt) : 0);
            }}
            placeholder="$0.00"
            className={`h-9 ${adjAmtDisabled ? 'bg-slate-50 text-slate-400' : ''}`}
            disabled={adjAmtDisabled}
            data-testid={`installment-adj-amount-${index}`}
          />
        </div>
        )}

        {/* Total Amount — editable when Delinquent (back-calculates penalty), read-only otherwise */}
        {!breakdownNa && (
        <div>
          <Label className="text-xs text-slate-600 mb-1.5 block font-medium">Total Amt</Label>
          {isDelinquentStatus && !readOnly ? (
            <LocalCurrencyInput
              value={computedTotalAmount}
              onCommit={handleTotalAmountEdit}
              placeholder="$0.00"
              className="h-9 bg-amber-50 border-amber-300 font-medium"
              data-testid={`installment-total-amount-${index}`}
            />
          ) : (
            <Input
              value={computedTotalAmount !== null ? formatCurrency(computedTotalAmount) : ''}
              readOnly
              placeholder="Auto"
              className="h-9 bg-slate-100 text-slate-700 font-medium cursor-default border-slate-200"
              data-testid={`installment-total-amount-${index}`}
            />
          )}
        </div>
        )}

        {/* Paid Amount — hidden when (year, jurisdiction) group-level
            Paid Breakdown N/A is ON; the lump-sum total lives on the
            year-group header instead. */}
        {!breakdownNa && (
        <div>
          <Label className="text-xs text-slate-600 mb-1.5 block font-medium">Paid Amt</Label>
          <LocalCurrencyInput
            value={installment.paid_amount}
            onCommit={(val) => handleFieldChange('paid_amount', val)}
            placeholder="$0.00"
            className="h-9"
            disabled={readOnly}
            data-testid={`installment-paid-amount-${index}`}
          />
        </div>
        )}

        {/* Balance (auto-calculated, read-only, flat display) — hidden
            under group-level breakdown N/A (always 0 by definition). */}
        {!breakdownNa && (
        <div>
          <Label className="text-xs text-slate-500 mb-1.5 block font-medium">Balance</Label>
          <div
            className={`h-9 flex items-center px-2.5 rounded-md text-sm font-semibold tabular-nums cursor-default select-text ${
              computedBalance === null ? 'text-slate-400 bg-slate-50' :
              derivedStatus === 'Paid' ? 'text-slate-400 bg-slate-50' :
              derivedStatus === 'Delinquent' || derivedStatus === 'Partially Delinquent' ? 'text-red-700 bg-red-50' :
              derivedStatus === 'Partially Paid' ? 'text-orange-700 bg-orange-50' :
              'text-amber-700 bg-amber-50'
            }`}
            data-testid={`installment-balance-${index}`}
          >
            {computedBalance !== null ? formatCurrency(computedBalance) : '—'}
          </div>
        </div>
        )}

        {/* Remove Button */}
        <div className="flex items-end">
          {canRemove && !readOnly ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
              onClick={() => onRemove(index)}
              data-testid={`remove-installment-${index}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          ) : (
            <div className="h-9 w-9" />
          )}
        </div>
      </div>

      {/* Row 3: Payoff fields — only for Delinquent / Partially Delinquent */}
      {!breakdownNa && (derivedStatus === 'Delinquent' || derivedStatus === 'Partially Delinquent') && (
        <div className="grid grid-cols-12 gap-3 pt-1 border-t border-red-200 mt-1">
          <div className="col-span-3">
            <Label className="text-xs text-red-600 mb-1.5 block font-medium">
              Payoff Amount
              {installment.payoff_manual && (
                <span className="ml-1 text-amber-600 font-normal">(manual)</span>
              )}
            </Label>
            <LocalCurrencyInput
              value={installment.payoff_amount}
              onCommit={(val) => {
                const updated = { ...installment, payoff_amount: val, payoff_manual: true };
                onUpdate(index, updated);
              }}
              placeholder="$0.00"
              className={`h-9 ${payoffFieldsDisabled ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed' : 'border-red-200 focus:border-red-400 focus:ring-red-200'}`}
              disabled={readOnly || payoffFieldsDisabled}
              data-testid={`installment-payoff-amount-${index}`}
            />
          </div>
          <div className="col-span-2">
            {(() => {
              const gt = parseValidationDate(installment.payoff_good_through_date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const isPast = gt && gt <= today;
              const dlqChronoError = dateErrors.payoff_good_through_date;
              const hasError = !payoffFieldsDisabled && (isPast || dlqChronoError);
              return (
                <>
                  <Label className={`text-xs mb-1.5 block font-medium ${hasError ? 'text-red-700' : 'text-red-600'}`}>Payoff Good Through{isPast && !payoffFieldsDisabled ? ' (Invalid)' : ''}</Label>
                  <LocalDatePickerInput
                    value={installment.payoff_good_through_date || ''}
                    onCommit={(val) => handleFieldChange('payoff_good_through_date', val)}
                    placeholder="MM/DD/YYYY"
                    className={`h-9 ${payoffFieldsDisabled ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed' : (hasError ? 'border-red-500 ring-1 ring-red-300' : 'border-red-200 focus:border-red-400 focus:ring-red-200')}`}
                    disabled={readOnly || payoffFieldsDisabled}
                    data-testid={`installment-payoff-date-${index}`}
                  />
                  {!payoffFieldsDisabled && isPast && (
                    <p className="text-xs text-red-500 mt-0.5" data-testid={`payoff-date-future-warning-${index}`}>Payoff Good Through must be a future date.</p>
                  )}
                  {!payoffFieldsDisabled && dlqChronoError && !isPast && (
                    <p className="text-xs text-red-500 mt-0.5" data-testid={`payoff-date-chrono-error-${index}`}>{dlqChronoError}</p>
                  )}
                </>
              );
            })()}
          </div>
          <div className="col-span-7 flex items-end">
            {/* Jun 2026 — "Payoff details apply to delinquent taxes only"
                helper removed per operator feedback (visual noise). */}
            {payoffFieldsDisabled && (
              <span className="text-xs text-slate-500 italic" data-testid={`installment-payoff-combined-hint-${index}`}>
                Combined payoff mode — use the jurisdiction-level Combined Payoff fields above.
              </span>
            )}
          </div>
        </div>
      )}

      {/* Entity Breakdown — optional expandable section */}
      <EntityBreakdownSection
        entities={installment.entity_breakdown || []}
        onUpdate={(updatedEntities) => onUpdate(index, { ...installment, entity_breakdown: updatedEntities })}
        readOnly={readOnly}
        installmentIndex={index}
        installmentTotal={installment.amount_due ?? installment.base_tax}
      />
    </div>
  );
});

/**
 * Entity Breakdown Section — lightweight, guided breakdown beneath installments.
 *
 * Collapsed: compact summary pill — "Entity Breakdown (4 entities) — $5,348.73"
 * Expanded: card-per-entity list with Name + Amount prominent;
 *           secondary fields (Rate, Exemption, Notes) tucked into a per-row
 *           detail toggle so the UI never feels like a spreadsheet.
 * Empty:    a single subtle "+ Add Entity Breakdown" link.
 */
const EntityBreakdownSection = React.memo(({ entities, onUpdate, readOnly = false, installmentIndex, installmentTotal }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [detailOpenIdx, setDetailOpenIdx] = useState(null);
  const [editingAmountIdx, setEditingAmountIdx] = useState(null);
  const [rawAmountValue, setRawAmountValue] = useState('');
  // May 5, 2026 — raw rate state mirrors the amount pattern so the user's
  // exact typed text (e.g. "0.380960") is preserved while editing instead
  // of being normalized through parseFloat() on every keystroke. The
  // previous `parseFloat(val) || null` shape ate leading zeros (`0`
  // is falsy) and trimmed trailing zeros, producing `380960` from a
  // typed `0.380960`.
  const [editingRateIdx, setEditingRateIdx] = useState(null);
  const [rawRateValue, setRawRateValue] = useState('');
  const hasEntities = entities && entities.length > 0;

  const handleAdd = () => {
    const newEntity = {
      id: crypto.randomUUID(),
      entity_name: '',
      amount: null,
      rate: null,
      exemption: '',
      notes: ''
    };
    const updated = [...(entities || []), newEntity];
    onUpdate(updated);
    setDetailOpenIdx(updated.length - 1);
  };

  const handleRemove = (idx) => {
    onUpdate(entities.filter((_, i) => i !== idx));
    if (detailOpenIdx === idx) setDetailOpenIdx(null);
    else if (detailOpenIdx !== null && detailOpenIdx > idx) setDetailOpenIdx(detailOpenIdx - 1);
  };

  const handleFieldChange = (idx, field, value) => {
    onUpdate(entities.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  };

  const entitySum = (entities || []).reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  const parsedTotal = parseFloat(installmentTotal) || 0;
  const hasMismatch = hasEntities && parsedTotal > 0 && Math.abs(entitySum - parsedTotal) > 0.01;

  // ── Collapsed / empty state ─────────────────────────────────────
  if (!isExpanded) {
    return (
      <div className="mt-1.5" data-testid={`entity-breakdown-section-${installmentIndex}`}>
        {hasEntities ? (
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className="inline-flex items-center gap-1.5 text-xs py-1 px-2.5 rounded-full border transition-colors
                       border-indigo-200 bg-indigo-50/60 text-indigo-700 hover:bg-indigo-100 hover:border-indigo-300"
            data-testid={`entity-breakdown-toggle-${installmentIndex}`}
          >
            <Building className="w-3 h-3" />
            <span className="font-medium">Entity Breakdown</span>
            <span className="text-indigo-500">({entities.length} {entities.length === 1 ? 'entity' : 'entities'})</span>
            <span className="text-slate-400 mx-0.5">—</span>
            <span className="tabular-nums font-medium">{formatCurrency(entitySum)}</span>
            {hasMismatch && (
              <span className="ml-1 text-amber-600" data-testid={`entity-mismatch-badge-${installmentIndex}`}>
                <AlertTriangle className="w-3 h-3 inline" />
              </span>
            )}
            <ChevronRight className="w-3 h-3 text-indigo-400" />
          </button>
        ) : !readOnly ? (
          <button
            type="button"
            onClick={() => { handleAdd(); setIsExpanded(true); }}
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-indigo-600 py-1 px-2 rounded transition-colors hover:bg-slate-50"
            data-testid={`entity-breakdown-add-first-${installmentIndex}`}
          >
            <Plus className="w-3 h-3" />
            Add Entity Breakdown
          </button>
        ) : null}
      </div>
    );
  }

  // ── Expanded state ──────────────────────────────────────────────
  return (
    <div className="mt-1.5" data-testid={`entity-breakdown-section-${installmentIndex}`}>
      {/* Header bar */}
      <div className="flex items-center justify-between mb-1.5">
        <button
          type="button"
          onClick={() => setIsExpanded(false)}
          className="inline-flex items-center gap-1.5 text-xs py-1 px-2.5 rounded-full border transition-colors
                     border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
          data-testid={`entity-breakdown-toggle-${installmentIndex}`}
        >
          <Building className="w-3 h-3" />
          <span className="font-medium">Entity Breakdown</span>
          <span className="text-indigo-500">({entities.length})</span>
          <span className="text-slate-400 mx-0.5">—</span>
          <span className="tabular-nums font-medium">{formatCurrency(entitySum)}</span>
          {hasMismatch && (
            <AlertTriangle className="w-3 h-3 text-amber-600 ml-1" />
          )}
          <ChevronDown className="w-3 h-3 text-indigo-400" />
        </button>
      </div>

      {/* Entity list */}
      <div className="ml-1 border-l-2 border-indigo-100 pl-3 space-y-1" data-testid={`entity-breakdown-list-${installmentIndex}`}>
        {entities.map((entity, idx) => {
          const isDetailOpen = detailOpenIdx === idx;
          const hasSecondary = entity.rate || entity.exemption || entity.notes;

          return (
            <div
              key={entity.id || idx}
              className={`rounded-md border transition-colors ${isDetailOpen ? 'border-indigo-200 bg-slate-50/50' : 'border-slate-150 bg-white hover:border-slate-300'}`}
              data-testid={`entity-row-${installmentIndex}-${idx}`}
            >
              {/* Primary row: Name + Amount */}
              <div className="flex items-center gap-2 px-2.5 py-1.5">
                {/* Detail toggle */}
                <button
                  type="button"
                  onClick={() => setDetailOpenIdx(isDetailOpen ? null : idx)}
                  className="p-0.5 text-slate-300 hover:text-indigo-500 transition-colors flex-shrink-0"
                  data-testid={`entity-detail-toggle-${installmentIndex}-${idx}`}
                >
                  <ChevronRight className={`w-3 h-3 transition-transform duration-150 ${isDetailOpen ? 'rotate-90' : ''}`} />
                </button>

                {/* Entity Name */}
                <input
                  type="text"
                  value={entity.entity_name || ''}
                  onChange={(e) => handleFieldChange(idx, 'entity_name', e.target.value)}
                  placeholder="Entity name"
                  className="flex-1 text-sm bg-transparent border-0 outline-none placeholder:text-slate-300 text-slate-800 font-medium"
                  disabled={readOnly}
                  data-testid={`entity-name-${installmentIndex}-${idx}`}
                />

                {/* Amount */}
                <input
                  type="text"
                  value={editingAmountIdx === idx
                    ? rawAmountValue
                    : (entity.amount !== null && entity.amount !== undefined ? formatCurrency(entity.amount) : '')}
                  onFocus={() => {
                    setEditingAmountIdx(idx);
                    setRawAmountValue(entity.amount !== null && entity.amount !== undefined ? String(entity.amount) : '');
                  }}
                  onChange={(e) => setRawAmountValue(e.target.value.replace(/[^0-9.]/g, ''))}
                  onBlur={() => {
                    const parsed = rawAmountValue === '' ? null : parseFloat(rawAmountValue);
                    handleFieldChange(idx, 'amount', parsed !== null && !isNaN(parsed) ? Math.round(parsed * 100) / 100 : null);
                    setEditingAmountIdx(null);
                    setRawAmountValue('');
                  }}
                  placeholder="$0.00"
                  className="w-28 text-sm text-right tabular-nums bg-transparent border-0 outline-none placeholder:text-slate-300 text-slate-700 font-medium"
                  disabled={readOnly}
                  data-testid={`entity-amount-${installmentIndex}-${idx}`}
                />

                {/* Secondary indicator dot */}
                {hasSecondary && !isDetailOpen && (
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-300 flex-shrink-0" title="Has additional details" />
                )}

                {/* Delete */}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => handleRemove(idx)}
                    className="p-0.5 text-slate-300 hover:text-red-500 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"
                    style={{ opacity: 1 }}
                    data-testid={`entity-remove-${installmentIndex}-${idx}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>

              {/* Secondary fields — shown only when detail is open */}
              {isDetailOpen && (
                <div className="px-2.5 pb-2 pt-0.5 ml-5 flex flex-wrap gap-x-4 gap-y-1.5">
                  <label className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="w-14 text-right">Rate %</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={editingRateIdx === idx
                        ? rawRateValue
                        : (entity.rate !== null && entity.rate !== undefined ? String(entity.rate) : '')}
                      onFocus={() => {
                        setEditingRateIdx(idx);
                        setRawRateValue(entity.rate !== null && entity.rate !== undefined ? String(entity.rate) : '');
                      }}
                      onChange={(e) => {
                        // Allow only digits and a single decimal point during
                        // editing so trailing zeros and the leading "0." are
                        // preserved exactly as typed.
                        const cleaned = e.target.value.replace(/[^0-9.]/g, '');
                        // Collapse multiple dots to one.
                        const firstDot = cleaned.indexOf('.');
                        const normalized = firstDot === -1
                          ? cleaned
                          : cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
                        setRawRateValue(normalized);
                      }}
                      onBlur={() => {
                        // Parse on blur. Preserve the raw string form when
                        // a Number round-trip would lose information (e.g.
                        // user typed "0.380960" and `String(parseFloat(...))`
                        // would drop the trailing zero, breaking the
                        // "display matches input" requirement). Backend
                        // calls `float(rate)` on read, so storing a string
                        // is fully backwards-compatible.
                        let normalized = null;
                        const raw = rawRateValue;
                        if (raw !== '' && raw !== '.') {
                          const parsed = parseFloat(raw);
                          if (!isNaN(parsed)) {
                            normalized = (String(parsed) === raw) ? parsed : raw;
                          }
                        }
                        handleFieldChange(idx, 'rate', normalized);
                        setEditingRateIdx(null);
                        setRawRateValue('');
                      }}
                      placeholder="0.00"
                      className="w-20 text-xs border border-slate-200 rounded px-1.5 py-0.5 text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-indigo-300"
                      disabled={readOnly}
                      data-testid={`entity-rate-${installmentIndex}-${idx}`}
                    />
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="w-14 text-right">Exempt</span>
                    <input
                      type="text"
                      value={entity.exemption || ''}
                      onChange={(e) => handleFieldChange(idx, 'exemption', e.target.value)}
                      placeholder="e.g., HS"
                      className="w-20 text-xs border border-slate-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                      disabled={readOnly}
                      data-testid={`entity-exemption-${installmentIndex}-${idx}`}
                    />
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="w-14 text-right">Notes</span>
                    <input
                      type="text"
                      value={entity.notes || ''}
                      onChange={(e) => handleFieldChange(idx, 'notes', e.target.value)}
                      placeholder="Optional"
                      className="w-44 text-xs border border-slate-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                      disabled={readOnly}
                      data-testid={`entity-notes-${installmentIndex}-${idx}`}
                    />
                  </label>
                </div>
              )}
            </div>
          );
        })}

        {/* Add + Mismatch footer */}
        <div className="flex items-center justify-between pt-0.5">
          {!readOnly && (
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 py-0.5 px-1.5 rounded hover:bg-indigo-50 transition-colors"
              data-testid={`entity-add-row-${installmentIndex}`}
            >
              <Plus className="w-3 h-3" />
              Add Entity
            </button>
          )}
          {hasMismatch && (
            <span className="inline-flex items-center gap-1 text-[11px] text-amber-600" data-testid={`entity-mismatch-warning-${installmentIndex}`}>
              <AlertTriangle className="w-3 h-3" />
              Entities {formatCurrency(entitySum)} vs Total {formatCurrency(parsedTotal)}
            </span>
          )}
        </div>
        {/* G8: Prominent soft warning when entity totals don't match installment total */}
        {hasMismatch && (
          <div
            className="flex items-start gap-2 mt-1 px-2.5 py-2 rounded-md bg-amber-50 border border-amber-200"
            data-testid={`entity-mismatch-banner-${installmentIndex}`}
          >
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
            <span className="text-xs text-amber-700 leading-relaxed">
              Entity breakdown total ({formatCurrency(entitySum)}) does not match the installment financial total ({formatCurrency(parsedTotal)}).
              Please review before completion.
            </span>
          </div>
        )}
      </div>
    </div>
  );
});


/**
 * Contact Info Tab Content
 */

// ─────────────────────────────────────────────────────────────────────
// Feb 19, 2026 — "Auto-populated data before Jurisdiction Name pick" fix.
//
// When the Jurisdiction Type requires a sub-jurisdiction selection
// (city, township, village, school, special_district, …) but the user
// has not yet picked a specific Jurisdiction Name from the dropdown,
// every contact / workflow / MLC field must read as blank so the
// reviewer can't accidentally save a Tax Cert against the previously-
// stored or first-available jurisdiction's contact details.
//
// We mask the *display* via `getDisplayJurisdiction(...)` and run a
// parallel `useClearStaleJurisdictionData(...)` hook so any stale
// values left in state are also wiped — keeping persisted JSON in sync
// with what the user actually sees.
// ─────────────────────────────────────────────────────────────────────
const NAME_REQUIRED_FIELDS = [
  'make_checks_payable_to',
  'tax_collector_name',
  'tax_collector_phone',
  'tax_collector_phone_ext',
  'tax_collector_email',
  'tax_collector_fax',
  'tax_collector_website',
  'assessor_website',
  'tax_collector_working_hours',
  'tax_collector_mailing_address',
  'tax_collector_physical_address',
  'mlc_charges',
  'mlc_24_month_charges',
  'mlc_eta_days',
  'sase_required',
  'info_obtained_back',
];

const isJurisdictionNameMissing = (jurisdiction) => {
  if (!jurisdiction) return false;
  const type = (jurisdiction.jurisdiction_type || '').toLowerCase();
  if (!type) return false;
  // Borough is a sub-jurisdiction (requires Name pick) in every state
  // except Alaska. Phase A.7.5 (Feb 7, 2026) — fixes the PA "Darby
  // Borough" trap where the contact masking didn't fire for an
  // unmatched borough row and reviewers could accidentally save
  // stale collector contact details against the wrong jurisdiction.
  const jurisdictionState = jurisdiction.state || jurisdiction.property_state || '';
  const isMunicipalBorough = type === 'borough' && isMunicipalBoroughState(jurisdictionState);
  if (!SUB_JURISDICTION_TYPES.includes(type) && !isMunicipalBorough) return false;
  return !jurisdiction.jurisdiction_id;
};

const getDisplayJurisdiction = (jurisdiction) => {
  if (!isJurisdictionNameMissing(jurisdiction)) return jurisdiction;
  // Shallow clone with every directory-sourced field blanked. Keep
  // identity/type/name + any user-managed structural data (parcels,
  // installments, exemptions, …) intact.
  const masked = { ...jurisdiction };
  for (const field of NAME_REQUIRED_FIELDS) {
    if (field === 'sase_required') {
      masked[field] = false;
    } else if (
      field === 'mlc_charges' ||
      field === 'mlc_24_month_charges' ||
      field === 'mlc_eta_days' ||
      field === 'info_obtained_back'
    ) {
      masked[field] = null;
    } else {
      masked[field] = '';
    }
  }
  return masked;
};

const useClearStaleJurisdictionData = (jurisdiction, onBatchUpdate) => {
  useEffect(() => {
    if (!jurisdiction || !onBatchUpdate) return;
    if (!isJurisdictionNameMissing(jurisdiction)) return;
    // Walk the canonical fields; only call onBatchUpdate if at least one
    // is dirty so we don't trigger a re-render storm on every mount.
    const dirty = {};
    for (const field of NAME_REQUIRED_FIELDS) {
      const v = jurisdiction[field];
      const isEmpty = v === '' || v === null || v === undefined || v === false;
      if (!isEmpty) {
        if (field === 'sase_required') {
          dirty[field] = false;
        } else if (
          field === 'mlc_charges' ||
          field === 'mlc_24_month_charges' ||
          field === 'mlc_eta_days' ||
          field === 'info_obtained_back'
        ) {
          dirty[field] = null;
        } else {
          dirty[field] = '';
        }
      }
    }
    if (Object.keys(dirty).length > 0) {
      onBatchUpdate(dirty);
    }
  }, [jurisdiction?.id, jurisdiction?.jurisdiction_type, jurisdiction?.jurisdiction_id]);
};

const ContactInfoTab = React.memo(({ jurisdiction, onUpdate, readOnly, county, rowState, onJurisdictionTypeChange, onClearJurisdiction, onBatchUpdate, onAcceptFuzzySuggestion, canEditContactFields = true, canLinkCollector = true, isExternalPortalClient = false, isJurisdictionInherited = false, stateCode = '', allJurisdictions = [], orderId = null, orderNumber = null, currentUser = null }) => {
  // Feb 19, 2026 fix: blank contact fields until a Jurisdiction Name is picked.
  useClearStaleJurisdictionData(jurisdiction, onBatchUpdate);
  const collectorEntryRef = useRef({ available: false });
  const displayJurisdiction = getDisplayJurisdiction(jurisdiction);
  // FULFILLMENT-CONTACT-RO-001 — a directory-linked jurisdiction renders the same read-only Contact Details card for every role
  // (Admin/JM included); edits go through a Directory Correction Request. Unlinked/legacy rows keep the role-based inputs.
  const isDirectoryManaged = !!jurisdiction?.jurisdiction_id;
  const canEditContact = canEditContactFields && !isDirectoryManaged;

  // Feb 2026 (Option C) — Compute the sibling municipality's
  // jurisdiction_id on the same parcel so the TaxCollectorPicker /
  // guidance banner can bias the resolver to a township-scoped
  // assignment (e.g. Salem Township ↔ Western Wayne School override).
  // Municipal rows scope to themselves; school/county rows scope to
  // the parcel's first township/borough/city/town/village sibling.
  // Falls back to null when no sibling exists — the resolver then
  // uses the standard NULL-scope catch-all.
  const municipalityJurisdictionId = useMemo(() => {
    const municipalTypes = new Set([
      'township', 'borough', 'city', 'town', 'village',
    ]);
    const targetType = (jurisdiction?.jurisdiction_type || '').toLowerCase();
    if (municipalTypes.has(targetType)) {
      return jurisdiction?.jurisdiction_id || null;
    }
    for (const j of (allJurisdictions || [])) {
      const jt = (j?.jurisdiction_type || '').toLowerCase();
      if (municipalTypes.has(jt) && j?.jurisdiction_id) {
        return j.jurisdiction_id;
      }
    }
    return null;
  }, [
    jurisdiction?.jurisdiction_id,
    jurisdiction?.jurisdiction_type,
    allJurisdictions,
  ]);

  // Handle sub-jurisdiction (jurisdiction name) selection
  const handleSubJurisdictionChange = (selectedId) => {
    // Handle clear — user selected placeholder to reset jurisdiction name
    if (!selectedId) {
      if (onBatchUpdate) {
        onBatchUpdate({
          jurisdiction_id: null,
          jurisdiction_name: '',
          make_checks_payable_to: '',
          tax_collector_name: '',
          tax_collector_phone: '',
          tax_collector_phone_ext: '',
          tax_collector_email: '',
          tax_collector_fax: '',
          tax_collector_website: '',
          assessor_website: '',
          tax_collector_working_hours: '',
          tax_collector_mailing_address: '',
          tax_collector_physical_address: '',
          mlc_charges: null,
          mlc_24_month_charges: null,
          mlc_eta_days: null,
          sase_required: false,
          info_obtained_back: null
        });
      }
      return;
    }

    const subJurisdictions = rowState?.subJurisdictions || [];
    const selectedJurisdiction = subJurisdictions.find(j => j.id === selectedId);
    
    if (selectedJurisdiction) {
      // ─────────────────────────────────────────────────────────────────
      // BUG FIX (P1 Jurisdiction Data Mismatch): Unconditionally replace
      // ALL contact fields with the newly-selected jurisdiction's values.
      //
      // Previously, each field used a conditional truthy check
      // (if (newJur.field) updates.field = newJur.field), which silently
      // preserved stale values from a previously-selected jurisdiction
      // whenever the new jurisdiction had a missing/empty field. This
      // caused the UI to display the new jurisdiction's NAME while the
      // payee/address/phone/email block remained bound to the prior
      // jurisdiction's data (e.g., Lauderdale name + Dale/Ozark contact).
      //
      // Fix: always write every contact field, coercing missing values to
      // '' / null so the block is a clean replacement, never a partial
      // merge over stale state.
      // ─────────────────────────────────────────────────────────────────
      // Phase A.7.3 (Feb 2026) — Prefer the backend-resolved
      // `effective_payee` over the directory's legacy `tax_collector_*`
      // fields when the resolver matched an active assignment (e.g.
      // HAB-MISC). Without this, picking a sub-jurisdiction in the
      // dropdown clobbered the resolved CURRENT payee block (which the
      // backend had already enriched via `enrich_cert_jurisdictions_from_directory`)
      // by writing back the jurisdiction's own name as the payee.
      //
      // `effective_payee` is hydrated on `GET /api/jurisdictions` by
      // `_hydrate_effective_payee_many` for `tax_period='current'`. We
      // only override the legacy fields when `source === 'assignment'`
      // (real assignment row) — for `legacy_fallback` or missing
      // resolver data, fall through to the original directory values.
      // ─────────────────────────────────────────────────────────────────
      const newName = selectedJurisdiction.jurisdiction_name || selectedJurisdiction.name || '';
      const ep = selectedJurisdiction.effective_payee;
      const hasAssignment = !!(ep && ep.source === 'assignment' && (ep.name || ep.make_checks_payable_to));

      const taxCollectorName = hasAssignment
        ? (ep.name || '').trim()
        : (selectedJurisdiction.tax_collector_name || '').trim();
      // PJR-001 P2: without an assignment, the jurisdiction's own saved payee wins over the collector name.
      const makeChecksPayableTo = hasAssignment
        ? ((ep.make_checks_payable_to || ep.name || '').trim() || newName)
        : ((selectedJurisdiction.make_checks_payable_to || '').trim()
            || taxCollectorName
            || newName);

      // Pull contact fields from the resolved payee when an assignment
      // exists, otherwise from the directory record.
      const pick = (epField, dirField) => {
        if (hasAssignment) {
          const v = ep?.[epField];
          if (v !== undefined && v !== null && v !== '') return v;
        }
        return selectedJurisdiction[dirField] || '';
      };

      const updates = {
        jurisdiction_id: selectedId,
        // JX-LOOKUP-001: adopt the directory's stored type (Town↔Township siblings share one dropdown)
        jurisdiction_type: selectedJurisdiction.jurisdiction_type || jurisdiction.jurisdiction_type,
        jurisdiction_name: newName,
        make_checks_payable_to: makeChecksPayableTo,
        // JURDIR-PAYEE-002: where the payee came from — 'stored' is a true directory value; anything else is a fallback.
        _payee_source: hasAssignment ? 'assignment'
          : (selectedJurisdiction.make_checks_payable_to || '').trim() ? 'stored'
          : taxCollectorName ? 'collector_name_fallback' : 'jurisdiction_name_fallback',
        tax_collector_name: taxCollectorName,
        tax_collector_phone: pick('phone', 'tax_collector_phone'),
        tax_collector_phone_ext: pick('phone_ext', 'tax_collector_phone_ext'),
        tax_collector_email: pick('email', 'tax_collector_email'),
        tax_collector_fax: pick('fax', 'tax_collector_fax'),
        tax_collector_website: selectedJurisdiction.tax_collector_website || '',
        assessor_website: selectedJurisdiction.assessor_website || '',
        tax_collector_working_hours: pick('working_hours', 'tax_collector_working_hours'),
        tax_collector_mailing_address: pick('mailing_address', 'tax_collector_mailing_address'),
        tax_collector_physical_address: selectedJurisdiction.tax_collector_physical_address || '',
        // FULFILL-MLC-GATE-001: the Directory "MLC Required?" toggle is authoritative — when it is not Yes the
        // MLC-dependent values are NOT operative and are never copied onto the certificate row. SASE stays
        // independent (Mail-Away dependency) and is copied as before.
        mlc_required: selectedJurisdiction.mlc_required === true,
        mlc_charges: selectedJurisdiction.mlc_required === true ? (selectedJurisdiction.mlc_charges ?? null) : null,
        mlc_24_month_charges: selectedJurisdiction.mlc_required === true ? (selectedJurisdiction.mlc_24_month_charges ?? null) : null,
        mlc_eta_days: selectedJurisdiction.mlc_required === true ? (selectedJurisdiction.mlc_eta_days ?? null) : null,
        sase_required: selectedJurisdiction.sase_required ?? false,
        info_obtained_back: selectedJurisdiction.mlc_required === true ? (selectedJurisdiction.info_obtained_back ?? null) : null,
        // Phase A.7.3 — track the resolver-linked collector_id so the
        // Tax Collector picker shows the linked badge and downstream
        // operational defaults pick up the correct collector record.
        current_collector_id: hasAssignment ? (ep.collector_id || null) : (selectedJurisdiction.current_collector_id || null),
        _collector_source_current: hasAssignment ? 'assignment_resolver' : (selectedJurisdiction._collector_source_current || ''),
        changed_by_user: false,
      };

      // Use batch update to apply all changes at once
      if (onBatchUpdate) {
        onBatchUpdate(updates);
      } else {
        // Fallback: apply updates one by one
        Object.entries(updates).forEach(([field, value]) => {
          onUpdate(field, value);
        });
      }
    }
  };

  return (
    <div className="space-y-3">
      {/* Jurisdiction Selection */}
      <div className="tcp-card space-y-4 relative" data-testid="section-jurisdiction-selection">
        {/* JCR-001 — request a correction to the selected directory record (processors / TLs / JMs / admins) */}
        {!readOnly && orderId && jurisdiction?.jurisdiction_id && holdsAnyRole(currentUser, CAN_REQUEST_ROLES) && (
          <div className="absolute right-4 top-3 z-[1]">
            {/* REPORT-ISSUE-REMOVAL-001 — single entry: Jurisdiction / MLC → JCR dialog; Collector / Assignment → banner flag modal */}
            <CorrectionEntry orderData={{ id: orderId, order_number: orderNumber }} jurisdictionId={jurisdiction.jurisdiction_id} jurisdiction={displayJurisdiction} collectorEntryRef={collectorEntryRef} variant="inline" testId="ci-request-correction-btn" />
          </div>
        )}
        <div>
          <JurisdictionSelector
            variant="native"
            jurisdictionType={jurisdiction.jurisdiction_type}
            jurisdictionId={jurisdiction.jurisdiction_id}
            propertyState={stateCode}
            currentJurisdictionName={jurisdiction.jurisdiction_name || ''}
            onJurisdictionTypeChange={(type) => onJurisdictionTypeChange(type, jurisdiction, county)}
            onSubJurisdictionChange={handleSubJurisdictionChange}
            onClear={() => onClearJurisdiction(jurisdiction)}
            subJurisdictions={rowState?.subJurisdictions || []}
            showSubJurisdictionSelector={rowState?.showSubJurisdictionSelector || false}
            subJurisdictionsLoading={rowState?.subJurisdictionsLoading || false}
            disabled={readOnly}
            idPrefix="jm-jurisdiction"
            testIdPrefix="jm-jurisdiction"
          />
          {/* May 27, 2026 — Fuzzy "Did you mean?" warning + chips.
              Rendered when the type-select handler couldn't find an
              exact match but found state+type scoped suggestions
              above the 60% similarity cutoff. Clicking a chip binds
              the full contact block (payee, phone, address, MLC,
              hours, etc.) via onAcceptFuzzySuggestion. */}
          {/* Hide the warning once the processor has unblocked the row by
              picking a Tax Collector from the directory (Feb 2026 —
              "Darby Borough" defect fix). The collector link populates
              payee/phone/address atomically, so the warning is no
              longer relevant. The data may still be non-canonical
              (jurisdiction_id is still null), which is a separate
              admin-side concern surfaced via the Tax Collector
              Directory, not the row UI. */}
          {rowState?.jurisdictionMatchError && !jurisdiction.current_collector_id && (
            <div
              className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md"
              data-testid="jm-fuzzy-warning"
            >
              <p className="text-sm text-amber-800">{rowState.jurisdictionMatchError}</p>
              {(rowState.fuzzySuggestions || []).length > 0 && (
                <div
                  className="mt-2 flex flex-wrap gap-2"
                  data-testid="jm-fuzzy-suggestions"
                >
                  {(rowState.fuzzySuggestions || []).map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      disabled={readOnly}
                      onClick={() => onAcceptFuzzySuggestion(
                        s,
                        rowState.fuzzyQueryContext || {}
                      )}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 hover:border-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`jm-fuzzy-chip-${s.id}`}
                    >
                      <span>{s.jurisdiction_name}</span>
                      <span className="text-amber-600 font-mono text-[10px]">
                        {Math.round((s.similarity || 0) * 100)}%
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* PJR-001-C: the single "Request Missing Jurisdiction" control lives in the Jurisdictions rail — no duplicate hint here. */}
        </div>
        {/* Jul 2026 — TAX-CATEGORY-001: type of tax BILL this entry
            represents, independent of the jurisdiction entity type.
            One collector (e.g. City of Utica) may carry County + City +
            School bills — the category keeps every entry distinguishable
            on the Tax Summary, notes and generated certificate:
            "CITY OF UTICA (COUNTY)" / "(CITY)" / "(SCHOOL)". Optional —
            when unset, behavior is unchanged (entity type drives labels). */}
        <div className="flex items-center gap-3">
          <Label className="text-[11px] text-slate-500 font-medium whitespace-nowrap">
            Tax Category
            <span className="ml-1 text-slate-400 font-normal">(bill type)</span>
          </Label>
          {/* TAX-CATEGORY-PREMIUM-SELECT-001 — premium listbox; same values (incl. TAX-CATEGORY-002 City / School), same onUpdate contract */}
          <TaxCategorySelect
            value={jurisdiction.tax_category || ''}
            onChange={(v) => onUpdate('tax_category', v)}
            disabled={readOnly}
          />
        </div>
      </div>

      {/* Surface 4 — PA-only collector auto-guidance banner. Renders
          NOTHING for non-PA jurisdictions (Source-of-Truth Policy
          Option B). The gate uses the JURISDICTION's state (NOT the
          order's `stateCode`), so a non-PA jurisdiction in a PA order
          still gets zero visual treatment — and vice versa.

          Feb 6, 2026 — Pass `taxCategoryCode` derived from the
          jurisdiction's own type so the resolver matches the
          explicit-category assignment rows. Without this, the resolver
          (collector_resolver.py:269-279) only matches NULL catch-all
          rows and falls through to legacy_fallback, surfacing the
          yellow banner even when a perfect explicit assignment exists.
          See /app/memory/CHANGELOG.md "Feb 6, 2026 — Fulfillment
          consumer-side fix" for the full RCA. */}
      <CollectorGuidanceBanner
        jurisdictionId={displayJurisdiction.jurisdiction_id}
        jurisdictionState={displayJurisdiction.state || stateCode}
        jurisdictionName={displayJurisdiction.jurisdiction_name}
        taxPeriod="current"
        taxCategoryCode={resolveTaxCategoryCode(
          {
            state: displayJurisdiction.state || stateCode,
            jurisdiction_type: displayJurisdiction.jurisdiction_type,
          },
          'current',
        )}
        municipalityJurisdictionId={municipalityJurisdictionId}
        orderId={orderId}
        currentUser={currentUser}
        entryRef={collectorEntryRef}
      />

      {/* Contact Details Section — Always Visible Compact Layout */}
      {/* Contact Details — TAX-FULFILLMENT-CONTACT-DELINQUENT-PREMIUM-UI-001 (presentation only) */}
      <div className="tcp-card space-y-3" data-testid="section-contact-details">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="tcp-card-title flex items-center gap-2.5">
            <span className="tcp-tile tcp-tile-blue" style={{ width: 30, height: 30 }}><Phone className="w-4 h-4" /></span>
            Contact Details
          </h3>
          {!canEditContact && !readOnly && (
            <span className="text-[10px] font-medium text-slate-400" data-testid="contact-details-directory-indicator">Directory-managed</span>
          )}
        </div>

        {/* Collector / payee row — picker + status pills live inside TaxCollectorPicker (logic unchanged) */}
        <div className="min-w-0 relative" data-testid="contact-details-payee-row">
          <Label className="mb-1 block text-[11px] text-slate-500 font-medium">Make Checks Payable To</Label>
            {/* Tax Collector Picker — PA-only initial release. Caller is the
                source of truth on state gating; the picker itself is reusable.
                Gating order (Feb 2026 fix):
                  1. non-PA  → legacy free-text LocalInput
                  2. PA + no jurisdiction selected → disabled placeholder
                     with a friendly "Select a jurisdiction first" hint;
                     prevents linking a collector to an orphan row.
                  3. PA + jurisdiction selected → picker active. */}
            {((displayJurisdiction.state || stateCode) || '').toUpperCase() !== 'PA' ? (
              <LocalInput
                value={displayJurisdiction.make_checks_payable_to || ''}
                onCommit={canEditContact ? (val) => {
                  onUpdate('make_checks_payable_to', val);
                  if (displayJurisdiction._payee_source && displayJurisdiction._payee_source !== 'stored') onUpdate('_payee_source', 'manual');
                } : undefined}
                onKeyDown={!canEditContact ? (e) => e.preventDefault() : undefined}
                onPaste={!canEditContact ? (e) => e.preventDefault() : undefined}
                placeholder="Payee name"
                className={`h-8 text-sm ${!canEditContact && !readOnly ? 'cursor-default select-text bg-white' : ''}`}
                disabled={readOnly}
                readOnly={!canEditContact && !readOnly}
                data-testid="jurisdiction-payable-to"
              />
            ) : !displayJurisdiction.jurisdiction_id && ((displayJurisdiction.state || stateCode) || '').toUpperCase() !== 'PA' ? (
              <div
                className="h-8 px-2.5 text-sm flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 text-slate-400 italic cursor-not-allowed"
                data-testid="jurisdiction-current-collector-picker-disabled"
              >
                <span>Select a Jurisdiction Name first to pick a Tax Collector</span>
                <ChevronsUpDown className="w-3.5 h-3.5 text-slate-300 shrink-0" />
              </div>
            ) : (
              /* Feb 2026 — "Darby Borough" defect fix: when the
                 jurisdictions table has no row matching the parcel
                 city + type, the picker stays available so the
                 processor isn't blocked. The ★ assignment-suggested
                 hint is intentionally suppressed inside the picker
                 when jurisdictionId is null (TaxCollectorPicker line
                 126 short-circuits). */
              <TaxCollectorPicker
                state="PA"
                taxPeriod="current"
                jurisdictionId={displayJurisdiction.jurisdiction_id || null}
                jurisdictionName={displayJurisdiction.jurisdiction_name || null}
                jurisdictionType={displayJurisdiction.jurisdiction_type || null}
                municipalityJurisdictionId={municipalityJurisdictionId}
                taxCategoryCode={resolveTaxCategoryCode(
                  {
                    state: displayJurisdiction.state || stateCode,
                    jurisdiction_type: displayJurisdiction.jurisdiction_type,
                  },
                  'current',
                )}
                collectorId={jurisdiction.current_collector_id || null}
                payeeName={displayJurisdiction.make_checks_payable_to || ''}
                disabled={readOnly || !canLinkCollector}
                onLinkMasterData={(c, ctx) => {
                  // Atomic write: FK + the three denormalized strings the
                  // existing PDF/snapshot path reads (see pdf_service.py
                  // lines 3166–3196 and cert_snapshot_collectors.py).
                  const physAddr = c.physical_address || c.mailing_address || '';
                  const batch = {
                    current_collector_id: c.id,
                    make_checks_payable_to: c.make_checks_payable_to || c.display_name || '',
                    tax_collector_phone: c.phone || '',
                    tax_collector_phone_ext: c.phone_ext || '',
                    tax_collector_physical_address: physAddr,
                    tax_collector_mailing_address: c.mailing_address || '',
                    tax_collector_email: c.email || '',
                    tax_collector_website: c.website || '',
                    tax_collector_working_hours: c.working_hours || '',
                  };
                  // Feb 7, 2026 — When the parcel had no jurisdiction FK
                  // and the backend resolved one from canonical context,
                  // persist it so future loads bind directly to the master
                  // record and surface the formal assignment.
                  if (
                    ctx?.resolved_jurisdiction_id
                    && !displayJurisdiction.jurisdiction_id
                  ) {
                    batch.jurisdiction_id = ctx.resolved_jurisdiction_id;
                  }
                  onBatchUpdate(batch);
                }}
                onUnlink={() => onUpdate('current_collector_id', null)}
                onPickCustom={() => onUpdate('current_collector_id', null)}
                testIdPrefix="jurisdiction-current-collector-picker"
              />
            )}
            {/* JURDIR-PAYEE-002: tell the processor when the payee is only a directory fallback, not a stored payee */}
            {['collector_name_fallback', 'jurisdiction_name_fallback'].includes(displayJurisdiction._payee_source) && (
              <p className="mt-1 text-[11px] text-amber-700" data-testid="jurisdiction-payee-fallback-hint">
                No dedicated payee is stored in the Jurisdiction Directory for this jurisdiction — this value defaults from the
                {displayJurisdiction._payee_source === 'collector_name_fallback' ? ' Tax Collector Name' : ' jurisdiction name'}. Verify before use.
              </p>
            )}
            {/* Operational Defaults strip — view only · master-data driven */}
            {((displayJurisdiction.state || stateCode) || '').toUpperCase() === 'PA' && displayJurisdiction.jurisdiction_id && jurisdiction.current_collector_id && (
              <div className="mt-2">
                <OperationalDefaultsStrip
                  jurisdictionId={displayJurisdiction.jurisdiction_id}
                  collectorId={jurisdiction.current_collector_id}
                  taxPeriod="current"
                  testIdPrefix="jurisdiction-current-operational-defaults"
                />
              </div>
            )}
        </div>

        {/* Compact information tiles — read mode shows value or "—"; edit mode (Admin/JM) keeps the inputs */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4" data-testid="contact-details-tiles">
          <ContactTile
            icon={Phone} label="Phone" tone="tcp-tile-blue"
            value={[displayJurisdiction.tax_collector_phone, displayJurisdiction.tax_collector_phone_ext ? `ext. ${displayJurisdiction.tax_collector_phone_ext}` : ''].filter(Boolean).join(' ')}
            editable={canEditContact && !readOnly} testId="tile-phone"
          >
            <div className="flex gap-1.5">
              <LocalInput
                value={displayJurisdiction.tax_collector_phone || ''}
                onCommit={!jurisdiction.current_collector_id ? (val) => onUpdate('tax_collector_phone', val) : undefined}
                onKeyDown={jurisdiction.current_collector_id ? (e) => e.preventDefault() : undefined}
                onPaste={jurisdiction.current_collector_id ? (e) => e.preventDefault() : undefined}
                placeholder="(XXX) XXX-XXXX"
                className={`h-7 text-sm ${jurisdiction.current_collector_id ? 'bg-slate-50 text-slate-600 cursor-default' : 'bg-white'}`}
                disabled={readOnly}
                readOnly={!!jurisdiction.current_collector_id}
                data-testid="jurisdiction-phone"
              />
              <LocalInput
                value={displayJurisdiction.tax_collector_phone_ext || ''}
                onCommit={(val) => onUpdate('tax_collector_phone_ext', val)}
                placeholder="Ext"
                className="h-7 w-16 text-sm bg-white"
                disabled={readOnly}
                data-testid="jurisdiction-phone-ext"
              />
            </div>
          </ContactTile>
          <ContactTile icon={MapPin} label="Mailing Address" value={displayJurisdiction.tax_collector_mailing_address || ''} tone="tcp-tile-indigo" editable={canEditContact && !readOnly} testId="tile-mailing-address">
            <LocalInput
              value={displayJurisdiction.tax_collector_mailing_address || ''}
              onCommit={(val) => onUpdate('tax_collector_mailing_address', val)}
              placeholder="Mailing address"
              className="h-7 text-sm bg-white"
              disabled={readOnly}
              data-testid="jurisdiction-mailing-address"
            />
          </ContactTile>
          <ContactTile icon={MapPin} label="Physical Address" value={displayJurisdiction.tax_collector_physical_address || ''} tone="tcp-tile-indigo" editable={canEditContact && !readOnly} testId="tile-physical-address">
            <LocalInput
              value={displayJurisdiction.tax_collector_physical_address || ''}
              onCommit={(val) => onUpdate('tax_collector_physical_address', val)}
              placeholder="Physical address"
              className="h-7 text-sm bg-white"
              disabled={readOnly}
              data-testid="jurisdiction-physical-address"
            />
          </ContactTile>
          <ContactTile icon={Mail} label="Email" value={displayJurisdiction.tax_collector_email || ''} tone="tcp-tile-violet" editable={canEditContact && !readOnly} testId="tile-email">
            <LocalInput
              value={displayJurisdiction.tax_collector_email || ''}
              onCommit={(val) => onUpdate('tax_collector_email', val)}
              placeholder="email@example.com"
              className="h-7 text-sm bg-white"
              disabled={readOnly}
              data-testid="jurisdiction-email"
            />
          </ContactTile>
          <ContactTile icon={Printer} label="Fax" value={displayJurisdiction.tax_collector_fax || ''} tone="tcp-tile-slate" editable={canEditContact && !readOnly} testId="tile-fax">
            <LocalInput
              value={displayJurisdiction.tax_collector_fax || ''}
              onCommit={(val) => onUpdate('tax_collector_fax', val)}
              placeholder="Fax number"
              className="h-7 text-sm bg-white"
              disabled={readOnly}
              data-testid="jurisdiction-fax"
            />
          </ContactTile>
          <ContactTile icon={Clock} label="Working Hours" value={displayJurisdiction.tax_collector_working_hours || ''} tone="tcp-tile-green" editable={canEditContact && !readOnly} testId="tile-hours">
            <LocalInput
              value={displayJurisdiction.tax_collector_working_hours || ''}
              onCommit={(val) => onUpdate('tax_collector_working_hours', val)}
              placeholder="Mon-Fri 9AM-5PM"
              className="h-7 text-sm bg-white"
              disabled={readOnly}
              data-testid="jurisdiction-hours"
            />
          </ContactTile>
          <ContactTile icon={Globe} label="Tax Collector Website" value={displayJurisdiction.tax_collector_website || ''} tone="tcp-tile-blue" editable={canEditContact && !readOnly} link copy testId="tile-website">
            <LocalInput
              value={displayJurisdiction.tax_collector_website || ''}
              onCommit={(val) => onUpdate('tax_collector_website', val)}
              placeholder="https://..."
              className="h-7 text-sm bg-white"
              disabled={readOnly}
              data-testid="jurisdiction-website"
            />
          </ContactTile>
          <ContactTile icon={Globe} label="Assessor Website" value={displayJurisdiction.assessor_website || ''} tone="tcp-tile-blue" editable={canEditContact && !readOnly} link copy testId="tile-assessor-website">
            <LocalInput
              value={displayJurisdiction.assessor_website || ''}
              onCommit={(val) => onUpdate('assessor_website', val)}
              placeholder="https://..."
              className="h-7 text-sm bg-white"
              disabled={readOnly}
              data-testid="jurisdiction-assessor-website"
            />
          </ContactTile>
        </div>
      </div>


      <div className="tcp-card space-y-2" data-testid="section-delinquent-payee">
          <div className="flex items-center justify-between">
            <h4 className="tcp-card-title flex items-center gap-2.5">
              <span className="tcp-tile tcp-tile-amber" style={{ width: 30, height: 30 }}><AlertCircle className="w-4 h-4" /></span>
              Delinquent Taxes Payable To
            </h4>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="delinquent-same-as-current-toggle">
              <span className="text-[11px] text-slate-500 font-medium">Same as Current Payee</span>
              <Switch
                checked={jurisdiction.delinquent_same_as_current !== false}
                onCheckedChange={(checked) => {
                  if (checked) {
                    // Mirror current payee fields into delinquent fields
                    onBatchUpdate({
                      delinquent_same_as_current: true,
                      delinquent_payable_to_name: displayJurisdiction.make_checks_payable_to || '',
                      delinquent_payable_to_address: displayJurisdiction.tax_collector_physical_address || displayJurisdiction.tax_collector_mailing_address || '',
                      delinquent_payable_to_phone: displayJurisdiction.tax_collector_phone || '',
                      delinquent_collecting_authority_level: jurisdiction.jurisdiction_type || '',
                      // Keep the master-data link in sync when mirroring
                      delinquent_collector_id: jurisdiction.current_collector_id || null,
                    });
                  } else {
                    // Toggling OFF for the first time: preserve current
                    // payee values as the starting point for delinquent.
                    // Processor can then pick a different Tax Collector
                    // (e.g. Lancaster County Tax Claim Bureau) without
                    // re-typing the whole record.
                    onBatchUpdate({
                      delinquent_same_as_current: false,
                      delinquent_payable_to_name: displayJurisdiction.make_checks_payable_to || '',
                      delinquent_payable_to_address: displayJurisdiction.tax_collector_physical_address || displayJurisdiction.tax_collector_mailing_address || '',
                      delinquent_payable_to_phone: displayJurisdiction.tax_collector_phone || '',
                      delinquent_collecting_authority_level: jurisdiction.jurisdiction_type || '',
                      delinquent_collector_id: jurisdiction.current_collector_id || null,
                    });
                  }
                }}
                disabled={readOnly}
              />
            </label>
          </div>

          {/* DELINQUENT-SAME-PAYEE-FLICKER-001 — height-animated swap; only this content transitions */}
          <AnimatedHeight testId="delinquent-payee-body">
          {/* When ON: show inherited values as read-only */}
          {jurisdiction.delinquent_same_as_current !== false && (
            <div className="pl-1 space-y-1 tcp-fade-in" key="same-on">
              {(displayJurisdiction.make_checks_payable_to || displayJurisdiction.tax_collector_phone) ? (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3" data-testid="delinquent-inherited-tiles">
                  <ContactTile icon={Users} label="Payee Name" tone="tcp-tile-indigo" value={displayJurisdiction.make_checks_payable_to || ''} testId="tile-delinquent-inherited-payee" />
                  <ContactTile icon={MapPin} label="Address" tone="tcp-tile-blue" value={displayJurisdiction.tax_collector_physical_address || displayJurisdiction.tax_collector_mailing_address || ''} testId="tile-delinquent-inherited-address" />
                  <ContactTile icon={Phone} label="Phone" tone="tcp-tile-green" value={displayJurisdiction.tax_collector_phone || ''} testId="tile-delinquent-inherited-phone" />
                </div>
              ) : (
                <p className="text-[11px] text-slate-400 italic">Current payee details will be used for delinquent taxes.</p>
              )}
            </div>
          )}

          {/* When OFF: show editable fields with authority type auto-fill */}
          {jurisdiction.delinquent_same_as_current === false && (
            <div className="space-y-2 pl-1 tcp-fade-in" key="same-off">
              <div className="w-48 mb-2">
                <Label className="text-[11px] text-slate-500 font-medium mb-1 block">Authority Type</Label>
                {/* AUTHORITY-TYPE-PREMIUM-SELECT-001 — premium listbox; same state-aware options (lib/jurisdictionTaxonomy), legacy value display-only, same handler */}
                <AuthorityTypeSelect
                  value={jurisdiction.delinquent_collecting_authority_level || ''}
                  legacyValue={displayJurisdiction.delinquent_authority_type || ''}
                  stateCode={stateCode}
                  onChange={(val) => {
                    // Feb 2026 bug fix — changing Authority Type must
                    // unlink the currently selected Delinquent
                    // Collector and wipe its derived fields. Without
                    // this, `delinquent_collector_id` stays set, the
                    // picker keeps showing "Linked · Assignment" with
                    // the old collector (e.g. MBM Collections), the
                    // operational defaults strip keeps showing the old
                    // MLC fee / ETA / SASE, and the payee
                    // name/address/phone snap back to read-only "from
                    // master" mode even after we overwrite them below.
                    // Sequence is critical:
                    //   1) clear collector_id + payee/address/phone +
                    //      the authority level (the auto-fill paths
                    //      below will repopulate from the chosen type
                    //      if a sibling/directory match exists).
                    //   2) attempt sibling auto-fill (cheapest path).
                    //   3) fall through to directory lookup if no
                    //      sibling match.
                    // The user can still hit "Same as Current Payee"
                    // separately — that flow is unchanged.
                    const cleared = {
                      delinquent_collecting_authority_level: val,
                      delinquent_collector_id: null,
                      delinquent_payable_to_name: '',
                      delinquent_payable_to_address: '',
                      delinquent_payable_to_phone: '',
                    };

                    // Auto-fill from matching jurisdiction by type
                    // Step 1: Check sibling jurisdictions on this order
                    const siblingMatch = allJurisdictions.find(
                      j => (j.jurisdiction_type || '').toLowerCase() === val.toLowerCase()
                           && (j.jurisdiction_name || '') !== (jurisdiction.jurisdiction_name || '')
                    );
                    if (siblingMatch && (siblingMatch.make_checks_payable_to || siblingMatch.tax_collector_phone)) {
                      onBatchUpdate({
                        ...cleared,
                        delinquent_payable_to_name: siblingMatch.make_checks_payable_to || siblingMatch.jurisdiction_name || '',
                        delinquent_payable_to_address: siblingMatch.tax_collector_physical_address || siblingMatch.tax_collector_mailing_address || '',
                        delinquent_payable_to_phone: siblingMatch.tax_collector_phone || '',
                      });
                      return;
                    }
                    // Step 2: Look up from jurisdiction directory via API.
                    // Apply the cleared payload immediately so the UI
                    // unlinks the collector and goes blank while the
                    // network call is in flight. The fetch callback
                    // will overwrite with the directory match if one
                    // is found.
                    onBatchUpdate(cleared);
                    if (stateCode) {
                      // Build search query: for county type, search by the order's county; for others, search by county too
                      const searchName = county || '';
                      const searchParam = searchName ? `&search=${encodeURIComponent(searchName)}` : '';
                      fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/jurisdictions?state=${stateCode}&jurisdiction_type=${val}&active_only=true&page_size=5${searchParam}`,
                        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
                      )
                        .then(resp => resp.ok ? resp.json() : null)
                        .then(data => {
                          if (!data) return;
                          const items = data.jurisdictions || data.items || [];
                          // Prefer exact county name match, then any match that isn't the current jurisdiction
                          const countyLower = (county || '').toLowerCase();
                          const dirMatch = (countyLower
                            ? items.find(j => (j.jurisdiction_name || '').toLowerCase().includes(countyLower))
                            : null
                          ) || items.find(
                            j => (j.jurisdiction_name || j.name || '') !== (jurisdiction.jurisdiction_name || '')
                          ) || items[0];
                          if (dirMatch && (dirMatch.tax_collector_name || dirMatch.jurisdiction_name)) {
                            onBatchUpdate({
                              ...cleared,
                              delinquent_payable_to_name: dirMatch.tax_collector_name || dirMatch.jurisdiction_name || dirMatch.name || '',
                              delinquent_payable_to_address: dirMatch.tax_collector_physical_address || dirMatch.tax_collector_mailing_address || '',
                              delinquent_payable_to_phone: dirMatch.tax_collector_phone || '',
                            });
                          }
                        })
                        .catch(() => {});
                    }
                  }}
                  disabled={readOnly}
                />
              </div>
              {/* Delinquent collector picker — PA-only. Falls back to
                  the legacy 3-input free-text grid for non-PA states so
                  the existing workflow stays identical there. */}
              {((displayJurisdiction.state || stateCode) || '').toUpperCase() === 'PA' ? (
                <div className="space-y-2">
                  <Label className="text-[11px] text-slate-500 font-medium block">Delinquent Collector</Label>
                  {!displayJurisdiction.jurisdiction_id && ((displayJurisdiction.state || stateCode) || '').toUpperCase() !== 'PA' ? (
                    <div
                      className="h-8 px-2.5 text-sm flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 text-slate-400 italic cursor-not-allowed"
                      data-testid="jurisdiction-delinquent-collector-picker-disabled"
                    >
                      <span>Select a Jurisdiction Name first to pick a Tax Collector</span>
                      <ChevronsUpDown className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                    </div>
                  ) : (
                    /* Feb 2026 — "Darby Borough" defect fix: parity with
                       the current collector picker above. Stays available
                       even when no jurisdictions row matches. */
                    <TaxCollectorPicker
                      state="PA"
                      taxPeriod="delinquent"
                      jurisdictionId={displayJurisdiction.jurisdiction_id || null}
                      jurisdictionName={displayJurisdiction.jurisdiction_name || null}
                      jurisdictionType={displayJurisdiction.jurisdiction_type || null}
                      municipalityJurisdictionId={municipalityJurisdictionId}
                      taxCategoryCode={resolveTaxCategoryCode(
                        {
                          state: displayJurisdiction.state || stateCode,
                          jurisdiction_type: displayJurisdiction.jurisdiction_type,
                        },
                        'delinquent',
                      )}
                      collectorId={jurisdiction.delinquent_collector_id || null}
                      payeeName={jurisdiction.delinquent_payable_to_name || ''}
                      disabled={readOnly}
                      onLinkMasterData={(c, ctx) => {
                        const physAddr = c.physical_address || c.mailing_address || '';
                        const batch = {
                          delinquent_collector_id: c.id,
                          delinquent_payable_to_name: c.make_checks_payable_to || c.display_name || '',
                          delinquent_payable_to_address: physAddr,
                          delinquent_payable_to_phone: c.phone || '',
                        };
                        // Feb 7, 2026 — Persist resolved jurisdiction FK
                        // back onto the parcel/order row if missing.
                        if (
                          ctx?.resolved_jurisdiction_id
                          && !displayJurisdiction.jurisdiction_id
                        ) {
                          batch.jurisdiction_id = ctx.resolved_jurisdiction_id;
                        }
                        onBatchUpdate(batch);
                      }}
                      onUnlink={() => onUpdate('delinquent_collector_id', null)}
                      onPickCustom={() => onUpdate('delinquent_collector_id', null)}
                      testIdPrefix="jurisdiction-delinquent-collector-picker"
                    />
                  )}
                  {/* Operational Defaults strip — view only · master-data driven */}
                  {displayJurisdiction.jurisdiction_id && jurisdiction.delinquent_collector_id && (
                    <OperationalDefaultsStrip
                      jurisdictionId={displayJurisdiction.jurisdiction_id}
                      collectorId={jurisdiction.delinquent_collector_id}
                      taxPeriod="delinquent"
                      testIdPrefix="jurisdiction-delinquent-operational-defaults"
                    />
                  )}
                  {/* Payee Details — linked collector → read tiles (from master); unlinked → compact inputs */}
                  <div className="space-y-1.5" data-testid="delinquent-payee-details">
                    <div className="flex items-center gap-2 text-[12px] font-semibold text-slate-700">
                      <span className="tcp-tile tcp-tile-indigo" style={{ width: 24, height: 24 }}><Users className="w-3.5 h-3.5" /></span>
                      Payee Details
                    </div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                    <ContactTile icon={Users} label="Payee Name" value={jurisdiction.delinquent_payable_to_name || ''} tone="tcp-tile-indigo" editable={!jurisdiction.delinquent_collector_id && !readOnly} testId="tile-delinquent-payee">
                      <LocalInput
                        value={jurisdiction.delinquent_payable_to_name || ''}
                        onCommit={(val) => onUpdate('delinquent_payable_to_name', val)}
                        placeholder="e.g., Wayne County Treasurer"
                        className="h-7 text-sm bg-white"
                        disabled={readOnly}
                        data-testid="jurisdiction-delinquent-payee"
                      />
                    </ContactTile>
                    <ContactTile icon={MapPin} label="Address" value={jurisdiction.delinquent_payable_to_address || ''} tone="tcp-tile-blue" editable={!jurisdiction.delinquent_collector_id && !readOnly} testId="tile-delinquent-address">
                      <LocalInput
                        value={jurisdiction.delinquent_payable_to_address || ''}
                        onCommit={(val) => onUpdate('delinquent_payable_to_address', val)}
                        placeholder="Full address"
                        className="h-7 text-sm bg-white"
                        disabled={readOnly}
                        data-testid="jurisdiction-delinquent-address"
                      />
                    </ContactTile>
                    <ContactTile icon={Phone} label="Phone" value={jurisdiction.delinquent_payable_to_phone || ''} tone="tcp-tile-green" editable={!jurisdiction.delinquent_collector_id && !readOnly} testId="tile-delinquent-phone">
                      <LocalInput
                        value={jurisdiction.delinquent_payable_to_phone || ''}
                        onCommit={(val) => onUpdate('delinquent_payable_to_phone', val)}
                        placeholder="(XXX) XXX-XXXX"
                        className="h-7 text-sm bg-white"
                        disabled={readOnly}
                        data-testid="jurisdiction-delinquent-phone"
                      />
                    </ContactTile>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[11px] text-slate-500 font-medium mb-1 block">Payee Name</Label>
                    <LocalInput
                      value={jurisdiction.delinquent_payable_to_name || ''}
                      onCommit={(val) => onUpdate('delinquent_payable_to_name', val)}
                      placeholder="e.g., Wayne County Treasurer"
                      className="h-8 text-sm"
                      disabled={readOnly}
                      data-testid="jurisdiction-delinquent-payee"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500 font-medium mb-1 block">Address</Label>
                    <LocalInput
                      value={jurisdiction.delinquent_payable_to_address || ''}
                      onCommit={(val) => onUpdate('delinquent_payable_to_address', val)}
                      placeholder="Full address"
                      className="h-8 text-sm"
                      disabled={readOnly}
                      data-testid="jurisdiction-delinquent-address"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500 font-medium mb-1 block">Phone</Label>
                    <LocalInput
                      value={jurisdiction.delinquent_payable_to_phone || ''}
                      onCommit={(val) => onUpdate('delinquent_payable_to_phone', val)}
                      placeholder="(XXX) XXX-XXXX"
                      className="h-8 text-sm"
                      disabled={readOnly}
                      data-testid="jurisdiction-delinquent-phone"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          </AnimatedHeight>
      </div>
    </div>
  );
});

/**
 * MLC & Workflow Tab Content
 */
const MLCWorkflowTab = React.memo(({ jurisdiction, onUpdate, readOnly, orderId, canEditMLCFields: canEditMLCFieldsProp = true, mailAwayMatch = null, orderFiles = [], allParcelsData = [], activeParcelIndex = 0 }) => {
  // Feb 19, 2026 fix: blank MLC/workflow fields until a Jurisdiction Name
  // is picked (same guard used in ContactInfoTab). Prevents the user from
  // seeing — and accidentally saving — auto-populated charges/ETA/SASE
  // values from a previously-selected jurisdiction.
  const displayJurisdiction = getDisplayJurisdiction(jurisdiction);
  // FULFILL-MLC-GATE-001: the Directory "MLC Required?" value rides on the row. False → the four
  // MLC-dependent fields are not operative (view-only, shown empty). SASE stays independent (Mail-Away).
  const mlcOff = displayJurisdiction.mlc_required === false;
  const canEditMLCFields = canEditMLCFieldsProp && !mlcOff;
  const canEditSase = canEditMLCFieldsProp;
  return (
    <div className="space-y-3">
      {mlcOff && (
        <div className="flex items-start gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" role="status" data-testid="mlc-not-required-banner">
          <Info className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
          <div className="text-xs text-slate-600">
            <span className="font-semibold text-slate-700">MLC not required (Jurisdiction Directory).</span> MLC Charges, 24-Month MLC, MLC ETA and Info Obtained Back are not operative for this jurisdiction and are not carried into the certificate. SASE Required follows its own Mail-Away setting.
          </div>
        </div>
      )}
      {/* MLC Charges Section */}
      <div className={`tcp-card tcp-card-head ${mlcOff ? 'opacity-60' : ''}`} data-testid="section-mlc-charges" aria-disabled={mlcOff || undefined}>
        <div className="tcp-tile tcp-tile-green"><DollarSign className="w-[18px] h-[18px]" /></div>
        <div className="tcp-card-body space-y-2.5">
        <h3 className="tcp-card-title flex items-center gap-2">
          MLC Charges
          {mlcOff && <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-px text-[10.5px] font-semibold text-slate-500" data-testid="mlc-not-required-badge">MLC not required</span>}
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-slate-600 mb-1.5 block font-medium">
              MLC Charges
              {!canEditMLCFields && !readOnly && (
                <span className="text-xs text-slate-400 italic ml-2">View only</span>
              )}
            </Label>
            <LocalCurrencyInput
              value={displayJurisdiction.mlc_charges != null ? displayJurisdiction.mlc_charges : ''}
              onCommit={canEditMLCFields ? (val) => onUpdate('mlc_charges', val) : undefined}
              onKeyDown={!canEditMLCFields ? (e) => e.preventDefault() : undefined}
              onPaste={!canEditMLCFields ? (e) => e.preventDefault() : undefined}
              placeholder="$0.00"
              className={`h-9 ${!canEditMLCFields && !readOnly ? 'cursor-default select-text bg-white' : ''}`}
              disabled={readOnly}
              readOnly={!canEditMLCFields && !readOnly}
              data-testid="jurisdiction-mlc-charges"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1.5 block font-medium">
              24-Month MLC
              {!canEditMLCFields && !readOnly && (
                <span className="text-xs text-slate-400 italic ml-2">View only</span>
              )}
            </Label>
            <LocalCurrencyInput
              value={displayJurisdiction.mlc_24_month_charges != null ? displayJurisdiction.mlc_24_month_charges : ''}
              onCommit={canEditMLCFields ? (val) => onUpdate('mlc_24_month_charges', val) : undefined}
              onKeyDown={!canEditMLCFields ? (e) => e.preventDefault() : undefined}
              onPaste={!canEditMLCFields ? (e) => e.preventDefault() : undefined}
              placeholder="$0.00"
              className={`h-9 ${!canEditMLCFields && !readOnly ? 'cursor-default select-text bg-white' : ''}`}
              disabled={readOnly}
              readOnly={!canEditMLCFields && !readOnly}
              data-testid="jurisdiction-mlc-24month"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1.5 block font-medium">
              MLC ETA (Days)
              {!canEditMLCFields && !readOnly && (
                <span className="text-xs text-slate-400 italic ml-2">View only</span>
              )}
            </Label>
            <LocalInput
              value={displayJurisdiction.mlc_eta_days != null ? String(displayJurisdiction.mlc_eta_days) : ''}
              onCommit={canEditMLCFields ? (val) => onUpdate('mlc_eta_days', val ? parseInt(val) : null) : undefined}
              onKeyDown={!canEditMLCFields ? (e) => e.preventDefault() : undefined}
              onPaste={!canEditMLCFields ? (e) => e.preventDefault() : undefined}
              placeholder="Days"
              className={`h-9 ${!canEditMLCFields && !readOnly ? 'cursor-default select-text bg-white' : ''}`}
              disabled={readOnly}
              readOnly={!canEditMLCFields && !readOnly}
              data-testid="jurisdiction-mlc-eta"
            />
          </div>
        </div>
        </div>
      </div>

      {/* Workflow Options Section */}
      <div className="tcp-card tcp-card-head" data-testid="section-workflow-options">
        <div className="tcp-tile tcp-tile-indigo"><GitBranch className="w-[18px] h-[18px]" /></div>
        <div className="tcp-card-body space-y-2.5">
        <h3 className="tcp-card-title">
          Workflow Options
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-600 mb-1.5 block font-medium">
              SASE Required
              {!canEditSase && !readOnly && (
                <span className="text-xs text-slate-400 italic ml-2">View only</span>
              )}
            </Label>
            {canEditSase || readOnly ? (
              <Select
                value={displayJurisdiction.sase_required ? 'yes' : 'no'}
                onValueChange={(val) => onUpdate('sase_required', val === 'yes')}
                disabled={readOnly}
              >
                <SelectTrigger className="h-9" data-testid="jurisdiction-sase">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={displayJurisdiction.sase_required ? 'Yes' : 'No'}
                readOnly
                className="h-9 cursor-default select-text bg-white"
                data-testid="jurisdiction-sase"
              />
            )}
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1.5 block font-medium">
              Info Obtained Back
              {!canEditMLCFields && !readOnly && (
                <span className="text-xs text-slate-400 italic ml-2">View only</span>
              )}
            </Label>
            {canEditMLCFields || readOnly ? (
              <Select
                key={`info-obtained-${jurisdiction.id || 'default'}`}
                value={displayJurisdiction.info_obtained_back || ''}
                onValueChange={(val) => onUpdate('info_obtained_back', val)}
                disabled={readOnly}
              >
                <SelectTrigger className="h-9" data-testid="jurisdiction-info-obtained-back">
                  <SelectValue placeholder="Select method..." />
                </SelectTrigger>
                <SelectContent>
                  {RESPONSE_METHOD_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={RESPONSE_METHOD_OPTIONS.find(o => o.value === displayJurisdiction.info_obtained_back)?.label || displayJurisdiction.info_obtained_back || ''}
                readOnly
                className="h-9 cursor-default select-text bg-white"
                data-testid="jurisdiction-info-obtained-back"
              />
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Information Source Section */}
      <div className="tcp-card tcp-card-head" data-testid="section-information-source">
        <div className="tcp-tile tcp-tile-blue"><FileText className="w-[18px] h-[18px]" /></div>
        <div className="tcp-card-body space-y-2.5">
        <h3 className="tcp-card-title">
          Information Source
        </h3>

        {/* Cross-parcel "already captured" hint
            (Jun 1, 2026 — strictly scoped to Source + Source-Evidence only).
            When the same jurisdiction in another parcel already has a source
            selected, surface that fact here as a PASSIVE indicator. The
            completion validator already treats this as covered (see
            `isSourceCoveredByAnotherParcel`), so no physical copy is needed.
            The processor doesn't need to click anything — they can leave
            this parcel's Source empty, or pick a different value below to
            override per-parcel. */}
        {(() => {
          const coverage = !jurisdiction.source
            ? findSourceCoverageFromOtherParcel(
                jurisdiction.jurisdiction_name,
                jurisdiction.jurisdiction_type,
                allParcelsData,
                activeParcelIndex,
              )
            : null;
          if (!coverage) return null;
          const sourceLabel = (SOURCE_OPTIONS.find(o => o.value === coverage.source) || {}).label || coverage.source;
          return (
            <div
              className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2"
              data-testid="source-cross-parcel-hint"
            >
              <Link2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              <div className="text-[12px] text-emerald-900 leading-relaxed">
                <span className="font-medium">Source already captured from Parcel {coverage.parcelIndex + 1}</span>
                <span className="text-emerald-700"> — {sourceLabel}</span>
                <span className="block text-[11px] text-emerald-700/80 mt-0.5">
                  This parcel is already covered. You can leave Source empty, or pick a different value below to override.
                </span>
              </div>
            </div>
          );
        })()}

        <div className="flex items-center gap-3">
          <Label className="text-xs text-slate-600 font-medium whitespace-nowrap shrink-0">Source <span className="text-rose-500">*</span></Label>
          <Select
            key={`source-${jurisdiction.id || 'default'}`}
            value={jurisdiction.source || ''}
            onValueChange={(val) => onUpdate('source', val)}
            disabled={readOnly}
          >
            <SelectTrigger className="h-9 flex-1" data-testid="jurisdiction-source">
              <SelectValue placeholder="Select source..." />
            </SelectTrigger>
            <SelectContent>
              {SOURCE_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        </div>
      </div>

        {/* Evidence Upload - Ultra Compact Layout */}
        {jurisdiction.source && (
          <div className="tcp-card tcp-card-head" data-testid="section-evidence">
            <div className="tcp-tile tcp-tile-violet"><Paperclip className="w-[18px] h-[18px]" /></div>
            <div className="tcp-card-body space-y-2.5">
              <h3 className="tcp-card-title">Evidence</h3>
              <CompactEvidenceSection
                source={jurisdiction.source}
                sourceEvidence={jurisdiction.source_evidence}
                jurisdictionId={jurisdiction.id}
                jurisdictionName={jurisdiction.jurisdiction_name}
                jurisdictionType={jurisdiction.jurisdiction_type}
                onUpdate={onUpdate}
                readOnly={readOnly}
                orderId={orderId}
                mailAwayMatch={mailAwayMatch}
                orderFiles={orderFiles}
                allParcelsData={allParcelsData}
                activeParcelIndex={activeParcelIndex}
              />
            </div>
          </div>
        )}

      {/* Mail-Away Shipment Details — only shown when a Mail-Away request
          exists for this jurisdiction (i.e. order has entered mail_away).
          Editable by admin + tax_search_team_leader only; other roles see
          a read-only summary. */}
      {mailAwayMatch && (
        <MailAwayShipmentDetails
          orderId={orderId}
          mailAwayRequest={mailAwayMatch}
        />
      )}
    </div>
  );
});

/**
 * Tax Summary Tab Content (Tax Records)
 */
const TaxSummaryTab = React.memo(({ jurisdiction, onUpdate, onBatchUpdate, onAddInstallment, readOnly, orderFinancialSummary, stateCode = '', isNewParcel = false, validationAttempted = false }) => {
  const installments = jurisdiction.installments || [];

  // Ref for stable callbacks — avoids recreating handlers when installments array changes
  const installmentsRef = useRef(installments);
  installmentsRef.current = installments;

  // ── Feb 6, 2026 — Combined Delinquent Payoff visibility helper ─────
  // See `/app/frontend/src/lib/combinedPayoffVisibility.js` for the full
  // rule set + Jest coverage. Extracted from the component so the
  // multi-frequency logic is testable in isolation.
  const combinedBreakdownAvailable = isCombinedBreakdownAvailable(
    jurisdiction,
    installments,
    derivePayStatus,
  );

  // ── Self-heal stale combined-mode data ─────────────────────────────
  // If a jurisdiction was previously saved with mode='combined' (e.g.,
  // 3 delinquent years), then installments changed so the section is no
  // longer available (e.g., 2 paid off → only 1 delinquent + Annual), the
  // stored mode is now inconsistent with the UI. Reset it to 'year_wise'
  // so per-row Payoff fields re-enable and the PDF doesn't read stale
  // combined-mode data on the next render.
  useEffect(() => {
    if (
      !combinedBreakdownAvailable
      && (jurisdiction.dlq_payoff_breakdown_mode || 'year_wise') === 'combined'
      && !readOnly
    ) {
      onUpdate('dlq_payoff_breakdown_mode', 'year_wise');
    }
  }, [combinedBreakdownAvailable, jurisdiction.dlq_payoff_breakdown_mode, readOnly, onUpdate]);

  // Calculate totals — Total Due = sum of balance for non-paid records
  // For delinquent/partially delinquent with payoff, use payoff amount instead of balance
  // Jun 2026 — rows covered by a Paid Breakdown N/A lump sum (selected-years
  // entry or per-year group) are confirmed paid with balance $0.00; they must
  // never contribute to Total Due even though their row-level paid_amount is
  // intentionally blank ("See Notes").
  const _pbnaDueExemptYears = (() => {
    const s = computeCoveredYears(jurisdiction);
    for (const [y, e] of Object.entries(jurisdiction.paid_breakdown_na_groups || {})) {
      if (e && e.enabled) s.add(String(y).trim());
    }
    return s;
  })();
  const totalDue = installments.reduce((sum, inst) => {
    if (_pbnaDueExemptYears.has(String(inst.tax_year || '').trim())) return sum;
    const derived = derivePayStatus(inst);
    if (derived === 'Paid') return sum;
    // For delinquent cases with a payoff amount, the payoff IS the operative due amount
    if ((derived === 'Delinquent' || derived === 'Partially Delinquent') && 
        inst.payoff_amount !== null && inst.payoff_amount !== undefined && inst.payoff_amount !== '') {
      const payoff = parseFloat(inst.payoff_amount) || 0;
      if (payoff > 0) return rc(sum + payoff);
    }
    // Standard: Compute balance: (base_tax + disc/penalty) - paid_amount
    const baseTax = parseFloat(inst.base_tax) || 0;
    const discPenalty = parseFloat(inst.discount_penalty) || 0;
    const totalAmount = (inst.base_tax !== null && inst.base_tax !== undefined && inst.base_tax !== '') 
      ? rc(baseTax + discPenalty) 
      : (parseFloat(inst.amount_due) || 0);
    const paidAmount = parseFloat(inst.paid_amount) || 0;
    const balance = rc(totalAmount - paidAmount);
    return rc(sum + balance);
  }, 0);
  
  // Generate auto-generated tax notes based on installments
  // Stabilize memo with serialized key — jurisdiction is a new reference on every render,
  // but notes only change when name/type/exemption/frequency/installment data changes.
  const notesDepKey = useMemo(() => JSON.stringify({
    n: jurisdiction.jurisdiction_name,
    t: jurisdiction.jurisdiction_type,
    tc: jurisdiction.tax_category, // Jul 2026 — TAX-CATEGORY-001
    fe: jurisdiction.fully_exempt,
    et: jurisdiction.exemption_types,
    eo: jurisdiction.exemption_other,
    cf: jurisdiction.collection_frequency,
    // Feb 2026 v2.2 — group-level Paid Breakdown N/A entries affect the
    // notes preview (clarifier sentence appended per active group).
    pbng: jurisdiction.paid_breakdown_na_groups,
    // Jun 2026 v2.5 — All-Years lump-sum entry affects the preview too.
    pbnaAll: jurisdiction.paid_breakdown_na_all_years,
    inst: installments.map(i => ({
      nm: i.installment_name, yr: i.tax_year, ps: i.pay_status,
      bt: i.base_tax, dp: i.discount_penalty, pa: i.paid_amount,
      dd: i.due_date, dl: i.dlq_date, md: i.mailing_issue_date,
      pd: i.paid_date, ba: i.balance_amount, po: i.payoff_amount,
      pbna: i.paid_breakdown_na, // legacy v1 fallback affects preview too
    })),
  }), [jurisdiction, installments]);

  // autoGeneratedNotes depends on notesDepKey (serialized) to avoid recomputation on jurisdiction reference changes
  const autoGeneratedNotes = useMemo(() => {
    // notesDepKey captures all notes-relevant fields; jurisdiction/installments are stable via notesDepKey
    // Feb 19, 2026: Specialty Tax Notes are produced by a separate
    // additive generator that the standard engine never touches.
    // Feb 2026 v2.2: Paid Breakdown N/A clarifier (per active (year,
    // jurisdiction) group) is appended LAST so the on-screen preview
    // matches what the PDF renderer emits via the backend appender.
    const engineNotes = generateTaxCertificationNotes(jurisdiction, installments, stateCode);
    const withSpecialty = appendSpecialtyNotes(engineNotes, installments);
    return appendBreakdownNaNotes(withSpecialty, jurisdiction);
  }, [notesDepKey, jurisdiction, installments, stateCode]);
  
  const handleInstallmentUpdate = useCallback((instIndex, updatedInstallment) => {
    const current = installmentsRef.current;
    const newInstallments = [...current];
    newInstallments[instIndex] = updatedInstallment;

    // MI: Auto-generate installment_name from season when season changes
    if (isMichigan(stateCode) && updatedInstallment.season) {
      const season = updatedInstallment.season.toUpperCase();
      // Count how many prior installments (before this one) share the same season
      let sameSeasonBefore = 0;
      for (let i = 0; i < newInstallments.length; i++) {
        if (i === instIndex) continue;
        if ((newInstallments[i].season || '').toUpperCase() === season && i < instIndex) {
          sameSeasonBefore++;
        }
      }
      // Also count after to renumber all same-season rows
      const allSameSeason = newInstallments
        .map((inst, i) => ({ inst, i }))
        .filter(({ inst }) => (inst.season || '').toUpperCase() === season);

      if (allSameSeason.length <= 1) {
        // Only one of this season → plain label (e.g., "SUMMER")
        newInstallments[instIndex].installment_name = season;
      } else {
        // Multiple → renumber all of this season (SUMMER-1, SUMMER-2, ...)
        allSameSeason.forEach(({ i }, seqIdx) => {
          newInstallments[i] = {
            ...newInstallments[i],
            installment_name: seqIdx === 0 ? season : `${season}-${seqIdx + 1}`,
          };
        });
      }
    }

    onUpdate('installments', newInstallments);
  }, [onUpdate, stateCode]);
  
  const handleInstallmentRemove = useCallback((instIndex) => {
    const newInstallments = installmentsRef.current.filter((_, i) => i !== instIndex);

    // MI: Renumber installment_names after removal
    if (isMichigan(stateCode)) {
      const seasons = ['SUMMER', 'WINTER'];
      for (const season of seasons) {
        const indices = newInstallments
          .map((inst, i) => ({ inst, i }))
          .filter(({ inst }) => (inst.season || '').toUpperCase() === season);
        if (indices.length <= 1) {
          // Single or zero → plain label
          indices.forEach(({ i }) => {
            newInstallments[i] = { ...newInstallments[i], installment_name: season };
          });
        } else {
          indices.forEach(({ i }, seqIdx) => {
            newInstallments[i] = {
              ...newInstallments[i],
              installment_name: seqIdx === 0 ? season : `${season}-${seqIdx + 1}`,
            };
          });
        }
      }
    }

    onUpdate('installments', newInstallments);
  }, [onUpdate, stateCode]);

  // ── Paid Breakdown N/A — group-level handlers (Feb 2026 v2) ──
  // Canonical storage: `jurisdiction.paid_breakdown_na_groups[year] = {
  //   enabled, total_paid_amount, paid_date }`. Toggling ON also
  //   propagates to every installment of that year: pay_status = "Paid",
  //   discount_penalty = 0, balance_amount = 0, paid_breakdown_na = false
  //   (migration of legacy row-level flag).
  const breakdownNaGroups = jurisdiction.paid_breakdown_na_groups || {};

  const handleGroupBreakdownNaToggle = useCallback((year, checked) => {
    const yearKey = String(year || '').trim();
    if (!yearKey) return;
    const currentGroups = jurisdiction.paid_breakdown_na_groups || {};
    const existing = currentGroups[yearKey] || {};
    const nextGroups = {
      ...currentGroups,
      [yearKey]: {
        enabled: !!checked,
        total_paid_amount: existing.total_paid_amount ?? null,
        paid_date: existing.paid_date ?? '',
      },
    };
    if (checked) {
      // Propagate to every installment in the year-group.
      const newInstallments = installmentsRef.current.map((inst) => {
        const instYear = String(inst.tax_year || '').trim();
        if (instYear !== yearKey) return inst;
        return {
          ...inst,
          pay_status: 'Paid',
          discount_penalty: 0,
          balance_amount: 0,
          paid_breakdown_na: false, // migrate legacy v1 flag to v2 group
        };
      });
      onBatchUpdate({
        paid_breakdown_na_groups: nextGroups,
        installments: newInstallments,
      });
    } else {
      onUpdate('paid_breakdown_na_groups', nextGroups);
    }
  }, [jurisdiction.paid_breakdown_na_groups, onUpdate, onBatchUpdate]);

  const handleGroupBreakdownNaField = useCallback((year, field, value) => {
    const yearKey = String(year || '').trim();
    if (!yearKey) return;
    const currentGroups = jurisdiction.paid_breakdown_na_groups || {};
    const existing = currentGroups[yearKey] || { enabled: true };
    const nextGroups = {
      ...currentGroups,
      [yearKey]: { ...existing, [field]: value },
    };
    if (field === 'paid_date') {
      // Jun 2026 — auto-fill the year's rows with the group-level Paid
      // Date (same semantics as the selected-years entry): rows whose
      // date is empty or still equals the previous group date follow it;
      // manual row overrides are preserved.
      const prevDate = existing.paid_date || '';
      const newInstallments = installmentsRef.current.map((inst) => {
        if (String(inst.tax_year || '').trim() !== yearKey) return inst;
        const rowDate = inst.paid_date || '';
        if (rowDate === '' || rowDate === prevDate) {
          return { ...inst, paid_date: value || '' };
        }
        return inst;
      });
      onBatchUpdate({ paid_breakdown_na_groups: nextGroups, installments: newInstallments });
      return;
    }
    onUpdate('paid_breakdown_na_groups', nextGroups);
  }, [jurisdiction.paid_breakdown_na_groups, onUpdate, onBatchUpdate]);

  // ── Paid Breakdown N/A — Selected Years (Jun 2026 v2.6) ──────────
  // One collector confirmation covers the operator-SELECTED tax years.
  // Storage: `jurisdiction.paid_breakdown_na_all_years = { enabled,
  // covered_years: ["2026", ...], total_paid_amount, paid_date }` —
  // stored ONCE so the lump-sum is never double-counted. Legacy v2.5
  // entries without `covered_years` are read as "all years".
  // 1a — toggle ON starts with NO years checked (protects years with
  //      real payment data). Checking a year converts its rows
  //      (Paid / $0 penalty / $0 balance) and replaces that year's
  //      per-year group entry (confirm dialog first). Unchecking
  //      leaves row data untouched.
  // 3a — new tax years appear UNCHECKED.
  // 4a — per-year toggles stay available for unselected years.
  const allYearsEntry = jurisdiction.paid_breakdown_na_all_years || {};
  const allYearsActive = !!allYearsEntry.enabled;
  const [pendingCoveredYear, setPendingCoveredYear] = useState(null); // year string | '__ALL__' | null

  // ── Jun 2026 — Tax Collected frequency change vs Paid Breakdown N/A ──
  // PBNA selections/amounts were entered under the PREVIOUS tax structure
  // (annual vs semi-annual vs quarterly rows), so switching frequency must
  // clear them — after operator confirmation when active PBNA state exists.
  const [pendingFrequency, setPendingFrequency] = useState(null); // 'annually' | 'semi_annually' | 'quarterly' | null

  const hasActivePbnaState = () => {
    const entry = jurisdiction.paid_breakdown_na_all_years || {};
    if (entry.enabled) return true;
    const groups = jurisdiction.paid_breakdown_na_groups || {};
    if (Object.values(groups).some((e) => e && e.enabled)) return true;
    return (jurisdiction.installments || []).some((i) => i && i.paid_breakdown_na);
  };

  const applyFrequencyChange = (value) => {
    // PRESERVE-SPECIALTY (Feb 2026):
    //   Specialty installments (SA / SB / Utility / Solid Waste etc.)
    //   are user-curated rows that must SURVIVE a Tax Collected
    //   frequency change — see contract in lib/specialtyInstallments.js
    //   header. We rebuild the STANDARD half from the new frequency's
    //   label set, keep specialty rows verbatim, and reindex so
    //   installment_index stays contiguous. Standard labels come from
    //   STANDARD_INSTALLMENT_LABELS (single source of truth shared with
    //   the per-row Installment dropdown).
    const labels = STANDARD_INSTALLMENT_LABELS[value] || ['Annual'];
    const [, specialtyRows] = partitionInstallments(jurisdiction.installments || []);
    const standardRows = labels.map((label) => ({
      id: crypto.randomUUID(),
      tax_year: '',
      installment_name: label,
      due_date: null,
      base_tax: null,
      amount_due: null,
      discount_penalty: 0,
      paid_amount: null,
      notes: '',
      pay_status: 'Due',
      paid_date: null,
      balance_amount: null,
      dlq_date: null,
      mailing_issue_date: null
    }));
    // Standard rows come first (rendered at the top of the Current Tax
    // Summary table), specialty rows preserved below (row-level PBNA
    // flags cleared — they belong to the previous tax structure).
    // installment_index is reassigned sequentially.
    const newInstallments = [
      ...standardRows,
      ...specialtyRows.map((row) => (row.paid_breakdown_na ? { ...row, paid_breakdown_na: false } : row)),
    ].map((row, idx) => ({
      ...row,
      installment_index: idx + 1,
    }));
    // Batch-update frequency, installments and the PBNA reset together
    // (avoids stale ref race).
    onBatchUpdate({
      collection_frequency: value,
      installments: newInstallments,
      // Jun 2026 — full PBNA reset: toggle, covered years, amount, date,
      // and every per-year group entry.
      paid_breakdown_na_all_years: { enabled: false, covered_years: [], total_paid_amount: null, paid_date: '' },
      paid_breakdown_na_groups: {},
    });
  };


  const currentCoveredList = () => {
    if (!allYearsActive) return [];
    if (Array.isArray(allYearsEntry.covered_years)) {
      return allYearsEntry.covered_years.map((y) => String(y).trim()).filter(Boolean);
    }
    return [...orderedYears]; // legacy v2.5 all-years entry
  };

  const handleAllYearsBreakdownNaToggle = (checked) => {
    if (checked) {
      onUpdate('paid_breakdown_na_all_years', {
        enabled: true,
        // 1a — start with no years checked (unless re-enabling a
        // previous selection, which is preserved).
        covered_years: Array.isArray(allYearsEntry.covered_years)
          ? allYearsEntry.covered_years
          : [],
        total_paid_amount: allYearsEntry.total_paid_amount ?? null,
        paid_date: allYearsEntry.paid_date ?? '',
      });
    } else {
      onUpdate('paid_breakdown_na_all_years', { ...allYearsEntry, enabled: false });
    }
  };

  const handleAllYearsBreakdownNaField = (field, value) => {
    const existing = jurisdiction.paid_breakdown_na_all_years || { enabled: true };
    if (field === 'paid_date') {
      // Auto-fill covered rows' Paid Date. Manual row overrides are
      // preserved: only rows whose date is empty or still equals the
      // PREVIOUS group date are updated.
      const covered = computeCoveredYears(jurisdiction);
      const prevDate = existing.paid_date || '';
      const newInstallments = installmentsRef.current.map((inst) => {
        if (!covered.has(String(inst.tax_year || '').trim())) return inst;
        const rowDate = inst.paid_date || '';
        if (rowDate === '' || rowDate === prevDate) {
          return { ...inst, paid_date: value || '' };
        }
        return inst; // manual override preserved
      });
      onBatchUpdate({
        paid_breakdown_na_all_years: { ...existing, paid_date: value },
        installments: newInstallments,
      });
      return;
    }
    onUpdate('paid_breakdown_na_all_years', { ...existing, [field]: value });
  };

  const applyCoveredYears = (yearsToAdd) => {
    // Check year(s): convert their rows + replace their per-year entries.
    const addSet = new Set(yearsToAdd.map((y) => String(y).trim()).filter(Boolean));
    const next = Array.from(new Set([...currentCoveredList(), ...addSet])).sort();
    const newInstallments = installmentsRef.current.map((inst) =>
      addSet.has(String(inst.tax_year || '').trim())
        ? { ...inst, pay_status: 'Paid', discount_penalty: 0, balance_amount: 0, paid_breakdown_na: false }
        : inst
    );
    const perYear = jurisdiction.paid_breakdown_na_groups || {};
    const updates = {
      paid_breakdown_na_all_years: { ...allYearsEntry, enabled: true, covered_years: next },
      installments: newInstallments,
    };
    const conflicting = Object.keys(perYear).filter(
      (y) => addSet.has(String(y).trim()) && perYear[y] && perYear[y].enabled
    );
    if (conflicting.length > 0) {
      const nextGroups = { ...perYear };
      conflicting.forEach((y) => { nextGroups[y] = { ...nextGroups[y], enabled: false }; });
      updates.paid_breakdown_na_groups = nextGroups;
    }
    onBatchUpdate(updates);
  };

  const handleCoveredYearToggle = (yearKey, checked) => {
    if (checked) {
      const perYear = jurisdiction.paid_breakdown_na_groups || {};
      if (perYear[yearKey] && perYear[yearKey].enabled) {
        setPendingCoveredYear(yearKey); // confirm before replacing that year's entry
        return;
      }
      applyCoveredYears([yearKey]);
    } else {
      // Uncheck: remove from selection and undo OUR auto-filled Paid Date
      // (rows whose date still equals the group date). Manual row data is
      // otherwise left untouched.
      const groupDate = allYearsEntry.paid_date || '';
      const newInstallments = installmentsRef.current.map((inst) => {
        if (String(inst.tax_year || '').trim() !== yearKey) return inst;
        if (groupDate && (inst.paid_date || '') === groupDate) {
          return { ...inst, paid_date: '' };
        }
        return inst;
      });
      onBatchUpdate({
        paid_breakdown_na_all_years: {
          ...allYearsEntry,
          covered_years: currentCoveredList().filter((y) => y !== yearKey),
        },
        installments: newInstallments,
      });
    }
  };

  const handleSelectAllCoveredYears = () => {
    const perYear = jurisdiction.paid_breakdown_na_groups || {};
    if (orderedYears.some((y) => perYear[y] && perYear[y].enabled)) {
      setPendingCoveredYear('__ALL__');
      return;
    }
    applyCoveredYears(orderedYears);
  };

  // Build the ordered list of unique tax years (first occurrence) for
  // year-group header rendering.
  const orderedYears = (() => {
    const seen = new Set();
    const out = [];
    for (const inst of installments) {
      const y = String(inst.tax_year || '').trim();
      if (!y || seen.has(y)) continue;
      seen.add(y);
      out.push(y);
    }
    return out;
  })();

  // v2.6 — years currently covered by the Selected-Years lump sum
  // (intersected with the years actually present; legacy v2.5 entries
  // without `covered_years` cover every year).
  const coveredYears = computeCoveredYears(jurisdiction);

  // Jun 2026 UX — when Tax Collected = Annual, the Selected-Years control
  // covers every lump-sum scenario (single year, multi-year, mixed), so the
  // per-year "Paid breakdown not available for YYYY" bars are hidden to
  // avoid duplicate functionality. A bar still renders for a year whose
  // per-year entry is ALREADY enabled (legacy data stays visible and
  // manageable). Semi-Annual / Quarterly keep the per-year bars for
  // installment-specific scenarios.
  const isAnnualFrequency = (jurisdiction.collection_frequency || 'annually') === 'annually';

  const isYearBreakdownNa = (year) => {
    const entry = breakdownNaGroups[String(year || '').trim()];
    return !!(entry && entry.enabled);
  };

  return (
    <div className="space-y-4">
      {/* ── Tax Summary Overview — order-level financial summary ── */}
      {orderFinancialSummary && (
        <div
          className="tcp-card tcp-card-head"
          data-testid="tax-summary-overview"
        >
          <div className="tcp-tile tcp-tile-green"><DollarSign className="w-[18px] h-[18px]" /></div>
          <div className="tcp-card-body">
          <h4 className="tcp-card-title mb-2.5" data-testid="tax-summary-overview-heading">
            Tax Summary Overview
          </h4>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between" data-testid="total-paid-row">
              <span className="text-[13px] font-medium text-slate-600">Total Paid</span>
              <span className="text-[16px] font-semibold text-slate-900 tabular-nums" data-testid="total-paid-value">
                {formatCurrency(orderFinancialSummary.totalPaid)}
              </span>
            </div>
            <div className="h-px bg-slate-200/80" />
            <div className="flex items-center justify-between" data-testid="total-balance-row">
              <span className="text-[13px] font-medium text-slate-600">Total Balance</span>
              <span className="text-[16px] font-semibold text-slate-900 tabular-nums" data-testid="total-balance-value">
                {formatCurrency(orderFinancialSummary.totalBalance)}
              </span>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Tax Collected — per-jurisdiction frequency selector */}
      <div className="flex items-center gap-4" data-testid="section-tax-collected">
        <Label className="text-sm font-semibold text-slate-800 whitespace-nowrap">Tax Collected:</Label>
        <div className="tcp-segmented flex flex-1 max-w-xl">
          {[
            { value: 'annually', label: 'Annual' },
            { value: 'semi_annually', label: 'Semi-Annual' },
            { value: 'quarterly', label: 'Quarterly' },
            // TAXCERT-INSTALLMENT-PLAN-001 — hidden for Michigan (season model).
            ...(isMichigan(stateCode) ? [] : [{ value: 'installment_plan', label: 'Installment' }])
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              disabled={readOnly}
              onClick={() => {
                if (jurisdiction.collection_frequency === option.value) return;
                // Jun 2026 — active Paid Breakdown N/A state was entered
                // under the current tax structure; confirm before clearing
                // it as part of the frequency change.
                if (hasActivePbnaState()) {
                  setPendingFrequency(option.value);
                  return;
                }
                applyFrequencyChange(option.value);
              }}
              data-testid={`tax-collected-${option.value}`}
              className={`tcp-segment flex-1 px-3 py-1.5 text-sm font-medium rounded-lg ${
                jurisdiction.collection_frequency === option.value
                  ? 'is-active text-white'
                  : 'text-slate-600 hover:text-blue-600 hover:bg-white/70'
              } ${readOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Feb 2026 — Delinquent Payoff Breakdown (jurisdiction-level) ──
          Some collectors provide a single combined payoff covering
          multiple delinquent years instead of a year-by-year breakdown.
          The toggle only appears when at least one installment is in a
          delinquent state — it has no relevance otherwise. Switching
          modes is non-destructive: per-installment payoff fields are
          soft-hidden (disabled, not cleared), and the combined fields
          are persisted independently so toggling back restores the old
          year-wise values. */}
      {(() => {
        // Feb 6, 2026 — visibility helper computed once at TaxSummaryTab
        // top so we can also self-heal stale combined-mode data via useEffect.
        // See `combinedBreakdownAvailable` and the useEffect just below it.
        if (!combinedBreakdownAvailable) return null;
        const mode = jurisdiction.dlq_payoff_breakdown_mode || 'year_wise';
        const isCombined = mode === 'combined';
        const setMode = (next) => {
          if (next === mode) return;
          onUpdate('dlq_payoff_breakdown_mode', next);
        };
        const missingAmt = isCombined && (
          jurisdiction.dlq_combined_payoff_amount === null
          || jurisdiction.dlq_combined_payoff_amount === undefined
          || jurisdiction.dlq_combined_payoff_amount === ''
        );
        const missingDate = isCombined && !jurisdiction.dlq_combined_payoff_good_through_date;
        return (
          <div
            className="rounded-xl border border-amber-200 bg-amber-50/40 px-4 py-3 space-y-3"
            data-testid="dlq-payoff-breakdown-section"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <Label className="text-sm font-semibold text-slate-800">
                  Delinquent Payoff Breakdown
                </Label>
              </div>
              <div
                className="flex bg-white border border-slate-200 rounded-lg p-1 shrink-0"
                role="radiogroup"
                aria-label="Delinquent payoff breakdown mode"
              >
                <button
                  type="button"
                  onClick={() => !readOnly && setMode('year_wise')}
                  role="radio"
                  aria-checked={!isCombined}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    !isCombined
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-slate-600 hover:text-blue-600 hover:bg-slate-50'
                  } ${readOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  data-testid="dlq-payoff-mode-year-wise"
                  disabled={readOnly}
                >
                  Year-wise Breakdown
                </button>
                <button
                  type="button"
                  onClick={() => !readOnly && setMode('combined')}
                  role="radio"
                  aria-checked={isCombined}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    isCombined
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-slate-600 hover:text-blue-600 hover:bg-slate-50'
                  } ${readOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  data-testid="dlq-payoff-mode-combined"
                  disabled={readOnly}
                >
                  Combined Payoff
                </button>
              </div>
            </div>

            {isCombined && (
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-amber-200/60"
                data-testid="dlq-combined-payoff-fields"
              >
                {/* Jun 2026 — UAT-COMBINED-PAYOFF-BASE-BYPASS. Subtle QC
                    badge so reviewers know the per-year Base Tax fields
                    are intentionally empty — the collector only
                    discloses combined payoff + good-through. The PDF
                    note "Tax Collector will not provide … base amount"
                    prints automatically on the certificate. */}
                <div
                  className="md:col-span-2 -mt-1 mb-1 inline-flex items-center gap-2 text-[11px] font-medium text-amber-800 bg-amber-100/70 border border-amber-200 rounded-md px-2 py-1 w-fit"
                  data-testid="combined-payoff-base-bypass-badge"
                  title="The collector provides only the combined payoff amount and good-through date. Year-rows still list the covered years; Base Tax is bypassed."
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  Base tax bypassed — combined payoff only
                </div>
                <div>
                  <Label
                    className={`text-xs mb-1.5 block font-medium ${
                      missingAmt ? 'text-red-700' : 'text-slate-700'
                    }`}
                  >
                    Combined Payoff Amount <span className="text-red-500">*</span>
                  </Label>
                  <LocalCurrencyInput
                    value={jurisdiction.dlq_combined_payoff_amount}
                    onCommit={(val) => onUpdate('dlq_combined_payoff_amount', val)}
                    placeholder="$0.00"
                    className={`h-9 ${
                      missingAmt
                        ? 'border-red-500 ring-1 ring-red-300'
                        : 'border-slate-300 focus:border-blue-400 focus:ring-blue-200'
                    }`}
                    disabled={readOnly}
                    data-testid="dlq-combined-payoff-amount"
                  />
                  {missingAmt && (
                    <p className="text-xs text-red-500 mt-0.5">
                      Combined Payoff Amount is required.
                    </p>
                  )}
                </div>
                <div>
                  <Label
                    className={`text-xs mb-1.5 block font-medium ${
                      missingDate ? 'text-red-700' : 'text-slate-700'
                    }`}
                  >
                    Combined Payoff Good Through <span className="text-red-500">*</span>
                  </Label>
                  <LocalDatePickerInput
                    value={jurisdiction.dlq_combined_payoff_good_through_date || ''}
                    onCommit={(val) => onUpdate('dlq_combined_payoff_good_through_date', val)}
                    placeholder="MM/DD/YYYY"
                    className={`h-9 ${
                      missingDate
                        ? 'border-red-500 ring-1 ring-red-300'
                        : 'border-slate-300 focus:border-blue-400 focus:ring-blue-200'
                    }`}
                    disabled={readOnly}
                    data-testid="dlq-combined-payoff-good-thru"
                  />
                  {missingDate && (
                    <p className="text-xs text-red-500 mt-0.5">
                      Good-through date is required.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Summary Header */}
      <div className="flex items-center justify-between tcp-card py-2.5" data-testid="section-tax-records">
        <h3 className="tcp-card-title flex items-center gap-2.5">
          <span className="tcp-tile tcp-tile-blue" style={{ width: 30, height: 30 }}><DollarSign className="w-4 h-4" /></span>
          Tax Records ({installments.length})
        </h3>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-slate-600">
            Total Due: <span className="font-semibold text-slate-900 tabular-nums">{formatCurrency(totalDue)}</span>
          </span>
        </div>
      </div>

      {/* ── Paid Breakdown N/A — Selected Years (Jun 2026 v2.6) ──
          Jurisdiction-level lump-sum toggle: the operator checks which
          tax years are covered by the collector's single total. Years
          NOT checked keep their actual payment details; per-year group
          bars stay available for unchecked years.
          Jun 2026 UX — always shown for Annual (it replaces the per-year
          bars there, incl. the single-year case). */}
      {(isAnnualFrequency || orderedYears.length > 1 || allYearsActive) && (
        <div
          className={`rounded-lg border ${allYearsActive ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200 bg-slate-50/40'} px-4 py-3 space-y-2`}
          data-testid="paid-breakdown-na-all-years-bar"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <Switch
              checked={allYearsActive}
              onCheckedChange={handleAllYearsBreakdownNaToggle}
              disabled={readOnly}
              data-testid="paid-breakdown-na-all-years-toggle"
            />
            <Label
              className="text-sm font-semibold text-slate-800 cursor-pointer select-none"
              onClick={() => !readOnly && handleAllYearsBreakdownNaToggle(!allYearsActive)}
              data-testid="paid-breakdown-na-all-years-label"
            >
              Multi-Year Paid Amount
            </Label>
            <span
              className="inline-flex items-center text-slate-400 cursor-help"
              title="Turn this ON when the tax collector provided ONE total paid amount covering some or all tax years in this jurisdiction with no year-level or installment-level breakdown. Check the covered years: their tax records are set to Paid with $0.00 balance and show 'See Notes' in the Paid Amt column (UI + PDF), and a single clarifier sentence with the lump-sum total and paid date is appended to the notes. Unchecked years keep their actual payment details. Tax years added later are NOT covered automatically — check them explicitly."
            >
              <HelpCircle className="w-3.5 h-3.5" />
            </span>
            {allYearsActive && coveredYears.size > 0 && (
              <span
                className="ml-auto inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200"
                data-testid="paid-breakdown-na-all-years-badge"
              >
                See Notes
              </span>
            )}
          </div>
          {allYearsActive && (
            <>
              <div
                className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1"
                data-testid="paid-breakdown-na-covered-years"
              >
                {orderedYears.map((y) => (
                  <label
                    key={y}
                    className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer select-none"
                    data-testid={`paid-breakdown-na-year-checkbox-label-${y}`}
                  >
                    <Checkbox
                      checked={coveredYears.has(y)}
                      onCheckedChange={(c) => handleCoveredYearToggle(y, !!c)}
                      disabled={readOnly}
                      data-testid={`paid-breakdown-na-year-checkbox-${y}`}
                    />
                    {y}
                  </label>
                ))}
                {orderedYears.some((y) => !coveredYears.has(y)) && (
                  <button
                    type="button"
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
                    onClick={handleSelectAllCoveredYears}
                    disabled={readOnly}
                    data-testid="paid-breakdown-na-select-all-years"
                  >
                    Select all
                  </button>
                )}
              </div>
              {/* Jun 2026 UX — the zero-years instructional warning was removed
                  per operator feedback: the year checkboxes already communicate
                  the behavior. The coverage summary still renders once years
                  are checked. */}
              {coveredYears.size > 0 && (
                <p
                  className="text-[11px] text-slate-500 italic leading-snug"
                  data-testid="paid-breakdown-na-all-years-coverage"
                >
                  Lump sum covers {Array.from(coveredYears).sort().join(', ')}. Unselected
                  years keep their actual payment details. Tax years added later are NOT
                  covered automatically.
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-amber-200/70">
                <div>
                  <Label
                    className="text-xs mb-1.5 block font-medium text-slate-700"
                    htmlFor="paid-breakdown-na-all-years-total"
                  >
                    Total Paid Amount <span className="text-red-500">*</span>
                  </Label>
                  <LocalCurrencyInput
                    value={allYearsEntry.total_paid_amount}
                    onCommit={(val) => handleAllYearsBreakdownNaField('total_paid_amount', val)}
                    placeholder="$0.00"
                    className="h-9"
                    disabled={readOnly}
                    data-testid="paid-breakdown-na-all-years-total"
                  />
                </div>
                <div>
                  <Label
                    className="text-xs mb-1.5 block font-medium text-slate-700"
                    htmlFor="paid-breakdown-na-all-years-date"
                  >
                    Paid Date <span className="text-red-500">*</span>
                  </Label>
                  <LocalDatePickerInput
                    value={allYearsEntry.paid_date || ''}
                    onCommit={(val) => handleAllYearsBreakdownNaField('paid_date', val)}
                    placeholder="MM/DD/YYYY"
                    className="h-9"
                    disabled={readOnly}
                    data-testid="paid-breakdown-na-all-years-date"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Confirmation when including a year would replace its per-year entry (1a) */}
      <AlertDialog open={pendingCoveredYear !== null} onOpenChange={(open) => !open && setPendingCoveredYear(null)}>
        <AlertDialogContent data-testid="paid-breakdown-na-all-years-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingCoveredYear === '__ALL__' ? 'Replace per-year entries?' : 'Replace this per-year entry?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingCoveredYear === '__ALL__'
                ? 'One or more tax years already have a per-year "Paid breakdown not available" entry. Selecting all years will replace those per-year entries — their amounts and dates will be removed, and the single lump-sum total will cover every selected year.'
                : `Tax year ${pendingCoveredYear} already has a per-year "Paid breakdown not available" entry. Including it in the lump-sum selection will replace that per-year entry — its amount and date will be removed.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="paid-breakdown-na-all-years-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="paid-breakdown-na-all-years-confirm-replace"
              onClick={() => {
                if (pendingCoveredYear === '__ALL__') {
                  applyCoveredYears(orderedYears);
                } else if (pendingCoveredYear) {
                  applyCoveredYears([pendingCoveredYear]);
                }
                setPendingCoveredYear(null);
              }}
            >
              Replace and include
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation when changing Tax Collected frequency would clear
          Paid Breakdown N/A selections (Jun 2026) */}
      <AlertDialog open={pendingFrequency !== null} onOpenChange={(open) => !open && setPendingFrequency(null)}>
        <AlertDialogContent data-testid="tax-collected-pbna-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Paid Breakdown N/A data?</AlertDialogTitle>
            <AlertDialogDescription>
              Changing the tax collection frequency will clear Paid Breakdown
              N/A selections and amounts for this jurisdiction. Do you want to
              continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tax-collected-pbna-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="tax-collected-pbna-confirm-continue"
              onClick={() => {
                if (pendingFrequency) applyFrequencyChange(pendingFrequency);
                setPendingFrequency(null);
              }}
            >
              Continue and clear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tax Record Rows */}
      <div className="space-y-3">
        {(() => {
          // Build set of duplicate due dates for real-time highlighting
          // MI: Only flag duplicates within the SAME season (SUMMER vs WINTER can share dates)
          const isMI = isMichigan(stateCode);
          const dueDateCounts = {};
          for (const inst of installments) {
            const d = (inst.due_date || '').trim();
            if (!d) continue;
            const key = isMI ? `${(inst.season || '').toUpperCase()}::${d}` : d;
            dueDateCounts[key] = (dueDateCounts[key] || 0) + 1;
          }
          const duplicateKeys = new Set(
            Object.entries(dueDateCounts).filter(([, c]) => c > 1).map(([k]) => k)
          );
          return installments.map((installment, index) => {
            const d = (installment.due_date || '').trim();
            const key = isMI ? `${(installment.season || '').toUpperCase()}::${d}` : d;
            const instYear = String(installment.tax_year || '').trim();
            const yearActive = isYearBreakdownNa(instYear);
            // Render a year-group header BEFORE the first installment
            // for each tax_year that appears in this jurisdiction. The
            // header carries the Paid Breakdown N/A toggle + (when ON)
            // the Total Paid Amount + Paid Date inputs that act as the
            // lump-sum source of truth for every row in the group.
            // Feb 2026 v2.4 — Visibility gate removed: the toggle is
            // ALWAYS shown per year-group regardless of installment
            // status. The previous rule (only show when all rows Paid)
            // created a circular blocker for the exact workflow the
            // feature exists to support — the operator has a single
            // lump-sum confirmation and no per-row amounts to set Pay
            // Status with. Toggling ON now force-sets pay_status='Paid'
            // on every row in the group via handleGroupBreakdownNaToggle.
            const isFirstOfYear =
              !!instYear && installments.findIndex(
                (i) => String(i.tax_year || '').trim() === instYear,
              ) === index;
            const groupEntry = breakdownNaGroups[instYear] || {};
            return (
            <React.Fragment key={`grp-${installment.id || index}`}>
              {isFirstOfYear && !coveredYears.has(instYear) && (!isAnnualFrequency || yearActive) && (
                <div
                  className={`rounded-lg border ${yearActive ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200 bg-slate-50/40'} px-4 py-3 space-y-2`}
                  data-testid={`paid-breakdown-na-group-${instYear}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <Switch
                      checked={yearActive}
                      onCheckedChange={(checked) => handleGroupBreakdownNaToggle(instYear, checked)}
                      disabled={readOnly}
                      data-testid={`paid-breakdown-na-toggle-${instYear}`}
                    />
                    <Label
                      className="text-sm font-semibold text-slate-800 cursor-pointer select-none"
                      onClick={() => !readOnly && handleGroupBreakdownNaToggle(instYear, !yearActive)}
                      data-testid={`paid-breakdown-na-label-${instYear}`}
                    >
                      Year Paid Amount
                    </Label>
                    <span
                      className="inline-flex items-center text-slate-400 cursor-help"
                      title="Turn this ON when the tax collector confirmed all installments for this tax year are paid but did NOT provide a per-installment breakdown. Every installment in this year will show 'See Notes' in the PDF Paid Amt column and a clarifier sentence will be appended to the notes block with the lump-sum total and paid date you enter below. Adj Type, Adj Amt, Total Amt, Paid Amt and Balance are hidden on every row in this group; Base Tax stays visible so the assessed value is still recorded."
                    >
                      <HelpCircle className="w-3.5 h-3.5" />
                    </span>
                    {yearActive && (
                      <span
                        className="ml-auto inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200"
                        data-testid={`paid-breakdown-na-badge-${instYear}`}
                      >
                        See Notes
                      </span>
                    )}
                  </div>
                  {yearActive && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-amber-200/70">
                      <div>
                        <Label
                          className="text-xs mb-1.5 block font-medium text-slate-700"
                          htmlFor={`paid-breakdown-na-total-${instYear}`}
                        >
                          Total Paid Amount <span className="text-red-500">*</span>
                        </Label>
                        <LocalCurrencyInput
                          value={groupEntry.total_paid_amount}
                          onCommit={(val) => handleGroupBreakdownNaField(instYear, 'total_paid_amount', val)}
                          placeholder="$0.00"
                          className="h-9"
                          disabled={readOnly}
                          data-testid={`paid-breakdown-na-total-${instYear}`}
                        />
                      </div>
                      <div>
                        <Label
                          className="text-xs mb-1.5 block font-medium text-slate-700"
                          htmlFor={`paid-breakdown-na-date-${instYear}`}
                        >
                          Paid Date <span className="text-red-500">*</span>
                        </Label>
                        <LocalDatePickerInput
                          value={groupEntry.paid_date || ''}
                          onCommit={(val) => handleGroupBreakdownNaField(instYear, 'paid_date', val)}
                          placeholder="MM/DD/YYYY"
                          className="h-9"
                          disabled={readOnly}
                          data-testid={`paid-breakdown-na-date-${instYear}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              <InstallmentRow
                installment={installment}
                index={index}
                onUpdate={handleInstallmentUpdate}
                onRemove={handleInstallmentRemove}
                canRemove={installments.length > 1}
                readOnly={readOnly}
                hasDuplicateDueDate={installments.length > 1 && d && duplicateKeys.has(key)}
                allInstallments={installments}
                stateCode={stateCode}
                fullyExempt={!!jurisdiction.fully_exempt}
                isNewParcel={isNewParcel}
                payoffFieldsDisabled={
                  (jurisdiction.dlq_payoff_breakdown_mode || 'year_wise') === 'combined'
                }
                collectionFrequency={jurisdiction.collection_frequency || ''}
                groupBreakdownNa={yearActive || coveredYears.has(instYear)}
                validationAttempted={validationAttempted}
              />
            </React.Fragment>
          );});
        })()}
      </div>

      {/* Add Tax Record Button */}
      {!readOnly && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onAddInstallment}
          className="w-full border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50"
          data-testid="add-tax-record-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Tax Record
        </Button>
      )}

      {/* Combined Tax Certification Notes Section
          May 17, 2026 — when the parent parcel is in New Parcel mode,
          the standard tax-history language ("All prior taxes are paid"
          / "<year> taxes are paid") conflicts with the new-parcel
          scenario. We replace the entire block with a single info pill
          so processors/QC/clients see exactly one source of truth: the
          dedicated PDF Note Preview rendered on the Parcel Details
          card above. */}
      {installments.length > 0 && isNewParcel && (
        <div className="pt-4 border-t border-slate-200" data-testid="combined-notes-suppressed">
          <div className="flex items-start gap-2 p-3 rounded-md border border-emerald-200 bg-emerald-50/60">
            <FileText className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-emerald-800 leading-snug">
              <span className="font-semibold">Standard tax certification notes are disabled while New Parcel mode is enabled.</span>
              {' '}
              The dedicated New Parcel note (see PDF Note Preview at the top of this card) will be appended to the PDF instead.
            </p>
          </div>
          {/* Additional Notes textarea is still available so processors
              can supplement the standardised line if necessary. */}
          <div className="mt-3">
            <Label className="text-xs text-slate-600 mb-1.5 block font-medium">
              Additional Notes (Optional)
            </Label>
            <LocalTextarea
              value={(jurisdiction.tax_notes_override || '').toUpperCase()}
              onCommit={(val) => onUpdate('tax_notes_override', (val || '').toUpperCase())}
              placeholder="ADD ANY ADDITIONAL NOTES TO SUPPLEMENT THE NEW PARCEL NOTE..."
              className="min-h-[60px] text-sm uppercase"
              rows={2}
              disabled={readOnly}
              data-testid="additional-tax-notes"
            />
          </div>
        </div>
      )}
      {installments.length > 0 && !isNewParcel && (
        <div className="tcp-card tcp-card-head" data-testid="section-combined-notes">
          <div className="tcp-tile tcp-tile-slate"><FileText className="w-[18px] h-[18px]" /></div>
          <div className="tcp-card-body space-y-2.5">
            <div className="flex items-center justify-between">
              <h3 className="tcp-card-title">
                Combined Tax Certification Notes
              </h3>
            </div>
            <div 
              className="bg-slate-50 border border-slate-200 rounded-lg p-3.5"
              data-testid="combined-tax-notes"
            >
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                {autoGeneratedNotes || 'Notes will be generated based on tax record data.'}
                {jurisdiction.tax_notes_override && jurisdiction.tax_notes_override.trim() && (
                  <>{'\n\n'}
                    <span className="font-medium">Note:</span> {jurisdiction.tax_notes_override.trim().toUpperCase()}
                  </>
                )}
              </p>
            </div>
            {/* Optional: Manual notes override */}
            <div>
              <Label className="text-xs text-slate-600 mb-1.5 block font-medium">
                Additional Notes (Optional)
              </Label>
              <LocalTextarea
                value={(jurisdiction.tax_notes_override || '').toUpperCase()}
                /* May 5, 2026 — uppercase at the commit boundary so DB,
                   UI display, and PDF all show consistent ALL CAPS for
                   "Additional Notes" per ops standardization request. */
                onCommit={(val) => onUpdate('tax_notes_override', (val || '').toUpperCase())}
                placeholder="ADD ANY ADDITIONAL NOTES TO SUPPLEMENT THE AUTO-GENERATED TEXT..."
                className="min-h-[60px] text-sm uppercase"
                rows={2}
                disabled={readOnly}
                data-testid="additional-tax-notes"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

/**
 * Workflow Actions Section
 * Final workflow options: Pending, Clarification, Mail-Away, QC Review, Complete
 * Uses existing business logic - only UI placement changed
 */
const WORKFLOW_STATUS_OPTIONS = [
  { 
    value: 'pending', 
    label: 'Pending', 
    description: 'Waiting for information',
    Icon: Clock,
    colors: {
      bg: 'bg-amber-50',
      bgHover: 'hover:bg-amber-100',
      bgSelected: 'bg-amber-100',
      border: 'border-amber-200',
      borderSelected: 'border-amber-400',
      icon: 'text-amber-500',
      iconSelected: 'text-amber-600'
    }
  },
  { 
    value: 'clarification', 
    label: 'Clarification', 
    description: 'Need customer input',
    Icon: HelpCircle,
    colors: {
      bg: 'bg-purple-50',
      bgHover: 'hover:bg-purple-100',
      bgSelected: 'bg-purple-100',
      border: 'border-purple-200',
      borderSelected: 'border-purple-400',
      icon: 'text-purple-500',
      iconSelected: 'text-purple-600'
    }
  },
  { 
    value: 'mailaway', 
    label: 'Mail-Away', 
    description: 'Send request letter',
    Icon: Mail,
    colors: {
      bg: 'bg-blue-50',
      bgHover: 'hover:bg-blue-100',
      bgSelected: 'bg-blue-100',
      border: 'border-blue-200',
      borderSelected: 'border-blue-400',
      icon: 'text-blue-500',
      iconSelected: 'text-blue-600'
    }
  },
  { 
    value: 'qc_review', 
    label: 'QC Review', 
    description: 'Send for QC review',
    Icon: CheckCircle,
    colors: {
      bg: 'bg-violet-50',
      bgHover: 'hover:bg-violet-100',
      bgSelected: 'bg-violet-100',
      border: 'border-violet-200',
      borderSelected: 'border-violet-400',
      icon: 'text-violet-500',
      iconSelected: 'text-violet-600'
    }
  }
];



const WorkflowActionsSection = ({ 
  selectedStatus, 
  onStatusSelect, 
  onComplete, 
  completeError = null,
  mlsGuard = null,
  dualTax = null,
  onSubmitStatus,
  isReadOnly = false,
  hasJurisdiction = false,
  hasParcelAPN = false,
  isTaxProduct = true,
  isSubmitting = false,
  reason = '',
  onReasonChange,
  jurisdictionData = {},
  statusNotes = [],
  editMode = false,
  setEditMode,
  // Tax Sheet Review props
  taxSheetReview = null,
  onReviewTaxSheet = null,
  isAdmin = false,
  // Readiness props
  reviewReadiness = null,
  // Active tab — controls visibility of review checkpoint
  activeTab = 'tax',
  // External portal bypass
  isExternalPortalClient = false,
  // Order data for fax APN validation
  orderData = null,
  // User role for chip visibility filtering
  userRole = '',
  // TAX-EMAIL-APN-GATE-001: live certificate parcels for the strict Email Parcel/APN check
  liveParcels = null,
  // TAX-EMAIL-CONFIRM-001: the jurisdiction the processor is working on (scope of the Email action)
  emailJurisdiction = null,
  // MAILAWAY-PENDING-GUARD-001 / MAILAWAY-FOLLOWUP-001
  activeMailAway = [],
  canOverridePending = false
}) => {
  const mailAwayActive = Array.isArray(activeMailAway) && activeMailAway.length > 0;
  const pendingGuarded = mailAwayActive && orderData?.status !== 'pending';
  // TAX-PENDING-EMAIL-001: structured chip ids selected in StatusReasonField (automation triggers)
  const [selectedChipIds, setSelectedChipIds] = useState([]);
  const [hasEmailChip, setHasEmailChip] = useState(false);
  const handleChipIdsChange = (ids, meta) => {
    setSelectedChipIds(ids);
    setHasEmailChip(!!meta?.hasEmailAutomation);
  };
  // TAX-EMAIL-CONFIRM-001: {open, loading, preflight, error}
  const [emailConfirm, setEmailConfirm] = useState(null);
  const openEmailConfirm = async () => {
    setEmailConfirm({ open: true, loading: true, preflight: null, error: null });
    try {
      const pf = await fetchTaxEmailPreflight(orderData?.id, emailJurisdiction);
      setEmailConfirm({ open: true, loading: false, preflight: pf, error: null });
    } catch (e) {
      setEmailConfirm({ open: true, loading: false, preflight: null, error: e.message });
    }
  };
  // Filter out Mail-Away for non-tax products
  const availableOptions = isTaxProduct 
    ? WORKFLOW_STATUS_OPTIONS 
    : WORKFLOW_STATUS_OPTIONS.filter(opt => opt.value !== 'mailaway');
  
  // Check if Mail-Away is disabled
  const isMailAwayDisabled = isTaxProduct && (!hasJurisdiction || !hasParcelAPN);
  
  const getMailAwayDisabledReason = () => {
    if (!hasJurisdiction && !hasParcelAPN) return 'Requires Jurisdiction & APN';
    if (!hasJurisdiction) return 'Select Jurisdiction first';
    if (!hasParcelAPN) return 'Enter Parcel/APN first';
    return null;
  };

  // Check if selected status requires a reason
  const statusRequiresReason = ['pending', 'clarification'].includes(selectedStatus);
  // Require at least 10 characters for reason (matching existing validation)
  // MAILAWAY-RECOVERY-001: follow-up only while the order IS in Mail-Away; from Pending/Clarification
  // with an active request, selecting Mail-Away means "Return to existing request" (handled by OrderTabs).
  const inMailAway = orderData?.status === 'mail_away';
  const isFollowUpMode = selectedStatus === 'mailaway' && mailAwayActive && inMailAway;
  const canSubmit = isFollowUpMode
    ? (reason && reason.trim().length >= 10)
    : selectedStatus && (!statusRequiresReason || (reason && reason.trim().length >= 10));

  return (
    <div className="border-t border-slate-200 pt-4 mt-4" data-testid="workflow-actions-section">
      <h3 className="text-[15px] font-semibold text-slate-900 mb-3 flex items-center gap-2.5">
        <Send className="w-[18px] h-[18px] text-indigo-500" />
        Workflow Actions
      </h3>
      
      {/* Jurisdiction Type Guard — blocks all workflow actions until at least one jurisdiction has a type */}
      {!hasJurisdiction && (
        <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-100 px-3 py-2.5 rounded-lg border border-slate-200 mb-4" data-testid="workflow-guard-message">
          <Lock className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
          <span className="font-medium">Select Jurisdiction Type to enable workflow actions.</span>
        </div>
      )}
      
      {/* Content wrapper: greyed out when no jurisdiction type selected */}
      <div className={!hasJurisdiction ? 'opacity-40 pointer-events-none select-none' : ''}>
      {/* Status Options Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {availableOptions.map((option) => {
          const { Icon, colors } = option;
          const isSelected = selectedStatus === option.value;
          const pendingBlocked = option.value === 'pending' && pendingGuarded && !canOverridePending;
          const isOptionDisabled = isReadOnly || !hasJurisdiction ||
            (option.value === 'mailaway' && isMailAwayDisabled) || pendingBlocked;
          const disabledReason = option.value === 'mailaway' && isMailAwayDisabled
            ? getMailAwayDisabledReason()
            : pendingBlocked ? 'Mail-Away request active — add a follow-up instead'
              : (option.value === 'mailaway' && mailAwayActive)
                ? (inMailAway ? 'Add follow-up to active request' : 'Return to existing Mail-Away request — no new charge')
                : null;
          
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                if (isOptionDisabled) return;
                if (isSelected && !editMode) {
                  // Re-click on pre-selected status → enter edit mode
                  setEditMode && setEditMode(true);
                } else if (isSelected && editMode) {
                  // Exit edit mode + clear draft reason directly
                  if (['pending', 'clarification'].includes(option.value)) {
                    // Reason-required statuses: keep status highlighted, just close edit panel
                    onReasonChange && onReasonChange('');
                  } else {
                    // Non-reason statuses (QC Review, Mail-Away): fully deselect
                    onStatusSelect(null);
                  }
                  setEditMode && setEditMode(false);
                } else {
                  // New status selection → select + enter edit mode
                  onStatusSelect(option.value);
                  setEditMode && setEditMode(true);
                }
              }}
              disabled={isOptionDisabled}
              className={`
                tcp-wf-card relative px-3 py-2.5 rounded-xl border text-left
                ${isSelected 
                  ? `${colors.bgSelected} ${colors.borderSelected} shadow-md` 
                  : `${colors.bg} ${colors.border} ${colors.bgHover}`
                }
                ${isOptionDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
              data-testid={`workflow-status-${option.value}`}
            >
              {/* Selected indicator */}
              {isSelected && (
                <div className={`absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white border-2 ${colors.borderSelected} flex items-center justify-center`}>
                  <div className={`w-2 h-2 rounded-full ${colors.icon.replace('text-', 'bg-')}`} />
                </div>
              )}
              
              <div className="flex items-center gap-3">
                <span className="tcp-wf-icon">
                  <Icon className={`w-[18px] h-[18px] ${isSelected ? colors.iconSelected : colors.icon}`} />
                </span>
                <div className="min-w-0">
                  <div className={`font-semibold text-sm ${isSelected ? 'text-slate-900' : 'text-slate-800'}`}>
                    {option.label}
                  </div>
                  <div className={`text-xs truncate ${disabledReason ? 'text-amber-600' : 'text-slate-500'}`}>
                    {disabledReason || option.description}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      
      {/* MAILAWAY-FOLLOWUP-001: follow-up on the ACTIVE request — status stays Mail-Away */}
      {isFollowUpMode && (
        <div className="mb-4 rounded-lg border border-cyan-200 bg-cyan-50/60 p-4" data-testid="mailaway-followup-panel">
          <div className="text-xs font-semibold text-cyan-900 mb-2">
            Follow-up on {activeMailAway.map((r) => r.jurisdiction_name).filter(Boolean).join(', ') || 'active Mail-Away request'} — status stays Mail-Away, no new fees or letter
          </div>
          <label htmlFor="mailaway-followup-note" className="block text-xs font-medium text-slate-700 mb-1">
            Follow-Up Notes <span className="text-red-500">*</span> <span className="text-slate-400 font-normal">(min 10 chars)</span>
          </label>
          <textarea
            id="mailaway-followup-note"
            data-testid="mailaway-followup-note"
            value={reason || ''}
            onChange={(e) => onReasonChange && onReasonChange(e.target.value.slice(0, 2000))}
            placeholder="e.g. Called the tax office; collector confirmed the request is in queue, expects response next week…"
            rows={3}
            readOnly={isReadOnly}
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-300"
          />
          <div className="mt-1 text-right text-[11px] text-slate-400">{(reason || '').length}/2000</div>
        </div>
      )}
      {/* Reason field for Pending/Clarification - Using StatusReasonField with templates */}
      {statusRequiresReason && selectedStatus && (
        <StatusReasonField
          selectedStatus={selectedStatus}
          reason={reason}
          onReasonChange={onReasonChange}
          validationError={null}
          isReadOnly={isReadOnly}
          clearValidationErrors={() => {}}
          jurisdictionData={jurisdictionData}
          statusNotes={statusNotes}
          editMode={editMode}
          activeTab={activeTab}
          orderData={orderData}
          userRole={userRole}
          liveParcels={liveParcels}
          onChipIdsChange={handleChipIdsChange}
        />
      )}
      
      {/* Tax Sheet Review Checkpoint — only visible on Tax Summary tab, hidden for external portal clients */}
      {activeTab === 'tax' && onReviewTaxSheet && !isExternalPortalClient && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="tax-sheet-review-section">
          {taxSheetReview?.status === 'reviewed' ? (
            /* Stage 3: Reviewed */
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                <ClipboardCheck className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-emerald-700" data-testid="review-status-text">Reviewed</div>
                <div className="text-xs text-slate-500 truncate" data-testid="review-meta">
                  by {taxSheetReview.reviewed_by_name} &middot; {formatDateTimeShortET(taxSheetReview.reviewed_at)}
                </div>
              </div>
            </div>
          ) : reviewReadiness?.ready ? (
            /* Stage 2: Ready for Review */
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                    <ClipboardCheck className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-blue-700" data-testid="review-status-text">Ready for Review</div>
                    <div className="text-xs text-slate-500">All prerequisites met — review to proceed</div>
                  </div>
                </div>
                {!isReadOnly && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onReviewTaxSheet}
                    className="shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400"
                    data-testid="review-tax-sheet-btn"
                  >
                    <ClipboardCheck className="w-4 h-4 mr-1.5" />
                    Review Tax Sheet
                  </Button>
                )}
              </div>
              {/* G8: Soft warnings (non-blocking) — entity breakdown mismatches */}
              {reviewReadiness?.warnings?.length > 0 && (
                <div className="ml-11 space-y-1" data-testid="review-warnings-section">
                  {reviewReadiness.warnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Stage 1: In Progress */
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
                    <ShieldAlert className="w-4 h-4 text-slate-500" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-600" data-testid="review-status-text">Tax Summary In Progress</div>
                    <div className="text-xs text-slate-500">
                      {reviewReadiness?.missing?.length > 0 
                        ? `${reviewReadiness.missing.length} missing field${reviewReadiness.missing.length > 1 ? 's' : ''} — complete before review`
                        : 'Complete required fields before review'}
                    </div>
                  </div>
                </div>
                {/* Jun 2026 — the button stays CLICKABLE while fields are
                    missing: clicking it runs the full validation, toasts the
                    error list, and unlocks the per-row inline required errors
                    (validationAttempted). A disabled button made the
                    "errors appear on review attempt" flow unreachable. */}
                {!isReadOnly && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onReviewTaxSheet}
                    className="shrink-0 border-slate-300 text-slate-600 hover:bg-slate-50 hover:border-slate-400"
                    data-testid="review-tax-sheet-btn"
                  >
                    <ClipboardCheck className="w-4 h-4 mr-1.5" />
                    Review Tax Sheet
                  </Button>
                )}
              </div>
              {reviewReadiness?.missing?.length > 0 && (
                <div className="ml-11 flex flex-wrap gap-1.5">
                  {reviewReadiness.missing.map((item, i) => (
                    <span key={i} className="inline-flex items-center text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded px-2 py-0.5">
                      {item}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {emailConfirm && (
        <TaxEmailConfirmDialog
          open={emailConfirm.open}
          loading={emailConfirm.loading}
          preflight={emailConfirm.preflight}
          error={emailConfirm.error}
          submitting={isSubmitting}
          onCancel={() => setEmailConfirm(null)}
          onConfirm={() => { setEmailConfirm(null); onSubmitStatus(selectedStatus, reason, selectedChipIds); }}
        />
      )}
      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        {/* Submit Status Button */}
        {selectedStatus && (
          <Button
            type="button"
            onClick={() => {
                      // TAX-EMAIL-APN-GATE-001: Email requires a Parcel/APN on every applicable parcel (backend enforces too)
                      if (selectedStatus === 'pending' && hasEmailChip) {
                        const emailCheck = validateParcelsForEmail(orderData, liveParcels);
                        if (!emailCheck.valid) {
                          toast.error(emailCheck.message, { duration: 6000 });
                          return;
                        }
                        // TAX-EMAIL-CONFIRM-001: jurisdiction-scoped dry-run → confirmation dialog → submit
                        openEmailConfirm();
                        return;
                      }
                      if (isFollowUpMode) {
                        onSubmitStatus('mailaway_followup', reason, []);
                        return;
                      }
                      onSubmitStatus(selectedStatus, reason, selectedChipIds);
                    }}
            disabled={isReadOnly || isSubmitting || !canSubmit}
            isLoading={isSubmitting}
            className="flex-1"
            data-testid="submit-status-btn"
          >
            {isSubmitting ? (
              'Submitting...'
            ) : isFollowUpMode ? (
              <>
                <Send className="w-4 h-4 mr-2" />
                Add Follow-Up
              </>
            ) : (selectedStatus === 'mailaway' && mailAwayActive && !inMailAway) ? (
              <>
                <Send className="w-4 h-4 mr-2" />
                Return to Mail-Away
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit as {WORKFLOW_STATUS_OPTIONS.find(o => o.value === selectedStatus)?.label}
              </>
            )}
          </Button>
        )}
        
        {/* Complete Button — disabled for ALL users until review is done (bypassed for external portal clients) */}
        {activeTab !== 'contact' && (() => {
          const reviewPending = !isExternalPortalClient && (!taxSheetReview || taxSheetReview.status !== 'reviewed');
          // MLS-DUAL-001: dual-component guard — visibly disable with the reason
          const mlsBlocked = !!mlsGuard?.blocked;

          // WF-CONSOLIDATE (Aug 23, 2026): while the TAX COMPONENT of a dual
          // MLS order is open, this same button completes the component via
          // the existing /components/tax/complete flow (same Tax Certificate
          // validation + Mail-Away gates, enforced by the backend).
          if (dualTax?.phase === 'component') {
            // TAX-REVIEW-GATE-001: the component path obeys the SAME review gate as the standalone order —
            // disabled until the Tax Sheet is reviewed; any tax-data change resets the review (backend-enforced too).
            return (
              <div className="flex-1 relative group">
                <Button
                  type="button"
                  onClick={() => dualTax.onCompleteComponent()}
                  disabled={isReadOnly || isSubmitting || dualTax.busy || reviewPending}
                  title={reviewPending ? 'Review the Tax Sheet before completing' : undefined}
                  className={`tcp-complete w-full h-10 rounded-xl font-semibold ${reviewPending ? 'is-disabled bg-slate-200 text-slate-600 cursor-not-allowed' : 'is-enabled text-white'}`}
                  data-testid="complete-order-btn"
                  data-review-pending={reviewPending}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {dualTax.busy ? 'Completing…' : reviewPending ? 'Complete Tax Search — review required' : 'Complete Tax Search'}
                </Button>
                {reviewPending && (
                  <p className="mt-1 text-[11px] text-slate-500 text-center" data-testid="complete-order-review-hint">Review Tax Sheet and confirm it is accurate to enable completion.</p>
                )}
                {dualTax.qcRequiredNote && (
                  <p className="mt-1.5 text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-md px-2 py-1 text-center" data-testid="complete-component-qc-note">
                    QC Required: this order is sent to QC Review automatically once all required components are complete.
                  </p>
                )}
              </div>
            );
          }

          return (
            <div className="flex-1 relative group">
              <Button
                type="button"
                onClick={() => onComplete()}
                disabled={isReadOnly || isSubmitting || reviewPending || mlsBlocked}
                aria-busy={isSubmitting || undefined}
                className={`tcp-complete w-full h-10 rounded-xl font-semibold ${(reviewPending || mlsBlocked)
                  ? 'is-disabled cursor-not-allowed text-white'
                  : 'is-enabled text-white'
                }`}
                data-testid="complete-order-btn"
                data-state={isSubmitting ? 'submitting' : 'idle'}
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" data-testid="complete-order-spinner" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                {isSubmitting ? 'Completing…' : 'Complete'}
              </Button>
              {/* COMPLETE-BTN-FEEDBACK-001: persistent inline reason (backend detail / pre-flight) */}
              {completeError && !isSubmitting && (
                <div
                  className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
                  role="alert"
                  data-testid="complete-order-error"
                >
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600" />
                  <div className="min-w-0">
                    {completeError.title && (
                      <div className="font-semibold" data-testid="complete-order-error-title">{completeError.title}</div>
                    )}
                    <ul className={completeError.lines?.length > 1 ? 'list-disc pl-4 mt-0.5 space-y-0.5' : 'mt-0.5'}>
                      {(completeError.lines || []).map((line, idx) => (
                        <li key={idx} className="break-words" data-testid={`complete-order-error-line-${idx}`}>{line}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {mlsBlocked ? (
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block whitespace-nowrap max-w-[520px] overflow-hidden text-ellipsis bg-slate-800 text-white text-xs px-2 py-1 rounded shadow z-10"
                  data-testid="mls-complete-blocked-tooltip">
                  {mlsGuard.message}
                </div>
              ) : reviewPending && (
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block whitespace-nowrap bg-slate-800 text-white text-xs px-2 py-1 rounded shadow z-10">
                  Review tax sheet first
                </div>
              )}
            </div>
          );
        })()}
      </div>
      </div>{/* End jurisdiction-type-guard wrapper */}
    </div>
  );
};

/**
 * Validate all jurisdictions for tax sheet review readiness.
 * Logic-based: checks meaningful data per pay-status scenario, not rigid "all fields required".
 *
 * May 17, 2026 — `isNewParcel`. When the parent parcel is in New Parcel mode,
 * the financial-amount integrity rules (base_tax required, paid_date required
 * for Paid/Partially Paid, G1 Paid Amount required when Paid Date entered,
 * mandatory DLQ Date, mailing_issue_date required) are skipped because a
 * brand-new parcel ships with $0.00 amounts and the standardised PDF note
 * already documents the missing payment context. Identity fields (tax_year,
 * installment_name, due_date) remain required per ops spec.
 *
 * Returns { valid: true } or { valid: false, errors: string[] }
 */
const validateForReview = (jurisdictions, isExternalPortal = false, crossParcelData = null, faxDelivery = null, mailAwayRequests = [], orderFiles = [], stateCode = '', isNewParcel = false) => {
  const errors = [];
  const warnings = [];
  
  if (!jurisdictions || jurisdictions.length === 0) {
    errors.push('No jurisdictions have been added.');
    return { valid: false, errors, warnings };
  }
  
  jurisdictions.forEach((jur, jIdx) => {
    const label = toTitleCase(jur.jurisdiction_name) || `Jurisdiction ${jIdx + 1}`;
    
    // 1. Jurisdiction Type
    if (!jur.jurisdiction_type) {
      errors.push(`${label}: Jurisdiction type is not selected.`);
    }
    
    // Find matching mail-away request (needed for workflow bypass check)
    const jurMailMatch = mailAwayRequests.find(
      r => r.jurisdiction_name === jur.jurisdiction_name && r.jurisdiction_type === jur.jurisdiction_type
    ) || null;
    
    // ── Workflow response bypass ──
    // If a Mail-Away/Fax response already exists, skip source + evidence checks
    const wfBypass = hasWorkflowResponse(
      jur.jurisdiction_name, jur.jurisdiction_type,
      jurMailMatch, faxDelivery, mailAwayRequests, orderFiles
    );
    
    if (!wfBypass) {
      // 2. Information Source (located in MLC & Workflow tab)
      //    Skip if the same jurisdiction in another parcel already has source (shared setup)
      if (!jur.source) {
        const coveredByOtherParcel = crossParcelData
          ? isSourceCoveredByAnotherParcel(
              jur.jurisdiction_name, jur.jurisdiction_type,
              crossParcelData.allParcelsData, crossParcelData.parcelIndex
            )
          : false;
        if (!coveredByOtherParcel) {
          errors.push(`${label}: Information Source is not selected — open the "MLC & Workflow" tab to set it.`);
        }
      }
      
      // 2b. Evidence validation — source-specific evidence files must be uploaded
      if (jur.source) {
        const evidenceResult = validateEvidenceRequirements(jur.source_evidence, jur.source, faxDelivery, jurMailMatch, orderFiles);
        if (!evidenceResult.valid) {
          errors.push(`${label}: ${evidenceResult.message} — open the "MLC & Workflow" tab to upload.`);
        }
      }
    }

    // 2c. Fully Exempt validation — exemption type is mandatory
    if (jur.fully_exempt) {
      const realExemptions = (jur.exemption_types || []).filter(
        t => t && t !== 'No Exemptions (N/A)'
      );
      if (realExemptions.length === 0) {
        errors.push(`${label}: Exemption type is required when Fully Exempt is enabled.`);
      }
    }
    
    // External portal orders only require identity + source — skip tax-record checks
    if (isExternalPortal) {
      return;
    }
    
    // 3. At least one tax record with meaningful data
    const installments = jur.installments || [];
    if (installments.length === 0) {
      errors.push(`${label}: No tax records have been entered.`);
      return; // skip per-installment checks
    }
    
    let hasValidRecord = false;

    // Paid Breakdown N/A treatment (v2 per-year groups + v2.6 Selected
    // Years): rows covered by a lump-sum group carry their canonical
    // Paid Date on the group/jurisdiction-level entry, not the row —
    // suppress the row-level Paid Date / DLQ Date requirements for them.
    const _pbnaCovered = computeCoveredYears(jur);
    const _pbnaYears = new Set(
      Object.entries(jur.paid_breakdown_na_groups || {})
        .filter(([, e]) => e && e.enabled)
        .map(([y]) => String(y).trim())
    );

    installments.forEach((inst, iIdx) => {
      const instLabel = inst.installment_name || `Record ${iIdx + 1}`;
      const prefix = `${label} > ${instLabel}`;
      const _instPbna = _pbnaCovered.has(String(inst.tax_year || '').trim())
        || _pbnaYears.has(String(inst.tax_year || '').trim())
        || !!inst.paid_breakdown_na;
      
      // Tax year is required and must be either a 4-digit year (YYYY)
      // or a fiscal/tax-year range (YYYY/YYYY) for MI/PA/IL etc.
      const tyVal = (inst.tax_year || '').toString().trim();
      if (!tyVal) {
        errors.push(`${prefix}: Tax year is missing.`);
        return;
      }
      if (!/^\d{4}(\/\d{4})?$/.test(tyVal)) {
        errors.push(`${prefix}: Tax Year must be a 4-digit year (e.g., 2024) or a fiscal range YYYY/YYYY (e.g., 2025/2026).`);
        return;
      }
      
      // Installment name
      if (!inst.installment_name || !inst.installment_name.trim()) {
        errors.push(`${prefix}: Installment name is missing.`);
        return;
      }
      
      // Base tax is the core financial field (skip when fully exempt — zero is valid)
      // May 17, 2026 — also skipped in New Parcel mode (rows ship with $0.00).
      // Jun 2026 — UAT-COMBINED-PAYOFF-BASE-BYPASS. Also skipped in
      // Combined Payoff mode — the collector only provides the
      // combined payoff + good-thru and never the per-year base.
      // The PDF auto-prints "Tax Collector will not provide … base amount".
      const _isCombinedPayoff = (jur.dlq_payoff_breakdown_mode || 'year_wise') === 'combined';
      const hasBaseTax = inst.base_tax !== null && inst.base_tax !== undefined && inst.base_tax !== '';
      // Jul 2026 — Exempt rows carry no meaningful base amount.
      const _rowIsExemptStatus = String(inst.pay_status || '').trim().toLowerCase() === 'exempt';
      // Jul 2026 — N/A rows explicitly have no amount (NA-PAY-STATUS-001).
      const _rowIsNaStatus = isNaStatusRow(inst);
      if (!hasBaseTax && !jur.fully_exempt && !isNewParcel && !_isCombinedPayoff && !_rowIsExemptStatus && !_rowIsNaStatus) {
        errors.push(`${prefix}: Base tax amount is missing.`);
        return;
      }
      
      // Pay-status-specific validation
      const derived = derivePayStatus(inst);
      const _exemptZero2 = isExemptZeroRow(jur, { ...inst, pay_status: derived });
      
      if ((derived === 'Delinquent' || derived === 'Partially Delinquent') && !isNewParcel) {
        if (!inst.dlq_date) {
          errors.push(`${prefix}: Delinquent date is required for delinquent status.`);
        }
      }
      
      if ((derived === 'Paid' || derived === 'Partially Paid') && !isNewParcel && !_instPbna) {
        if (!inst.paid_date && !(derived === 'Paid' && _exemptZero2)) {
          errors.push(`${prefix}: Paid Date is required when Pay Status is ${derived}.`);
        }
      }

      // G1: Paid Date present without Paid Amount (cross-field integrity)
      // May 17, 2026 — skipped in New Parcel mode; a brand-new parcel may
      // legitimately carry a Paid Date placeholder without a payment amount.
      // Jun 2026 v2.6 — skipped under Paid Breakdown N/A treatment: covered
      // rows intentionally have no row-level Paid Amount ("See Notes").
      if (inst.paid_date && !hasAmount(inst.paid_amount) && !jur.fully_exempt && !isNewParcel && !_instPbna) {
        errors.push(`${prefix}: Paid Amount is required when a Paid Date is entered.`);
      }

      // DLQ Date is always required (drives notes generation)
      // May 17, 2026 — relaxed in New Parcel mode (no prior delinquent history).
      // Jun 2026 v2.5 — also relaxed for Paid Breakdown N/A rows (lump-sum
      // confirmed paid; DLQ comparison is irrelevant without a row paid_date).
      if (!inst.dlq_date && !isNewParcel && !_instPbna) {
        errors.push(`${prefix}: DLQ Date is required.`);
      }

      // Jun 2026 — Due Date & Mail Date are mandatory (user-reported:
      // certs were generated without them). Same exemptions as the DLQ
      // rule: New Parcel mode and Paid Breakdown N/A rows. Backend twins
      // live in validate_tax_certificate_completion + review_tax_sheet.
      if (!inst.due_date && !isNewParcel && !_instPbna) {
        errors.push(`${prefix}: Due Date is required.`);
      }
      if (!inst.mailing_issue_date && !isNewParcel && !_instPbna) {
        errors.push(`${prefix}: Mail Date is required.`);
      }

      // ── Date chronology validation (hard blockers) ──
      if (inst.mailing_issue_date && inst.due_date) {
        const md = parseValidationDate(inst.mailing_issue_date);
        const dueD = parseValidationDate(inst.due_date);
        if (md && dueD && md > dueD) {
          errors.push(`${prefix}: Mail date cannot be after due date.`);
        }
      }
      if (inst.paid_date && inst.mailing_issue_date) {
        const pd = parseValidationDate(inst.paid_date);
        const md = parseValidationDate(inst.mailing_issue_date);
        if (pd && md && pd < md) {
          errors.push(`${prefix}: Paid date cannot be earlier than mail date.`);
        }
      }
      // G4: Paid Date must not be in the future
      // Exception: exempt zero-tax rows may use a paid_date that equals the
      // due_date even if the due_date is in the future.
      if (inst.paid_date) {
        const pd = parseValidationDate(inst.paid_date);
        if (pd) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (pd > today) {
            let allowFuture = false;
            if (_exemptZero2 && inst.due_date) {
              const dueD = parseValidationDate(inst.due_date);
              if (dueD && pd.getTime() === dueD.getTime()) allowFuture = true;
            }
            if (!allowFuture) {
              errors.push(`${prefix}: Paid date cannot be in the future.`);
            }
          }
        }
      }
      if (inst.dlq_date && inst.due_date) {
        const dd = parseValidationDate(inst.dlq_date);
        const dueD = parseValidationDate(inst.due_date);
        if (dd && dueD && dd < dueD) {
          errors.push(`${prefix}: Delinquent date cannot be earlier than due date.`);
        }
      }
      if (jur.dlq_good_thru_date && inst.dlq_date) {
        const gd = parseValidationDate(jur.dlq_good_thru_date);
        const dd = parseValidationDate(inst.dlq_date);
        if (gd && dd && gd < dd) {
          errors.push(`${prefix}: Good thru date cannot be earlier than delinquent date.`);
        }
      }
      if (inst.payoff_good_through_date && (jur.dlq_payoff_breakdown_mode || 'year_wise') !== 'combined') {
        const gt = parseValidationDate(inst.payoff_good_through_date);
        if (gt) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (gt <= today) {
            errors.push(`${prefix}: Payoff Good Through must be a future date.`);
          }
          if (inst.dlq_date) {
            const dd = parseValidationDate(inst.dlq_date);
            if (dd && gt < dd) {
              errors.push(`${prefix}: Good thru date cannot be earlier than delinquent date.`);
            }
          }
        }
      }
      
      // If we get here the record has meaningful data
      hasValidRecord = true;
    });
    
    if (!hasValidRecord && installments.length > 0) {
      // Jun 2026 — UAT-COMBINED-PAYOFF-BASE-BYPASS. In Combined Payoff
      // mode the year-rows legitimately lack per-year Base Tax — the
      // combined payoff fields supply the amount. Re-check: every row
      // must at least have a tax_year so the PDF can list the covered
      // years. If that's satisfied, treat the jurisdiction as having
      // meaningful data; otherwise surface the normal blocker.
      const _isCombinedPayoffBucket = (jur.dlq_payoff_breakdown_mode || 'year_wise') === 'combined';
      if (_isCombinedPayoffBucket) {
        const _allRowsHaveYear = installments.every((inst) => !!inst.tax_year);
        if (!_allRowsHaveYear) {
          errors.push(`${label}: Each tax record needs a Tax Year (Combined Payoff covers the amounts).`);
        }
      } else {
        errors.push(`${label}: None of the tax records contain meaningful data.`);
      }
    }

    // ── Feb 2026 — Combined Delinquent Payoff (jurisdiction-level) ──
    // Mirror of the early-return validator in computeJurisdictionStatus.
    // When the jurisdiction is in combined breakdown mode AND has any
    // delinquent installment, BOTH the Combined Payoff Amount and
    // Combined Good-Through Date are required. Note remains optional.
    if ((jur.dlq_payoff_breakdown_mode || 'year_wise') === 'combined') {
      const anyDelinq = (installments || []).some((inst) => {
        const s = (inst.pay_status || '').toLowerCase();
        return s === 'delinquent' || s === 'partially delinquent' || s === 'partially dlq';
      });
      if (anyDelinq) {
        const combinedAmtNum = parseFloat(jur.dlq_combined_payoff_amount);
        const hasCombinedAmt = !isNaN(combinedAmtNum) && combinedAmtNum > 0;
        if (!hasCombinedAmt) {
          errors.push(`${label}: Combined Payoff Amount is required when "Combined Payoff" is selected.`);
        }
        if (!jur.dlq_combined_payoff_good_through_date) {
          errors.push(`${label}: Combined Payoff Good-Through Date is required when "Combined Payoff" is selected.`);
        }
        if (jur.dlq_combined_payoff_good_through_date) {
          const cgt = parseValidationDate(jur.dlq_combined_payoff_good_through_date);
          if (cgt) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (cgt <= today) {
              errors.push(`${label}: Combined Payoff Good Through must be a future date.`);
            }
          }
        }
      }
    }
    
    // 4. Notes must be generated from actual data, not just default fallback
    const engineNotes = generateTaxCertificationNotes(jur, installments, stateCode);
    const withSpecialty = appendSpecialtyNotes(engineNotes, installments);
    const notes = appendBreakdownNaNotes(withSpecialty, jur);
    if (!notes || notes.trim() === '' || notes.trim() === 'All prior taxes are paid.') {
      errors.push(`${label}: Tax notes contain only default text — verify that tax records have complete data.`);
    }

    // G8: Entity breakdown sum mismatch (soft warning — does NOT block review)
    installments.forEach((inst, iIdx) => {
      const entities = inst.entity_breakdown || [];
      if (entities.length > 0) {
        const entitySum = entities.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
        const instTotal = parseFloat(inst.amount_due ?? inst.base_tax) || 0;
        if (instTotal > 0 && Math.abs(entitySum - instTotal) > 0.01) {
          const instLabel = inst.installment_name || `Record ${iIdx + 1}`;
          warnings.push(`${label}, ${instLabel}: Entity breakdown total ($${entitySum.toFixed(2)}) does not match installment total ($${instTotal.toFixed(2)}). Please review before completion.`);
        }
      }
    });
  });
  
  return { valid: errors.length === 0, errors, warnings };
};

/**
 * Main JurisdictionManager Component
 */
export const JurisdictionManager = ({
  jurisdictions = [],
  onJurisdictionsChange,
  onJurisdictionTypeChange,
  onClearJurisdiction,
  onNotesChange,
  county = '',
  readOnly = false,
  getRowState = () => ({}),
  // Fuzzy "Did you mean?" suggestion handler — called when user
  // clicks a chip rendered below the Jurisdiction Name banner.
  onAcceptFuzzySuggestion = () => {},
  createEmptyJurisdiction = () => ({}),
  // Workflow action props
  workflowActions = null,
  // Order ID for file uploads
  orderId = null,
  // RBAC: Can edit jurisdiction contact fields (Admin/Jurisdiction Manager only)
  canEditContactFields = true,
  // RBAC: Can link/unlink a collector from the master Tax Collector
  // Directory. Processors/TL/QC are allowed for this structured action
  // even though they can't free-text edit contact fields (Feb 2026 —
  // JURIS-BOROUGH-PICKER-001 follow-up).
  canLinkCollector = true,
  // RBAC: Can edit MLC fields (Admin/Jurisdiction Manager only)
  canEditMLCFields = true,
  // External Portal: Hide Tax Summary tab and related sections
  isExternalPortalClient = false,
  // Parcel context for multi-parcel orders
  activeParcelIndex = 0,
  totalParcels = 1,
  activeParcelAPN = '',
  // All parcels data for cross-parcel validation
  allParcelsData = [],
  // Sequential parcel access control (from parent TaxCertificatePanel)
  canAccessParcel = [],
  canAddParcel = true,
  isParcelDataComplete = null,
  getParcelMissingFields = null,
  // Multi-parcel switcher callbacks
  onParcelSwitch = null,
  onAddParcelWithCopy = null,
  // Compact parcel details (live-synced with parent)
  parcelFieldValues = null,
  onParcelFieldChange = null,
  onResetParcelDetails = null,
  parcelValidationErrors = {},
  onExemptionTypeChange = null,
  onExemptionValueChange = null,
  onExemptionOtherChange = null,
  onFullyExemptChange = null,
  onTavBreakdownToggle = null,
  // ── New Parcel toggle (May 17, 2026) ────────────────────────────
  // Per-parcel operational helper for brand-new parcels with no prior
  // tax history. When `isNewParcel` is true, a standardised note is
  // appended to the parcel's notes in the PDF (NEVER written into the
  // editable notes field), and amount-based validations are relaxed
  // so the row can ship with $0.00 amounts and "REFER NOTE" status.
  // `newParcelFirstYear` / `newParcelFirstDueDate` are optional
  // processor overrides — when blank, the note falls back to the
  // tax_year / due_date of the parcel's first installment row.
  isNewParcel = false,
  newParcelFirstYear = '',
  newParcelFirstDueDate = '',
  onNewParcelToggle = null,
  onNewParcelOverrideChange = null,
  // Soft Save validation warnings from backend (structured field-level warnings)
  saveValidationWarnings = [],
  // Global order files for evidence resolution (Files tab, any upload location)
  orderFiles = []
}) => {
  // ── Order-level Financial Summary (all parcels, all jurisdictions, all installments) ──
  const orderFinancialSummary = useMemo(() => {
    if (isExternalPortalClient) return null;
    let totalPaid = 0;
    let totalBalance = 0;
    const parcels = allParcelsData || [];
    for (let pIdx = 0; pIdx < parcels.length; pIdx++) {
      // For the active parcel, use the live jurisdictions prop (updated on every keystroke).
      // For other parcels, use allParcelsData (synced on parcel switch).
      const jurs = pIdx === activeParcelIndex
        ? (jurisdictions || [])
        : (parcels[pIdx].jurisdictions || []);
      for (const jur of jurs) {
        // Feb 2026 v2.2 — Paid Breakdown N/A reconciliation.
        // When a (tax_year, jurisdiction) group is active, the
        // group's `total_paid_amount` is the authoritative lump-sum.
        // The per-installment `paid_amount` values are NOT trusted
        // (operator may not have entered them — the whole point is
        // that the collector did not provide a breakdown), so we
        // skip those rows in the per-row sum and add the group total
        // once instead. Balance for rows in an active group is forced
        // to 0 (group means "paid in full per collector").
        const groups = jur.paid_breakdown_na_groups || {};
        // Jun 2026 v2.6 — Selected-Years lump sum: add the jurisdiction-
        // level total ONCE when at least one year is covered; covered
        // rows are skipped (balance = 0 by definition). Per-year groups
        // remain valid for uncovered years and are skipped for covered
        // years (never double-count). Legacy v2.5 entries without
        // `covered_years` cover every year present.
        const coveredSet = computeCoveredYears(jur);
        if (coveredSet.size > 0) {
          const allEntry = jur.paid_breakdown_na_all_years || {};
          const allTotal = parseFloat(allEntry.total_paid_amount);
          if (isFinite(allTotal) && allTotal > 0) {
            totalPaid = rc(totalPaid + allTotal);
          }
        }
        const activeYears = new Set();
        for (const [year, entry] of Object.entries(groups)) {
          if (entry && entry.enabled) {
            const yearKey = String(year).trim();
            if (coveredSet.has(yearKey)) continue; // replaced by lump sum
            activeYears.add(yearKey);
            const groupTotal = parseFloat(entry.total_paid_amount);
            if (isFinite(groupTotal) && groupTotal > 0) {
              totalPaid = rc(totalPaid + groupTotal);
            }
          }
        }
        const installments = jur.installments || [];
        for (const inst of installments) {
          const instYear = String(inst.tax_year || '').trim();
          // Skip rows whose (year) belongs to an active group or the
          // Selected-Years lump sum — their contribution to totalPaid
          // has already been added via the lump-sum, and their balance
          // is by definition 0.
          if (coveredSet.has(instYear) || activeYears.has(instYear)) continue;
          // Legacy v1 row-level flag: paid_amount IS the lump-sum for
          // that row; trust it and treat balance as 0.
          if (inst.paid_breakdown_na) {
            const paidAmount = parseFloat(inst.paid_amount) || 0;
            totalPaid = rc(totalPaid + paidAmount);
            continue;
          }
          const baseTax = parseFloat(inst.base_tax) || 0;
          const discPenalty = parseFloat(inst.discount_penalty) || 0;
          const paidAmount = parseFloat(inst.paid_amount) || 0;
          totalPaid = rc(totalPaid + paidAmount);
          const rawBalance = parseFloat(inst.balance_amount ?? (baseTax + discPenalty - paidAmount)) || 0;
          const balance = Math.max(0, rc(rawBalance));
          totalBalance = rc(totalBalance + balance);
        }
      }
    }
    return { totalPaid, totalBalance: Math.max(0, totalBalance) };
  }, [allParcelsData, jurisdictions, activeParcelIndex, isExternalPortalClient]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('contact');

  // Jul 2026 — UAT feedback: the Phase A.7.17 'Send Mail-Away from
  // here' badge (state + fetch + helper) was removed from the sidebar
  // jurisdiction cards to keep the left panel navigation/status-only.
  // The recommended-lead logic lives on in lib/recommendedMailAwayLead
  // for the Mail-Away workflow itself.
  // Jun 2026 — set on the first "Review Tax Sheet" attempt; unlocks the
  // required-field inline errors inside every InstallmentRow (until then
  // they only show per-field after the operator touched that field).
  const [validationAttempted, setValidationAttempted] = useState(false);
  const listRef = useRef(null);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
  const [resetConfirmIndex, setResetConfirmIndex] = useState(null);
  
  // Fax delivery data for evidence validation (supports both old + new formats)
  const faxDeliveryLegacy = workflowActions?.orderData?.fax_delivery || null;
  const faxDeliveriesArr = workflowActions?.orderData?.fax_deliveries || [];
  // For evidence validation: check if ANY fax entry is received with a file
  const faxDelivery = (() => {
    const received = faxDeliveriesArr.find(f => f.status === 'received' && f.received_file_id);
    if (received) return received;
    if (faxDeliveryLegacy?.status === 'received' && faxDeliveryLegacy?.received_file_id) return faxDeliveryLegacy;
    return faxDeliveryLegacy;
  })();
  
  // Mail-Away requests for evidence validation (per-jurisdiction matching)
  const mailAwayRequests = workflowActions?.orderData?.mail_away_requests || [];
  
  // State code for Michigan-specific logic
  const orderStateCode = workflowActions?.orderData?.state || workflowActions?.orderData?.property_state || '';

  // Surface 4: derive current user for the per-jurisdiction guidance banner's
  // Flag-for-Review permission gate. Cheap — getUserFromToken just decodes JWT.
  const currentUser = useMemo(() => getUserFromToken(), []);
  
  // Refs for stable callback closures — prevents callback recreation on every jurisdictions change
  const jurisdictionsRef = useRef(jurisdictions);
  jurisdictionsRef.current = jurisdictions;
  
  const selectedIndexRef = useRef(selectedIndex);
  selectedIndexRef.current = selectedIndex;

  // Ref for deferred "Add Jurisdiction" after parcel switch to P1
  // When user clicks Add Jurisdiction while on P2, we must:
  //   1. Switch to P1 first (so P1's jurisdictions load into flat state)
  //   2. THEN add the new jurisdiction to P1's data
  // This ref signals step 2 should execute after step 1 completes.
  const pendingAddJurisdictionRef = useRef(false);

  // Safety guard: clamp selectedIndex when jurisdictions array changes
  // (prevents "No Jurisdiction Selected" when switching parcels with different jurisdiction counts)
  useEffect(() => {
    if (jurisdictions.length > 0 && selectedIndex >= jurisdictions.length) {
      setSelectedIndex(jurisdictions.length - 1);
    }
  }, [jurisdictions.length, selectedIndex]);

  // Clamp selectedIndex to valid range during render (prevents single-frame "No Jurisdiction Selected"
  // flicker when parcel switch loads fewer jurisdictions before the useEffect corrects state)
  const clampedSelectedIndex = jurisdictions.length > 0
    ? Math.min(selectedIndex, jurisdictions.length - 1)
    : selectedIndex;
  const selectedJurisdiction = jurisdictions[clampedSelectedIndex] || null;

  // Detect if the current parcel's jurisdiction setup is shared with another parcel
  // (same jurisdiction name + type exists in another parcel with source already set)
  // Determine if the SELECTED jurisdiction on the CURRENT parcel is inherited.
  // Rules:
  //   - P1 is NEVER inherited (it is always the source).
  //   - P2+ is inherited ONLY when P1 of the SAME jurisdiction has matching data (name+type+source).
  //   - A new/empty jurisdiction (no name or type) is NEVER inherited.
  const isJurisdictionInherited = useMemo(() => {
    // P1 is always the source, never inherited
    if (activeParcelIndex === 0) return false;
    if (totalParcels <= 1 || !allParcelsData || allParcelsData.length <= 1) return false;
    if (!jurisdictions || jurisdictions.length === 0) return false;
    // Only check the SELECTED jurisdiction, not all jurisdictions
    const selJur = jurisdictions[clampedSelectedIndex];
    if (!selJur || !selJur.jurisdiction_name || !selJur.jurisdiction_type) return false;
    const selName = selJur.jurisdiction_name.toLowerCase().trim();
    const selType = selJur.jurisdiction_type.toLowerCase().trim();
    if (!selName || !selType) return false;
    // Check if P1 has this same jurisdiction (by name+type) with a source uploaded
    const p1 = allParcelsData[0];
    if (!p1 || !p1.jurisdictions) return false;
    return p1.jurisdictions.some(j => {
      const jName = (j.jurisdiction_name || '').toLowerCase().trim();
      const jType = (j.jurisdiction_type || '').toLowerCase().trim();
      return jName === selName && jType === selType && !!j.source;
    });
  }, [jurisdictions, allParcelsData, activeParcelIndex, totalParcels, clampedSelectedIndex]);

  // Auto-open Tax Summary for same-jurisdiction parcels on parcel switch
  const prevParcelRef = useRef(activeParcelIndex);
  useEffect(() => {
    if (prevParcelRef.current !== activeParcelIndex) {
      prevParcelRef.current = activeParcelIndex;
      // If the jurisdiction is inherited and has a type, jump to Tax Summary
      const selJur = jurisdictions[clampedSelectedIndex];
      if (isJurisdictionInherited && selJur?.jurisdiction_type) {
        setActiveTab(isExternalPortalClient ? 'workflow' : 'tax');
      }
    }
  }, [activeParcelIndex, isJurisdictionInherited, jurisdictions, selectedIndex, clampedSelectedIndex]);

  // Parcel pill click handler: delegates to parent onParcelSwitch which enforces sequential rules
  const handleParcelPillClick = useCallback((targetIndex) => {
    if (targetIndex === activeParcelIndex || !onParcelSwitch) return;
    // External portal: open MLC & Workflow; Internal portal: open Tax Summary
    setActiveTab(isExternalPortalClient ? 'workflow' : 'tax');
    onParcelSwitch(targetIndex);
  }, [activeParcelIndex, onParcelSwitch, isExternalPortalClient]);

  // + button handler: delegates to parent onAddParcelWithCopy which enforces sequential rules
  const handleAddParcelPill = useCallback(() => {
    if (!onAddParcelWithCopy) return;
    // External portal: open MLC & Workflow; Internal portal: open Tax Summary
    setActiveTab(isExternalPortalClient ? 'workflow' : 'tax');
    onAddParcelWithCopy();
  }, [onAddParcelWithCopy, isExternalPortalClient]);
  
  // Compute combined notes inline and propagate BOTH jurisdictions + notes to parent
  // in the same synchronous call — React 18 batches these into a single render,
  // eliminating the double-paint flicker that the useEffect approach caused.
  // NOTE: These must be defined BEFORE any useEffect that references them to avoid TDZ errors.
  const computeCombinedNotes = useCallback((jurs) => {
    if (!jurs || jurs.length === 0) return '';
    
    // ── Detect split-installment pattern (semi-annual OR quarterly) ──
    // Same parcel, same tax year, installments split across jurisdictions
    // E.g., 1st/2nd collected by Town, 3rd/4th by County
    //
    // MI-SPLIT-GUARD (Aug 2026): Michigan is EXCLUDED from split detection.
    // MI Summer/Winter bills are independent parallel bills (e.g. county
    // collects Summer, township collects Winter) — never sequential halves
    // of one bill. Because MI auto-derives installment_name from the season
    // ("Summer"/"Winter"), the distinct-names gate below misclassified such
    // parcels as a semi-annual split and persisted the generic
    // "All prior taxes are paid. Taxes are collected semi-annually."
    // instead of the MI-aware narrative (order TF-20260824-90579).
    // Mirrored in services/tax_notes_engine.py::compute_combined_notes.
    if (jurs.length >= 2 && !isMichigan(orderStateCode)) {
      // Collect all installments across jurisdictions with their source jurisdiction
      const allInstallments = [];
      for (const jur of jurs) {
        for (const inst of (jur.installments || [])) {
          allInstallments.push({ ...inst, _sourceJurName: jur.jurisdiction_name || 'Unknown' });
        }
      }
      
      // Group by tax_year
      const byYear = {};
      for (const inst of allInstallments) {
        const yr = inst.tax_year || 'Unknown';
        if (!byYear[yr]) byYear[yr] = [];
        byYear[yr].push(inst);
      }
      
      // Check if ANY year has installments from DIFFERENT jurisdictions
      const hasMultiJurisdictionYear = Object.values(byYear).some(yearInsts => {
        if (yearInsts.length < 2) return false;
        const jurNames = new Set(yearInsts.map(i => i._sourceJurName));
        return jurNames.size > 1;
      });

      // Bug-fix (May 8, 2026 — TF Tax Cert PDF NOTES showing "semi-annually"
      // when two annual jurisdictions sit on the same parcel, e.g. Fulton
      // County + City of College Park):
      //
      // The previous routing gate `hasSplitPattern = hasMultiJurisdictionYear`
      // could not distinguish:
      //
      //   (a) Town(1st) + County(2nd) for tax year 2026
      //       → ONE bill, paid in TWO halves to two collectors
      //       → genuine semi-annual split
      //
      //   (b) Fulton County(Annual) + City of College Park(Annual) for 2024
      //       → TWO independent annual bills, two collectors
      //       → NOT a split; should produce per-jurisdiction notes
      //
      // Both shapes have ≥2 installments per year from ≥2 jurisdictions,
      // so the previous gate routed both into `generateSplitInstallmentNotes`,
      // which emits "Taxes are collected semi-annually" — wrong for (b).
      //
      // The discriminator that reliably separates the two: in case (a) the
      // installment names within the year are DIFFERENT ("1st" vs "2nd",
      // "Q1" vs "Q2"). In case (b) the installment names are IDENTICAL
      // ("Annual" + "Annual"). Two parallel bills are never named for
      // sequential halves of one bill.
      //
      // Preserved by this gate:
      //   • Real semi-annual splits (1st + 2nd, First + Second) → still split
      //   • Real quarterly splits (Q1..Q4 across jurs)            → still split
      //   • Single-jurisdiction multi-installment annual flows    → never enter this branch (jurs.length < 2)
      //   • Single-jurisdiction quarterly                          → never enter this branch
      const hasDistinctInstallmentNamesInSharedYear = Object.values(byYear).some(yearInsts => {
        if (yearInsts.length < 2) return false;
        const jurNames = new Set(yearInsts.map(i => i._sourceJurName));
        if (jurNames.size < 2) return false;
        const instNames = new Set(
          yearInsts.map(i => ((i.installment_name || '') + '').trim().toLowerCase())
        );
        // Drop empty strings — they shouldn't promote a "different names" verdict.
        instNames.delete('');
        return instNames.size > 1;
      });

      // ─────────────────────────────────────────────────────────────
      // Phase A.7.5 (Feb 2026) — Parallel-cycle + frequency-mismatch
      // detectors. The earlier "distinct names in shared year" gate
      // alone misroutes Syracuse-style parallel quarterly orders
      // (City has Q1..Q4 AND County has Q1..Q4 — distinct-names gate
      // passes because {Q1..Q4} has 4 distinct names, but it's
      // semantically NOT a split). Likewise, mixed-frequency orders
      // (City quarterly + County semi-annual within the same year)
      // need the per-jurisdiction narrative path so each jurisdiction
      // gets its own frequency line.
      //
      // Mirrored on the Python side in
      // services/tax_notes_engine.py::compute_combined_notes.
      // ─────────────────────────────────────────────────────────────
      const hasOverlappingInstallments = Object.values(byYear).some(yearInsts => {
        if (yearInsts.length < 2) return false;
        // Build per-jur installment-name sets for this year.
        const perJur = new Map();
        for (const inst of yearInsts) {
          const jur = inst._sourceJurName || 'Unknown';
          const name = ((inst.installment_name || '') + '').trim().toLowerCase();
          if (!name) continue;
          if (!perJur.has(jur)) perJur.set(jur, new Set());
          perJur.get(jur).add(name);
        }
        if (perJur.size < 2) return false;
        const sets = Array.from(perJur.values());
        for (let i = 0; i < sets.length; i++) {
          for (let j = i + 1; j < sets.length; j++) {
            for (const name of sets[i]) {
              if (sets[j].has(name)) return true;
            }
          }
        }
        return false;
      });

      const hasMismatchedFrequencies = Object.values(byYear).some(yearInsts => {
        if (yearInsts.length < 2) return false;
        const perJurCounts = new Map();
        for (const inst of yearInsts) {
          const jur = inst._sourceJurName || 'Unknown';
          perJurCounts.set(jur, (perJurCounts.get(jur) || 0) + 1);
        }
        if (perJurCounts.size < 2) return false;
        const counts = new Set(perJurCounts.values());
        return counts.size > 1;
      });

      // TAXCERT-NOTES-RENDER-001 (Sep 2026) — split-vocabulary gate. Only
      // "1st/2nd" (semi-annual) or "Q1..Q4 / first..fourth / -1..-4"
      // (quarterly) names describe sequential parts of ONE bill. Anything
      // else sharing a year across jurisdictions ("Annual" + "Solid Waste",
      // TF-20260909-103743 / Product 533898) is parallel billing → the
      // per-jurisdiction narrative below. Without this gate the split
      // generator skipped every year and emitted only
      // "Taxes are collected semi-annually." Mirrored in
      // services/tax_notes_engine.py::_shared_years_use_split_vocabulary.
      const hasRecognizedSplitVocabulary = (() => {
        const maxInsts = Math.max(0, ...Object.values(byYear).map(y => y.length));
        const quarterly = maxInsts > 2;
        const tokens = quarterly
          ? ['1st', 'first', '2nd', 'second', '3rd', 'third', '4th', 'fourth', 'q1', 'q2', 'q3', 'q4']
          : ['1st', 'first', '2nd', 'second'];
        const ok = (inst) => {
          const n = ((inst.installment_name || '') + '').trim().toLowerCase();
          if (!n) return false;
          if (tokens.some(t => n.includes(t))) return true;
          return quarterly && /-[1-4]$/.test(n);
        };
        let checked = false;
        for (const yearInsts of Object.values(byYear)) {
          if (yearInsts.length < 2 || new Set(yearInsts.map(i => i._sourceJurName)).size < 2) continue;
          checked = true;
          if (!yearInsts.every(ok)) return false;
        }
        return checked;
      })();
      // TAXCERT-INSTALLMENT-PLAN-001 — an Installment Plan jurisdiction never
      // enters the semi-annual/quarterly split narrative ("1st/2nd Installment"
      // would otherwise satisfy the split vocabulary). Mirrored in
      // services/tax_notes_engine.py::compute_combined_notes.
      const hasInstallmentPlanJur = jurs.some(j =>
        ((j.collection_frequency || '') + '').toLowerCase() === 'installment_plan'
      );
      const hasSplitPattern =
        hasMultiJurisdictionYear &&
        hasDistinctInstallmentNamesInSharedYear &&
        !hasOverlappingInstallments &&
        !hasMismatchedFrequencies &&
        hasRecognizedSplitVocabulary &&
        !hasInstallmentPlanJur;

      if (hasSplitPattern) {
        // Determine if semi-annual or quarterly based on installment count per year
        const maxInstsPerYear = Math.max(...Object.values(byYear).map(y => y.length));
        if (maxInstsPerYear <= 2) {
          return generateSplitInstallmentNotes(jurs, byYear, orderStateCode);
        } else {
          return generateSplitQuarterlyNotes(jurs, byYear, orderStateCode);
        }
      }
    }
    
    const allNotes = jurs
      .map(jur => {
        const installments = jur.installments || [];
        if (installments.length === 0) return '';
        const engineNotes = generateTaxCertificationNotes(jur, installments, orderStateCode);
        const withSpecialty = appendSpecialtyNotes(engineNotes, installments);
        const notes = appendBreakdownNaNotes(withSpecialty, jur);
        if (jurs.length > 1 && notes) {
          // Phase A.7.5 (Feb 2026) — Edge Case #2 (a): use FULL
          // jurisdiction name UPPERCASED as the per-jurisdiction header
          // for parity with the Python pdf_service per-jur generator
          // (e.g. "CITY OF SYRACUSE:" / "ONONDAGA COUNTY:"). Previously
          // used Title Case which diverged from the PDF output.
          // Jul 2026 — TAX-CATEGORY-001: append the bill category so
          // duplicate collectors read "CITY OF UTICA (COUNTY):" etc.
          const cat = ((jur.tax_category || '') + '').trim();
          const name = (jur.jurisdiction_name || 'Unknown Jurisdiction').toUpperCase()
            + (cat ? ` (${cat.toUpperCase()})` : '');
          return `${name}:\n${notes}`;
        }
        return notes;
      })
      .filter(Boolean);
    return allNotes.join('\n\n');
    // MI-SPLIT-GUARD-2 (Aug 2026): orderStateCode MUST be a dependency.
    // With [] this closure captured the first-render value — '' while
    // orderData was still loading (direct /fulfillment URL) — so
    // isMichigan('') stayed false and MI orders kept routing into the
    // split branch (order TF-20260826-1527), and every state-aware
    // generator inside received '' too.
  }, [orderStateCode]);

  const propagateJurisdictions = useCallback((newJurisdictions) => {
    onJurisdictionsChange(newJurisdictions);
    if (onNotesChange) {
      onNotesChange(computeCombinedNotes(newJurisdictions));
    }
  }, [onJurisdictionsChange, onNotesChange, computeCombinedNotes]);

  // ── Stage 1: Backend notes-engine refinement ──────────────────────────
  // After jurisdictions settle (500ms debounce), call /api/tax-notes/preview
  // to get the backend-authoritative split notes. On success with a real
  // split-source result, update onNotesChange — this is the single source
  // of truth path for split cases. On any failure (network/5xx/timeout),
  // the local-engine output set by propagateJurisdictions above stays in
  // place. Zero-regression by design.
  useEffect(() => {
    if (!onNotesChange) return;
    if (!jurisdictions || jurisdictions.length < 2) return;

    const controller = new AbortController();
    const timerId = setTimeout(async () => {
      try {
        const { notes, source } = await fetchTaxNotesPreview(
          { jurisdictions, state: orderStateCode || '' },
          controller.signal
        );
        if (source === 'split_semiannual' || source === 'split_quarterly') {
          if (notes) onNotesChange(notes);
        }
      } catch (_err) {
        // Silent fallback — local engine output already applied synchronously.
      }
    }, 500);

    return () => {
      clearTimeout(timerId);
      controller.abort();
    };
  }, [jurisdictions, orderStateCode, onNotesChange]);

  // ── Jun 2026 — load-time notes emission (engine-drift healing) ──────────
  // onNotesChange previously fired ONLY from propagateJurisdictions (user
  // edits), so after a notes-engine update the parent panel kept the stale
  // stored auto_generated_notes (or '' from hydration) until the operator
  // happened to edit something — the Review Tax Certification PDF then
  // printed stale/blank notes while the UI showed corrected text
  // (user-reported on TF-20260611-0757). Emit whenever the computed
  // combined notes change so panel state always mirrors what the UI
  // displays; the pre-review manualSave flush persists it.
  const lastEmittedNotesRef = useRef(null);
  useEffect(() => {
    if (!onNotesChange) return;
    if (!jurisdictions || jurisdictions.length === 0) return;
    const combined = computeCombinedNotes(jurisdictions);
    // Never emit an empty string here — transitional states must not
    // clobber stored notes; propagateJurisdictions handles real clears.
    if (combined && combined !== lastEmittedNotesRef.current) {
      lastEmittedNotesRef.current = combined;
      onNotesChange(combined);
    }
  }, [jurisdictions, computeCombinedNotes, onNotesChange]);
  
  // Listen for file deletions from Files tab to keep source_evidence in sync
  useEffect(() => {
    const handleFileDeletedFromFilesTab = (event) => {
      const { fileId } = event.detail || {};
      if (!fileId) return;
      
      let modified = false;
      const updatedJurisdictions = jurisdictionsRef.current.map(jur => {
        const evidence = jur.source_evidence;
        if (!evidence || typeof evidence !== 'object') return jur;
        
        let evidenceModified = false;
        const newEvidence = { ...evidence };
        
        for (const [sourceType, fileList] of Object.entries(newEvidence)) {
          if (Array.isArray(fileList)) {
            const filtered = fileList.filter(f => f.id !== fileId);
            if (filtered.length !== fileList.length) {
              newEvidence[sourceType] = filtered;
              evidenceModified = true;
            }
          }
        }
        
        if (evidenceModified) {
          modified = true;
          return { ...jur, source_evidence: newEvidence };
        }
        return jur;
      });
      
      if (modified) {
        propagateJurisdictions(updatedJurisdictions);
      }
    };
    
    window.addEventListener('fileDeletedFromFilesTab', handleFileDeletedFromFilesTab);
    return () => window.removeEventListener('fileDeletedFromFilesTab', handleFileDeletedFromFilesTab);
  }, [propagateJurisdictions]);

  // Deferred Add Jurisdiction: fires after parcel switch loads P1's data
  // Placed AFTER propagateJurisdictions definition to avoid TDZ
  useEffect(() => {
    if (!pendingAddJurisdictionRef.current || activeParcelIndex !== 0) return;

    // Wait for parent sync cycle to complete:
    // activeParcelIndex changed → parent useEffect runs syncParcelToFlat() →
    // setCertificateData with P1's data → re-render → jurisdictionsRef.current updates to P1's jurisdictions
    const timer = setTimeout(() => {
      if (!pendingAddJurisdictionRef.current) return;
      pendingAddJurisdictionRef.current = false;

      const newJurisdiction = createEmptyJurisdiction();
      const currentJurisdictions = jurisdictionsRef.current;
      const newJurisdictions = [...currentJurisdictions, newJurisdiction];
      propagateJurisdictions(newJurisdictions);

      setTimeout(() => {
        setSelectedIndex(newJurisdictions.length - 1);
        setActiveTab('contact');
        if (listRef.current) {
          const item = listRef.current.querySelector(`[data-jurisdiction-index="${newJurisdictions.length - 1}"]`);
          item?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 50);
    }, 150);

    return () => clearTimeout(timer);
  }, [activeParcelIndex, createEmptyJurisdiction, propagateJurisdictions]);

  // Handle adding new jurisdiction
  const handleAddJurisdiction = useCallback(() => {
    if (activeParcelIndex !== 0 && onParcelSwitch) {
      // ON P2+: Switch to P1 first, then add jurisdiction after P1's data loads.
      // The deferred useEffect (pendingAddJurisdictionRef) handles step 2.
      pendingAddJurisdictionRef.current = true;
      onParcelSwitch(0);
    } else {
      // ON P1: Add jurisdiction directly to the current (P1) flat data
      const newJurisdiction = createEmptyJurisdiction();
      const newJurisdictions = [...jurisdictionsRef.current, newJurisdiction];
      propagateJurisdictions(newJurisdictions);

      setTimeout(() => {
        setSelectedIndex(newJurisdictions.length - 1);
        setActiveTab('contact');
        if (listRef.current) {
          const item = listRef.current.querySelector(`[data-jurisdiction-index="${newJurisdictions.length - 1}"]`);
          item?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 50);
    }
  }, [propagateJurisdictions, createEmptyJurisdiction, activeParcelIndex, onParcelSwitch]);
  
  // Handle removing jurisdiction
  const handleRemoveJurisdiction = useCallback((index) => {
    if (jurisdictionsRef.current.length <= 1) return;
    
    const newJurisdictions = jurisdictionsRef.current.filter((_, i) => i !== index);
    propagateJurisdictions(newJurisdictions);
    
    // Adjust selected index if needed
    setSelectedIndex(prev => {
      if (prev >= newJurisdictions.length) {
        return Math.max(0, newJurisdictions.length - 1);
      } else if (prev > index) {
        return prev - 1;
      }
      return prev;
    });
    // Reset to P1 when removing a jurisdiction
    if (activeParcelIndex !== 0 && onParcelSwitch) {
      onParcelSwitch(0);
    }
  }, [propagateJurisdictions, activeParcelIndex, onParcelSwitch]);
  
  // Handle resetting a jurisdiction (clear all data, keep the card)
  const handleResetJurisdiction = useCallback((index) => {
    const existing = jurisdictionsRef.current[index];
    if (!existing) return;
    
    const blank = createEmptyJurisdiction();
    // Preserve the id so the slot is kept
    const resetJurisdiction = { ...blank, id: existing.id };
    
    const newJurisdictions = jurisdictionsRef.current.map((j, i) =>
      i === index ? resetJurisdiction : j
    );
    propagateJurisdictions(newJurisdictions);

    // Also reset parcel details (APN, assessed values, exemptions, TAV toggle)
    if (onResetParcelDetails) onResetParcelDetails();

    setActiveTab('contact');
    toast.success('Jurisdiction data has been reset');
  }, [propagateJurisdictions, createEmptyJurisdiction, onResetParcelDetails]);
  
  // Handle updating jurisdiction field — stable via ref
  const handleUpdateField = useCallback((field, value) => {
    const currentIdx = selectedIndexRef.current;
    const newJurisdictions = jurisdictionsRef.current.map((j, i) => 
      i === currentIdx ? { ...j, [field]: value } : j
    );
    propagateJurisdictions(newJurisdictions);
  }, [propagateJurisdictions]);
  
  // Handle batch updating multiple jurisdiction fields at once (avoids stale closure issues)
  const handleBatchUpdate = useCallback((updates) => {
    const currentIdx = selectedIndexRef.current;
    const newJurisdictions = jurisdictionsRef.current.map((j, i) => 
      i === currentIdx ? { ...j, ...updates } : j
    );
    propagateJurisdictions(newJurisdictions);
  }, [propagateJurisdictions]);
  
  // Handle adding tax record (installment)
  const handleAddInstallment = useCallback(() => {
    const currentIdx = selectedIndexRef.current;
    const currentJurisdiction = jurisdictionsRef.current[currentIdx];
    const currentInstallments = currentJurisdiction?.installments || [];
    const freq = currentJurisdiction?.collection_frequency || 'annually';
    // Auto-assign installment name based on frequency and existing count
    const LABELS = { annually: ['Annual'], semi_annually: ['1st Half', '2nd Half'], quarterly: ['Q1', 'Q2', 'Q3', 'Q4'] };
    const labels = LABELS[freq] || ['Annual'];
    const nextIndex = currentInstallments.length;
    let autoName = labels[nextIndex % labels.length] || labels[labels.length - 1];
    if (freq === 'installment_plan') {
      // TAXCERT-INSTALLMENT-PLAN-001 — next ordinal after the highest existing
      // "Nth Installment" row (specialty rows ignored), capped at 6th.
      const maxOrd = Math.max(0, ...currentInstallments.map(i => installmentPlanOrdinal(i.installment_name)));
      if (maxOrd >= INSTALLMENT_PLAN_MAX) {
        toast.error(`Installment supports up to ${INSTALLMENT_PLAN_MAX} installments.`);
        return;
      }
      autoName = installmentPlanLabel(maxOrd + 1);
    }
    const newInstallment = {
      id: crypto.randomUUID(),
      tax_year: '',
      installment_name: autoName,
      installment_index: currentInstallments.length + 1,
      due_date: null,
      base_tax: null,
      amount_due: null,
      discount_penalty: 0,
      paid_amount: null,
      notes: '',
      pay_status: 'Due',
      paid_date: null,
      balance_amount: null,
      dlq_date: null,
      mailing_issue_date: null
    };
    const newJurisdictions = jurisdictionsRef.current.map((j, i) =>
      i === currentIdx ? { ...j, installments: [...currentInstallments, newInstallment] } : j
    );
    propagateJurisdictions(newJurisdictions);
  }, [propagateJurisdictions]);
  
  // Get row state for selected jurisdiction
  const currentRowState = getRowState(`nested_${clampedSelectedIndex}`);

  // Auto-redirect to Contact Info if on a disabled tab when jurisdiction type is cleared
  // Bypass: if jurisdiction is inherited from another parcel, don't redirect
  useEffect(() => {
    if (!selectedJurisdiction?.jurisdiction_type && !isJurisdictionInherited && (activeTab === 'workflow' || activeTab === 'tax')) {
      setActiveTab('contact');
    }
  }, [selectedJurisdiction?.jurisdiction_type, activeTab, isJurisdictionInherited]);

  // JCR-HISTORY-001 — one read of this order's correction requests, rolled up per jurisdiction card
  const { corrections: orderCorrections } = useOrderCorrections(orderId);
  const correctionGroups = useMemo(() => groupCorrectionsByJurisdiction(orderCorrections, jurisdictions), [orderCorrections, jurisdictions]);

  return (
    <>
    <div className="min-h-[600px] border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden" data-testid="jurisdiction-manager">
      <PanelGroup direction="horizontal">
        {/* Left Panel - Jurisdiction List (~15% width) */}
        <Panel defaultSize={15} minSize={12} maxSize={20}>
          <div className="tcp-rail min-h-[600px] h-full flex flex-col border-r border-slate-200 bg-slate-50/60">
            {/* Header with Add Button — TAX-SIDEBAR-PREMIUM-001 (Oct 2026) compact premium rail */}
            <div className="p-3 border-b border-slate-200 bg-white sticky top-0 z-10">
              <div className="flex items-center justify-between mb-2.5">
                <h3 className="text-[15px] font-semibold text-slate-900">Jurisdictions</h3>
                <Badge variant="secondary" className="text-xs rounded-md bg-slate-100 text-slate-700">
                  {jurisdictions.length}
                </Badge>
              </div>
              <button
                type="button"
                onClick={handleAddJurisdiction}
                className="tcp-rail-action w-full h-12 py-1.5 flex items-center gap-2.5 px-3 rounded-xl border border-slate-200 bg-white text-indigo-600 text-[13px] font-semibold leading-tight hover:border-indigo-200 hover:text-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={readOnly}
                data-testid="add-jurisdiction-btn"
              >
                <Plus className="w-4 h-4 shrink-0" />
                <span className="flex-1 text-left line-clamp-2">Add Jurisdiction</span>
                <ChevronRight className="tcp-chev w-4 h-4 shrink-0 text-slate-400" />
              </button>
              {/* PROCESSOR-JURISDICTION-REQUEST-001 — single entry point for processors / tax TLs */}
              {!readOnly && orderId && holdsAnyRole(currentUser, CAN_REQUEST_ROLES) && (
                <RequestMissingJurisdictionAction
                  variant="rail"
                  orderData={{ id: orderId, order_number: workflowActions?.orderData?.order_number || null, property_state: orderStateCode, property_county: county }}
                  defaultType={selectedJurisdiction?.jurisdiction_type || 'county'}
                  defaultName={selectedJurisdiction?.jurisdiction_type && requiresSubJurisdiction(selectedJurisdiction.jurisdiction_type, orderStateCode) ? '' : (county || '')}
                  parentCountyName={county || ''}
                  onRefreshList={selectedJurisdiction?.jurisdiction_type ? () => onJurisdictionTypeChange?.(selectedJurisdiction.jurisdiction_type, selectedJurisdiction, county) : null}
                  testId="jm-request-missing-jurisdiction"
                  corrections={correctionGroups.unmatched}
                />
              )}
            </div>
            
            {/* Jurisdiction List */}
            <div className="p-2.5 space-y-2 flex-1 flex flex-col" ref={listRef}>
                {jurisdictions.map((jurisdiction, index) => {
                  const jurMailMatch = mailAwayRequests.find(
                    r => r.jurisdiction_name === jurisdiction.jurisdiction_name && r.jurisdiction_type === jurisdiction.jurisdiction_type
                  ) || null;
                  const status = getJurisdictionStatus(jurisdiction, isExternalPortalClient, {
                    allParcelsData, parcelIndex: activeParcelIndex
                  }, faxDelivery, jurMailMatch, orderFiles, mailAwayRequests, isNewParcel);
                  const isSelected = index === clampedSelectedIndex;
                  const jurName = toTitleCase(jurisdiction.jurisdiction_name) || `Jurisdiction #${index + 1}`;
                  const selectCard = () => {
                    setSelectedIndex(index);
                    // Reset to P1 when switching jurisdictions to avoid stale parcel context
                    if (activeParcelIndex !== 0 && onParcelSwitch) {
                      onParcelSwitch(0);
                    }
                  };
                  return (
                    <div
                      key={jurisdiction.id || index}
                      data-jurisdiction-index={index}
                      data-testid={`jurisdiction-list-item-${index}`}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isSelected}
                      className={`tcp-jur-card group relative flex flex-col gap-1 px-2.5 py-2 rounded-xl border cursor-pointer bg-white ${isSelected ? 'is-selected' : 'border-slate-200 shadow-sm hover:border-slate-300'}`}
                      onClick={selectCard}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectCard(); } }}
                    >
                      {/* Jurisdiction Name + Status Indicator */}
                      <span className="flex items-start gap-1.5 font-semibold text-[13px] text-slate-900 leading-snug">
                        <Landmark className={`w-3.5 h-3.5 shrink-0 mt-[3px] ${isSelected ? 'text-indigo-600' : 'text-slate-400'}`} />
                        <span className="flex-1 min-w-0 truncate" title={jurName}>{jurName}</span>
                        <span className="shrink-0 mt-0.5">
                          {status.status === 'ready' ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          ) : status.status === 'error' ? (
                            <AlertTriangle className="w-4 h-4 text-rose-500" />
                          ) : (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                          )}
                        </span>
                      </span>
                      {/* Feb 19, 2026 — Specialty parenthetical for UI parity
                          with PDF (e.g. "(SB-1)") under jurisdiction name. */}
                      {(() => {
                        const specialtyLabel = jurisdictionSpecialtyLabel(jurisdiction.installments || []);
                        if (!specialtyLabel) return null;
                        return (
                          <span
                            className="text-xs text-amber-700 font-medium -mt-0.5 pl-5"
                            data-testid={`jurisdiction-specialty-label-${index}`}
                            title="Specialty installment(s) on this jurisdiction"
                          >
                            {specialtyLabel}
                          </span>
                        );
                      })()}
                      
                      {/* Status pill */}
                      <div className="pl-5">
                        <span className={`inline-block whitespace-nowrap text-[11px] font-semibold cursor-pointer rounded-md px-2 py-0.5 ${
                          status.status === 'ready' ? 'text-emerald-700 bg-emerald-50' :
                          status.status === 'error' ? 'text-rose-700 bg-rose-50' :
                          'text-amber-700 bg-amber-50'
                        }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedIndex(index);
                            // Reset to P1 when switching jurisdictions
                            if (activeParcelIndex !== 0 && onParcelSwitch) {
                              onParcelSwitch(0);
                            }
                            // Navigate to relevant tab based on first error
                            const firstError = (status.errors || [])[0] || '';
                            if (firstError.includes('Source')) {
                              setActiveTab('workflow');
                            } else {
                              setActiveTab('tax');
                            }
                          }}
                          title={status.errors?.length ? status.errors.join('\n') : ''}
                        >
                          {status.label}
                        </span>
                      </div>

                      {/* Type · installments — one secondary line with trailing chevron */}
                      <div className="flex items-center gap-1 pl-5 text-xs text-slate-500">
                        <span className="flex-1 min-w-0 truncate">
                          {jurisdiction.jurisdiction_type && (
                            <span className={`font-medium ${getTypeBadgeColor(jurisdiction.jurisdiction_type).split(' ').filter((c) => c.startsWith('text-')).join(' ') || 'text-slate-600'}`}>
                              {/* Jun 2026 — LA display mapping: legacy
                                  `county` renders as "Parish" for LA orders */}
                              {getJurisdictionTypeLabelForState(jurisdiction.jurisdiction_type, orderStateCode)}
                            </span>
                          )}
                          {/* Quick Info - Hide installment count for external portal */}
                          {!isExternalPortalClient && jurisdiction.installments?.length > 0 && (
                            <>{jurisdiction.jurisdiction_type ? ' · ' : ''}{jurisdiction.installments.length} installment{jurisdiction.installments.length !== 1 ? 's' : ''}</>
                          )}
                        </span>
                        <ChevronRight className={`tcp-chev w-3.5 h-3.5 shrink-0 ${isSelected ? 'text-indigo-500' : 'text-slate-400'} ${!readOnly && jurisdictions.length > 1 ? 'group-hover:opacity-0' : ''}`} />
                      </div>

                      {/* JCR-HISTORY-001 — correction requests for this jurisdiction, collapsed under the card */}
                      <CorrectionHistorySection
                        items={correctionGroups.byIndex.get(index) || []}
                        jurisdiction={jurisdiction}
                        orderData={{ id: orderId, order_number: workflowActions?.orderData?.order_number || null }}
                        index={index}
                      />

                      {/* Jul 2026 — "Send Mail-Away from here" badge removed
                          from sidebar cards (UAT feedback: keep the left
                          panel navigation/status-only; the Mail-Away
                          workflow lives in the MLC & Workflow tab). */}

                      {/* Remove button (on hover) */}
                      {!readOnly && jurisdictions.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute bottom-1 right-1.5 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity text-slate-400 hover:text-red-500 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveJurisdiction(index);
                          }}
                          data-testid={`remove-jurisdiction-${index}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  );
                })}
                
                {/* Empty State */}
                {jurisdictions.length === 0 && <JurisdictionSidebarEmpty />}
                {jurisdictions.length > 0 && <JurisdictionSidebarArt />}
            </div>
          </div>
        </Panel>

        {/* Resize Handle */}
        <PanelResizeHandle className="w-1.5 bg-slate-100 hover:bg-slate-200 transition-colors flex items-center justify-center">
          <GripVertical className="w-3 h-3 text-slate-400" />
        </PanelResizeHandle>

        {/* Right Panel - Detail View */}
        <Panel>
          <div className="min-h-[600px] flex flex-col bg-white shadow-[inset_4px_0_8px_-4px_rgba(0,0,0,0.04)]">
            {selectedJurisdiction ? (
              <>
                {/* Detail Header */}
                <div className="tcp-detail-gradient h-14 px-5 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center shrink-0">
                      <Landmark className="w-[18px] h-[18px] text-white" />
                    </div>
                    <h2 className="text-lg font-semibold text-white truncate" data-testid="selected-jurisdiction-title">
                      {toTitleCase(selectedJurisdiction.jurisdiction_name) || `Jurisdiction #${clampedSelectedIndex + 1}`}
                    </h2>
                    {selectedJurisdiction.jurisdiction_type && (
                      <Badge 
                        variant="secondary" 
                        className={`text-[11px] rounded-full bg-white/15 text-white border-white/25 hover:bg-white/15`}
                      >
                        {getJurisdictionTypeLabelForState(selectedJurisdiction.jurisdiction_type, orderStateCode)}
                      </Badge>
                    )}
                    {totalParcels > 1 && (
                      <span className="text-xs bg-slate-800/40 text-white/90 border border-white/15 rounded-full px-2.5 py-0.5" data-testid="parcel-context-badge">
                        Parcel {activeParcelIndex + 1} of {totalParcels}{activeParcelAPN ? ` \u2014 APN: ${activeParcelAPN}` : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const selMailMatch = mailAwayRequests.find(
                        r => r.jurisdiction_name === selectedJurisdiction.jurisdiction_name && r.jurisdiction_type === selectedJurisdiction.jurisdiction_type
                      ) || null;
                      const status = getJurisdictionStatus(selectedJurisdiction, isExternalPortalClient, {
                        allParcelsData, parcelIndex: activeParcelIndex
                      }, faxDelivery, selMailMatch, orderFiles, mailAwayRequests, isNewParcel);
                      return (
                        <Badge 
                          variant="outline"
                          className={`text-xs font-semibold cursor-pointer rounded-lg px-2.5 py-1 shadow-sm ${
                            status.status === 'ready' 
                              ? 'border-white/30 bg-white text-emerald-600' 
                              : status.status === 'error'
                              ? 'border-rose-200 bg-rose-50 text-rose-700'
                              : 'border-amber-200 bg-amber-50 text-amber-700'
                          }`}
                          onClick={() => {
                            const firstError = (status.errors || [])[0] || '';
                            if (firstError.includes('Source')) setActiveTab('workflow');
                            else setActiveTab('tax');
                          }}
                          title={status.errors?.length ? status.errors.join('\n') : ''}
                        >
                          {status.status === 'ready' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                          {status.status !== 'ready' && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {status.label}
                        </Badge>
                      );
                    })()}
                    
                    {!readOnly && (
                      <>
                        <button
                          onClick={() => setResetConfirmIndex(clampedSelectedIndex)}
                          className="h-8 px-2.5 rounded-lg text-xs font-medium text-white/85 hover:text-white hover:bg-white/15 transition-colors duration-150"
                          title="Reset all data for this jurisdiction"
                          data-testid="reset-jurisdiction-btn"
                        >
                          <RotateCcw className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                          Reset
                        </button>
                        <button
                          onClick={() => jurisdictions.length > 1 && setDeleteConfirmIndex(clampedSelectedIndex)}
                          disabled={jurisdictions.length <= 1}
                          className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors duration-150 ${
                            jurisdictions.length <= 1
                              ? 'text-white/30 cursor-not-allowed'
                              : 'text-rose-200 hover:text-white hover:bg-rose-500/40'
                          }`}
                          title={jurisdictions.length <= 1 ? 'At least one jurisdiction is required' : 'Delete this jurisdiction'}
                          data-testid="delete-jurisdiction-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Tabbed Content */}
                <Tabs value={activeTab} onValueChange={(val) => {
                  // Guard: block MLC & Tax Summary tabs until jurisdiction type is selected
                  // Bypass: if jurisdiction setup is inherited from another parcel, allow access
                  if (!selectedJurisdiction?.jurisdiction_type && !isJurisdictionInherited && (val === 'workflow' || val === 'tax')) {
                    toast.info('Select Jurisdiction Type to enable this section', { id: 'tab-guard', duration: 3000 });
                    return;
                  }
                  setActiveTab(val);
                }} className="flex-1 flex flex-col">
                  <TabsList className="tcp-detail-gradient px-5 pt-1 pb-0 border-b border-white/10 justify-start h-auto gap-1 rounded-none">
                    <TabsTrigger 
                      value="contact" 
                      className="tcp-tab text-white/75 data-[state=active]:text-white data-[state=active]:bg-white/15 data-[state=active]:shadow-none data-[state=active]:border-b-0 rounded-t-lg rounded-b-none pb-2.5 pt-2 px-3.5 text-sm font-medium hover:text-white"
                      data-testid="tab-contact"
                    >
                      <Phone className="w-4 h-4 mr-2 text-white" />
                      Contact Info
                      {isJurisdictionInherited && (
                        <Badge variant="secondary" className="ml-1.5 text-[10px] bg-emerald-500/30 text-emerald-100 border-0 py-0 px-1.5" data-testid="badge-contact-inherited">Inherited</Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger 
                      value="workflow" 
                      className={`tcp-tab text-white/75 data-[state=active]:text-white data-[state=active]:bg-white/15 data-[state=active]:shadow-none data-[state=active]:border-b-0 rounded-t-lg rounded-b-none pb-2.5 pt-2 px-3.5 text-sm font-medium hover:text-white ${!selectedJurisdiction?.jurisdiction_type && !isJurisdictionInherited ? 'opacity-40 cursor-not-allowed' : ''}`}
                      data-testid="tab-workflow"
                      title={!selectedJurisdiction?.jurisdiction_type && !isJurisdictionInherited ? 'Select Jurisdiction Type to enable this section' : undefined}
                    >
                      <GitBranch className="w-4 h-4 mr-2 text-white" />
                      MLC & Workflow
                      {isJurisdictionInherited && (
                        <Badge variant="secondary" className="ml-1.5 text-[10px] bg-emerald-500/30 text-emerald-100 border-0 py-0 px-1.5" data-testid="badge-workflow-inherited">Inherited</Badge>
                      )}
                    </TabsTrigger>
                    {!isExternalPortalClient && (
                    <TabsTrigger 
                      value="tax" 
                      className={`tcp-tab text-white/75 data-[state=active]:text-white data-[state=active]:bg-white/15 data-[state=active]:shadow-none data-[state=active]:border-b-0 rounded-t-lg rounded-b-none pb-2.5 pt-2 px-3.5 text-sm font-medium hover:text-white ${!selectedJurisdiction?.jurisdiction_type && !isJurisdictionInherited ? 'opacity-40 cursor-not-allowed' : ''}`}
                      data-testid="tab-tax"
                      title={!selectedJurisdiction?.jurisdiction_type && !isJurisdictionInherited ? 'Select Jurisdiction Type to enable this section' : undefined}
                    >
                      <DollarSign className="w-4 h-4 mr-2 text-white" />
                      Tax Summary
                      {selectedJurisdiction.installments?.length > 0 && (
                        <Badge variant="secondary" className="ml-2 text-xs bg-white/20 text-white border-0">
                          {selectedJurisdiction.installments.length}
                        </Badge>
                      )}
                    </TabsTrigger>
                    )}

                    {/* Parcel Switcher Pills — multi-parcel orders only */}
                    {totalParcels > 1 && onParcelSwitch && (
                      <>
                        <div className="h-6 w-px bg-white/25 mx-1 self-center" />
                        {Array.from({ length: totalParcels }, (_, i) => {
                          const tooltipText = `Switch to Parcel ${i + 1}${allParcelsData[i]?.parcel_apn ? ` — APN: ${allParcelsData[i].parcel_apn}` : ''}`;
                          return (
                            <button
                              key={`parcel-pill-${i}`}
                              type="button"
                              onClick={() => handleParcelPillClick(i)}
                              className={`px-2.5 py-1 rounded-md text-xs font-bold transition-all ${
                                i === activeParcelIndex
                                  ? 'bg-white text-indigo-700 shadow-sm'
                                  : 'text-white/70 hover:text-white hover:bg-white/15'
                              }`}
                              title={tooltipText}
                              data-testid={`parcel-pill-${i}`}
                            >
                              P{i + 1}
                            </button>
                          );
                        })}
                        {onAddParcelWithCopy && !readOnly && (
                          <button
                            type="button"
                            onClick={handleAddParcelPill}
                            disabled={!canAddParcel}
                            className={`px-1.5 py-1 rounded-md transition-all ${
                              !canAddParcel
                                ? 'text-white/20 cursor-not-allowed opacity-50'
                                : 'text-white/50 hover:text-white hover:bg-white/15'
                            }`}
                            title="Add parcel with jurisdiction setup pre-copied"
                            data-testid="parcel-pill-add"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </>
                    )}
                  </TabsList>

                  <div className="flex-1 bg-slate-50/40">
                    <TabsContent value="contact" className="tcp-tab-fade p-4 m-0">
                      <ContactInfoTab
                        jurisdiction={selectedJurisdiction}
                        onUpdate={handleUpdateField}
                        onBatchUpdate={handleBatchUpdate}
                        readOnly={readOnly}
                        county={county}
                        rowState={currentRowState}
                        onJurisdictionTypeChange={onJurisdictionTypeChange}
                        onClearJurisdiction={onClearJurisdiction}
                        onAcceptFuzzySuggestion={onAcceptFuzzySuggestion}
                        canEditContactFields={canEditContactFields}
                        canLinkCollector={canLinkCollector}
                        isExternalPortalClient={isExternalPortalClient}
                        isJurisdictionInherited={isJurisdictionInherited}
                        stateCode={orderStateCode}
                        allJurisdictions={jurisdictions}
                        orderId={orderId}
                        orderNumber={workflowActions?.orderData?.order_number || null}
                        currentUser={currentUser}
                      />
                    </TabsContent>
                    <TabsContent value="workflow" className="tcp-tab-fade p-4 m-0">
                      <MLCWorkflowTab
                        jurisdiction={selectedJurisdiction}
                        onUpdate={handleUpdateField}
                        readOnly={readOnly}
                        orderId={orderId}
                        canEditMLCFields={canEditMLCFields}
                        allParcelsData={allParcelsData}
                        activeParcelIndex={activeParcelIndex}
                        mailAwayMatch={(() => {
                          // Robust matcher (May 8, 2026): prefer canonical
                          // jurisdiction_id match; fall back to normalized
                          // name+type match (lowercased, trimmed, with the
                          // trailing " County"/" Parish"/" Borough"/" City"
                          // suffix stripped) to handle records where one
                          // side stores "New Castle" and the other stores
                          // "New Castle County".
                          const sel = selectedJurisdiction;
                          if (!sel || !Array.isArray(mailAwayRequests) || mailAwayRequests.length === 0) return null;
                          const normName = (s) => String(s || '')
                            .toLowerCase()
                            .trim()
                            .replace(/\s+(county|parish|borough|city)$/i, '')
                            .trim();
                          const normType = (t) => String(t || '').toLowerCase().trim();
                          // 1. Try jurisdiction_id (most reliable)
                          if (sel.jurisdiction_id) {
                            const byId = mailAwayRequests.find(r => r.jurisdiction_id && r.jurisdiction_id === sel.jurisdiction_id);
                            if (byId) return byId;
                          }
                          // 2. Try normalized name+type
                          const selName = normName(sel.jurisdiction_name);
                          const selType = normType(sel.jurisdiction_type);
                          const byName = mailAwayRequests.find(r =>
                            normName(r.jurisdiction_name) === selName &&
                            normType(r.jurisdiction_type) === selType
                          );
                          if (byName) return byName;
                          // 3. Last resort — normalized name only (handles type drift like "county" vs "")
                          return mailAwayRequests.find(r => normName(r.jurisdiction_name) === selName) || null;
                        })()}
                        orderFiles={orderFiles}
                      />
                    </TabsContent>
                    {!isExternalPortalClient && (
                    <TabsContent value="tax" className="tcp-tab-fade p-4 m-0">
                      {/* ── Inline Parcel Details + Assessed Values ── */}
                      {/* Primary parcel-entry area for Internal Portal (replaces top sections) */}
                      {parcelFieldValues && onParcelFieldChange && (
                        <div className="tcp-card mb-4" data-testid="compact-parcel-details">
                          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                            <span className="flex items-center gap-2.5 text-[13.5px] font-semibold text-slate-900" data-testid="compact-parcel-label">
                              <span className="tcp-tile tcp-tile-indigo" style={{ width: 30, height: 30 }}><MapPin className="w-4 h-4" /></span>
                              {totalParcels > 1 ? `Parcel ${activeParcelIndex + 1} Details` : 'Parcel Details'}
                            </span>
                            <div className="flex items-center gap-4 flex-wrap">
                              {/* New Parcel toggle (May 17, 2026) — when ON,
                                  appends a standardised note to the PDF and
                                  relaxes amount validations for this parcel.
                                  The two optional override fields appear
                                  below the parcel-details row when active. */}
                              {onNewParcelToggle && (
                                <div className="flex items-center gap-2">
                                  <span className="text-[11px] font-medium text-slate-600">New Parcel</span>
                                  <button
                                    type="button"
                                    role="switch"
                                    onClick={onNewParcelToggle}
                                    disabled={readOnly}
                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 ${
                                      isNewParcel ? 'bg-emerald-600' : 'bg-gray-300'
                                    } ${readOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                    data-testid="new-parcel-toggle"
                                    aria-pressed={isNewParcel}
                                    aria-checked={isNewParcel}
                                    aria-label="Toggle New Parcel mode"
                                  >
                                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                      isNewParcel ? 'translate-x-[18px]' : 'translate-x-[3px]'
                                    }`} />
                                  </button>
                                </div>
                              )}
                              {/* TAV Breakdown toggle */}
                              {onTavBreakdownToggle && (
                                <div className="flex items-center gap-2">
                                  <span className="text-[11px] font-medium text-slate-600">TAV breakdowns N/A</span>
                                  <button
                                    type="button"
                                    role="switch"
                                    onClick={onTavBreakdownToggle}
                                    disabled={readOnly}
                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 ${
                                      parcelFieldValues.use_assessed_breakdown !== false ? 'bg-indigo-600' : 'bg-gray-300'
                                    } ${readOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                    data-testid="tav-breakdowns-na-toggle"
                                    aria-checked={parcelFieldValues.use_assessed_breakdown !== false}
                                    aria-label="Toggle TAV breakdowns N/A"
                                  >
                                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                      parcelFieldValues.use_assessed_breakdown !== false ? 'translate-x-[18px]' : 'translate-x-[3px]'
                                    }`} />
                                  </button>
                                </div>
                              )}
                              {/* Jul 2026 — AV-NA-001: Assessed Value Not Available.
                                  Independent of Fully Exempt and TAV breakdowns N/A.
                                  Values stay grayed (restored if toggled back off). */}
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] font-medium text-slate-600">Assessed Value N/A</span>
                                <button
                                  type="button"
                                  role="switch"
                                  onClick={() => {
                                    const turningOn = !parcelFieldValues.assessed_value_na;
                                    onParcelFieldChange('assessed_value_na', turningOn);
                                    if (!turningOn) {
                                      onParcelFieldChange('assessed_value_na_reason', null);
                                      onParcelFieldChange('assessed_value_na_explanation', '');
                                    }
                                  }}
                                  disabled={readOnly}
                                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 ${
                                    parcelFieldValues.assessed_value_na ? 'bg-amber-600' : 'bg-gray-300'
                                  } ${readOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                  data-testid="assessed-value-na-toggle"
                                  aria-checked={!!parcelFieldValues.assessed_value_na}
                                  aria-label="Toggle Assessed Value Not Available"
                                >
                                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                    parcelFieldValues.assessed_value_na ? 'translate-x-[18px]' : 'translate-x-[3px]'
                                  }`} />
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Jul 2026 — AV-NA-001: inline reason (required) +
                              explanation (required for "Other"). */}
                          {parcelFieldValues.assessed_value_na && (
                            <div className="mb-3 p-3 rounded-md border border-amber-300 bg-amber-50/70" data-testid="assessed-value-na-panel">
                              <p className="text-[11px] text-amber-800 leading-snug mb-2">
                                <span className="font-semibold">Assessed Value Not Available is on.</span>{' '}
                                The certificate will show Land / Building / Total Assessed Value as N/A (never $0.00) and the reason below will be added to the notes.
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-[11px] font-medium text-slate-600 mb-0.5 block">
                                    Reason <span className="text-red-500">*</span>
                                  </label>
                                  <select
                                    value={parcelFieldValues.assessed_value_na_reason || ''}
                                    onChange={(e) => onParcelFieldChange('assessed_value_na_reason', e.target.value || null)}
                                    disabled={readOnly}
                                    data-testid="assessed-value-na-reason"
                                    className="w-full h-8 px-2 text-sm border border-amber-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                                  >
                                    <option value="">Select a reason…</option>
                                    <option value="not_provided_by_assessor">Not provided by assessor</option>
                                    <option value="assessor_records_unavailable">Assessor records unavailable</option>
                                    <option value="exempt_property">Exempt property</option>
                                    <option value="other">Other</option>
                                  </select>
                                </div>
                                {parcelFieldValues.assessed_value_na_reason === 'other' && (
                                  <div>
                                    <label className="text-[11px] font-medium text-slate-600 mb-0.5 block">
                                      Explanation <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={parcelFieldValues.assessed_value_na_explanation || ''}
                                      onChange={(e) => onParcelFieldChange('assessed_value_na_explanation', e.target.value)}
                                      disabled={readOnly}
                                      data-testid="assessed-value-na-explanation"
                                      maxLength={200}
                                      placeholder="Short explanation (required)"
                                      className="w-full h-8 px-2 text-sm border border-amber-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* New Parcel — info banner + optional overrides
                              (May 17, 2026). Shown only when toggled on.
                              First Bill Year / First Bill Due Date override
                              the first installment row's values for the
                              PDF note; leave blank to inherit from the row.
                          */}
                          {isNewParcel && onNewParcelOverrideChange && (
                            <div className="mb-3 p-3 rounded-md border border-emerald-300 bg-emerald-50/70" data-testid="new-parcel-overrides">
                              <p className="text-[11px] text-emerald-800 leading-snug mb-2">
                                <span className="font-semibold">New Parcel mode is on.</span>{' '}
                                A standardised note will be appended to this parcel&apos;s PDF.
                                Amount validations are relaxed, and &quot;Refer Note&quot; is available as a Pay Status.
                                Set the optional First Bill Year override below to lock the wording — otherwise the first installment row&apos;s Tax Year and Due Date are used.
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-[11px] font-medium text-slate-600 mb-0.5 block">
                                    First Bill Year (optional)
                                  </label>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={4}
                                    value={newParcelFirstYear || ''}
                                    onChange={(e) => onNewParcelOverrideChange('new_parcel_first_year', e.target.value.replace(/[^\d]/g, ''))}
                                    placeholder="e.g. 2026"
                                    className="w-full h-8 px-2.5 text-sm border rounded-md focus:ring-1 bg-white border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                                    disabled={readOnly}
                                    data-testid="new-parcel-first-year"
                                  />
                                </div>
                                {/* May 17, 2026 — "First Bill Due Date
                                    (Optional)" input removed per ops
                                    refinement. The PDF Note Preview now
                                    falls back to the first installment row's
                                    Due Date for the <DATE> placeholder; an
                                    extra override input here was creating
                                    confusion in the New Parcel workflow. */}
                              </div>
                              {/* Live preview of the auto-generated note that
                                  will be appended to this parcel's PDF block.
                                  Pulls from the first installment row in the
                                  active parcel's jurisdictions[] when
                                  overrides are blank. */}
                              <div className="mt-3 p-2.5 rounded bg-white border border-emerald-200">
                                <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-1">
                                  PDF Note Preview
                                </p>
                                <p className="text-[12px] text-slate-800 leading-relaxed" data-testid="new-parcel-note-preview">
                                  {generateNewParcelNote({
                                    is_new_parcel: true,
                                    new_parcel_first_year: newParcelFirstYear,
                                    new_parcel_first_due_date: newParcelFirstDueDate,
                                    jurisdictions: jurisdictions || []
                                  })}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Row 1: APN + Assessed Values */}
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                            {/* Parcel/APN */}
                            <div>
                              <label className="text-[11px] font-medium text-slate-600 mb-0.5 block">Parcel/APN *</label>
                              <input
                                type="text"
                                value={parcelFieldValues.parcel_apns || ''}
                                onChange={(e) => onParcelFieldChange('parcel_apns', e.target.value)}
                                className={`w-full h-8 px-2.5 text-sm border rounded-md focus:ring-1 bg-white ${
                                  parcelValidationErrors.parcel_apns
                                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                    : 'border-slate-300 focus:border-indigo-500 focus:ring-indigo-500'
                                }`}
                                placeholder="Enter APN"
                                disabled={readOnly}
                                data-testid="compact-parcel-apn"
                              />
                              {parcelValidationErrors.parcel_apns && (
                                <p className="text-red-500 text-[10px] mt-0.5">{parcelValidationErrors.parcel_apns}</p>
                              )}
                            </div>
                            {/* Land Value */}
                            <div>
                              <label className="text-[11px] font-medium text-slate-600 mb-0.5 block">Land Value</label>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={parcelFieldValues.assessed_land_value || ''}
                                  onChange={(e) => onParcelFieldChange('assessed_land_value', e.target.value)}
                                  className={`w-full h-8 pl-5 pr-2 text-sm text-right border rounded-md focus:ring-1 ${
                                    parcelValidationErrors.assessed_land_value
                                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-white'
                                      : parcelFieldValues.use_assessed_breakdown === false
                                        ? 'bg-gray-100 text-gray-400 border-slate-200'
                                        : 'bg-white border-slate-300 focus:border-indigo-500 focus:ring-indigo-500'
                                  }`}
                                  placeholder="0"
                                  disabled={readOnly || parcelFieldValues.use_assessed_breakdown === false || parcelFieldValues.assessed_value_na === true}
                                  data-testid="compact-land-value"
                                />
                              </div>
                              {parcelValidationErrors.assessed_land_value && (
                                <p className="text-red-500 text-[10px] mt-0.5">{parcelValidationErrors.assessed_land_value}</p>
                              )}
                            </div>
                            {/* Building Value */}
                            <div>
                              <label className="text-[11px] font-medium text-slate-600 mb-0.5 block">Building Value</label>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={parcelFieldValues.assessed_building_value || ''}
                                  onChange={(e) => onParcelFieldChange('assessed_building_value', e.target.value)}
                                  className={`w-full h-8 pl-5 pr-2 text-sm text-right border rounded-md focus:ring-1 ${
                                    parcelValidationErrors.assessed_building_value
                                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-white'
                                      : parcelFieldValues.use_assessed_breakdown === false
                                        ? 'bg-gray-100 text-gray-400 border-slate-200'
                                        : 'bg-white border-slate-300 focus:border-indigo-500 focus:ring-indigo-500'
                                  }`}
                                  placeholder="0"
                                  disabled={readOnly || parcelFieldValues.use_assessed_breakdown === false || parcelFieldValues.assessed_value_na === true}
                                  data-testid="compact-building-value"
                                />
                              </div>
                              {parcelValidationErrors.assessed_building_value && (
                                <p className="text-red-500 text-[10px] mt-0.5">{parcelValidationErrors.assessed_building_value}</p>
                              )}
                            </div>
                            {/* Total Assessed Value */}
                            <div>
                              {/* May 17, 2026 — Total Assessed asterisk
                                  suppressed in New Parcel mode (Land /
                                  Building / Total Assessed are not
                                  required for brand-new parcels). */}
                              <label className="text-[11px] font-medium text-slate-600 mb-0.5 block">
                                Total Assessed{(isNewParcel || parcelFieldValues.assessed_value_na) ? '' : ' *'}
                              </label>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={parcelFieldValues.assessed_total_value || ''}
                                  onChange={(e) => onParcelFieldChange('assessed_total_value', e.target.value)}
                                  className={`w-full h-8 pl-5 pr-2 text-sm text-right border rounded-md focus:ring-1 ${
                                    parcelValidationErrors.assessed_total_value
                                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-white'
                                      : parcelFieldValues.use_assessed_breakdown !== false
                                        ? 'bg-gray-100 text-gray-400 border-slate-200'
                                        : 'bg-white border-slate-300 focus:border-indigo-500 focus:ring-indigo-500'
                                  }`}
                                  placeholder="0"
                                  disabled={readOnly || parcelFieldValues.use_assessed_breakdown !== false || parcelFieldValues.assessed_value_na === true}
                                  data-testid="compact-total-assessed"
                                />
                                {parcelFieldValues.use_assessed_breakdown !== false && (
                                  <Lock className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                                )}
                              </div>
                              {parcelValidationErrors.assessed_total_value && (
                                <p className="text-red-500 text-[10px] mt-0.5">{parcelValidationErrors.assessed_total_value}</p>
                              )}
                            </div>
                          </div>

                          {/* Row 2: Fully Exempt Toggle + Exemption Type pills + Exemption Value */}
                          {/* Fully Exempt Toggle */}
                          <div className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200 bg-slate-50/50" data-testid="fully-exempt-toggle-row">
                            <div className="flex items-center gap-2">
                              <ShieldCheck className={`w-4 h-4 ${jurisdictions[selectedIndex]?.fully_exempt ? 'text-emerald-600' : 'text-slate-400'}`} />
                              <div>
                                <label htmlFor={`fully-exempt-${selectedIndex}`} className="text-xs font-semibold text-slate-700 cursor-pointer">
                                  Fully Exempt
                                </label>
                                {jurisdictions[selectedIndex]?.fully_exempt && (
                                  <p className="text-[10px] text-emerald-600 font-medium mt-0.5" data-testid="fully-exempt-helper">
                                    Tax amounts for this jurisdiction will be zero
                                  </p>
                                )}
                              </div>
                            </div>
                            <Switch
                              id={`fully-exempt-${selectedIndex}`}
                              checked={!!jurisdictions[selectedIndex]?.fully_exempt}
                              onCheckedChange={(checked) => onFullyExemptChange && onFullyExemptChange(checked, selectedIndex)}
                              disabled={readOnly}
                              data-testid="fully-exempt-switch"
                            />
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-[1fr_200px] gap-3">
                            {/* Exemption Type pills */}
                            <div>
                              <label className="text-[11px] font-medium text-slate-600 mb-1 block">
                                Exemption Type(s) {jurisdictions[selectedIndex]?.fully_exempt ? <span className="text-red-500">* (required)</span> : '*'}
                              </label>
                              <div className="flex items-center gap-1.5 flex-nowrap" data-testid="compact-exemption-pills">
                                {COMPACT_EXEMPTION_PILLS.map((pill) => {
                                  const jurExemptions = jurisdictions[selectedIndex]?.exemption_types || [];
                                  const selected = jurExemptions.includes(pill.stored);
                                  const isNoneSelected = jurExemptions.includes(COMPACT_NA_OPTION);
                                  const isFullyExempt = !!jurisdictions[selectedIndex]?.fully_exempt;
                                  // When fully exempt, disable N/A pill; when N/A selected, disable other pills
                                  const disabled = readOnly || (isFullyExempt && pill.isNone) || (isNoneSelected && !pill.isNone);
                                  return (
                                    <button
                                      key={pill.stored}
                                      type="button"
                                      disabled={disabled}
                                      onClick={() => onExemptionTypeChange && onExemptionTypeChange(pill, selectedIndex)}
                                      data-testid={`compact-exemption-pill-${pill.label.toLowerCase().replace(/\s+/g, '-')}`}
                                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-150 whitespace-nowrap ${
                                        selected
                                          ? pill.isNone
                                            ? 'bg-slate-600 text-white border-slate-600'
                                            : 'bg-indigo-700 text-white border-indigo-700'
                                          : disabled
                                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
                                            : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400 hover:text-indigo-700'
                                      }`}
                                    >
                                      {pill.label}
                                      {selected && <Check className="w-2.5 h-2.5" />}
                                    </button>
                                  );
                                })}

                                {/* "+ More" popover trigger */}
                                {(() => {
                                  const jurExemptions = jurisdictions[selectedIndex]?.exemption_types || [];
                                  const moreCount = MORE_EXEMPTION_OPTIONS.filter(
                                    opt => jurExemptions.includes(opt.stored)
                                  ).length;
                                  const isNoneSelected = jurExemptions.includes(COMPACT_NA_OPTION);
                                  return (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <button
                                          type="button"
                                          disabled={readOnly || isNoneSelected}
                                          data-testid="compact-exemption-more-btn"
                                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-150 whitespace-nowrap ${
                                            isNoneSelected
                                              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
                                              : moreCount > 0
                                                ? 'bg-indigo-50 text-indigo-700 border-indigo-300 hover:bg-indigo-100'
                                                : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400 hover:text-indigo-700'
                                          }`}
                                        >
                                          + More{moreCount > 0 && ` (${moreCount})`}
                                          <ChevronDown className="w-3 h-3" />
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent align="start" className="w-72 p-3">
                                        <p className="text-xs font-semibold text-slate-700 mb-2">Additional Exemptions</p>
                                        <div className="space-y-1 max-h-56 overflow-y-auto">
                                          {MORE_EXEMPTION_OPTIONS.map((opt) => {
                                            const checked = (jurisdictions[selectedIndex]?.exemption_types || []).includes(opt.stored);
                                            return (
                                              <label
                                                key={opt.stored}
                                                className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer transition-colors"
                                                data-testid={`more-exemption-${opt.stored.toLowerCase().replace(/[\s\/()]+/g, '-')}`}
                                              >
                                                <input
                                                  type="checkbox"
                                                  checked={checked}
                                                  onChange={() => onExemptionTypeChange && onExemptionTypeChange(opt, selectedIndex)}
                                                  className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                />
                                                <span className="text-xs text-slate-700">{opt.label}</span>
                                              </label>
                                            );
                                          })}
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  );
                                })()}
                              </div>

                              {/* Selected "+ More" chips row */}
                              {(() => {
                                const moreSelected = MORE_EXEMPTION_OPTIONS.filter(
                                  opt => (jurisdictions[selectedIndex]?.exemption_types || []).includes(opt.stored)
                                );
                                if (moreSelected.length === 0) return null;
                                return (
                                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid="more-exemption-chips">
                                    <span className="text-[10px] text-slate-400 font-medium mr-0.5">Selected:</span>
                                    {moreSelected.map((opt) => (
                                      <span
                                        key={opt.stored}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"
                                      >
                                        {opt.label}
                                        {!readOnly && (
                                          <button
                                            type="button"
                                            onClick={() => onExemptionTypeChange && onExemptionTypeChange(opt, selectedIndex)}
                                            className="ml-0.5 text-indigo-400 hover:text-indigo-700"
                                            data-testid={`remove-more-exemption-${opt.stored.toLowerCase().replace(/[\s\/()]+/g, '-')}`}
                                          >
                                            &times;
                                          </button>
                                        )}
                                      </span>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                            {/* Exemption Value */}
                            <div>
                              <label className="text-[11px] font-medium text-slate-600 mb-0.5 block">Exemption Value ($)</label>
                              <div className="relative">
                                <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-xs ${
                                  (jurisdictions[selectedIndex]?.exemption_types || []).includes(COMPACT_NA_OPTION) ? 'text-gray-300' : 'text-slate-400'
                                }`}>$</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={(jurisdictions[selectedIndex]?.exemption_types || []).includes(COMPACT_NA_OPTION) ? '0.00' : (jurisdictions[selectedIndex]?.exemption_value || '')}
                                  onChange={(e) => onExemptionValueChange && onExemptionValueChange(e.target.value, selectedIndex)}
                                  className={`w-full h-8 pl-5 pr-2 text-sm text-right border border-slate-300 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 ${
                                    (jurisdictions[selectedIndex]?.exemption_types || []).includes(COMPACT_NA_OPTION) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white'
                                  }`}
                                  placeholder="0.00"
                                  disabled={readOnly || (jurisdictions[selectedIndex]?.exemption_types || []).includes(COMPACT_NA_OPTION)}
                                  data-testid="compact-exemption-value"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Other exemption description (only when "Other" is selected) */}
                          {(jurisdictions[selectedIndex]?.exemption_types || []).includes('Other') && (
                            <div className="mt-2">
                              <label className="text-[11px] font-medium text-slate-600 mb-0.5 block">Enter exemption name *</label>
                              <input
                                type="text"
                                value={jurisdictions[selectedIndex]?.exemption_other || ''}
                                onChange={(e) => onExemptionOtherChange && onExemptionOtherChange(e.target.value, selectedIndex)}
                                onBlur={(e) => {
                                  // May 5, 2026 — Title-Case normalize on blur (HOMESTEAD -> Homestead).
                                  // Only fires the change if the normalized value differs, to avoid
                                  // spurious dirty state on first focus-out.
                                  const normalized = titleCaseExemption(e.target.value);
                                  if (normalized !== (e.target.value || '') && onExemptionOtherChange) {
                                    onExemptionOtherChange(normalized, selectedIndex);
                                  }
                                }}
                                className="w-full h-8 px-2.5 text-sm border border-slate-300 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-white"
                                placeholder="Enter exemption name"
                                disabled={readOnly}
                                data-testid="compact-exemption-other"
                              />
                            </div>
                          )}
                        </div>
                      )}
                      <TaxSummaryTab
                        jurisdiction={selectedJurisdiction}
                        onUpdate={handleUpdateField}
                        onBatchUpdate={handleBatchUpdate}
                        onAddInstallment={handleAddInstallment}
                        readOnly={readOnly}
                        orderFinancialSummary={orderFinancialSummary}
                        stateCode={orderStateCode}
                        isNewParcel={isNewParcel}
                        validationAttempted={validationAttempted}
                      />
                    </TabsContent>
                    )}
                  </div>
                </Tabs>
                
                {/* Workflow Actions Section - Only show when workflow actions are provided */}
                {workflowActions && (
                  <div className="px-4 pb-4 bg-slate-50/40">
                    <WorkflowActionsSection
                      selectedStatus={workflowActions.selectedStatus}
                      onStatusSelect={workflowActions.onStatusSelect}
                      onComplete={workflowActions.onComplete}
                      completeError={workflowActions.completeError}
                      mlsGuard={workflowActions.mlsGuard}
                      dualTax={workflowActions.dualTax}
                      onSubmitStatus={(status, reason, selectedChipIds) => {
                        // Enrich the callback with the currently selected jurisdiction context
                        const currentJur = jurisdictionsRef.current[selectedIndexRef.current];
                        const jurisdictionContext = currentJur ? {
                          jurisdiction_name: currentJur.jurisdiction_name || '',
                          jurisdiction_type: currentJur.jurisdiction_type || '',
                          jurisdiction_id: currentJur.jurisdiction_id || currentJur.id || ''
                        } : null;
                        workflowActions.onSubmitStatus(status, reason, jurisdictionContext, selectedChipIds);
                      }}
                      isReadOnly={readOnly}
                      activeTab={activeTab}
                      hasJurisdiction={jurisdictions.some(j => !!j.jurisdiction_type) || isJurisdictionInherited}
                      hasParcelAPN={workflowActions.hasParcelAPN}
                      isTaxProduct={workflowActions.isTaxProduct}
                      isSubmitting={workflowActions.isSubmitting}
                      activeMailAway={workflowActions.activeMailAway}
                      canOverridePending={workflowActions.canOverridePending}
                      reason={workflowActions.reason}
                      onReasonChange={workflowActions.onReasonChange}
                      statusNotes={workflowActions.statusNotes || []}
                      editMode={workflowActions.editMode || false}
                      setEditMode={workflowActions.setEditMode}
                      taxSheetReview={workflowActions.taxSheetReview}
                      isExternalPortalClient={isExternalPortalClient}
                      orderData={workflowActions.orderData}
                      userRole={workflowActions.userRole}
                      liveParcels={(() => {
                        // TAX-EMAIL-CONFIRM-001: only parcels carrying the selected jurisdiction
                        const scoped = parcelsForJurisdiction(allParcelsData, jurisdictions[selectedIndex]
                          ? { jurisdiction_id: jurisdictions[selectedIndex].jurisdiction_id || jurisdictions[selectedIndex].id,
                              jurisdiction_name: jurisdictions[selectedIndex].jurisdiction_name,
                              jurisdiction_type: jurisdictions[selectedIndex].jurisdiction_type }
                          : null);
                        return scoped.length ? scoped : (allParcelsData.length ? allParcelsData : parcelFieldValues ? [parcelFieldValues] : null);
                      })()}
                      emailJurisdiction={jurisdictions[selectedIndex] ? {
                        jurisdiction_id: jurisdictions[selectedIndex].jurisdiction_id || jurisdictions[selectedIndex].id,
                        jurisdiction_name: jurisdictions[selectedIndex].jurisdiction_name || '',
                        jurisdiction_type: jurisdictions[selectedIndex].jurisdiction_type || ''
                      } : null}
                      reviewReadiness={(() => {
                        const missing = [];
                        const warnings = [];
                        
                        // Use the shared isParcelDataComplete function (reads live data for active parcel)
                        const parcelsToCheck = allParcelsData.length > 0 ? allParcelsData : [{ jurisdictions }];
                        parcelsToCheck.forEach((parcel, pIdx) => {
                          const pLabel = parcelsToCheck.length > 1 ? `Parcel ${pIdx + 1}` : '';
                          const pPrefix = pLabel ? `${pLabel}: ` : '';
                          
                          // Shared validation: APN, Assessed Value, Exemption, Jurisdiction Type, Tax Record
                          if (isParcelDataComplete && !isParcelDataComplete(pIdx)) {
                            const parcelMissing = getParcelMissingFields ? getParcelMissingFields(pIdx) : [];
                            parcelMissing.forEach(field => missing.push(`${pPrefix}${field}`));
                          }
                          
                          // Full jurisdiction-level validation (same logic as Review Tax Sheet click)
                          // This ensures reviewReadiness and the actual review click never disagree.
                          const parcelJurs = pIdx === activeParcelIndex ? jurisdictions : (parcel.jurisdictions || []);
                          // May 17, 2026 — per-parcel New Parcel flag. For the
                          // active parcel use the live prop (state-of-truth);
                          // for other parcels read from the persisted parcel
                          // object so unsaved toggles on the active parcel
                          // don't leak across parcels.
                          const parcelIsNewParcel = pIdx === activeParcelIndex
                            ? !!isNewParcel
                            : !!parcel.is_new_parcel;
                          const reviewResult = validateForReview(parcelJurs, isExternalPortalClient, {
                            allParcelsData, parcelIndex: pIdx
                          }, faxDelivery, mailAwayRequests, orderFiles, orderStateCode, parcelIsNewParcel);
                          if (!reviewResult.valid) {
                            reviewResult.errors.forEach(err => {
                              const prefixed = pLabel ? `${pLabel}: ${err}` : err;
                              // Avoid duplicate messages
                              if (!missing.includes(prefixed)) missing.push(prefixed);
                            });
                          }
                          // G8: Collect entity breakdown mismatch warnings (non-blocking)
                          if (reviewResult.warnings && reviewResult.warnings.length > 0) {
                            reviewResult.warnings.forEach(w => {
                              const prefixed = pLabel ? `${pLabel}: ${w}` : w;
                              if (!warnings.includes(prefixed)) warnings.push(prefixed);
                            });
                          }
                        });
                        return { ready: missing.length === 0, missing, warnings };
                      })()}
                      onReviewTaxSheet={() => {
                        // Jun 2026 — a Review attempt unlocks every row's
                        // required-field inline errors (see InstallmentRow
                        // validationAttempted gating).
                        setValidationAttempted(true);
                        // Pre-flight: Use shared validation function for ALL parcels
                        // This reads from certificateData for active parcel (live data),
                        // preventing stale-data inconsistencies with the Add Parcel gate.
                        const preflight = [];
                        
                        allParcelsData.forEach((p, pIdx) => {
                          const pPrefix = allParcelsData.length > 1 ? `Parcel ${pIdx + 1}: ` : '';
                          if (isParcelDataComplete && !isParcelDataComplete(pIdx)) {
                            const parcelMissing = getParcelMissingFields ? getParcelMissingFields(pIdx) : [];
                            parcelMissing.forEach(field => {
                              preflight.push(`${pPrefix}${field} is required before review.`);
                            });
                          }
                        });
                        
                        if (preflight.length > 0) {
                          toast.error(preflight.join('\n'), { 
                            duration: 8000, 
                            id: 'review-preflight',
                            description: 'Please fill in required property fields before reviewing.'
                          });
                          return;
                        }
                        
                        // Validate ALL parcels' jurisdictions before allowing review
                        const allErrors = [];
                        // May 17, 2026 — include is_new_parcel in the
                        // single-parcel fallback so the New Parcel mode
                        // gate works even when allParcelsData is empty.
                        const parcelsToValidate = allParcelsData.length > 0
                          ? allParcelsData
                          : [{ jurisdictions, is_new_parcel: isNewParcel }];
                        parcelsToValidate.forEach((parcel, pIdx) => {
                          const pLabel = parcelsToValidate.length > 1 ? `Parcel ${pIdx + 1}` : '';
                          const parcelJurs = pIdx === activeParcelIndex ? jurisdictions : (parcel.jurisdictions || []);
                          // May 17, 2026 — per-parcel New Parcel flag (see
                          // sibling call-site for rationale).
                          const parcelIsNewParcel = pIdx === activeParcelIndex
                            ? !!isNewParcel
                            : !!parcel.is_new_parcel;
                          const result = validateForReview(parcelJurs, isExternalPortalClient, {
                            allParcelsData, parcelIndex: pIdx
                          }, faxDelivery, mailAwayRequests, orderFiles, orderStateCode, parcelIsNewParcel);
                          if (!result.valid) {
                            result.errors.forEach(err => {
                              allErrors.push(pLabel ? `${pLabel}: ${err}` : err);
                            });
                          }
                        });
                        
                        if (allErrors.length > 0) {
                          // Auto-navigate to the first jurisdiction with a Source issue
                          const firstSourceIssueIdx = jurisdictions.findIndex(j => !j.source);
                          if (firstSourceIssueIdx !== -1) {
                            setSelectedIndex(firstSourceIssueIdx);
                            setActiveTab('workflow');
                            // Reset to P1 when auto-navigating to a source issue
                            if (activeParcelIndex !== 0 && onParcelSwitch) {
                              onParcelSwitch(0);
                            }
                          }
                          
                          // Show a single consolidated toast with all errors
                          const message = allErrors.length <= 3
                            ? allErrors.join('\n')
                            : allErrors.slice(0, 3).join('\n') + `\n… and ${allErrors.length - 3} more issue${allErrors.length - 3 > 1 ? 's' : ''}`;
                          toast.error(message, { 
                            duration: 8000, 
                            id: 'review-validation',
                            description: 'Please complete all parcels and jurisdictions before reviewing.'
                          });
                          return;
                        }
                        workflowActions.onReviewTaxSheet();
                      }}
                      isAdmin={workflowActions.isAdmin}
                      jurisdictionData={{
                        // Active jurisdiction takes priority — workflowActions.jurisdictionData is stale (hardcoded to jurisdictions[0])
                        county: selectedJurisdiction?.jurisdiction_name || workflowActions.jurisdictionData?.county || '',
                        jurisdiction_name: selectedJurisdiction?.jurisdiction_name || workflowActions.jurisdictionData?.jurisdiction_name || '',
                        tax_collector_phone: selectedJurisdiction?.tax_collector_phone || workflowActions.jurisdictionData?.tax_collector_phone || '',
                        phone_number: selectedJurisdiction?.tax_collector_phone || workflowActions.jurisdictionData?.phone_number || '',
                        primary_phone: selectedJurisdiction?.tax_collector_phone || workflowActions.jurisdictionData?.primary_phone || '',
                        fax: selectedJurisdiction?.tax_collector_fax || workflowActions.jurisdictionData?.fax || '',
                        tax_collector_fax: selectedJurisdiction?.tax_collector_fax || workflowActions.jurisdictionData?.fax || '',
                        email: selectedJurisdiction?.tax_collector_email || workflowActions.jurisdictionData?.email || '',
                        website: selectedJurisdiction?.assessor_website || workflowActions.jurisdictionData?.website || '',
                        // Order-level data from workflowActions
                        parcel_apn: workflowActions.jurisdictionData?.parcel_apn || '',
                        owner_name: workflowActions.jurisdictionData?.owner_name || '',
                        tax_year: selectedJurisdiction?.installments?.[0]?.tax_year || workflowActions.jurisdictionData?.tax_year || ''
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              /* Empty State */
              <JurisdictionEmptyState onAdd={handleAddJurisdiction} readOnly={readOnly} />
            )}
          </div>
        </Panel>
      </PanelGroup>
    </div>
    
    {/* Delete Jurisdiction Confirmation */}
    <AlertDialog open={deleteConfirmIndex !== null} onOpenChange={(open) => !open && setDeleteConfirmIndex(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Jurisdiction</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <span>Are you sure you want to delete this jurisdiction?</span>
            <span className="block text-sm mt-2">This will permanently remove:</span>
            <ul className="list-disc list-inside text-sm text-slate-500 ml-2">
              <li>Contact Info</li>
              <li>Tax Summary</li>
              <li>Notes</li>
            </ul>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-jurisdiction-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="delete-jurisdiction-confirm"
            onClick={() => {
              if (deleteConfirmIndex !== null) {
                handleRemoveJurisdiction(deleteConfirmIndex);
              }
              setDeleteConfirmIndex(null);
            }}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    
    {/* Reset Jurisdiction Confirmation */}
    <AlertDialog open={resetConfirmIndex !== null} onOpenChange={(open) => !open && setResetConfirmIndex(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset Jurisdiction</AlertDialogTitle>
          <AlertDialogDescription>
            Reset all entered data for this jurisdiction? The jurisdiction card will remain but all fields will be cleared.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="reset-jurisdiction-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="reset-jurisdiction-confirm"
            onClick={() => {
              if (resetConfirmIndex !== null) {
                handleResetJurisdiction(resetConfirmIndex);
              }
              setResetConfirmIndex(null);
            }}
          >
            Reset
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
  );
};

export default JurisdictionManager;

// Feb 2026 P0 (FULLY-EXEMPT-MULTI-YEAR-NOTE) — Named export so the
// notes-engine logic can be exercised by Jest without rendering the
// (9k-line) React component. No behaviour change.
export { generateTaxCertificationNotes };
